import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import type { StudentSimulatorAppView } from "../../shared/domain/index.js";

type MachineKey = "CLIENT01" | "SRV01";

type NativeCommandResult = {
  type: "SIMULATOR_COMMAND_RESULT";
  requestId: string;
  ok: boolean;
  status: number;
  body?: unknown;
};

type NativeSimulatorAction = {
  id: string;
  appId: string;
  appName: string;
  commandType: string;
  capability: string;
  label: string;
  description: string;
  machine: MachineKey | "both";
  payload: Record<string, unknown>;
};

type ShellPhase = "locked" | "signIn" | "desktop";

type NativeSimulatorDesktopProps = {
  activeMachine: MachineKey;
  attemptId: string;
  initialShellPhase?: ShellPhase;
  simulatorApps: StudentSimulatorAppView[];
  ticketId: string;
  onCommandMessage?: (message: string) => void;
  onCommandComplete?: (result: NativeCommandResult, action: NativeSimulatorAction) => void;
};

type AppMeta = {
  icon: string;
  accent: string;
  windowTitle: string;
  kind: "client" | "server" | "admin" | "security" | "utility";
};

type NativeAppCatalogEntry = {
  appId: string;
  name: string;
  family: "Client" | "Server" | "Security" | "M365" | "Utility" | "Network" | "Identity" | "Policy" | "Filesystem" | "Audit";
  machine: MachineKey | "both";
  source: "legacy-js" | "fase10";
  aliases: string[];
};

type DesktopContextMenu = {
  x: number;
  y: number;
} | null;

type TrayFlyout = "quick" | "calendar" | null;

type ElevatedPrompt = {
  appId: string;
  appName: string;
} | null;

type NativeFsNode = {
  id?: string;
  name: string;
  type: "folder" | "file";
  parentId?: string | null;
  content?: string;
  hidden?: boolean;
  modifiedAt?: string;
  shareName?: string;
  shared?: boolean;
};

type NativeFilesystemState = {
  rootId?: string;
  nodes?: Record<string, NativeFsNode>;
};

type NativeNetworkDrive = {
  id?: string;
  letter?: string;
  path?: string;
};

type NativeNetworkAdapter = {
  id?: string;
  adapterId?: string;
  name?: string;
  adapterName?: string;
  enabled?: boolean;
  dhcp?: boolean;
  ipv4?: string;
  ipAddress?: string;
  subnet?: string;
  subnetMask?: string;
  gateway?: string;
  dns?: string[];
  dnsServers?: string[];
  description?: string;
  physicalAddress?: string;
  macAddress?: string;
  type?: string;
};

type NativeSimulatorState = {
  filesystem?: NativeFilesystemState;
  machines?: Partial<Record<MachineKey, NativeSimulatorState>>;
  network?: {
    adapters?: NativeNetworkAdapter[];
    clientDns?: string;
    drives?: NativeNetworkDrive[];
  };
};

type NativeRunAction = (action: NativeSimulatorAction) => Promise<NativeCommandResult>;

declare global {
  interface Window {
    fdBridge?: {
      dispatchV2Command?: (
        appId: string,
        commandType: string,
        capability: string,
        payload: Record<string, unknown>
      ) => Promise<NativeCommandResult>;
    };
    __fdAttemptId?: string;
  }
}

const nativeActions: NativeSimulatorAction[] = [
  {
    id: "network-dns",
    appId: "netSettings",
    appName: "Network & Internet Settings",
    commandType: "NETWORK_SET_DNS_SERVER",
    capability: "network.dnsClient.write",
    label: "Interne DNS instellen",
    description: "Zet CLIENT01 op de interne DNS baseline voor domain join tickets.",
    machine: "CLIENT01",
    payload: {
      adapterName: "Ethernet0",
      dnsServer: "10.50.10.10",
      controllerIp: "10.50.10.10",
      domainName: "advanced-ti.local",
      reachable: true
    }
  },
  {
    id: "domain-join",
    appId: "sysdm",
    appName: "System Properties",
    commandType: "SYSTEM_JOIN_DOMAIN",
    capability: "domain.membership.write",
    label: "Join advanced-ti.local",
    description: "Registreer de client als domeinlid met bereikbare domain controller.",
    machine: "CLIENT01",
    payload: {
      domainName: "advanced-ti.local",
      controllerIp: "10.50.10.10",
      reachable: true
    }
  },
  {
    id: "dhcp-scope",
    appId: "dhcpManager",
    appName: "DHCP Manager",
    commandType: "DHCP_CREATE_SCOPE",
    capability: "dhcp.scope.write",
    label: "DHCP scope Balie-152",
    description: "Maak de scope aan die in de smoke-tickettargets wordt verwacht.",
    machine: "SRV01",
    payload: {
      scopeName: "Balie-152",
      startIp: "10.50.20.100",
      endIp: "10.50.20.180",
      subnetMask: "255.255.255.0",
      router: "10.50.20.1",
      dnsServer: "10.50.20.10",
      domainName: "advanced-ti.local"
    }
  },
  {
    id: "dhcp-options",
    appId: "dhcpManager",
    appName: "DHCP Manager",
    commandType: "DHCP_SET_OPTION",
    capability: "dhcp.option.write",
    label: "DHCP opties opslaan",
    description: "Leg router, DNS en domain-name vast op de scope.",
    machine: "SRV01",
    payload: {
      scopeName: "Balie-152",
      router: "10.50.20.1",
      dnsServer: "10.50.20.10",
      domainName: "advanced-ti.local"
    }
  },
  {
    id: "dhcp-reservation",
    appId: "dhcpManager",
    appName: "DHCP Manager",
    commandType: "DHCP_ADD_RESERVATION",
    capability: "dhcp.reservation.write",
    label: "DHCP reservering WS-152",
    description: "Voeg een vaste reservering toe voor de balieclient.",
    machine: "SRV01",
    payload: {
      scopeName: "Balie-152",
      name: "WS-152",
      reservationName: "WS-152",
      ipAddress: "10.50.20.152",
      macAddress: "00-15-5D-15-20-52"
    }
  },
  {
    id: "gpo-create",
    appId: "gpmc",
    appName: "Group Policy Management",
    commandType: "GPO_CREATE",
    capability: "policy.gpo.write",
    label: "GPO FD-Hardening-154",
    description: "Maak de hardening-GPO aan voor de GPMC smoke-flow.",
    machine: "SRV01",
    payload: { gpoName: "FD-Hardening-154", wmiFilter: "native-smoke" }
  },
  {
    id: "explorer-folder",
    appId: "explorer",
    appName: "File Explorer",
    commandType: "FILE_CREATE_FOLDER",
    capability: "file.folder.write",
    label: "Map Intake-161 maken",
    description: "Maak de intake-map aan in de native filesystem flow.",
    machine: "both",
    payload: { parentPath: "C:\\Servicedesk", name: "Intake-161", path: "C:\\Servicedesk\\Intake-161" }
  },
  {
    id: "explorer-share",
    appId: "explorer",
    appName: "File Explorer",
    commandType: "SHARE_CREATE",
    capability: "file.share.write",
    label: "Share Intake-161",
    description: "Publiceer de map als share met basisrechten.",
    machine: "both",
    payload: {
      path: "C:\\Servicedesk\\Intake-161",
      shareName: "Intake-161",
      permissions: [{ principal: "IntakeTeam", permission: "modify", type: "allow" }]
    }
  },
  {
    id: "explorer-acl",
    appId: "explorer",
    appName: "File Explorer",
    commandType: "FILE_SET_PERMISSION",
    capability: "file.permission.write",
    label: "ACL IntakeTeam",
    description: "Zet NTFS modify-rechten voor IntakeTeam.",
    machine: "both",
    payload: { path: "C:\\Servicedesk\\Intake-161", principal: "IntakeTeam", permission: "modify", type: "allow" }
  },
  {
    id: "aduc-user",
    appId: "aduc",
    appName: "Active Directory Users and Computers",
    commandType: "SERVER_CREATE_DOMAIN_USER",
    capability: "domainUsers.write",
    label: "Gebruiker selina.166",
    description: "Maak de domeingebruiker voor Ticket 166 aan.",
    machine: "SRV01",
    payload: { username: "selina.166", displayName: "Selina Ticket 166", samAccountName: "selina.166", ouName: "Users" }
  },
  {
    id: "dns-zone",
    appId: "dns-manager",
    appName: "DNS Manager",
    commandType: "SERVER_DNS_CREATE_ZONE",
    capability: "dns.zone.write",
    label: "DNS zone adv166.local",
    description: "Maak de forward lookup zone aan.",
    machine: "SRV01",
    payload: { zoneName: "adv166.local", zoneType: "primary" }
  },
  {
    id: "dns-record",
    appId: "dns-manager",
    appName: "DNS Manager",
    commandType: "SERVER_DNS_ADD_RECORD",
    capability: "dns.record.write",
    label: "A-record ws166",
    description: "Voeg het A-record toe voor de AD/DNS-integratiecheck.",
    machine: "SRV01",
    payload: { zoneName: "adv166.local", recordName: "ws166", recordType: "A", value: "10.50.166.20" }
  },
  {
    id: "server-role",
    appId: "server-manager",
    appName: "Server Manager",
    commandType: "SERVER_ROLE_INSTALL",
    capability: "serverRoles.write",
    label: "AD DS rol installeren",
    description: "Installeer Active Directory Domain Services.",
    machine: "SRV01",
    payload: { roleId: "role-ad-ds", roleName: "Active Directory Domain Services", role: "Active Directory Domain Services" }
  },
  {
    id: "server-promote",
    appId: "server-manager",
    appName: "Server Manager",
    commandType: "SERVER_DC_PROMOTE",
    capability: "serverRoles.write",
    label: "DC promoten",
    description: "Promoveer SRV01 naar domain controller.",
    machine: "SRV01",
    payload: {
      domainName: "cutover170.local",
      forestName: "cutover170.local",
      netbiosName: "CUTOVER170",
      isFirstDcInForest: true,
      installDnsRole: true
    }
  },
  {
    id: "service-rdp",
    appId: "services",
    appName: "Services",
    commandType: "SERVICE_START",
    capability: "service.write",
    label: "Remote Desktop service starten",
    description: "Start TermService voor het RDP-ticket.",
    machine: "SRV01",
    payload: { serviceName: "TermService", displayName: "Extern bureaublad-services" }
  },
  {
    id: "firewall-domain",
    appId: "wf",
    appName: "Windows Defender Firewall",
    commandType: "FIREWALL_SET_PROFILE",
    capability: "firewall.profile.write",
    label: "Domain firewall aan",
    description: "Zet het domain firewallprofiel aan.",
    machine: "SRV01",
    payload: { profile: "domain", enabled: true }
  },
  {
    id: "rdp-enable",
    appId: "remoteDesktop",
    appName: "Remote Desktop",
    commandType: "RDP_ENABLE",
    capability: "remoteDesktop.write",
    label: "RDP inschakelen",
    description: "Schakel externe bureaubladverbindingen in.",
    machine: "SRV01",
    payload: { enabled: true, firewallRuleName: "RDP-156" }
  },
  {
    id: "audit-4624",
    appId: "eventvwr",
    appName: "Event Viewer",
    commandType: "AUDIT_RECORD_EVENT",
    capability: "audit.event.write",
    label: "Audit event 4624",
    description: "Registreer het logon-auditevent voor Ticket 164.",
    machine: "SRV01",
    payload: {
      category: "Security",
      logName: "Security",
      source: "Microsoft-Windows-Security-Auditing",
      eventId: 4624,
      level: "Information",
      message: "Audit policy success event for ticket 164",
      auditPolicy: { logon: "success" }
    }
  }
];

function appAction(
  appId: string,
  appName: string,
  commandType: string,
  capability: string,
  label: string,
  description: string,
  payload: Record<string, unknown>,
  machine: MachineKey | "both" = "both"
): NativeSimulatorAction {
  return {
    appId,
    appName,
    capability,
    commandType,
    description,
    id: `native-${canonicalAppId(appId)}-${commandType.toLowerCase()}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    label,
    machine,
    payload
  };
}

const defaultNativeAppActions: NativeSimulatorAction[] = [
  appAction("explorer", "File Explorer", "FILE_CREATE_FOLDER", "file.folder.write", "Nieuwe map", "Maak een map in de huidige Windows-map.", { parentPath: "C:\\Users\\Student\\Documents", name: "Nieuwe map", path: "C:\\Users\\Student\\Documents\\Nieuwe map" }),
  appAction("explorer", "File Explorer", "FILE_CREATE", "file.file.write", "Nieuw tekstbestand", "Maak een bestand aan in de simulatorfilesystem.", { parentPath: "C:\\Users\\Student\\Documents", name: "notitie.txt", path: "C:\\Users\\Student\\Documents\\notitie.txt", content: "" }),
  appAction("explorer", "File Explorer", "SHARE_CREATE", "file.share.write", "Map delen", "Open de sharing-flow en publiceer een map als share.", { path: "C:\\Servicedesk\\Intake", shareName: "Intake", permissions: [{ principal: "Servicedesk", permission: "modify", type: "allow" }] }),
  appAction("explorer", "File Explorer", "FILE_SET_PERMISSION", "file.permission.write", "Beveiliging instellen", "Pas NTFS-permissies toe via de Security-tab.", { path: "C:\\Servicedesk\\Intake", principal: "Servicedesk", permission: "modify", type: "allow" }),
  appAction("explorer", "File Explorer", "FILE_DELETE", "file.file.write", "Bestand verwijderen", "Verplaats een geselecteerd bestand naar de prullenbak.", { path: "C:\\Users\\Student\\Documents\\notitie.txt" }),

  appAction("dhcpManager", "DHCP", "DHCP_CREATE_SCOPE", "dhcp.scope.write", "Nieuwe scope", "Start de New Scope Wizard.", { scopeName: "Balie-152", startIp: "10.50.20.100", endIp: "10.50.20.180", subnetMask: "255.255.255.0", router: "10.50.20.1", dnsServer: "10.50.20.10", domainName: "advanced-ti.local" }, "SRV01"),
  appAction("dhcpManager", "DHCP", "DHCP_SET_OPTION", "dhcp.option.write", "Scope-opties", "Wijzig router-, DNS- en domain-name opties.", { scopeName: "Balie-152", router: "10.50.20.1", dnsServer: "10.50.20.10", domainName: "advanced-ti.local" }, "SRV01"),
  appAction("dhcpManager", "DHCP", "DHCP_ADD_RESERVATION", "dhcp.reservation.write", "Nieuwe reservering", "Voeg een DHCP-reservering toe.", { scopeName: "Balie-152", reservationName: "WS-152", ipAddress: "10.50.20.152", macAddress: "00-15-5D-15-20-52" }, "SRV01"),

  appAction("netSettings", "Network & Internet Settings", "NETWORK_SET_ADAPTER_CONFIG", "network.adapter.write", "IP-adres instellen", "Wijzig de Ethernet IPv4-instellingen.", { adapterName: "Ethernet0", ipv4: "10.50.20.25", subnet: "255.255.255.0", gateway: "10.50.20.1" }, "CLIENT01"),
  appAction("netSettings", "Network & Internet Settings", "NETWORK_SET_DNS_SERVER", "network.dnsClient.write", "DNS-server instellen", "Zet de DNS-client naar de interne resolver.", { adapterName: "Ethernet0", dnsServer: "10.50.10.10", controllerIp: "10.50.10.10", domainName: "advanced-ti.local", reachable: true }, "CLIENT01"),
  appAction("netSettings", "Network & Internet Settings", "NETWORK_RELEASE_RENEW_LEASE", "network.dhcp.write", "Lease vernieuwen", "Voer release/renew uit voor de geselecteerde adapter.", { adapterName: "Ethernet0" }, "CLIENT01"),
  appAction("netSettings", "Network & Internet Settings", "NETWORK_ENABLE_DHCP", "network.adapter.write", "DHCP inschakelen", "Zet de geselecteerde adapter terug op automatische DHCP-configuratie.", { adapterName: "Ethernet0" }, "CLIENT01"),

  appAction("sysdm", "System Properties", "SYSTEM_RENAME_COMPUTER", "system.identity.write", "Computernaam wijzigen", "Wijzig de clientnaam via System Properties.", { computerName: "CLIENT01" }, "CLIENT01"),
  appAction("sysdm", "System Properties", "SYSTEM_JOIN_DOMAIN", "domain.membership.write", "Domein joinen", "Join de client aan het domein.", { domainName: "advanced-ti.local", controllerIp: "10.50.10.10", reachable: true }, "CLIENT01"),
  appAction("sysdm", "System Properties", "SYSTEM_LEAVE_DOMAIN", "domain.membership.write", "Werkgroep instellen", "Verplaats de client naar een werkgroep via System Properties.", { workgroupName: "WORKGROUP" }, "CLIENT01"),
  appAction("sysdm", "System Properties", "RDP_ENABLE", "remoteDesktop.write", "Remote Desktop inschakelen", "Schakel externe bureaubladverbindingen in vanuit Systeemeigenschappen.", { enabled: true, firewallRuleName: "RDP-156" }, "CLIENT01"),
  appAction("sysdm", "System Properties", "SYSTEM_SET_ENVIRONMENT_VARIABLE", "local.system.environment.write", "Omgevingsvariabele toevoegen", "Voeg een systeemvariabele toe via Systeemeigenschappen.", { scope: "system", name: "FIRSTDESK_HOME", value: "C:\\FirstDesk" }, "CLIENT01"),
  appAction("remoteDesktop", "Remote Desktop", "RDP_ENABLE", "remoteDesktop.write", "RDP inschakelen", "Schakel Remote Desktop en de firewallregel in.", { enabled: true, firewallRuleName: "RDP-156" }, "SRV01"),
  appAction("remoteDesktop", "Remote Desktop", "RDP_SET_ALLOWED_GROUP", "remoteDesktop.write", "RDP-groep toestaan", "Sta Remote Desktop Users toe.", { groupName: "Remote Desktop Users" }, "SRV01"),

  appAction("server-manager", "Server Manager", "SERVER_ROLE_INSTALL", "serverRoles.write", "Add roles and features", "Open de Add Roles and Features Wizard.", { roleName: "Active Directory Domain Services", features: ["DNS Server"] }, "SRV01"),
  appAction("server-manager", "Server Manager", "SERVER_DC_PROMOTE", "serverRoles.write", "Promote to domain controller", "Open de AD DS Configuration Wizard.", { domainName: "cutover170.local", forestMode: "WinThreshold", domainMode: "WinThreshold", isFirstDcInForest: true }, "SRV01"),
  appAction("server-manager", "Server Manager", "SERVER_ROLE_UNINSTALL", "serverRoles.write", "Remove role", "Verwijder een geinstalleerde serverrol.", { roleName: "Web Server (IIS)" }, "SRV01"),

  appAction("aduc", "Active Directory Users and Computers", "SERVER_CREATE_OU", "domainOu.write", "New Organizational Unit", "Maak een OU aan in de geselecteerde container.", { ouName: "ServiceDesk", parentOuName: "Users" }, "SRV01"),
  appAction("aduc", "Active Directory Users and Computers", "SERVER_CREATE_DOMAIN_USER", "domainUsers.write", "New User", "Open de New Object - User wizard.", { username: "selina.166", displayName: "Selina Ticket 166", samAccountName: "selina.166", ouName: "Users" }, "SRV01"),
  appAction("aduc", "Active Directory Users and Computers", "SERVER_CREATE_DOMAIN_GROUP", "domainGroups.write", "New Group", "Maak een security group in Active Directory.", { groupName: "Support-Team", ouName: "Users", scope: "global", type: "security" }, "SRV01"),
  appAction("aduc", "Active Directory Users and Computers", "SERVER_ADD_DOMAIN_GROUP_MEMBER", "domainGroups.write", "Add to group", "Voeg de geselecteerde gebruiker toe aan de groep.", { groupName: "Support-Team", username: "selina.166" }, "SRV01"),
  appAction("aduc", "Active Directory Users and Computers", "SERVER_SET_DOMAIN_USER_STATE", "domainUsers.state.write", "Enable account", "Schakel de geselecteerde gebruiker in.", { username: "selina.166", enabled: true }, "SRV01"),
  appAction("aduc", "Active Directory Users and Computers", "SERVER_RESET_DOMAIN_USER_PASSWORD", "domainUsers.password.write", "Reset password", "Reset het wachtwoord en forceer wijziging bij volgende aanmelding.", { username: "selina.166", mustChangeAtNextLogon: true }, "SRV01"),

  appAction("dns-manager", "DNS Manager", "SERVER_DNS_CREATE_ZONE", "dns.zone.write", "New Zone", "Start de New Zone Wizard.", { zoneName: "adv166.local", zoneType: "primary", replication: "domain" }, "SRV01"),
  appAction("dns-manager", "DNS Manager", "SERVER_DNS_ADD_RECORD", "dns.record.write", "New Host (A)", "Maak een DNS hostrecord aan.", { zoneName: "adv166.local", recordName: "ws166", recordType: "A", value: "10.50.166.20" }, "SRV01"),
  appAction("dns-manager", "DNS Manager", "SERVER_DNS_DELETE_RECORD", "dns.record.write", "Delete Record", "Verwijder het geselecteerde DNS-record.", { zoneName: "adv166.local", recordName: "oldws", recordType: "A" }, "SRV01"),

  appAction("gpmc", "Group Policy Management", "GPO_CREATE", "policy.gpo.write", "New GPO", "Maak een nieuwe Group Policy Object.", { gpoName: "FD-Hardening-154" }, "SRV01"),
  appAction("gpmc", "Group Policy Management", "GPO_LINK_OU", "policy.gpo.link.write", "Link GPO", "Koppel de GPO aan een OU.", { gpoName: "FD-Hardening-154", ouName: "ServiceDesk" }, "SRV01"),
  appAction("gpmc", "Group Policy Management", "GPO_SET_SETTING", "policy.gpo.setting.write", "Edit setting", "Wijzig een beleidsinstelling.", { gpoName: "FD-Hardening-154", settingPath: "Computer Configuration\\Policies\\Security Options", value: "Enabled" }, "SRV01"),
  appAction("gpmc", "Group Policy Management", "GPO_ENFORCE_LINK", "policy.gpo.link.write", "Enforced", "Forceer de geselecteerde GPO-link.", { gpoName: "FD-Hardening-154", ouName: "Workstations", enforced: true }, "SRV01"),

  appAction("services", "Services", "SERVICE_START", "service.write", "Start service", "Start de geselecteerde service.", { serviceName: "TermService", displayName: "Remote Desktop Services" }, "SRV01"),
  appAction("services", "Services", "SERVICE_STOP", "service.write", "Stop service", "Stop de geselecteerde service.", { serviceName: "Spooler", displayName: "Print Spooler" }, "SRV01"),
  appAction("services", "Services", "SERVICE_SET_STARTUP_TYPE", "service.write", "Startup type", "Wijzig het opstarttype.", { serviceName: "TermService", startupType: "automatic" }, "SRV01"),
  appAction("wf", "Windows Defender Firewall", "FIREWALL_ENABLE_RULE", "firewall.rule.write", "Enable rule", "Schakel een inkomende firewallregel in.", { ruleName: "Remote Desktop - User Mode (TCP-In)", profile: "domain", port: 3389 }, "SRV01"),
  appAction("wf", "Windows Defender Firewall", "FIREWALL_SET_PROFILE", "firewall.profile.write", "Domain profile", "Zet het domain firewallprofiel aan.", { profile: "domain", enabled: true }, "SRV01"),
  appAction("eventvwr", "Event Viewer", "EVENT_VIEWER_FILTER", "audit.event.read", "Filter Current Log", "Filter het huidige logboek.", { logName: "Security", eventId: 4624, source: "Microsoft-Windows-Security-Auditing" }, "SRV01"),
  appAction("eventvwr", "Event Viewer", "EVENT_VIEWER_RECORD_CHECK", "audit.event.check", "Open Event", "Inspecteer het geselecteerde event.", { eventId: 4624, logName: "Security" }, "SRV01"),
  appAction("eventvwr", "Event Viewer", "AUDIT_RECORD_EVENT", "audit.event.write", "Record audit event", "Registreer een audit event in simulatorstate.", { eventId: 4624, logName: "Security", level: "Information", source: "Microsoft-Windows-Security-Auditing", message: "Successful logon" }, "SRV01"),
  appAction("eventvwr", "Event Viewer", "EVENT_VIEWER_CREATE_VIEW", "audit.view.write", "Create custom view", "Maak een aangepaste logboekweergave.", { viewName: "FirstDesk Security", filter: { logName: "Security", eventId: 4624 } }, "SRV01"),

  appAction("lusrmgr", "Local Users and Groups", "LOCAL_USER_CREATE", "local.user.write", "New User", "Maak een lokale gebruiker.", { username: "local.student", fullName: "Local Student", mustChangePassword: true }),
  appAction("lusrmgr", "Local Users and Groups", "LOCAL_GROUP_CREATE", "local.group.write", "New Group", "Maak een lokale groep.", { groupName: "Helpdesk Operators" }),
  appAction("lusrmgr", "Local Users and Groups", "LOCAL_GROUP_ADD_MEMBER", "local.group.write", "Add to group", "Voeg gebruiker toe aan lokale groep.", { groupName: "Remote Desktop Users", username: "local.student" }),
  appAction("lusrmgr", "Local Users and Groups", "LOCAL_USER_SET_STATE", "local.user.write", "Disable account", "Schakel lokale gebruiker uit.", { username: "Guest", enabled: false }),
  appAction("lusrmgr", "Local Users and Groups", "LOCAL_USER_RESET_PASSWORD", "local.user.password.write", "Reset password", "Reset het wachtwoord van de geselecteerde lokale gebruiker.", { username: "local.student" }),
  appAction("lusrmgr", "Local Users and Groups", "LOCAL_USER_DELETE", "local.user.write", "Delete user", "Verwijder de geselecteerde lokale gebruiker.", { username: "old.local" }),
  appAction("netplwiz", "User Accounts", "LOCAL_USER_AUTOLOGIN_TOGGLE", "local.user.autologin.write", "Auto logon", "Wijzig automatisch aanmelden.", { username: "Student", enabled: false }, "CLIENT01"),
  appAction("netplwiz", "User Accounts", "LOCAL_USER_REQUIRE_CTRL_ALT_DEL", "local.session.write", "Ctrl+Alt+Del", "Vereis Ctrl+Alt+Del bij aanmelden.", { enabled: true }, "CLIENT01"),
  appAction("secpol", "Local Security Policy", "SECURITY_POLICY_SET", "local.securityPolicy.write", "Security setting", "Wijzig een lokaal beveiligingsbeleid.", { policyPath: "Account Policies\\Password Policy\\Minimum password length", value: 12 }),
  appAction("secpol", "Local Security Policy", "AUDIT_POLICY_SET", "local.auditPolicy.write", "Audit policy", "Wijzig lokale audit policy.", { category: "Logon", success: true, failure: true }),
  appAction("secpol", "Local Security Policy", "USER_RIGHTS_ASSIGN", "local.userRights.write", "User rights", "Wijs een user right toe.", { rightName: "Allow log on through Remote Desktop Services", principals: ["Remote Desktop Users"] }),
  appAction("uac", "User Account Control", "UAC_SET_LEVEL", "local.uac.write", "UAC level", "Zet UAC op het aanbevolen niveau.", { level: 3 }, "CLIENT01"),
  appAction("uac", "User Account Control", "UAC_TOGGLE_ADMIN_APPROVAL", "local.uac.write", "Admin approval", "Wijzig Admin Approval Mode.", { enabled: true }, "CLIENT01"),
  appAction("gpedit", "Local Group Policy Editor", "LOCAL_GPO_SET_SETTING", "local.gpo.setting.write", "Policy setting", "Wijzig een lokale groepsbeleidsinstelling.", { path: "Computer Configuration\\Administrative Templates\\System", value: "Enabled" }),
  appAction("taskschd", "Task Scheduler", "TASK_CREATE", "local.scheduledTask.write", "Create Task", "Maak een geplande taak.", { taskName: "FirstDesk Health Check", action: "powershell.exe -File health.ps1", trigger: "At startup", principal: "SYSTEM" }),
  appAction("taskschd", "Task Scheduler", "TASK_DELETE", "local.scheduledTask.write", "Delete Task", "Verwijder een geplande taak.", { taskName: "CleanupCache" }),
  appAction("taskschd", "Task Scheduler", "TASK_TOGGLE", "local.scheduledTask.write", "Enable Task", "Schakel de geselecteerde taak in.", { taskName: "FirstDesk Health Check", enabled: true }),
  appAction("taskschd", "Task Scheduler", "TASK_RUN_NOW", "local.scheduledTask.execute", "Run", "Voer de taak nu uit.", { taskName: "FirstDesk Health Check" }),
  appAction("taskmgr", "Task Manager", "PROCESS_END", "local.process.write", "End task", "Beeindig het geselecteerde proces.", { processName: "notepad.exe" }),
  appAction("taskmgr", "Task Manager", "STARTUP_ITEM_TOGGLE", "local.startup.write", "Startup item", "Wijzig een opstartitem.", { itemName: "Teams", enabled: false }),
  appAction("taskmgr", "Task Manager", "SERVICE_START_FROM_TASKMGR", "service.write", "Start service", "Start een service vanuit Task Manager.", { serviceName: "Spooler" }),
  appAction("devmgmt", "Device Manager", "DEVICE_ENABLE", "local.device.write", "Enable device", "Schakel het geselecteerde apparaat in.", { deviceId: "net-ethernet0" }),
  appAction("devmgmt", "Device Manager", "DEVICE_DISABLE", "local.device.write", "Disable device", "Schakel het geselecteerde apparaat uit.", { deviceId: "wifi-0" }),
  appAction("devmgmt", "Device Manager", "DEVICE_UPDATE_DRIVER", "local.device.driver.write", "Update driver", "Werk het stuurprogramma bij.", { deviceId: "net-ethernet0", driverVersion: "12.19.2.57" }),
  appAction("diskmgmt", "Disk Management", "VOLUME_CREATE", "local.disk.write", "New Simple Volume", "Maak een nieuw volume.", { driveLetter: "E:", sizeBytes: 21474836480, fileSystem: "NTFS" }),
  appAction("diskmgmt", "Disk Management", "VOLUME_FORMAT", "local.disk.write", "Format", "Formatteer het geselecteerde volume.", { driveLetter: "D:", fileSystem: "NTFS", label: "Data" }),
  appAction("diskmgmt", "Disk Management", "DISK_RUN_CHKDSK", "local.disk.maintenance.write", "Check disk", "Voer chkdsk uit.", { driveLetter: "C:", fix: true }),
  appAction("msinfo32", "System Information", "SYSINFO_RECORD_CHECK", "local.system.read", "Record system check", "Leg vast dat systeeminformatie is gecontroleerd.", { section: "System Summary" }),
  appAction("settingsSystem", "Settings System", "SYSTEM_SET_DISPLAY_SCALING", "local.system.display.write", "Display scaling", "Wijzig schermschaal.", { scalingPercent: 125 }, "CLIENT01"),
  appAction("settingsSystem", "Settings System", "SYSTEM_SET_NOTIFICATIONS", "local.system.notifications.write", "Notifications", "Wijzig notificaties.", { enabled: false }, "CLIENT01"),
  appAction("powercfg", "Power Options", "POWER_PLAN_SET_ACTIVE", "local.system.power.write", "Power plan", "Activeer het High performance schema.", { planName: "High performance" }, "CLIENT01"),

  appAction("appwiz", "Programs and Features", "APP_UNINSTALL", "local.app.write", "Uninstall", "Verwijder het geselecteerde programma.", { appName: "Contoso Toolbar" }, "CLIENT01"),
  appAction("optionalfeatures", "Windows Features", "FEATURE_ENABLE", "local.feature.write", "Enable feature", "Schakel een Windows feature in.", { featureName: "Telnet Client" }, "CLIENT01"),
  appAction("optionalfeatures", "Windows Features", "FEATURE_DISABLE", "local.feature.write", "Disable feature", "Schakel een Windows feature uit.", { featureName: "SMB 1.0/CIFS File Sharing Support" }, "CLIENT01"),
  appAction("windowsUpdate", "Windows Update", "WINDOWS_UPDATE_INSTALL", "local.update.write", "Install update", "Installeer een update.", { kbId: "KB5039212", mandatory: true }, "CLIENT01"),
  appAction("windowsUpdate", "Windows Update", "WINDOWS_UPDATE_PAUSE", "local.update.write", "Pause updates", "Pauzeer Windows Updates.", { days: 7 }, "CLIENT01"),
  appAction("defender", "Windows Security", "DEFENDER_RUN_SCAN", "local.defender.write", "Run scan", "Start een snelle Defender-scan.", { scanType: "quick" }, "CLIENT01"),
  appAction("defender", "Windows Security", "DEFENDER_TOGGLE_REALTIME", "local.defender.write", "Realtime protection", "Wijzig realtime bescherming.", { enabled: true }, "CLIENT01"),
  appAction("defender", "Windows Security", "DEFENDER_ADD_EXCLUSION", "local.defender.exclusion.write", "Add exclusion", "Voeg een uitsluiting toe.", { path: "C:\\Tools" }, "CLIENT01"),
  appAction("bitlocker", "BitLocker", "BITLOCKER_ENABLE", "local.bitlocker.write", "Turn on BitLocker", "Schakel BitLocker in voor C:.", { driveLetter: "C:", recoveryKeyDestination: "Active Directory" }),
  appAction("bitlocker", "BitLocker", "BITLOCKER_DISABLE", "local.bitlocker.write", "Turn off BitLocker", "Schakel BitLocker uit.", { driveLetter: "D:" }),
  appAction("cmd", "Command Prompt", "SHELL_EXECUTE_COMMAND", "local.shell.execute", "ipconfig /all", "Voer ipconfig uit binnen de simulator.", { shell: "cmd", commandLine: "ipconfig /all", workingDir: "C:\\Users\\Student" }),
  appAction("powershell", "PowerShell", "SHELL_EXECUTE_COMMAND", "local.shell.execute", "Get-NetIPConfiguration", "Voer een PowerShell netwerkcheck uit binnen de simulator.", { shell: "powershell", commandLine: "Get-NetIPConfiguration", workingDir: "C:\\Users\\Student" }),
  appAction("regedit", "Registry Editor", "REGISTRY_SET_VALUE", "local.registry.write", "Set value", "Wijzig een registry value in simulatorstate.", { hive: "HKEY_CURRENT_USER", keyPath: "Environment", valueName: "FIRSTDESK_ENV", valueType: "REG_SZ", valueData: "lab" }),
  appAction("regedit", "Registry Editor", "REGISTRY_DELETE_VALUE", "local.registry.write", "Delete value", "Verwijder een registry value.", { hive: "HKEY_CURRENT_USER", keyPath: "Environment", valueName: "OLD_ENV" }),
  appAction("controlPrinters", "Devices and Printers", "PRINTER_ADD", "local.printer.write", "Add printer", "Voeg een printer toe.", { printerName: "Reception-PRN", driver: "Generic PCL", port: "IP_192.168.1.81" }),
  appAction("controlPrinters", "Devices and Printers", "PRINTER_REMOVE", "local.printer.write", "Remove printer", "Verwijder een printer.", { printerName: "Old Inkjet" }),
  appAction("controlPrinters", "Devices and Printers", "PRINTER_SET_DEFAULT", "local.printer.write", "Set default", "Zet de standaardprinter.", { printerName: "Reception-PRN" }),
  appAction("ncpa", "Network Connections", "NETWORK_SET_ADAPTER_CONFIG", "network.adapter.write", "Adapter properties", "Wijzig adapterconfiguratie vanuit ncpa.cpl.", { adapterName: "Ethernet0", ipv4: "10.50.20.25", subnet: "255.255.255.0", gateway: "10.50.20.1" }, "CLIENT01"),
  appAction("ncpa", "Network Connections", "NETWORK_ENABLE_DHCP", "network.adapter.write", "Enable DHCP", "Zet de adapter op DHCP.", { adapterName: "Ethernet0" }, "CLIENT01"),
  appAction("ncpa", "Network Connections", "NETWORK_SET_DNS_SERVER", "network.dns.write", "Set DNS", "Wijzig DNS vanuit Network Connections.", { adapterName: "Ethernet0", dnsServer: "10.50.10.10" }, "CLIENT01"),
  appAction("ncpa", "Network Connections", "NETWORK_RELEASE_RENEW_LEASE", "network.dhcp.write", "Release/Renew", "Ververs de DHCP-lease vanuit Network Connections.", { adapterName: "Ethernet0" }, "CLIENT01"),
  appAction("rebootSimulator", "Restart", "SYSTEM_REBOOT", "local.system.write", "Restart now", "Simuleer een reboot van de actieve machine.", { machineId: "CLIENT01" }),

  appAction("calc", "Calculator", "CALC_RECORD_RESULT", "local.utility.read", "Record result", "Leg de berekening vast als bewijs.", { expression: "2+2", result: "4" }),
  appAction("paint", "Paint", "PAINT_SAVE_FILE", "local.utility.write", "Save picture", "Sla een Paint-bestand op in simulatorstate.", { path: "C:\\Users\\Student\\Pictures\\diagram.png" }),
  appAction("snippingTool", "Snipping Tool", "SCREENSHOT_RECORD", "local.evidence.write", "New snip", "Leg een screenshot-evidence vast.", { region: "desktop", path: "C:\\Users\\Student\\Pictures\\snip.png" }),
  appAction("clipboardHistory", "Clipboard History", "CLIPBOARD_RECORD_VIEW", "local.clipboard.read", "Open clipboard", "Leg vast dat de clipboard history is gecontroleerd.", { itemCount: 3 }),
  appAction("credentialManager", "Credential Manager", "CREDENTIAL_ADD", "local.credential.write", "Add credential", "Voeg een Windows credential toe.", { targetName: "fileserver", username: "ADVANCED-TI\\student" }),
  appAction("credentialManager", "Credential Manager", "CREDENTIAL_DELETE", "local.credential.write", "Delete credential", "Verwijder een Windows credential.", { targetName: "old-fileserver" }),
  appAction("certmgr", "Certificate Manager", "CERTIFICATE_ADD_USER_STORE", "local.cert.user.write", "Import certificate", "Importeer een certificaat in de user store.", { subject: "CN=Student", issuer: "FirstDesk Lab CA", storeName: "Personal" }),
  appAction("certlm", "Certificates - Local Computer", "CERTIFICATE_ADD_LOCAL_MACHINE_STORE", "local.cert.machine.write", "Import machine cert", "Importeer een certificaat in de machine store.", { subject: "CN=SRV01", issuer: "FirstDesk Lab CA", storeName: "Personal" }),
  appAction("diskCleanup", "Disk Cleanup", "DISK_CLEANUP_RUN", "local.disk.maintenance.write", "Clean up system files", "Voer schijfopruiming uit.", { driveLetter: "C:", categories: ["Temporary files", "Recycle Bin"] }),
  appAction("systemRestore", "System Restore", "RESTORE_POINT_CREATE", "local.system.restore.write", "Create restore point", "Maak een herstelpunt.", { restorePointName: "Before FirstDesk change" }),
  appAction("reliabilityMonitor", "Reliability Monitor", "RELIABILITY_RECORD_CHECK", "local.reliability.read", "Record check", "Leg vast dat reliability history is gecontroleerd.", { day: "today" }),
  appAction("resourceMonitor", "Resource Monitor", "RESOURCE_RECORD_CHECK", "local.resource.read", "Record resource check", "Leg vast dat resource monitor is gecontroleerd.", { section: "CPU" }),

  appAction("login", "Windows Sign-in", "SESSION_LOGIN_ATTEMPT", "session.write", "Sign in", "Simuleer een Windows/domain login.", { username: "ADVANCED-TI\\selina.166", password: "student-safe-placeholder", machineId: "CLIENT01" }),
  appAction("entraAdmin", "Entra Admin Center", "ENTRA_USER_CREATE", "entra.user.write", "New user", "Maak een Entra gebruiker.", { upn: "new.user@lab.local", displayName: "New User", license: "M365_E3" }),
  appAction("entraAdmin", "Entra Admin Center", "ENTRA_GROUP_CREATE", "entra.group.write", "New group", "Maak een Entra groep.", { groupName: "Cloud Support", type: "Security" }),
  appAction("entraAdmin", "Entra Admin Center", "ENTRA_ASSIGN_LICENSE", "entra.license.write", "Assign license", "Wijs een licentie toe.", { upn: "new.user@lab.local", licenseSku: "M365_E3" }),
  appAction("entraAdmin", "Entra Admin Center", "ENTRA_ROLE_ASSIGN", "entra.role.write", "Assign role", "Wijs een Entra rol toe.", { upn: "new.user@lab.local", roleName: "Helpdesk Administrator" }),
  appAction("entraAudit", "Entra Audit Logs", "ENTRA_AUDIT_RECORD_CHECK", "entra.audit.read", "Open audit log", "Controleer een Entra audit event.", { logType: "AuditLogs", eventId: "UserCreated" }),
  appAction("entraUsers", "Entra Users", "ENTRA_USER_CREATE", "cloud.entra.user.write", "Create user", "Maak een cloudgebruiker.", { upn: "native.user@lab.local", displayName: "Native User" }),
  appAction("entraUsers", "Entra Users", "ENTRA_USER_RESET_PASSWORD", "cloud.entra.user.write", "Reset password", "Reset een cloudgebruiker.", { upn: "native.user@lab.local" }),
  appAction("entraGroups", "Entra Groups", "ENTRA_GROUP_CREATE", "cloud.entra.group.write", "Create group", "Maak een cloudgroep.", { groupName: "Cloud Support", type: "Security" }),
  appAction("entraGroups", "Entra Groups", "ENTRA_GROUP_ADD_MEMBER", "cloud.entra.group.write", "Add member", "Voeg gebruiker toe aan groep.", { groupName: "Cloud Support", upn: "native.user@lab.local" }),
  appAction("entraDevices", "Entra Devices", "ENTRA_DEVICE_REGISTER", "cloud.entra.device.write", "Register device", "Registreer een Entra device.", { deviceName: "CLIENT01", owner: "native.user@lab.local" }),
  appAction("entraRoles", "Entra Roles", "ENTRA_ROLE_ASSIGN", "cloud.entra.role.write", "Assign role", "Wijs een rol toe.", { upn: "native.user@lab.local", roleName: "Global Reader" }),
  appAction("conditionalAccess", "Conditional Access", "CA_POLICY_CREATE", "cloud.entra.policy.write", "New policy", "Maak een Conditional Access policy.", { policyName: "Require MFA Remote", enabled: true }),
  appAction("authMethods", "Authentication Methods", "AUTH_METHOD_ENABLE", "cloud.entra.auth.write", "Enable method", "Schakel een authenticatiemethode in.", { methodName: "Microsoft Authenticator", enabled: true }),
  appAction("enterpriseApps", "Enterprise Apps", "ENTERPRISE_APP_REGISTER", "cloud.entra.app.write", "Register app", "Registreer een enterprise app.", { appName: "ServiceDesk Portal" }),
  appAction("intuneDevices", "Intune Devices", "INTUNE_DEVICE_ENROLL", "cloud.intune.device.write", "Enroll device", "Enroll een Intune device.", { deviceName: "CLIENT01", owner: "native.user@lab.local" }),
  appAction("compliancePolicies", "Compliance Policies", "COMPLIANCE_POLICY_CREATE", "cloud.intune.compliance.write", "Create policy", "Maak een compliance policy.", { policyName: "Windows Baseline", platform: "Windows 11" }),
  appAction("configurationProfiles", "Configuration Profiles", "CONFIG_PROFILE_CREATE", "cloud.intune.config.write", "Create profile", "Maak een configuration profile.", { profileName: "Security Baseline", platform: "Windows" }),
  appAction("m365Users", "M365 Users", "M365_USER_LICENSE_ASSIGN", "cloud.m365.user.write", "Assign license", "Wijs M365 licentie toe.", { upn: "native.user@lab.local", licenseSku: "M365_E3" }),
  appAction("m365Licenses", "M365 Licenses", "M365_LICENSE_PURCHASE", "cloud.m365.license.write", "Purchase license", "Registreer een licentieaankoop.", { licenseSku: "M365_E3", quantity: 10 }),
  appAction("defenderPortal", "Defender Portal", "DEFENDER_ALERT_INVESTIGATE", "cloud.defender.write", "Investigate alert", "Onderzoek een Defender alert.", { alertId: "Alert-445", analyst: "Smoke Student" }),

  appAction("adSites", "AD Sites", "AD_SITE_CREATE", "server.activeDirectory.sites.write", "Create site", "Maak een AD site.", { siteName: "HQ-Site", subnet: "10.50.20.0/24" }, "SRV01"),
  appAction("adTrusts", "AD Trusts", "AD_TRUST_CREATE", "server.activeDirectory.trusts.write", "Create trust", "Maak een domeintrust.", { trustName: "partner.local", direction: "two-way" }, "SRV01"),
  appAction("certAuthority", "Certification Authority", "CA_ISSUE_CERTIFICATE", "server.ca.write", "Issue certificate", "Issue een certificaat.", { subject: "CN=app.lab.local", template: "WebServer" }, "SRV01"),
  appAction("iisManager", "IIS Manager", "IIS_SITE_CREATE", "server.iis.write", "Create website", "Maak een IIS-site.", { siteName: "Intranet", binding: "intranet.lab.local:80" }, "SRV01"),
  appAction("hyperVManager", "Hyper-V Manager", "HYPERV_VM_CREATE", "server.hyperv.write", "Create VM", "Maak een Hyper-V VM.", { vmName: "LAB-VM-01", memoryMb: 4096 }, "SRV01"),
  appAction("printManagement", "Print Management", "PRINT_SERVER_ADD_PRINTER", "server.print.write", "Add printer", "Voeg serverprinter toe.", { printerName: "Reception-PRN", driver: "Generic PCL", port: "IP_192.168.1.81" }, "SRV01"),
  appAction("dfsManagement", "DFS Management", "DFS_NAMESPACE_CREATE", "server.dfs.write", "Create namespace", "Maak een DFS namespace.", { namespaceName: "Shares", path: "\\\\lab.local\\shares" }, "SRV01"),
  appAction("npsManager", "NPS", "NPS_POLICY_CREATE", "server.nps.write", "Create policy", "Maak een NPS policy.", { policyName: "WiFi-Allow-Staff", condition: "Windows-Group=Staff" }, "SRV01"),
  appAction("windowsServerBackup", "Windows Server Backup", "BACKUP_JOB_SCHEDULE", "server.backup.write", "Schedule backup", "Plan een backup job.", { jobName: "DailySystemState", target: "D:\\Backup" }, "SRV01"),
  appAction("failoverCluster", "Failover Cluster", "CLUSTER_NODE_ADD", "server.cluster.write", "Add node", "Voeg een clusternode toe.", { nodeName: "NODE02" }, "SRV01"),
  appAction("fsrmManager", "FSRM", "FSRM_QUOTA_CREATE", "server.fsrm.write", "Create quota", "Maak een quota.", { path: "D:\\Shares\\Profiles", sizeGb: 10 }, "SRV01"),
  appAction("storageManager", "Storage Manager", "STORAGE_POOL_CREATE", "server.storage.write", "Create pool", "Maak een storage pool.", { poolName: "Pool-A", disks: ["Disk 1", "Disk 2"] }, "SRV01")
];

function actionsForNativeApp(appId: string, ticketActions: NativeSimulatorAction[]) {
  const normalized = canonicalAppId(appId);
  const merged = new Map<string, NativeSimulatorAction>();
  for (const action of ticketActions) {
    if (canonicalAppId(action.appId) === normalized) {
      merged.set(`${action.commandType}:${action.label}`, action);
    }
  }
  for (const action of defaultNativeAppActions) {
    if (canonicalAppId(action.appId) === normalized) {
      merged.set(`${action.commandType}:${action.label}`, action);
    }
  }
  return Array.from(merged.values());
}

function findDefaultNativeAction(appId: string, commandType: string, capability?: string) {
  const normalized = canonicalAppId(appId);
  return defaultNativeAppActions.find((action) => (
    canonicalAppId(action.appId) === normalized &&
    action.commandType === commandType &&
    (!capability || action.capability === capability)
  )) ?? null;
}

function selectNativeAction(
  appId: string,
  actions: NativeSimulatorAction[],
  commandTypes: string[],
  capability?: string
) {
  const normalized = canonicalAppId(appId);
  for (const commandType of commandTypes) {
    const defaultExact = findDefaultNativeAction(normalized, commandType, capability);
    if (defaultExact) return defaultExact;

    const actionExact = actions.find((action) => (
      canonicalAppId(action.appId) === normalized &&
      action.commandType === commandType &&
      (!capability || action.capability === capability)
    ));
    if (actionExact) return actionExact;

    const defaultAny = findDefaultNativeAction(normalized, commandType);
    if (defaultAny) return defaultAny;

    const actionAny = actions.find((action) => (
      canonicalAppId(action.appId) === normalized &&
      action.commandType === commandType
    ));
    if (actionAny) return actionAny;
  }
  return null;
}

const appMeta: Record<string, AppMeta> = {
  adSites: { icon: "account_tree", accent: "#475569", windowTitle: "Active Directory Sites and Services", kind: "server" },
  adTrusts: { icon: "handshake", accent: "#475569", windowTitle: "Active Directory Domains and Trusts", kind: "server" },
  aduc: { icon: "group", accent: "#2563eb", windowTitle: "Active Directory Users and Computers", kind: "server" },
  appwiz: { icon: "apps", accent: "#64748b", windowTitle: "Programs and Features", kind: "utility" },
  authMethods: { icon: "passkey", accent: "#7c3aed", windowTitle: "Authentication Methods", kind: "admin" },
  bitlocker: { icon: "enhanced_encryption", accent: "#0f766e", windowTitle: "BitLocker Drive Encryption", kind: "security" },
  calc: { icon: "calculate", accent: "#475569", windowTitle: "Rekenmachine", kind: "utility" },
  certAuthority: { icon: "workspace_premium", accent: "#475569", windowTitle: "Certification Authority", kind: "server" },
  certlm: { icon: "verified_user", accent: "#475569", windowTitle: "Certificates - Local Computer", kind: "security" },
  certmgr: { icon: "badge", accent: "#475569", windowTitle: "Certificate Manager", kind: "security" },
  clipboardHistory: { icon: "content_paste", accent: "#64748b", windowTitle: "Clipboard History", kind: "utility" },
  cmd: { icon: "terminal", accent: "#111827", windowTitle: "Command Prompt", kind: "utility" },
  compliancePolicies: { icon: "rule", accent: "#7c3aed", windowTitle: "Compliance Policies", kind: "admin" },
  conditionalAccess: { icon: "lock_person", accent: "#7c3aed", windowTitle: "Conditional Access", kind: "security" },
  configurationProfiles: { icon: "tune", accent: "#7c3aed", windowTitle: "Configuration Profiles", kind: "admin" },
  controlPanel: { icon: "dashboard_customize", accent: "#2563eb", windowTitle: "Control Panel", kind: "utility" },
  controlPrinters: { icon: "print", accent: "#475569", windowTitle: "Devices and Printers", kind: "utility" },
  credentialManager: { icon: "key", accent: "#475569", windowTitle: "Credential Manager", kind: "security" },
  defender: { icon: "health_and_safety", accent: "#16a34a", windowTitle: "Windows Security", kind: "security" },
  defenderPortal: { icon: "security", accent: "#7c3aed", windowTitle: "Defender Portal", kind: "admin" },
  devmgmt: { icon: "devices", accent: "#475569", windowTitle: "Device Manager", kind: "utility" },
  dfsManagement: { icon: "folder_managed", accent: "#475569", windowTitle: "DFS Management", kind: "server" },
  dhcpManager: { icon: "lan", accent: "#0f766e", windowTitle: "DHCP", kind: "server" },
  diskCleanup: { icon: "cleaning_services", accent: "#64748b", windowTitle: "Schijfopruiming", kind: "utility" },
  diskmgmt: { icon: "storage", accent: "#475569", windowTitle: "Disk Management", kind: "utility" },
  "dns-manager": { icon: "dns", accent: "#7c3aed", windowTitle: "DNS Manager", kind: "server" },
  dnsManager: { icon: "dns", accent: "#7c3aed", windowTitle: "DNS Manager", kind: "server" },
  enterpriseApps: { icon: "apps", accent: "#7c3aed", windowTitle: "Enterprise Apps", kind: "admin" },
  edge: { icon: "language", accent: "#0ea5e9", windowTitle: "Microsoft Edge", kind: "client" },
  entraAdmin: { icon: "cloud", accent: "#2563eb", windowTitle: "Entra Admin Center", kind: "admin" },
  entraAudit: { icon: "manage_search", accent: "#2563eb", windowTitle: "Entra Audit Logs", kind: "admin" },
  entraDevices: { icon: "devices_other", accent: "#7c3aed", windowTitle: "Entra Devices", kind: "admin" },
  entraGroups: { icon: "groups", accent: "#7c3aed", windowTitle: "Entra Groups", kind: "admin" },
  entraRoles: { icon: "admin_panel_settings", accent: "#7c3aed", windowTitle: "Entra Roles", kind: "admin" },
  entraUsers: { icon: "person", accent: "#7c3aed", windowTitle: "Entra Users", kind: "admin" },
  eventvwr: { icon: "event_note", accent: "#dc2626", windowTitle: "Event Viewer", kind: "admin" },
  explorer: { icon: "folder", accent: "#f59e0b", windowTitle: "File Explorer", kind: "client" },
  failoverCluster: { icon: "hub", accent: "#475569", windowTitle: "Failover Cluster Manager", kind: "server" },
  fsrmManager: { icon: "folder_special", accent: "#475569", windowTitle: "File Server Resource Manager", kind: "server" },
  gpedit: { icon: "rule_settings", accent: "#4f46e5", windowTitle: "Local Group Policy Editor", kind: "admin" },
  gpmc: { icon: "policy", accent: "#4f46e5", windowTitle: "Group Policy Management", kind: "server" },
  hyperVManager: { icon: "developer_board", accent: "#475569", windowTitle: "Hyper-V Manager", kind: "server" },
  iisManager: { icon: "language", accent: "#475569", windowTitle: "IIS Manager", kind: "server" },
  intuneDevices: { icon: "important_devices", accent: "#7c3aed", windowTitle: "Intune Devices", kind: "admin" },
  login: { icon: "login", accent: "#2563eb", windowTitle: "Windows Sign-in", kind: "utility" },
  lusrmgr: { icon: "manage_accounts", accent: "#2563eb", windowTitle: "Local Users and Groups", kind: "admin" },
  m365Licenses: { icon: "license", accent: "#7c3aed", windowTitle: "Microsoft 365 Licenses", kind: "admin" },
  m365Users: { icon: "person_add", accent: "#7c3aed", windowTitle: "Microsoft 365 Users", kind: "admin" },
  msinfo32: { icon: "info", accent: "#64748b", windowTitle: "System Information", kind: "utility" },
  ncpa: { icon: "settings_ethernet", accent: "#0ea5e9", windowTitle: "Network Connections", kind: "client" },
  netplwiz: { icon: "supervisor_account", accent: "#2563eb", windowTitle: "User Accounts", kind: "admin" },
  netSettings: { icon: "settings_ethernet", accent: "#0ea5e9", windowTitle: "Network & Internet", kind: "client" },
  notepad: { icon: "description", accent: "#475569", windowTitle: "Naamloos - Kladblok", kind: "utility" },
  npsManager: { icon: "network_manage", accent: "#475569", windowTitle: "Network Policy Server", kind: "server" },
  optionalfeatures: { icon: "featured_play_list", accent: "#64748b", windowTitle: "Windows Features", kind: "utility" },
  paint: { icon: "palette", accent: "#ec4899", windowTitle: "Paint", kind: "utility" },
  powercfg: { icon: "power_settings_new", accent: "#16a34a", windowTitle: "Power Options", kind: "utility" },
  powershell: { icon: "terminal", accent: "#2563eb", windowTitle: "Windows PowerShell", kind: "utility" },
  printManagement: { icon: "print", accent: "#475569", windowTitle: "Print Management", kind: "server" },
  rebootSimulator: { icon: "restart_alt", accent: "#dc2626", windowTitle: "Restart", kind: "utility" },
  regedit: { icon: "account_tree", accent: "#475569", windowTitle: "Registry Editor", kind: "utility" },
  reliabilityMonitor: { icon: "monitor_heart", accent: "#64748b", windowTitle: "Reliability Monitor", kind: "utility" },
  remoteDesktop: { icon: "desktop_windows", accent: "#0891b2", windowTitle: "Remote Desktop", kind: "client" },
  recycleBin: { icon: "delete", accent: "#475569", windowTitle: "Prullenbak", kind: "utility" },
  resourceMonitor: { icon: "monitoring", accent: "#64748b", windowTitle: "Resource Monitor", kind: "utility" },
  secpol: { icon: "gpp_maybe", accent: "#4f46e5", windowTitle: "Local Security Policy", kind: "security" },
  "server-manager": { icon: "dns", accent: "#334155", windowTitle: "Server Manager", kind: "server" },
  serverManager: { icon: "dns", accent: "#334155", windowTitle: "Server Manager", kind: "server" },
  services: { icon: "manufacturing", accent: "#64748b", windowTitle: "Services", kind: "admin" },
  settingsSystem: { icon: "settings", accent: "#2563eb", windowTitle: "Settings", kind: "client" },
  snippingTool: { icon: "screenshot", accent: "#64748b", windowTitle: "Knipprogramma", kind: "utility" },
  storageManager: { icon: "database", accent: "#475569", windowTitle: "Storage Manager", kind: "server" },
  sysdm: { icon: "computer", accent: "#2563eb", windowTitle: "System Properties", kind: "client" },
  systemRestore: { icon: "restore", accent: "#64748b", windowTitle: "Systeemherstel", kind: "utility" },
  taskmgr: { icon: "browse_activity", accent: "#64748b", windowTitle: "Task Manager", kind: "utility" },
  taskschd: { icon: "event_upcoming", accent: "#64748b", windowTitle: "Task Scheduler", kind: "admin" },
  uac: { icon: "admin_panel_settings", accent: "#f59e0b", windowTitle: "User Account Control Settings", kind: "security" },
  wf: { icon: "security", accent: "#16a34a", windowTitle: "Windows Defender Firewall", kind: "security" },
  windowsServerBackup: { icon: "backup", accent: "#475569", windowTitle: "Windows Server Backup", kind: "server" },
  windowsUpdate: { icon: "system_update", accent: "#2563eb", windowTitle: "Windows Update", kind: "utility" }
};

const nativeAppCatalog: NativeAppCatalogEntry[] = [
  { appId: "explorer", name: "File Explorer", family: "Filesystem", machine: "both", source: "legacy-js", aliases: ["explorer", "explorer.exe"] },
  { appId: "netSettings", name: "Network & Internet Settings", family: "Network", machine: "CLIENT01", source: "legacy-js", aliases: ["ncpa.cpl", "ms-settings:network"] },
  { appId: "sysdm", name: "System Properties", family: "Client", machine: "CLIENT01", source: "legacy-js", aliases: ["sysdm.cpl"] },
  { appId: "notepad", name: "Kladblok", family: "Utility", machine: "both", source: "legacy-js", aliases: ["notepad", "notepad.exe"] },
  { appId: "edge", name: "Microsoft Edge", family: "Client", machine: "both", source: "legacy-js", aliases: ["edge", "msedge", "msedge.exe", "microsoft edge", "browser", "packettracer.nl", "https://packettracer.nl", "entra.microsoft.com", "https://entra.microsoft.com", "portal.azure.com", "https://portal.azure.com", "portal.microsoft.com", "https://portal.microsoft.com", "admin.microsoft.com", "https://admin.microsoft.com", "intune.microsoft.com", "https://intune.microsoft.com", "security.microsoft.com", "https://security.microsoft.com"] },
  { appId: "remoteDesktop", name: "Remote Desktop Connection", family: "Client", machine: "CLIENT01", source: "fase10", aliases: ["mstsc", "mstsc.exe"] },
  { appId: "cmd", name: "Command Prompt", family: "Utility", machine: "both", source: "legacy-js", aliases: ["cmd", "cmd.exe"] },
  { appId: "powershell", name: "Windows PowerShell", family: "Utility", machine: "both", source: "legacy-js", aliases: ["powershell", "powershell.exe"] },
  { appId: "lusrmgr", name: "Local Users and Groups", family: "Identity", machine: "both", source: "legacy-js", aliases: ["lusrmgr", "lusrmgr.msc"] },
  { appId: "netplwiz", name: "User Accounts", family: "Identity", machine: "both", source: "legacy-js", aliases: ["netplwiz", "netplwiz.exe"] },
  { appId: "secpol", name: "Local Security Policy", family: "Security", machine: "both", source: "legacy-js", aliases: ["secpol.msc"] },
  { appId: "uac", name: "User Account Control Settings", family: "Security", machine: "both", source: "legacy-js", aliases: ["uac", "useraccountcontrolsettings"] },
  { appId: "gpedit", name: "Local Group Policy Editor", family: "Policy", machine: "both", source: "legacy-js", aliases: ["gpedit", "gpedit.msc"] },
  { appId: "taskschd", name: "Task Scheduler", family: "Utility", machine: "both", source: "legacy-js", aliases: ["taskschd", "taskschd.msc"] },
  { appId: "taskmgr", name: "Task Manager", family: "Utility", machine: "both", source: "legacy-js", aliases: ["taskmgr", "taskmgr.exe"] },
  { appId: "devmgmt", name: "Device Manager", family: "Utility", machine: "both", source: "legacy-js", aliases: ["devmgmt", "devmgmt.msc"] },
  { appId: "diskmgmt", name: "Disk Management", family: "Utility", machine: "both", source: "legacy-js", aliases: ["diskmgmt", "diskmgmt.msc"] },
  { appId: "msinfo32", name: "System Information", family: "Utility", machine: "both", source: "legacy-js", aliases: ["msinfo32", "msinfo32.exe"] },
  { appId: "settingsSystem", name: "Settings", family: "Client", machine: "both", source: "legacy-js", aliases: ["ms-settings:", "ms-settings:system", "ms-settings:display", "ms-settings:accounts", "ms-settings:dateandtime", "ms-settings:personalization", "ms-settings:personalization-background"] },
  { appId: "powercfg", name: "Power Options", family: "Utility", machine: "both", source: "legacy-js", aliases: ["powercfg.cpl"] },
  { appId: "appwiz", name: "Programs and Features", family: "Utility", machine: "both", source: "legacy-js", aliases: ["appwiz.cpl"] },
  { appId: "optionalfeatures", name: "Windows Features", family: "Utility", machine: "both", source: "legacy-js", aliases: ["optionalfeatures.exe"] },
  { appId: "windowsUpdate", name: "Windows Update", family: "Utility", machine: "both", source: "legacy-js", aliases: ["windowsupdate", "windowsupdate.exe", "wuapp", "ms-settings:windowsupdate"] },
  { appId: "defender", name: "Windows Security", family: "Security", machine: "both", source: "legacy-js", aliases: ["windowsdefender:", "securityhealthsystray"] },
  { appId: "bitlocker", name: "BitLocker Drive Encryption", family: "Security", machine: "both", source: "legacy-js", aliases: ["control /name microsoft.bitlockerdriveencryption"] },
  { appId: "controlPanel", name: "Control Panel", family: "Utility", machine: "both", source: "legacy-js", aliases: ["control", "control.exe", "control panel"] },
  { appId: "controlPrinters", name: "Devices and Printers", family: "Utility", machine: "both", source: "legacy-js", aliases: ["control printers"] },
  { appId: "ncpa", name: "Network Connections", family: "Network", machine: "both", source: "legacy-js", aliases: ["ncpa.cpl"] },
  { appId: "rebootSimulator", name: "Restart", family: "Utility", machine: "both", source: "legacy-js", aliases: ["shutdown /r", "restart-computer"] },
  { appId: "server-manager", name: "Server Manager", family: "Server", machine: "SRV01", source: "legacy-js", aliases: ["servermanager", "servermanager.exe", "compmgmt.msc"] },
  { appId: "aduc", name: "Active Directory Users and Computers", family: "Identity", machine: "SRV01", source: "legacy-js", aliases: ["dsa.msc"] },
  { appId: "dns-manager", name: "DNS Manager", family: "Network", machine: "SRV01", source: "legacy-js", aliases: ["dnsmgmt.msc"] },
  { appId: "dhcpManager", name: "DHCP", family: "Network", machine: "SRV01", source: "legacy-js", aliases: ["dhcpmgmt.msc"] },
  { appId: "gpmc", name: "Group Policy Management", family: "Policy", machine: "SRV01", source: "legacy-js", aliases: ["gpmc.msc"] },
  { appId: "services", name: "Services", family: "Server", machine: "SRV01", source: "legacy-js", aliases: ["services", "services.msc"] },
  { appId: "wf", name: "Windows Defender Firewall", family: "Security", machine: "SRV01", source: "legacy-js", aliases: ["wf.msc", "firewall.cpl"] },
  { appId: "eventvwr", name: "Event Viewer", family: "Audit", machine: "SRV01", source: "legacy-js", aliases: ["eventvwr", "eventvwr.msc"] },
  { appId: "login", name: "Windows Sign-in", family: "Utility", machine: "both", source: "legacy-js", aliases: ["login"] },
  { appId: "entraAdmin", name: "Entra Admin Center", family: "M365", machine: "both", source: "legacy-js", aliases: ["entra", "entraadmin", "entraadmin.exe", "entra.microsoft.com", "https://entra.microsoft.com", "portal.microsoft.com", "admin.microsoft.com"] },
  { appId: "entraAudit", name: "Entra Audit Logs", family: "M365", machine: "both", source: "legacy-js", aliases: ["entraaudit", "entraaudit.exe", "portal.azure.com", "https://portal.azure.com"] },
  { appId: "calc", name: "Rekenmachine", family: "Client", machine: "both", source: "fase10", aliases: ["calc", "calc.exe"] },
  { appId: "paint", name: "Paint", family: "Client", machine: "both", source: "fase10", aliases: ["mspaint", "mspaint.exe"] },
  { appId: "snippingTool", name: "Knipprogramma", family: "Client", machine: "both", source: "fase10", aliases: ["snippingtool", "snippingtool.exe"] },
  { appId: "regedit", name: "Registry Editor", family: "Client", machine: "both", source: "fase10", aliases: ["regedit", "regedit.exe"] },
  { appId: "credentialManager", name: "Credential Manager", family: "Client", machine: "both", source: "fase10", aliases: ["control keymgr.dll", "rundll32.exe keymgr.dll,krshowkeymgr"] },
  { appId: "certmgr", name: "Certificate Manager", family: "Client", machine: "both", source: "fase10", aliases: ["certmgr.msc"] },
  { appId: "certlm", name: "Certificates - Local Computer", family: "Client", machine: "both", source: "fase10", aliases: ["certlm.msc"] },
  { appId: "clipboardHistory", name: "Clipboard History", family: "Client", machine: "both", source: "fase10", aliases: ["clipboardhistory", "win+v"] },
  { appId: "diskCleanup", name: "Schijfopruiming", family: "Client", machine: "both", source: "fase10", aliases: ["cleanmgr", "cleanmgr.exe"] },
  { appId: "systemRestore", name: "Systeemherstel", family: "Client", machine: "both", source: "fase10", aliases: ["rstrui", "rstrui.exe"] },
  { appId: "reliabilityMonitor", name: "Reliability Monitor", family: "Client", machine: "both", source: "fase10", aliases: ["perfmon /rel", "reliabilitymonitor"] },
  { appId: "resourceMonitor", name: "Resource Monitor", family: "Client", machine: "both", source: "fase10", aliases: ["resmon", "resmon.exe", "perfmon", "perfmon.msc"] },
  { appId: "adSites", name: "Active Directory Sites and Services", family: "Server", machine: "SRV01", source: "fase10", aliases: ["dssite.msc"] },
  { appId: "adTrusts", name: "Active Directory Domains and Trusts", family: "Server", machine: "SRV01", source: "fase10", aliases: ["domain.msc"] },
  { appId: "certAuthority", name: "Certification Authority", family: "Server", machine: "SRV01", source: "fase10", aliases: ["certsrv.msc"] },
  { appId: "iisManager", name: "IIS Manager", family: "Server", machine: "SRV01", source: "fase10", aliases: ["inetmgr", "inetmgr.exe"] },
  { appId: "hyperVManager", name: "Hyper-V Manager", family: "Server", machine: "SRV01", source: "fase10", aliases: ["virtmgmt.msc"] },
  { appId: "printManagement", name: "Print Management", family: "Server", machine: "SRV01", source: "fase10", aliases: ["printmanagement.msc"] },
  { appId: "dfsManagement", name: "DFS Management", family: "Server", machine: "SRV01", source: "fase10", aliases: ["dfsmgmt.msc"] },
  { appId: "npsManager", name: "Network Policy Server", family: "Server", machine: "SRV01", source: "fase10", aliases: ["nps.msc"] },
  { appId: "windowsServerBackup", name: "Windows Server Backup", family: "Server", machine: "SRV01", source: "fase10", aliases: ["wbadmin.msc"] },
  { appId: "failoverCluster", name: "Failover Cluster Manager", family: "Server", machine: "SRV01", source: "fase10", aliases: ["cluadmin.msc"] },
  { appId: "fsrmManager", name: "File Server Resource Manager", family: "Server", machine: "SRV01", source: "fase10", aliases: ["fsrm.msc"] },
  { appId: "storageManager", name: "Storage Manager", family: "Server", machine: "SRV01", source: "fase10", aliases: ["storage"] },
  { appId: "entraUsers", name: "Entra Users", family: "M365", machine: "both", source: "fase10", aliases: ["entra users"] },
  { appId: "entraGroups", name: "Entra Groups", family: "M365", machine: "both", source: "fase10", aliases: ["entra groups"] },
  { appId: "entraDevices", name: "Entra Devices", family: "M365", machine: "both", source: "fase10", aliases: ["entra devices"] },
  { appId: "entraRoles", name: "Entra Roles", family: "M365", machine: "both", source: "fase10", aliases: ["entra roles"] },
  { appId: "conditionalAccess", name: "Conditional Access", family: "M365", machine: "both", source: "fase10", aliases: ["conditional access"] },
  { appId: "authMethods", name: "Authentication Methods", family: "M365", machine: "both", source: "fase10", aliases: ["auth methods"] },
  { appId: "enterpriseApps", name: "Enterprise Apps", family: "M365", machine: "both", source: "fase10", aliases: ["enterprise apps"] },
  { appId: "intuneDevices", name: "Intune Devices", family: "M365", machine: "both", source: "fase10", aliases: ["intune devices"] },
  { appId: "compliancePolicies", name: "Compliance Policies", family: "M365", machine: "both", source: "fase10", aliases: ["compliance policies"] },
  { appId: "configurationProfiles", name: "Configuration Profiles", family: "M365", machine: "both", source: "fase10", aliases: ["configuration profiles"] },
  { appId: "m365Users", name: "Microsoft 365 Users", family: "M365", machine: "both", source: "fase10", aliases: ["m365 users"] },
  { appId: "m365Licenses", name: "Microsoft 365 Licenses", family: "M365", machine: "both", source: "fase10", aliases: ["m365 licenses"] },
  { appId: "defenderPortal", name: "Defender Portal", family: "M365", machine: "both", source: "fase10", aliases: ["defender portal", "security.microsoft.com"] }
];

const runAliases: Record<string, string> = nativeAppCatalog.reduce<Record<string, string>>((aliases, app) => {
  for (const alias of app.aliases) aliases[alias.toLowerCase()] = app.appId;
  aliases[app.appId.toLowerCase()] = app.appId;
  aliases[app.name.toLowerCase()] = app.appId;
  return aliases;
}, {});

function normalizeBrowserUrl(raw: string) {
  const trimmed = raw.trim();
  if (!trimmed) return "https://packettracer.nl";
  if (/^[a-z]+:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.includes(".")) return `https://${trimmed}`;
  return trimmed;
}

const cloudAppIds = [
  "entraAdmin",
  "entraAudit",
  "entraUsers",
  "entraGroups",
  "entraDevices",
  "entraRoles",
  "conditionalAccess",
  "authMethods",
  "enterpriseApps",
  "intuneDevices",
  "compliancePolicies",
  "configurationProfiles",
  "m365Users",
  "m365Licenses",
  "defenderPortal"
];

const clientStartPinnedApps = [
  "explorer",
  "edge",
  "cmd",
  "lusrmgr",
  "settingsSystem",
  "controlPanel",
  "notepad",
  "calc",
  "paint",
  "regedit",
  "remoteDesktop",
  "snippingTool",
  "credentialManager",
  "certmgr",
  "resourceMonitor"
];
const clientTaskbarPinnedApps = ["explorer", "edge", "cmd", "settingsSystem"];
const serverStartPinnedApps = ["server-manager", "aduc", "dns-manager", "dhcpManager", "gpmc", "services", "wf", "eventvwr"];
const serverTaskbarPinnedApps = ["server-manager", "aduc", "dns-manager", "dhcpManager"];

const elevatedAppIds = new Set([
  "aduc",
  "appwiz",
  "bitlocker",
  "controlPrinters",
  "defender",
  "devmgmt",
  "dhcpManager",
  "diskmgmt",
  "dns-manager",
  "entraAdmin",
  "entraAudit",
  "eventvwr",
  "gpedit",
  "gpmc",
  "msinfo32",
  "netplwiz",
  "optionalfeatures",
  "powercfg",
  "secpol",
  "server-manager",
  "services",
  "sysdm",
  "taskmgr",
  "taskschd",
  "wf",
  "windowsUpdate"
]);

const appIconVariants: Record<string, string> = {
  adSites: "ad",
  adTrusts: "ad",
  aduc: "ad",
  appwiz: "control",
  authMethods: "cloud",
  bitlocker: "shield-lock",
  calc: "calc",
  certAuthority: "certificate",
  certlm: "certificate",
  certmgr: "certificate",
  clipboardHistory: "clipboard",
  cmd: "cmd",
  compliancePolicies: "policy",
  conditionalAccess: "shield-lock",
  configurationProfiles: "settings",
  controlPanel: "control",
  controlPrinters: "printer",
  credentialManager: "key",
  defender: "shield",
  defenderPortal: "shield",
  devmgmt: "devices",
  dfsManagement: "folder-network",
  dhcpManager: "dhcp",
  diskCleanup: "disk",
  diskmgmt: "disk",
  "dns-manager": "dns",
  dnsManager: "dns",
  edge: "edge",
  enterpriseApps: "cloud",
  entraAdmin: "cloud",
  entraAudit: "audit",
  entraDevices: "cloud-device",
  entraGroups: "cloud-users",
  entraRoles: "shield",
  entraUsers: "cloud-users",
  eventvwr: "event",
  explorer: "explorer",
  failoverCluster: "server",
  fsrmManager: "folder-network",
  gpedit: "policy",
  gpmc: "gpo",
  hyperVManager: "server",
  iisManager: "web-server",
  intuneDevices: "cloud-device",
  login: "login",
  lusrmgr: "users",
  m365Licenses: "license",
  m365Users: "cloud-users",
  msinfo32: "info",
  ncpa: "network",
  netplwiz: "users",
  netSettings: "network",
  notepad: "notepad",
  npsManager: "network",
  optionalfeatures: "control",
  paint: "paint",
  powercfg: "power",
  powershell: "powershell",
  printManagement: "printer",
  rebootSimulator: "restart",
  regedit: "registry",
  reliabilityMonitor: "monitor",
  remoteDesktop: "rdp",
  recycleBin: "recycle",
  resourceMonitor: "monitor",
  secpol: "policy",
  "server-manager": "server-manager",
  serverManager: "server-manager",
  services: "services",
  settingsSystem: "settings",
  snippingTool: "snip",
  storageManager: "disk",
  sysdm: "system",
  systemRestore: "restore",
  taskmgr: "taskmgr",
  taskschd: "scheduler",
  uac: "uac",
  wf: "firewall",
  windowsServerBackup: "backup",
  windowsUpdate: "update"
};

const legacyV1AppIconSvg: Record<string, string> = {
  aduc: `<svg viewBox="0 0 16 16" width="16" height="16">
    <circle cx="6" cy="5" r="2.5" fill="#dbeafe" stroke="#2563eb" stroke-width="1"/>
    <circle cx="11" cy="5" r="2" fill="#ede9fe" stroke="#7c3aed" stroke-width="1"/>
    <rect x="1.5" y="9" width="8" height="5" rx="2" fill="#dbeafe" stroke="#2563eb" stroke-width="1"/>
    <rect x="8" y="9.5" width="6" height="4.5" rx="2" fill="#ede9fe" stroke="#7c3aed" stroke-width="1"/>
  </svg>`,
  calc: `<svg viewBox="0 0 16 16" width="16" height="16"><rect width="16" height="16" rx="3" fill="#2563eb"/><text x="8" y="11.5" fill="white" font-size="7" text-anchor="middle" font-family="Segoe UI, sans-serif" font-weight="700">C</text></svg>`,
  certmgr: `<svg viewBox="0 0 16 16" width="16" height="16"><rect width="16" height="16" rx="3" fill="#b45309"/><text x="8" y="11.5" fill="white" font-size="7" text-anchor="middle" font-family="Segoe UI, sans-serif" font-weight="700">CE</text></svg>`,
  cmd: `<svg viewBox="0 0 16 16" width="16" height="16">
    <rect width="16" height="16" rx="2" fill="#1e1e1e"/>
    <text x="1.7" y="11" fill="#cccccc" font-size="5.5" font-family="monospace">C:\\&gt;_</text>
  </svg>`,
  controlPanel: `<svg viewBox="0 0 16 16" width="16" height="16">
    <rect width="16" height="16" rx="3" fill="#dbeafe"/>
    <text x="8" y="11.5" fill="#2563eb" font-size="7" text-anchor="middle" font-family="Segoe UI, sans-serif" font-weight="700">CP</text>
  </svg>`,
  credentialManager: `<svg viewBox="0 0 16 16" width="16" height="16"><rect width="16" height="16" rx="3" fill="#475569"/><text x="8" y="11.5" fill="white" font-size="7" text-anchor="middle" font-family="Segoe UI, sans-serif" font-weight="700">CM</text></svg>`,
  dhcpManager: `<svg viewBox="0 0 16 16" width="16" height="16">
    <rect x="1" y="4" width="14" height="9" rx="2" fill="#e0f2fe" stroke="#0891b2" stroke-width="1"/>
    <path d="M4 10h3v2H4zM7 10h2v2H7zM10 10h2v2h-2z" fill="#0891b2" opacity=".5"/>
    <path d="M8 4V2M6 2h4" stroke="#0891b2" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="13" cy="5" r="2" fill="#22c55e" stroke="white" stroke-width=".8"/>
  </svg>`,
  dnsManager: `<svg viewBox="0 0 16 16" width="16" height="16">
    <rect x="1" y="4" width="14" height="9" rx="2" fill="#e0f2fe" stroke="#0891b2" stroke-width="1"/>
    <path d="M4 10h3v2H4zM7 10h2v2H7zM10 10h2v2h-2z" fill="#0891b2" opacity=".5"/>
    <path d="M8 4V2M6 2h4" stroke="#0891b2" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="13" cy="5" r="2" fill="#22c55e" stroke="white" stroke-width=".8"/>
  </svg>`,
  edge: `<svg viewBox="0 0 16 16" width="16" height="16">
    <circle cx="8" cy="8" r="6" fill="none" stroke="#36b5f5" stroke-width="2"/>
    <path d="M2 8c0-2 2-4 6-4 2 0 3.5.5 5 2" stroke="#1a9de1" stroke-width="1.5" fill="none"/>
  </svg>`,
  eventvwr: `<svg viewBox="0 0 16 16" width="16" height="16">
    <rect x="1" y="1" width="14" height="14" rx="2" fill="#fef2f2" stroke="#dc2626" stroke-width="1"/>
    <path d="M8 3.5v5" stroke="#dc2626" stroke-width="1.8" stroke-linecap="round"/>
    <circle cx="8" cy="11" r="1.2" fill="#dc2626"/>
  </svg>`,
  explorer: `<svg viewBox="0 0 16 16" width="16" height="16">
    <path d="M1 5a2 2 0 0 1 2-2h3l1.5 1.5H13a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5z" fill="#f9c74f"/>
    <path d="M1 7.5h14" stroke="#e6a800" stroke-width=".7" opacity=".5"/>
    <path d="M1 5a2 2 0 0 1 2-2h3l1.5 1.5H3A2 2 0 0 0 1 7V5z" fill="#f0a500" opacity=".6"/>
  </svg>`,
  file: `<svg viewBox="0 0 16 16" width="16" height="16">
    <path d="M3 1.2h6.2L13 5v9.3c0 .3-.3.5-.6.5H3.6c-.3 0-.6-.2-.6-.5V1.8c0-.3.3-.6.6-.6z" fill="#ffffff" stroke="#b8c2cf" stroke-width=".8"/>
    <path d="M9.2 1.4V5H13" fill="#e8eef7" stroke="#b8c2cf" stroke-width=".7"/>
    <path d="M5 7h6M5 9h6M5 11h4" stroke="#8a97a8" stroke-width=".8" stroke-linecap="round"/>
  </svg>`,
  gpmc: `<svg viewBox="0 0 16 16" width="16" height="16">
    <rect x="1.5" y="1.5" width="13" height="13" rx="2" fill="#f8faff" stroke="#334155" stroke-width="1"/>
    <path d="M7.5 4L4 7.5 5 8.5 8.5 5l-1-1z" fill="#334155"/>
    <rect x="4" y="10" width="8" height="1" rx=".5" fill="#334155"/>
    <rect x="4" y="12" width="5" height="1" rx=".5" fill="#334155"/>
    <path d="M12 2l2 2-5 5-2-2 5-5z" fill="#0067c0"/>
    <path d="M10 9l-2 1 1-2 1 1z" fill="#0067c0"/>
  </svg>`,
  lusrmgr: `<svg viewBox="0 0 16 16" width="16" height="16">
    <circle cx="8" cy="5.2" r="2.9" fill="#4a9eff"/>
    <path d="M2.3 15c0-3.7 2.6-6.2 5.7-6.2s5.7 2.5 5.7 6.2" fill="#4a9eff"/>
  </svg>`,
  notepad: `<svg viewBox="0 0 16 16" width="16" height="16">
    <rect x="2" y="1" width="12" height="14" rx="1.3" fill="#ffffff" stroke="#dddddd" stroke-width=".9"/>
    <line x1="4" y1="5.5" x2="12" y2="5.5" stroke="#bbbbbb" stroke-width=".8"/>
    <line x1="4" y1="8" x2="12" y2="8" stroke="#bbbbbb" stroke-width=".8"/>
    <line x1="4" y1="10.5" x2="8.8" y2="10.5" stroke="#bbbbbb" stroke-width=".8"/>
  </svg>`,
  paint: `<svg viewBox="0 0 16 16" width="16" height="16"><rect width="16" height="16" rx="3" fill="#0f766e"/><text x="8" y="11.5" fill="white" font-size="7" text-anchor="middle" font-family="Segoe UI, sans-serif" font-weight="700">P</text></svg>`,
  recycleBin: `<svg viewBox="0 0 40 40" width="40" height="40">
    <rect x="9" y="4" width="22" height="4" rx="2" fill="#b0b0b0"/>
    <rect x="5" y="8" width="30" height="2" rx="1" fill="#b0b0b0"/>
    <path d="M7 11 L8.5 35 Q8.7 36 9.5 36 L30.5 36 Q31.3 36 31.5 35 L33 11Z" fill="#d0d8e8" stroke="#b0b0b0"/>
    <line x1="16" y1="16" x2="16" y2="32" stroke="#999" stroke-width="1.5"/>
    <line x1="20" y1="16" x2="20" y2="32" stroke="#999" stroke-width="1.5"/>
    <line x1="24" y1="16" x2="24" y2="32" stroke="#999" stroke-width="1.5"/>
  </svg>`,
  regedit: `<svg viewBox="0 0 16 16" width="16" height="16"><rect width="16" height="16" rx="3" fill="#7c3aed"/><text x="8" y="11.5" fill="white" font-size="7" text-anchor="middle" font-family="Segoe UI, sans-serif" font-weight="700">RG</text></svg>`,
  remoteDesktop: `<svg viewBox="0 0 16 16" width="16" height="16"><rect width="16" height="16" rx="3" fill="#0891b2"/><text x="8" y="11.5" fill="white" font-size="7" text-anchor="middle" font-family="Segoe UI, sans-serif" font-weight="700">RD</text></svg>`,
  resourceMonitor: `<svg viewBox="0 0 16 16" width="16" height="16"><rect width="16" height="16" rx="3" fill="#0f766e"/><text x="8" y="11.5" fill="white" font-size="7" text-anchor="middle" font-family="Segoe UI, sans-serif" font-weight="700">RS</text></svg>`,
  serverManager: `<svg viewBox="0 0 16 16" width="16" height="16">
    <rect x="2" y="1" width="12" height="14" rx="2" fill="#f1f5f9" stroke="#475569" stroke-width="1"/>
    <rect x="4" y="3" width="8" height="2" rx=".5" fill="#475569" opacity=".35"/>
    <rect x="4" y="7" width="8" height="2" rx=".5" fill="#475569" opacity=".45"/>
    <rect x="4" y="11" width="8" height="2" rx=".5" fill="#475569" opacity=".55"/>
  </svg>`,
  services: `<svg viewBox="0 0 16 16" width="16" height="16">
    <circle cx="8" cy="8" r="6.5" fill="#eff6ff" stroke="#2563eb" stroke-width="1"/>
    <path d="M8 4.5v3.5L10.5 10" stroke="#2563eb" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="8" cy="8" r="1" fill="#2563eb"/>
  </svg>`,
  settingsSystem: `<svg viewBox="0 0 16 16" width="16" height="16">
    <circle cx="8" cy="8" r="2.4" fill="none" stroke="#555555" stroke-width="1.2"/>
    <path d="M8 1.2v2M8 12.8v2M1.2 8h2M12.8 8h2M3.2 3.2l1.4 1.4M11.4 11.4l1.4 1.4M3.2 12.8l1.4-1.4M11.4 4.6l1.4-1.4" stroke="#555555" stroke-width="1.2" stroke-linecap="round"/>
  </svg>`,
  settingsNetwork: `<svg viewBox="0 0 16 16" width="16" height="16">
    <path d="M8 2a9 9 0 0 0-5.8 2.1l1.5 1.5A7 7 0 0 1 8 4a7 7 0 0 1 4.3 1.6l1.5-1.5A9 9 0 0 0 8 2z" fill="#0ea5e9"/>
    <path d="M8 5.5a5.5 5.5 0 0 0-3.5 1.3l1.5 1.5A3.5 3.5 0 0 1 8 7.5a3.5 3.5 0 0 1 2 .8l1.5-1.5A5.5 5.5 0 0 0 8 5.5z" fill="#0ea5e9" opacity=".7"/>
    <circle cx="8" cy="11.5" r="2" fill="#0ea5e9"/>
  </svg>`,
  snippingTool: `<svg viewBox="0 0 16 16" width="16" height="16"><rect width="16" height="16" rx="3" fill="#dc2626"/><text x="8" y="11.5" fill="white" font-size="7" text-anchor="middle" font-family="Segoe UI, sans-serif" font-weight="700">SN</text></svg>`,
  wf: `<svg viewBox="0 0 16 16" width="16" height="16">
    <path d="M8 1L2 3.8v5c0 4 2.4 6.3 6 7.2 3.6-.9 6-3.2 6-7.2V3.8L8 1z" fill="#fee2e2" stroke="#ef4444" stroke-width="1"/>
    <path d="M6 6h4M6 9h4" stroke="#ef4444" stroke-width="1.3" stroke-linecap="round"/>
    <circle cx="8" cy="7.5" r="1" fill="#ef4444" opacity=".4"/>
  </svg>`
};

const legacyV1IconAliases: Record<string, string> = {
  adSites: "aduc",
  adTrusts: "aduc",
  "dns-manager": "dnsManager",
  gpedit: "gpmc",
  ncpa: "settingsNetwork",
  netSettings: "settingsNetwork",
  "server-manager": "serverManager",
  sysdm: "settingsSystem"
};

const legacyShellLabels: Record<string, string> = {
  cmd: "Opdrachtprompt",
  controlPanel: "Configuratiescherm",
  calc: "Rekenmachine",
  certmgr: "Certificates",
  credentialManager: "Credential Manager",
  edge: "Microsoft Edge",
  explorer: "Verkenner",
  lusrmgr: "Gebruikers en groepen",
  notepad: "Kladblok",
  paint: "Paint",
  regedit: "Register-editor",
  remoteDesktop: "Remote Desktop",
  resourceMonitor: "Resource Monitor",
  snippingTool: "Knipprogramma",
  settingsSystem: "Instellingen"
};

function legacyShellNameFor(appId: string, simulatorApps: StudentSimulatorAppView[]) {
  const normalized = canonicalAppId(appId);
  return legacyShellLabels[normalized] ?? appNameFor(appId, simulatorApps);
}

function AppIcon({ appId, className = "" }: { appId: string; className?: string }) {
  const normalized = canonicalAppId(appId);
  const legacyIconKey = legacyV1IconAliases[normalized] ?? legacyV1IconAliases[appId] ?? normalized;
  const svg = legacyV1AppIconSvg[legacyIconKey] ?? legacyV1AppIconSvg[appIconVariants[normalized] ?? ""] ?? legacyV1AppIconSvg.settingsSystem;
  return (
    <span
      aria-hidden="true"
      className={`win11-app-icon fd-legacy-app-icon ${className}`.trim()}
      data-app-icon={normalized}
      data-legacy-app-icon={legacyIconKey}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function MmcGlyph({ kind, className = "" }: { kind: string; className?: string }) {
  return <span aria-hidden="true" className={`win11-mini-glyph win11-mini-glyph--${kind} ${className}`.trim()} />;
}

const legacyGlyphMap: Record<string, string> = {
  add: "add",
  add_task: "add",
  admin_panel_settings: "shield",
  airplanemode_active: "network",
  app_blocking: "block",
  article: "record",
  arrow_back: "back",
  bluetooth: "network",
  bolt: "warning",
  brush: "edit",
  check_circle: "toggle",
  cleaning_services: "clear",
  computer: "computer",
  crop_square: "block",
  delete: "clear",
  delete_forever: "clear",
  deployed_code: "server",
  description: "record",
  devices_other: "computer",
  done: "toggle",
  drive_file_rename_outline: "drive",
  enhanced_encryption: "shield",
  error: "warning",
  fact_check: "record",
  folder: "folder",
  folder_managed: "folder",
  folder_off: "folder",
  format_color_fill: "edit",
  format_color_reset: "clear",
  group: "group",
  groups: "group",
  health_and_safety: "shield",
  history: "refresh",
  info: "info",
  keyboard: "settings",
  lan: "network",
  lists: "list",
  lock: "lock",
  lock_open: "lock",
  language: "network",
  manage_accounts: "user",
  manage_search: "search",
  memory: "settings",
  monitoring: "record",
  network_check: "network",
  note_add: "add",
  notifications: "info",
  pause_circle: "stop",
  person: "user",
  play_arrow: "play",
  policy: "shield",
  print: "properties",
  public: "network",
  refresh: "refresh",
  restart_alt: "refresh",
  restore_from_trash: "refresh",
  rocket_launch: "play",
  schedule: "record",
  screenshot_region: "open",
  search: "search",
  security: "shield",
  settings: "settings",
  shield: "shield",
  speed: "settings",
  storage: "drive",
  support_agent: "user",
  system_update: "download",
  system_update_alt: "download",
  task_alt: "record",
  terminal: "terminal",
  toggle_on: "toggle",
  tune: "settings",
  verified: "toggle",
  vpn_key: "key",
  volume_up: "play",
  web: "network",
  widgets: "settings",
  wifi: "network"
};

function WinGlyph({ name, className = "" }: { name: string; className?: string }) {
  return <MmcGlyph kind={legacyGlyphMap[name] ?? name} className={className} />;
}

function visibleActions(machine: MachineKey) {
  return nativeActions.filter((action) => action.machine === "both" || action.machine === machine);
}

function appNameFor(appId: string, simulatorApps: StudentSimulatorAppView[]) {
  return simulatorApps.find((app) => canonicalAppId(app.appId) === canonicalAppId(appId))?.name
    ?? nativeAppCatalog.find((app) => canonicalAppId(app.appId) === canonicalAppId(appId))?.name
    ?? appMeta[appId]?.windowTitle
    ?? appId;
}

function metaFor(appId: string): AppMeta {
  return appMeta[appId] ?? {
    icon: "apps",
    accent: "#2563eb",
    windowTitle: appId,
    kind: "utility"
  };
}

function canonicalAppId(appId: string) {
  if (appId === "dnsManager") return "dns-manager";
  if (appId === "serverManager") return "server-manager";
  return appId;
}

function machineLabel(machine: MachineKey) {
  return machine === "CLIENT01" ? "Windows 11 Enterprise" : "Windows Server 2025";
}

const fallbackFilesystemNodes: Record<string, NativeFsNode> = {
  root: { id: "root", name: "C:", parentId: null, type: "folder" },
  users: { id: "users", name: "Users", parentId: "root", type: "folder" },
  student: { id: "student", name: "Student", parentId: "users", type: "folder" },
  desktop: { id: "desktop", name: "Desktop", parentId: "student", type: "folder" },
  documents: { id: "documents", name: "Documents", parentId: "student", type: "folder" },
  downloads: { id: "downloads", name: "Downloads", parentId: "student", type: "folder" }
};

function normalizeNativePath(path: string) {
  const trimmed = path.trim().replace(/\//g, "\\");
  if (!trimmed) return "C:\\";
  if (/^[a-z]:$/i.test(trimmed)) return `${trimmed.toUpperCase()}\\`;
  if (/^[a-z]:\\/i.test(trimmed)) return trimmed.replace(/^([a-z]):/i, (_, drive: string) => `${drive.toUpperCase()}:`).replace(/\\+/g, "\\");
  return normalizeNativePath(`C:\\Users\\Student\\${trimmed}`);
}

function normalizeNativeDriveLetter(value: string) {
  const raw = value.trim().replace(":", "");
  return /^[A-Za-z]$/.test(raw) ? raw.toUpperCase() : "";
}

function normalizeNativeNetworkTargetPath(path: string) {
  const trimmed = path.trim().replace(/\//g, "\\");
  if (!trimmed) return "";
  if (/^\\\\[^\\]+\\[^\\]+/.test(trimmed)) return trimmed.replace(/\\+$/g, "");
  if (/^[a-z]:/i.test(trimmed)) return normalizeNativePath(trimmed);
  return trimmed.replace(/\\+$/g, "");
}

function parentNativePath(path: string) {
  const normalized = normalizeNativePath(path).replace(/\\$/, "");
  const parts = normalized.split("\\").filter(Boolean);
  if (parts.length <= 1) return "C:\\";
  return parts.slice(0, -1).join("\\");
}

function baseNameForPath(path: string) {
  const parts = normalizeNativePath(path).replace(/\\$/, "").split("\\").filter(Boolean);
  return parts[parts.length - 1] ?? "C:";
}

function parseWindowsCommandLine(raw: string) {
  const matches = raw.match(/(?:[^\s"]+|"[^"]*")+/g) ?? [];
  return matches.map((token) => token.replace(/^"|"$/g, ""));
}

function joinCommandArgs(args: string[]) {
  return args.join(" ").trim();
}

function resolveCopyDestination(state: NativeSimulatorState | null | undefined, sourcePath: string, destinationArg: string) {
  const destination = normalizeNativePath(destinationArg);
  const destinationEntry = findNodeByPath(state, destination);
  if (destinationEntry?.[1].type === "folder") return `${nodePath(fsNodesFor(state), destinationEntry[0]).replace(/\\$/, "")}\\${baseNameForPath(sourcePath)}`;
  if (destinationArg.endsWith("\\") || destinationArg.endsWith("/")) return `${destination.replace(/\\$/, "")}\\${baseNameForPath(sourcePath)}`;
  return destination;
}

function folderCreationStepsForPath(parentPath: string) {
  const normalized = normalizeNativePath(parentPath).replace(/\\$/, "");
  const parts = normalized.split("\\").filter(Boolean);
  if (parts.length <= 1) return [] as Array<{ path: string; parentPath: string; name: string }>;
  const steps: Array<{ path: string; parentPath: string; name: string }> = [];
  let currentParent = `${parts[0]}\\`;
  for (let index = 1; index < parts.length; index += 1) {
    const name = parts[index];
    const path = `${currentParent.replace(/\\$/, "")}\\${name}`;
    steps.push({ path, parentPath: currentParent, name });
    currentParent = path;
  }
  return steps;
}

function fsNodesFor(state?: NativeSimulatorState | null) {
  return state?.filesystem?.nodes && Object.keys(state.filesystem.nodes).length > 0
    ? state.filesystem.nodes
    : fallbackFilesystemNodes;
}

function networkDrivesFor(state?: NativeSimulatorState | null) {
  return Array.isArray(state?.network?.drives) ? state.network.drives : [];
}

const fallbackNetworkAdapters: NativeNetworkAdapter[] = [
  {
    id: "ethernet0",
    adapterId: "ethernet0",
    name: "Ethernet",
    adapterName: "Ethernet0",
    enabled: true,
    dhcp: true,
    ipv4: "10.50.20.25",
    subnet: "255.255.255.0",
    gateway: "10.50.20.1",
    dns: ["10.50.10.10"],
    description: "Intel(R) Ethernet Connection",
    physicalAddress: "00-11-22-33-44-55",
    type: "ethernet"
  },
  {
    id: "wifi0",
    adapterId: "wifi0",
    name: "Wi-Fi",
    adapterName: "Wi-Fi",
    enabled: false,
    dhcp: true,
    ipv4: "",
    subnet: "",
    gateway: "",
    dns: [],
    description: "Wireless Adapter",
    type: "wifi"
  }
];

function networkAdaptersFor(state?: NativeSimulatorState | null) {
  return Array.isArray(state?.network?.adapters) && state.network.adapters.length > 0
    ? state.network.adapters
    : fallbackNetworkAdapters;
}

function adapterKey(adapter: NativeNetworkAdapter) {
  return adapter.id ?? adapter.adapterId ?? adapter.adapterName ?? adapter.name ?? "ethernet0";
}

function adapterCommandName(adapter: NativeNetworkAdapter) {
  return adapter.adapterName ?? adapter.name ?? adapter.id ?? "Ethernet0";
}

function adapterDisplayName(adapter: NativeNetworkAdapter) {
  const name = adapter.name ?? adapter.adapterName ?? "Ethernet";
  return name === "Ethernet0" ? "Ethernet" : name;
}

function adapterIpv4(adapter: NativeNetworkAdapter) {
  return adapter.ipv4 ?? adapter.ipAddress ?? "";
}

function adapterSubnet(adapter: NativeNetworkAdapter) {
  return adapter.subnet ?? adapter.subnetMask ?? "";
}

function adapterDisplayIpv4(adapter: NativeNetworkAdapter) {
  return adapterIpv4(adapter) || "10.50.20.25";
}

function adapterDisplaySubnet(adapter: NativeNetworkAdapter) {
  return adapterSubnet(adapter) || "255.255.255.0";
}

function adapterDisplayGateway(adapter: NativeNetworkAdapter) {
  return adapter.gateway || "10.50.20.1";
}

function adapterDnsServers(adapter: NativeNetworkAdapter, state?: NativeSimulatorState | null) {
  const values = adapter.dns ?? adapter.dnsServers;
  if (Array.isArray(values) && values.length > 0) return values.filter(Boolean);
  return state?.network?.clientDns ? [state.network.clientDns] : [];
}

function isValidNativeIpv4(value: string) {
  const parts = value.trim().split(".");
  return parts.length === 4 && parts.every((part) => {
    if (!/^\d{1,3}$/.test(part)) return false;
    const octet = Number(part);
    return octet >= 0 && octet <= 255;
  });
}

function actionWithPayload(
  actions: NativeSimulatorAction[],
  commandType: string,
  payload: Record<string, unknown>,
  overrides: Partial<Pick<NativeSimulatorAction, "appId" | "capability" | "label" | "description">> = {}
): NativeSimulatorAction | null {
  const appId = overrides.appId ?? actions.find((action) => action.commandType === commandType)?.appId ?? "";
  const base = appId
    ? selectNativeAction(appId, actions, [commandType], overrides.capability)
    : actions.find((action) => (
      action.commandType === commandType &&
      (!overrides.capability || action.capability === overrides.capability)
    ));
  if (!base) return null;
  return {
    ...base,
    ...overrides,
    id: `${base.id}-ui-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    payload: {
      ...base.payload,
      ...payload
    }
  };
}

function rootNodeIdFor(state?: NativeSimulatorState | null) {
  return state?.filesystem?.rootId ?? "root";
}

function nodePath(nodes: Record<string, NativeFsNode>, nodeId: string) {
  const node = nodes[nodeId];
  if (!node) return "C:\\";
  const parts: string[] = [];
  let current: NativeFsNode | undefined = node;
  let guard = 0;
  while (current && guard < 64) {
    parts.unshift(current.name);
    current = current.parentId ? nodes[current.parentId] : undefined;
    guard += 1;
  }
  const drive = parts.shift() ?? "C:";
  return parts.length === 0 ? `${drive}\\` : `${drive}\\${parts.join("\\")}`;
}

function findNodeByPath(state: NativeSimulatorState | null | undefined, path: string) {
  const nodes = fsNodesFor(state);
  const normalized = normalizeNativePath(path).replace(/\\$/, "").toLowerCase();
  return Object.entries(nodes).find(([nodeId]) => nodePath(nodes, nodeId).replace(/\\$/, "").toLowerCase() === normalized);
}

function childrenForPath(state: NativeSimulatorState | null | undefined, path: string) {
  const nodes = fsNodesFor(state);
  const rootId = rootNodeIdFor(state);
  const folderEntry = normalizeNativePath(path) === "C:\\" ? [rootId, nodes[rootId]] as [string, NativeFsNode] : findNodeByPath(state, path);
  if (!folderEntry) return [] as Array<[string, NativeFsNode]>;
  const folderId = folderEntry[0];
  return Object.entries(nodes)
    .filter(([, node]) => node.parentId === folderId)
    .sort(([, left], [, right]) => {
      if (left.type !== right.type) return left.type === "folder" ? -1 : 1;
      return left.name.localeCompare(right.name, "nl");
    });
}

function nodeDesktopIconAppId(node: NativeFsNode) {
  if (node.type === "folder") return "explorer";
  if (node.name.toLowerCase().endsWith(".txt")) return "notepad";
  return "file";
}

function formatFileType(node: NativeFsNode) {
  if (node.type === "folder") return node.shared || node.shareName ? "Gedeelde map" : "Bestandsmap";
  if (node.name.toLowerCase().endsWith(".txt")) return "Tekstdocument";
  return "Bestand";
}

type NetworkDraft = {
  dhcp: boolean;
  automaticDns: boolean;
  ipv4: string;
  subnet: string;
  gateway: string;
  dns1: string;
  dns2: string;
};

type AdapterContextMenu = {
  x: number;
  y: number;
  adapterId: string;
} | null;

function draftForAdapter(adapter: NativeNetworkAdapter, state?: NativeSimulatorState | null): NetworkDraft {
  const dns = adapterDnsServers(adapter, state);
  const ipv4 = adapterIpv4(adapter);
  const hasManualIpv4 = ipv4.trim().length > 0 && adapter.dhcp === false;
  return {
    dhcp: !hasManualIpv4,
    automaticDns: dns.length === 0,
    ipv4: ipv4 || "10.50.20.25",
    subnet: adapterSubnet(adapter) || "255.255.255.0",
    gateway: adapter.gateway || "10.50.20.1",
    dns1: dns[0] ?? "",
    dns2: dns[1] ?? ""
  };
}

function NetworkConnectionsSurface({
  actions,
  busyActionId,
  onRun,
  simulatorState,
  openApp
}: {
  actions: NativeSimulatorAction[];
  busyActionId: string | null;
  onRun: NativeRunAction;
  simulatorState: NativeSimulatorState | null;
  openApp: (appId: string, launchUrl?: string) => void;
}) {
  const adapters = networkAdaptersFor(simulatorState);
  const [selectedAdapterId, setSelectedAdapterId] = useState(adapterKey(adapters[0]));
  const selectedAdapter = adapters.find((adapter) => adapterKey(adapter) === selectedAdapterId) ?? adapters[0];
  const [contextMenu, setContextMenu] = useState<AdapterContextMenu>(null);
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [ipv4Open, setIpv4Open] = useState(false);
  const [draft, setDraft] = useState<NetworkDraft>(() => draftForAdapter(selectedAdapter, simulatorState));
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!adapters.some((adapter) => adapterKey(adapter) === selectedAdapterId)) {
      setSelectedAdapterId(adapterKey(adapters[0]));
    }
  }, [adapters, selectedAdapterId]);

  useEffect(() => {
    setDraft(draftForAdapter(selectedAdapter, simulatorState));
  }, [selectedAdapter, simulatorState]);

  const enabledCount = adapters.filter((adapter) => adapter.enabled !== false).length;
  const primaryDns = adapterDnsServers(selectedAdapter, simulatorState).join(", ");

  function openProperties(adapter = selectedAdapter) {
    setSelectedAdapterId(adapterKey(adapter));
    setPropertiesOpen(true);
    setContextMenu(null);
    setMessage(null);
    setError(null);
  }

  function openIpv4Properties() {
    setDraft(draftForAdapter(selectedAdapter, simulatorState));
    setIpv4Open(true);
    setContextMenu(null);
    setMessage(null);
    setError(null);
  }

  async function runAdapterCommand(commandType: string, payload: Record<string, unknown>, label: string, capability?: string) {
    const action = actionWithPayload(actions, commandType, payload, { label, capability });
    if (!action) {
      setError(`Actie ${commandType} is niet beschikbaar in dit scenario.`);
      return false;
    }
    const result = await onRun(action);
    if (!result.ok) {
      setError(`${label} is niet opgeslagen (${result.status}).`);
      return false;
    }
    setError(null);
    setMessage(`${label} is opgeslagen.`);
    return true;
  }

  async function setAdapterEnabled(enabled: boolean) {
    await runAdapterCommand(
      "NETWORK_SET_ADAPTER_CONFIG",
      {
        adapterId: selectedAdapter.id ?? selectedAdapter.adapterId,
        adapterName: adapterCommandName(selectedAdapter),
        enabled
      },
      enabled ? "Adapter inschakelen" : "Adapter uitschakelen",
      "network.adapter.write"
    );
  }

  function validateDraft() {
    if (!draft.dhcp) {
      if (!isValidNativeIpv4(draft.ipv4)) return "Vul een geldig IPv4-adres in.";
      if (!isValidNativeIpv4(draft.subnet)) return "Vul een geldig subnetmasker in.";
      if (draft.gateway.trim() && !isValidNativeIpv4(draft.gateway)) return "Vul een geldige standaardgateway in.";
    }
    if (!draft.automaticDns) {
      if (!isValidNativeIpv4(draft.dns1)) return "Vul een geldige voorkeurs-DNS-server in.";
      if (draft.dns2.trim() && !isValidNativeIpv4(draft.dns2)) return "Vul een geldige alternatieve DNS-server in.";
    }
    return null;
  }

  async function applyIpv4(closeAfterSave: boolean) {
    const validation = validateDraft();
    if (validation) {
      setError(validation);
      return;
    }

    const dnsServers = draft.automaticDns ? [] : [draft.dns1.trim(), draft.dns2.trim()].filter(Boolean);
    const basePayload = {
      adapterId: selectedAdapter.id ?? selectedAdapter.adapterId,
      adapterName: adapterCommandName(selectedAdapter)
    };

    if (draft.dhcp) {
      if (selectedAdapter.dhcp !== true) {
        const dhcpOk = await runAdapterCommand(
          "NETWORK_ENABLE_DHCP",
          {
            ...basePayload,
            macAddress: selectedAdapter.macAddress ?? selectedAdapter.physicalAddress,
            preferredIp: adapterIpv4(selectedAdapter)
          },
          "DHCP inschakelen",
          "network.adapter.write"
        );
        if (!dhcpOk) return;
      }
      if (!draft.automaticDns) {
        const dnsOk = await runAdapterCommand(
          "NETWORK_SET_DNS_SERVER",
          {
            ...basePayload,
            dns: dnsServers,
            dnsServers
          },
          "DNS-server instellen",
          "network.dns.write"
        );
        if (!dnsOk) return;
      }
    } else {
      const configOk = await runAdapterCommand(
        "NETWORK_SET_ADAPTER_CONFIG",
        {
          ...basePayload,
          dhcp: false,
          ipv4: draft.ipv4.trim(),
          subnet: draft.subnet.trim(),
          gateway: draft.gateway.trim(),
          dns: dnsServers,
          dnsServers
        },
        "IPv4-configuratie opslaan",
        "network.adapter.write"
      );
      if (!configOk) return;
    }

    if (closeAfterSave) {
      setIpv4Open(false);
      setPropertiesOpen(false);
    }
  }

  return (
    <div className="ncpa-app native-ncpa-surface" data-native-app="ncpa" data-native-parity="ncpa" data-oracle-source="ncpa.js">
      <div className="ncpa-menubar"><span>Bestand</span><span>Bewerken</span><span>Beeld</span><span>Extra</span><span>Geavanceerd</span></div>
      <div className="ncpa-chrome">
        <button className="ncpa-nav-btn" type="button" onClick={() => openApp("controlPanel")}><MmcGlyph kind="back" /></button>
        <button className="ncpa-nav-btn" type="button" onClick={() => openApp("controlPanel")}><MmcGlyph kind="up" /></button>
        <div className="ncpa-address-bar"><span className="ncpa-bc">Configuratiescherm &gt; Netwerk en internet &gt; <b>Netwerkverbindingen</b></span></div>
      </div>
      <div className="ncpa-toolbar">
        <button className="ncpa-btn" type="button" onClick={() => void setAdapterEnabled(false)} disabled={busyActionId !== null || selectedAdapter.enabled === false}>Uitschakelen</button>
        <button className="ncpa-btn" type="button" onClick={() => void setAdapterEnabled(true)} disabled={busyActionId !== null || selectedAdapter.enabled !== false}>Inschakelen</button>
        <span className="ncpa-tb-sep" />
        <button className="ncpa-btn" type="button" onClick={() => openProperties()} disabled={busyActionId !== null}>Eigenschappen</button>
        <button className="ncpa-btn" type="button" onClick={() => setMessage("Netwerkverbindingen zijn vernieuwd.")}>Vernieuwen</button>
        <button className="ncpa-btn" type="button" onClick={() => openApp("netSettings")}>Netwerkcentrum</button>
        <button className="ncpa-btn" type="button" onClick={() => setMessage("Diagnose gestart voor de geselecteerde adapter.")}>Problemen oplossen</button>
      </div>
      <div className="ncpa-body" onClick={() => setContextMenu(null)}>
        <aside className="ncpa-sidebar">
          <div className="ncpa-side-title">Configuratiescherm</div>
          <button className="ncpa-side-item active" type="button">Netwerkverbindingen</button>
          <div className="ncpa-side-note">Taken</div>
          <button className="ncpa-side-link" type="button" onClick={() => openProperties()}>Instellingen van deze verbinding wijzigen</button>
        </aside>
        <main className="ncpa-main">
          <div className="ncpa-main-header"><h2>Netwerkverbindingen</h2><p>Maak verbinding met een netwerk of wijzig netwerkinstellingen.</p></div>
          <div className="ncpa-list" role="listbox" aria-label="Netwerkadapters">
            {adapters.map((adapter) => {
              const key = adapterKey(adapter);
              const isSelected = key === selectedAdapterId;
              const enabled = adapter.enabled !== false;
              return (
                <button
                  className={`ncpa-item ${isSelected ? "selected" : ""} ${enabled ? "" : "disabled-adapter"}`}
                  key={key}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedAdapterId(key);
                    setContextMenu(null);
                  }}
                  onContextMenu={(event: ReactMouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setSelectedAdapterId(key);
                    setContextMenu({ x: event.clientX, y: event.clientY, adapterId: key });
                  }}
                  onDoubleClick={() => openProperties(adapter)}
                >
                  <MmcGlyph kind="network" className={enabled ? "ncpa-adapter-glyph" : "ncpa-adapter-glyph ncpa-adapter-glyph-off"} />
                  <span className="ncpa-item-meta">
                    <span className="ncpa-item-name">{adapterDisplayName(adapter)}</span>
                    <span className="ncpa-item-row2">
                      <span className={`ncpa-item-status ${enabled ? "ncpa-status-on" : "ncpa-status-off"}`}>{enabled ? "Ingeschakeld" : "Uitgeschakeld"}</span>
                      <span className="ncpa-item-ip">{adapterDisplayIpv4(adapter)}</span>
                      <span className="ncpa-item-desc">{adapter.description ?? "Netwerkadapter"}</span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          {message ? <div className="ncpa-inline-message" role="status">{message}</div> : null}
          {error ? <div className="ncpa-inline-error" role="alert">{error}</div> : null}
        </main>
      </div>
      <div className="ncpa-statusbar">{adapters.length} object(en) | {enabledCount} ingeschakeld{primaryDns ? ` | DNS ${primaryDns}` : ""}</div>
      {contextMenu ? (
        <div
          className="win11-context-menu ncpa-adapter-context-menu"
          style={fitMenuPosition(contextMenu.x, contextMenu.y)}
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" onClick={() => { setMessage(`${adapterDisplayName(selectedAdapter)} is ${selectedAdapter.enabled === false ? "uitgeschakeld" : "ingeschakeld"}.`); setContextMenu(null); }}>Status</button>
          <button type="button" onClick={() => { void setAdapterEnabled(selectedAdapter.enabled === false); setContextMenu(null); }}>{selectedAdapter.enabled === false ? "Inschakelen" : "Uitschakelen"}</button>
          <button type="button" onClick={() => openProperties()}>Eigenschappen</button>
        </div>
      ) : null}
      {propertiesOpen ? (
        <div className="ncpa-dialog-backdrop" role="presentation">
          <section className="ncpa-dialog ncpa-properties-dialog" role="dialog" aria-modal="true" aria-label={`${adapterDisplayName(selectedAdapter)} eigenschappen`}>
            <header className="ncpa-dialog-titlebar">
              <strong>{adapterDisplayName(selectedAdapter)} Eigenschappen</strong>
              <button type="button" onClick={() => setPropertiesOpen(false)} aria-label="Sluiten">x</button>
            </header>
            <div className="ncpa-tabs"><button className="ncpa-tab active" type="button">Netwerken</button><button className="ncpa-tab" type="button">Delen</button></div>
            <p className="ncpa-dialog-copy">Deze verbinding gebruikt de volgende onderdelen:</p>
            <div className="ncpa-component-list">
              <label className="ncpa-component-row"><input type="checkbox" checked readOnly /> Client voor Microsoft-netwerken</label>
              <label className="ncpa-component-row"><input type="checkbox" checked readOnly /> Bestands- en printerdeling voor Microsoft-netwerken</label>
              <label className="ncpa-component-row selected"><input type="checkbox" checked readOnly /> Internet Protocol versie 4 (TCP/IPv4)</label>
              <label className="ncpa-component-row"><input type="checkbox" checked readOnly /> Internet Protocol versie 6 (TCP/IPv6)</label>
            </div>
            <div className="ncpa-dialog-actions">
              <button type="button" onClick={openIpv4Properties}>Eigenschappen</button>
              <button type="button">Installeren...</button>
              <button type="button">Verwijderen</button>
            </div>
            <footer className="ncpa-dialog-footer">
              <button type="button" onClick={() => setPropertiesOpen(false)}>Annuleren</button>
              <button type="button" onClick={() => setPropertiesOpen(false)}>OK</button>
            </footer>
          </section>
        </div>
      ) : null}
      {ipv4Open ? (
        <div className="ncpa-dialog-backdrop" role="presentation">
          <section className="ncpa-dialog ncpa-ipv4-dialog" role="dialog" aria-modal="true" aria-label="Internet Protocol versie 4 eigenschappen">
            <header className="ncpa-dialog-titlebar">
              <strong>Internet Protocol versie 4 (TCP/IPv4) Eigenschappen</strong>
              <button type="button" onClick={() => setIpv4Open(false)} aria-label="Sluiten">x</button>
            </header>
            <div className="ncpa-ipv4-section">
              <label className="ncpa-radio-row"><input type="radio" checked={draft.dhcp} onChange={() => setDraft((current) => ({ ...current, dhcp: true }))} /> Automatisch een IP-adres laten toewijzen</label>
              <label className="ncpa-radio-row"><input type="radio" checked={!draft.dhcp} onChange={() => setDraft((current) => ({ ...current, dhcp: false }))} /> Het volgende IP-adres gebruiken:</label>
              <div className="ncpa-ipv4-grid">
                <label>IP-adres:<input disabled={draft.dhcp} value={draft.ipv4} onChange={(event) => setDraft((current) => ({ ...current, ipv4: event.target.value }))} /></label>
                <label>Subnetmasker:<input disabled={draft.dhcp} value={draft.subnet} onChange={(event) => setDraft((current) => ({ ...current, subnet: event.target.value }))} /></label>
                <label>Standaardgateway:<input disabled={draft.dhcp} value={draft.gateway} onChange={(event) => setDraft((current) => ({ ...current, gateway: event.target.value }))} /></label>
              </div>
            </div>
            <div className="ncpa-ipv4-section">
              <label className="ncpa-radio-row"><input type="radio" checked={draft.automaticDns} onChange={() => setDraft((current) => ({ ...current, automaticDns: true }))} /> Automatisch een DNS-serveradres laten toewijzen</label>
              <label className="ncpa-radio-row"><input type="radio" checked={!draft.automaticDns} onChange={() => setDraft((current) => ({ ...current, automaticDns: false }))} /> De volgende DNS-serveradressen gebruiken:</label>
              <div className="ncpa-ipv4-grid">
                <label>Voorkeurs-DNS-server:<input disabled={draft.automaticDns} value={draft.dns1} onChange={(event) => setDraft((current) => ({ ...current, dns1: event.target.value }))} /></label>
                <label>Alternatieve DNS-server:<input disabled={draft.automaticDns} value={draft.dns2} onChange={(event) => setDraft((current) => ({ ...current, dns2: event.target.value }))} /></label>
              </div>
            </div>
            {error ? <div className="ncpa-inline-error" role="alert">{error}</div> : null}
            <footer className="ncpa-dialog-footer">
              <button type="button" onClick={() => setIpv4Open(false)}>Annuleren</button>
              <button type="button" onClick={() => void applyIpv4(false)} disabled={busyActionId !== null}>Toepassen</button>
              <button type="button" onClick={() => void applyIpv4(true)} disabled={busyActionId !== null}>OK</button>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function NetworkSettingsSurface({
  actions,
  busyActionId,
  onRun,
  simulatorState,
  openApp
}: {
  actions: NativeSimulatorAction[];
  busyActionId: string | null;
  onRun: NativeRunAction;
  simulatorState: NativeSimulatorState | null;
  openApp: (appId: string, launchUrl?: string) => void;
}) {
  const adapter = networkAdaptersFor(simulatorState)[0];
  const [activeSection, setActiveSection] = useState<"status" | "ethernet" | "ip" | "advanced">("status");
  const [draft, setDraft] = useState<NetworkDraft>(() => draftForAdapter(adapter, simulatorState));
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(draftForAdapter(adapter, simulatorState));
  }, [adapter, simulatorState]);

  async function saveSettings() {
    if (!draft.dhcp) {
      if (!isValidNativeIpv4(draft.ipv4) || !isValidNativeIpv4(draft.subnet)) {
        setError("Controleer het IPv4-adres en subnetmasker.");
        return;
      }
      if (draft.gateway.trim() && !isValidNativeIpv4(draft.gateway)) {
        setError("Controleer de standaardgateway.");
        return;
      }
    }
    if (!draft.automaticDns && (!isValidNativeIpv4(draft.dns1) || (draft.dns2.trim() && !isValidNativeIpv4(draft.dns2)))) {
      setError("Controleer de DNS-serveradressen.");
      return;
    }

    const dnsServers = draft.automaticDns ? [] : [draft.dns1.trim(), draft.dns2.trim()].filter(Boolean);
    const action = actionWithPayload(actions, "NETWORK_SET_ADAPTER_CONFIG", {
      adapterId: adapter.id ?? adapter.adapterId,
      adapterName: adapterCommandName(adapter),
      dhcp: draft.dhcp,
      ipv4: draft.ipv4.trim(),
      subnet: draft.subnet.trim(),
      gateway: draft.gateway.trim(),
      dns: dnsServers,
      dnsServers
    }, { capability: "network.adapter.write", label: "Netwerkinstellingen opslaan" });
    if (!action) {
      setError("Netwerkinstellingen opslaan is niet beschikbaar in dit scenario.");
      return;
    }
    const result = await onRun(action);
    if (!result.ok) {
      setError(`Netwerkinstellingen zijn niet opgeslagen (${result.status}).`);
      return;
    }
    setError(null);
    setMessage("Netwerkinstellingen zijn opgeslagen.");
  }

  async function runNetworkCommand(commandType: string, payload: Record<string, unknown>, label: string) {
    const action = actionWithPayload(actions, commandType, {
      adapterId: adapter.id ?? adapter.adapterId,
      adapterName: adapterCommandName(adapter),
      ...payload
    }, { label });
    if (!action) {
      setError(`${label} is niet beschikbaar in dit scenario.`);
      return;
    }
    const result = await onRun(action);
    setError(result.ok ? null : `${label} mislukt (${result.status}).`);
    if (result.ok) setMessage(`${label} uitgevoerd.`);
  }

  const dnsServers = adapterDnsServers(adapter, simulatorState);

  return (
    <div className="settings-app native-network-settings" data-native-app="netSettings" data-native-parity="settingsNetwork">
      <aside className="settings-nav">
        <div className="settings-nav-title">Netwerk &amp; internet</div>
        <button className={`settings-nav-item ${activeSection === "status" ? "active" : ""}`} type="button" onClick={() => setActiveSection("status")}><MmcGlyph kind="network" />Status</button>
        <button className={`settings-nav-item ${activeSection === "ethernet" ? "active" : ""}`} type="button" onClick={() => setActiveSection("ethernet")}><MmcGlyph kind="settings" />Ethernet</button>
        <button className={`settings-nav-item ${activeSection === "ip" ? "active" : ""}`} type="button" onClick={() => setActiveSection("ip")}><MmcGlyph kind="terminal" />IP-configuratie</button>
        <button className={`settings-nav-item ${activeSection === "advanced" ? "active" : ""}`} type="button" onClick={() => setActiveSection("advanced")}><MmcGlyph kind="properties" />Geavanceerd</button>
      </aside>
      <main className="settings-content">
        <h2 className="settings-h2">{activeSection === "ethernet" ? "Ethernet" : activeSection === "ip" ? "IP-configuratie" : activeSection === "advanced" ? "Geavanceerde netwerkinstellingen" : "Netwerkstatus"}</h2>
        <section className="settings-card net-status-card" onDoubleClick={() => openApp("ncpa")}>
          <MmcGlyph kind="network" className="net-status-icon" />
          <div>
            <strong>{adapter.enabled === false ? "Uitgeschakeld" : "Verbonden"} - {adapterCommandName(adapter)}</strong>
            <p>IPv4: {adapterIpv4(adapter) || "Geen adres"} | Gateway: {adapter.gateway || "Geen gateway"}</p>
          </div>
        </section>
        <section className="settings-card net-connection-map" data-native-parity="netsettings-connection-map">
          <div><MmcGlyph kind="computer" /><strong>CLIENT01</strong><span>10.50.20.25</span></div>
          <div className="net-link-line" />
          <div><MmcGlyph kind="network" /><strong>Ethernet0</strong><span>1.0 Gbps</span></div>
          <div className="net-link-line" />
          <div><MmcGlyph kind="server" /><strong>SRV01 DNS/DHCP</strong><span>10.50.10.10</span></div>
        </section>
        <section className="settings-card">
          <h3 className="settings-h3">Eigenschappen</h3>
          <div className="settings-status-row"><span className="settings-status-key">Adapter</span><span className="settings-status-val">{adapter.description ?? adapterCommandName(adapter)}</span></div>
          <div className="settings-status-row"><span className="settings-status-key">IPv4-adres</span><span className="settings-status-val">{adapterDisplayIpv4(adapter)}</span></div>
          <div className="settings-status-row"><span className="settings-status-key">Subnetmasker</span><span className="settings-status-val">{adapterDisplaySubnet(adapter)}</span></div>
          <div className="settings-status-row"><span className="settings-status-key">Standaardgateway</span><span className="settings-status-val">{adapterDisplayGateway(adapter)}</span></div>
          <div className="settings-status-row"><span className="settings-status-key">DNS-servers</span><span className="settings-status-val">{dnsServers.join(", ") || "-"}</span></div>
          <div className="settings-status-row"><span className="settings-status-key">DHCP</span><span className="settings-status-val">{adapter.dhcp ? "Ingeschakeld" : "Uitgeschakeld"}</span></div>
          <div className="settings-status-row"><span className="settings-status-key">Fysiek adres</span><span className="settings-status-val">00-15-5D-15-20-25</span></div>
          <div className="settings-status-row"><span className="settings-status-key">DNS-achtervoegsel</span><span className="settings-status-val">advanced-ti.local</span></div>
        </section>
        <section className="settings-card settings-network-editor">
          <h3 className="settings-h3">Ethernet - {adapterCommandName(adapter)}</h3>
          <label className="settings-toggle-label">
            <span>DHCP ingeschakeld</span>
            <span className={`toggle-switch ${draft.dhcp ? "is-on" : ""}`}>
              <input type="checkbox" checked={draft.dhcp} onChange={(event) => setDraft((current) => ({ ...current, dhcp: event.target.checked }))} />
              <span className="toggle-slider" />
            </span>
          </label>
          <label className="settings-toggle-label">
            <span>DNS automatisch</span>
            <span className={`toggle-switch ${draft.automaticDns ? "is-on" : ""}`}>
              <input type="checkbox" checked={draft.automaticDns} onChange={(event) => setDraft((current) => ({ ...current, automaticDns: event.target.checked }))} />
              <span className="toggle-slider" />
            </span>
          </label>
          <div className="settings-form-grid">
            <label className="settings-field"><span>IPv4-adres</span><input className="settings-input" value={draft.ipv4} disabled={draft.dhcp} onChange={(event) => setDraft((current) => ({ ...current, ipv4: event.target.value }))} /></label>
            <label className="settings-field"><span>Subnetmasker</span><input className="settings-input" value={draft.subnet} disabled={draft.dhcp} onChange={(event) => setDraft((current) => ({ ...current, subnet: event.target.value }))} /></label>
            <label className="settings-field"><span>Standaardgateway</span><input className="settings-input" value={draft.gateway} disabled={draft.dhcp} onChange={(event) => setDraft((current) => ({ ...current, gateway: event.target.value }))} /></label>
            <label className="settings-field"><span>Voorkeurs-DNS-server</span><input className="settings-input" value={draft.dns1} disabled={draft.automaticDns} onChange={(event) => setDraft((current) => ({ ...current, dns1: event.target.value }))} /></label>
            <label className="settings-field"><span>Alternatieve DNS-server</span><input className="settings-input" value={draft.dns2} disabled={draft.automaticDns} onChange={(event) => setDraft((current) => ({ ...current, dns2: event.target.value }))} /></label>
          </div>
          <div className="settings-actions-row">
            <button className="settings-link-btn primary" type="button" onClick={() => void saveSettings()} disabled={busyActionId !== null}>Opslaan</button>
            <button className="settings-link-btn" type="button" onClick={() => void runNetworkCommand("NETWORK_RELEASE_RENEW_LEASE", {}, "Lease vernieuwen")}>Release/Renew</button>
            <button className="settings-link-btn" type="button" onClick={() => void runNetworkCommand("NETWORK_ENABLE_DHCP", { dhcp: true }, "DHCP inschakelen")}>DHCP automatisch</button>
            <button className="settings-link-btn" type="button" onClick={() => openApp("ncpa")}>Adapteropties openen</button>
            <button className="settings-link-btn" type="button" onClick={() => openApp("powershell")}>PowerShell controle</button>
          </div>
          {message ? <div className="ncpa-inline-message" role="status">{message}</div> : null}
          {error ? <div className="ncpa-inline-error" role="alert">{error}</div> : null}
        </section>
        <section className="settings-card net-advanced-grid" data-native-parity="netsettings-advanced-actions">
          <button type="button" onClick={() => openApp("ncpa")}><MmcGlyph kind="properties" /><span>IPv4 eigenschappen</span><small>Control Panel adapterdialoog</small></button>
          <button type="button" onClick={() => openApp("cmd")}><MmcGlyph kind="terminal" /><span>ipconfig /all</span><small>Command Prompt evidence</small></button>
          <button type="button" onClick={() => openApp("powershell")}><MmcGlyph kind="terminal" /><span>Get-NetIPConfiguration</span><small>PowerShell evidence</small></button>
        </section>
      </main>
    </div>
  );
}

function renderAppSurface(
  appId: string,
  actions: NativeSimulatorAction[],
  busyActionId: string | null,
  runAction: NativeRunAction,
  simulatorState: NativeSimulatorState | null,
  openApp: (appId: string, launchUrl?: string) => void,
  windowState?: NativeWindowState
) {
  const normalized = canonicalAppId(appId);
  const appActions = actionsForNativeApp(normalized, actions);
  const runAppCommand = (commandType: string, capability?: string) => {
    const action = selectNativeAction(normalized, appActions, [commandType], capability);
    if (action) void runAction(action);
  };
  const runFirstAppCommand = (...commandTypes: string[]) => {
    const action = selectNativeAction(normalized, appActions, commandTypes);
    if (action) void runAction(action);
  };
  const runPrimaryAppAction = () => {
    const action = selectNativeAction(normalized, appActions, appActions.map((item) => item.commandType));
    if (action) void runAction(action);
  };
  if (normalized === "explorer") {
    return <ExplorerSurface onRun={runAction} openApp={openApp} simulatorState={simulatorState} />;
  }

  if (normalized === "server-manager") {
    return (
      <div className="sm-shell native-server-manager" data-native-app="server-manager" data-native-parity="server-manager">
        <div className="sm-topbar">
          <div className="sm-topbar-left">
            <button className="sm-nav-glyph" type="button" aria-label="Back" disabled>&larr;</button>
            <button className="sm-nav-glyph" type="button" aria-label="Forward" disabled>&rarr;</button>
            <div className="sm-topbar-brand"><span className="sm-topbar-brand-icon">S</span><strong>Server Manager</strong></div>
            <span className="sm-topbar-sep" />
            <div className="sm-topbar-breadcrumb">Server Manager &rsaquo; <span>Dashboard</span></div>
          </div>
          <div className="sm-topbar-right">
            <button className="sm-menu-btn sm-icon-btn" type="button" title="Refresh" onClick={() => runFirstAppCommand("SERVER_ROLE_INSTALL", "SERVER_DC_PROMOTE")}>&#8635;</button>
            <button className="sm-menu-btn sm-flag-btn has-alert" type="button" title="Notifications" onClick={() => runAppCommand("SERVER_DC_PROMOTE")}>&#9873;<span className="sm-flag-count">1</span></button>
            <button className="sm-menu-btn" type="button" onClick={() => runAppCommand("SERVER_ROLE_INSTALL")}>Manage</button>
            <button className="sm-menu-btn" type="button" onClick={() => openApp("aduc")}>Tools</button>
            <button className="sm-menu-btn" type="button" onClick={() => openApp("msinfo32")}>View</button>
            <button className="sm-menu-btn" type="button" onClick={() => openApp("eventvwr")}>Help</button>
          </div>
        </div>
        <div className="sm-body">
          <aside className="sm-nav">
            <div className="sm-nav-section-label">Server Manager</div>
            <button className="sm-nav-item active" type="button" onClick={() => openApp("server-manager")}><MmcGlyph kind="monitor" />Dashboard</button>
            <button className="sm-nav-item" type="button" onClick={() => openApp("sysdm")}><MmcGlyph kind="server" />Local Server</button>
            <button className="sm-nav-item" type="button" onClick={() => openApp("services")}><MmcGlyph kind="network" />All Servers</button>
            <div className="sm-nav-section-label">Roles</div>
            <button className="sm-nav-item" type="button" onClick={() => openApp("aduc")}><MmcGlyph kind="group" />AD DS</button>
            <button className="sm-nav-item" type="button" onClick={() => openApp("dns-manager")}><MmcGlyph kind="network" />DNS</button>
            <button className="sm-nav-item" type="button" onClick={() => openApp("explorer")}><MmcGlyph kind="folder" />File and Storage Services</button>
          </aside>
          <main className="sm-main">
            <header className="sm-header">
              <div>
                <div className="sm-header-kicker">Server Manager</div>
                <h2>Microsoft Windows Server 2025 Datacenter</h2>
                <span className="sm-badge sm-badge-ok">Online</span>
              </div>
              <div className="sm-header-actions">
                <button className="sm-action-btn" type="button" onClick={() => runFirstAppCommand("SERVER_ROLE_INSTALL", "SERVER_DC_PROMOTE")}>Configure As Server</button>
                <button className="sm-action-btn sm-action-primary" type="button" onClick={() => runAppCommand("SERVER_ROLE_INSTALL")}>+ Add roles and features</button>
              </div>
            </header>
            <section className="sm-banner">
              <div className="sm-banner-icon">&#9873;</div>
              <div className="sm-banner-text"><strong>Configuration required for Active Directory Domain Services</strong><span>Use Promote this server to a domain controller to complete the AD DS deployment.</span></div>
              <button className="sm-action-btn sm-action-primary" type="button" onClick={() => runAppCommand("SERVER_DC_PROMOTE")}>Promote this server to a domain controller</button>
            </section>
            <section className="sm-content">
              <div className="sm-tile-grid">
                <button className="sm-tile" type="button" onClick={() => openApp("sysdm")}><strong>Local Server</strong><span>SRV01 online</span><small>Manageable</small></button>
                <button className="sm-tile sm-tile-warn" type="button" onClick={() => runAppCommand("SERVER_DC_PROMOTE")}><strong>AD DS</strong><span>Configuration required</span><small>Post-deployment task pending</small></button>
                <button className="sm-tile" type="button" onClick={() => openApp("dns-manager")}><strong>DNS</strong><span>Zones available</span><small>cutover170.local</small></button>
                <button className="sm-tile" type="button" onClick={() => openApp("dhcpManager")}><strong>DHCP</strong><span>Scope active</span><small>Cutover-170</small></button>
              </div>
              <div className="sm-server-list">
                <div className="sm-list-header"><span>Server</span><span>Roles</span><span>Status</span></div>
                <div className="sm-list-row" onDoubleClick={() => openApp("server-manager")}><strong>SRV01</strong><span>AD DS, DNS, DHCP</span><span className="sm-badge sm-badge-ok">Manageable</span></div>
                <div className="sm-list-row" onDoubleClick={() => openApp("sysdm")}><strong>CLIENT01</strong><span>Workstation</span><span className="sm-badge sm-badge-ok">Online</span></div>
              </div>
              <section className="sm-detail-grid" data-native-parity="server-manager-role-depth">
                <div className="sm-detail-card">
                  <header><MmcGlyph kind="server" /><strong>Local Server</strong><button type="button" onClick={() => openApp("sysdm")}>Properties</button></header>
                  <dl><div><dt>Computer name</dt><dd>SRV01</dd></div><div><dt>Domain</dt><dd>advanced-ti.local</dd></div><div><dt>NIC Teaming</dt><dd>Disabled</dd></div></dl>
                </div>
                <div className="sm-detail-card">
                  <header><MmcGlyph kind="warning" /><strong>Post-deployment Configuration</strong><button type="button" onClick={() => runAppCommand("SERVER_DC_PROMOTE")}>Complete task</button></header>
                  <p>Promote this server to a domain controller and verify DNS delegation before student evidence is accepted.</p>
                </div>
                <div className="sm-detail-card">
                  <header><MmcGlyph kind="list" /><strong>Events</strong><button type="button" onClick={() => openApp("eventvwr")}>Open logs</button></header>
                  <dl><div><dt>Critical</dt><dd>0</dd></div><div><dt>Warning</dt><dd>2</dd></div><div><dt>Information</dt><dd>18</dd></div></dl>
                </div>
              </section>
              <ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} />
            </section>
          </main>
        </div>
        <div className="sm-statusbar"><span className="sm-status-dot ok" />1 server online <span className="sm-status-dot warn" />1 pending task <span className="sm-status-spacer" />SRV01 - Administrator</div>
      </div>
    );
  }

  if (normalized === "dhcpManager") {
    return (
      <div className="mmc-app dhcp-shell native-mmc-surface" data-native-app="dhcpManager" data-native-parity="dhcpManager" data-oracle-source="dhcpManager.js">
        <div className="mmc-menubar" onClick={runPrimaryAppAction}><span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span></div>
        <div className="dhcp-toolbar">
          <button className="dhcp-tb-btn" type="button" onClick={() => runAppCommand("DHCP_CREATE_SCOPE")}><MmcGlyph kind="add" /><span>Nieuwe scope</span></button>
          <button className="dhcp-tb-btn" type="button" onClick={() => runAppCommand("DHCP_SET_OPTION")}><MmcGlyph kind="edit" /><span>Scope-opties</span></button>
          <span className="dhcp-tb-sep" />
          <button className="dhcp-tb-btn" type="button" onClick={() => runAppCommand("DHCP_ADD_RESERVATION")}><MmcGlyph kind="lock" /><span>Nieuwe reservering</span></button>
          <button className="dhcp-tb-btn" type="button" onClick={() => runAppCommand("DHCP_SET_OPTION")}><MmcGlyph kind="refresh" /><span>Verifiëren</span></button>
        </div>
        <div className="mmc-shell">
          <div className="mmc-tree">
            <button className="dhcp-tree-server" type="button" onClick={() => runAppCommand("DHCP_CREATE_SCOPE")}><MmcGlyph kind="server" /><span>SRV01.advanced-ti.local</span></button>
            <button className="dhcp-tree-section" type="button" onClick={() => runAppCommand("DHCP_SET_OPTION")}><MmcGlyph kind="network" /><span>IPv4</span></button>
            <button className="dhcp-tree-scope active" type="button" onClick={() => runAppCommand("DHCP_SET_OPTION")}><span className="dhcp-tree-scope-bullet" /><MmcGlyph kind="folder" /><span className="dhcp-tree-scope-name">Cutover-170</span><span className="dhcp-tree-scope-dot dhcp-dot-on" /></button>
            <button className="dhcp-tree-child" type="button" onClick={() => runAppCommand("DHCP_SET_OPTION")}><span className="dhcp-tree-line" /><MmcGlyph kind="folder" /><span>Adrespool</span></button>
            <button className="dhcp-tree-child" type="button" onClick={() => runAppCommand("DHCP_SET_OPTION")}><span className="dhcp-tree-line" /><MmcGlyph kind="list" /><span>Adresleases</span></button>
            <button className="dhcp-tree-child" type="button" onClick={() => runAppCommand("DHCP_ADD_RESERVATION")}><span className="dhcp-tree-line" /><MmcGlyph kind="lock" /><span>Reserveringen</span></button>
            <button className="dhcp-tree-child" type="button" onClick={() => runAppCommand("DHCP_SET_OPTION")}><span className="dhcp-tree-line" /><MmcGlyph kind="settings" /><span>Scope-opties</span></button>
            <button className="dhcp-tree-section dhcp-tree-section-muted" type="button" onClick={() => runAppCommand("DHCP_CREATE_SCOPE")}><MmcGlyph kind="network" /><span>IPv6</span></button>
          </div>
          <div className="mmc-main dhcp-main-pane">
            <div className="mmc-content">
              <div className="mmc-split">
                <div className="dhcp-list-pane">
                  <table className="dhcp-table">
                    <thead><tr><th>Scope</th><th>Bereik</th><th>Subnetmasker</th><th>Lease</th><th>Uitsluitingen</th><th>Status</th></tr></thead>
                    <tbody>
                      <tr className="dhcp-row-selected" onDoubleClick={() => runAppCommand("DHCP_SET_OPTION")}><td className="dhcp-td-name"><MmcGlyph kind="folder" /><span>Cutover-170</span></td><td className="dhcp-mono">10.50.20.100 - 10.50.20.180</td><td className="dhcp-mono">255.255.255.0</td><td>8</td><td>0</td><td><span className="dhcp-badge dhcp-badge-on">Actief</span></td></tr>
                      <tr onDoubleClick={() => runAppCommand("DHCP_ADD_RESERVATION")}><td className="dhcp-td-name"><MmcGlyph kind="lock" /><span>WS-152</span></td><td className="dhcp-mono">10.50.20.152</td><td className="dhcp-mono">00-15-5D-15-20-52</td><td>Reserved</td><td>0</td><td><span className="dhcp-badge dhcp-badge-reserved">Reservering</span></td></tr>
                      <tr onDoubleClick={() => runAppCommand("DHCP_SET_OPTION")}><td className="dhcp-td-name"><MmcGlyph kind="settings" /><span>003 Router</span></td><td className="dhcp-mono">10.50.20.1</td><td className="dhcp-mono">Scope option</td><td>-</td><td>-</td><td><span className="dhcp-badge dhcp-badge-option">Optie</span></td></tr>
                      <tr onDoubleClick={() => runAppCommand("DHCP_SET_OPTION")}><td className="dhcp-td-name"><MmcGlyph kind="settings" /><span>006 DNS Servers</span></td><td className="dhcp-mono">10.50.20.10</td><td className="dhcp-mono">Scope option</td><td>-</td><td>-</td><td><span className="dhcp-badge dhcp-badge-option">Optie</span></td></tr>
                    </tbody>
                  </table>
                </div>
                <div className="mmc-detailpane dhcp-detail-pane">
                  <div className="mmc-group"><div className="dhcp-detail-ip-range">10.50.20.100 - 10.50.20.180</div><div className="dhcp-detail-row"><span className="dhcp-detail-key">Scope</span><span className="dhcp-detail-val">Cutover-170</span></div></div>
                  <div className="mmc-group"><h4>Configuratie</h4><div className="dhcp-detail-row"><span className="dhcp-detail-key">Router</span><span className="dhcp-detail-val">10.50.20.1</span></div><div className="dhcp-detail-row"><span className="dhcp-detail-key">DNS-server</span><span className="dhcp-detail-val">10.50.20.10</span></div><div className="dhcp-detail-row"><span className="dhcp-detail-key">Domein</span><span className="dhcp-detail-val">advanced-ti.local</span></div></div>
                  <div className="mmc-group dhcp-lease-grid" data-native-parity="dhcp-scope-depth"><h4>Lease- en optiecontrole</h4><div><span>Adresleases</span><strong>8 actief</strong></div><div><span>Reserveringen</span><strong>WS-152</strong></div><div><span>Optie 003</span><strong>10.50.20.1</strong></div><div><span>Optie 006</span><strong>10.50.20.10</strong></div></div>
                </div>
              </div>
            </div>
            <div className="dhcp-statusbar">Scope: Cutover-170 | Leases: 8 | Reserveringen: 1 | Opties: 003, 006, 015 | Actief</div>
          </div>
        </div>
      </div>
    );
  }

  if (normalized === "services") {
    return (
      <div className="svc-shell native-mmc-surface" data-native-app="services" data-native-parity="services" data-oracle-source="services.js">
        <div className="svc-menubar" onClick={() => runFirstAppCommand("SERVICE_START", "SERVICE_SET_STARTUP_TYPE")}><span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span></div>
        <div className="svc-toolbar">
          <button className="svc-tb-btn" type="button" onClick={() => runAppCommand("SERVICE_START")}><MmcGlyph kind="play" /><span>Starten</span></button>
          <button className="svc-tb-btn" type="button" onClick={() => runAppCommand("SERVICE_STOP")}><MmcGlyph kind="stop" /><span>Stoppen</span></button>
          <button className="svc-tb-btn" type="button" onClick={() => runAppCommand("SERVICE_SET_STARTUP_TYPE")}><MmcGlyph kind="refresh" /><span>Opnieuw starten</span></button>
          <button className="svc-tb-btn" type="button" onClick={() => runAppCommand("SERVICE_SET_STARTUP_TYPE")}><MmcGlyph kind="settings" /><span>Eigenschappen</span></button>
          <div style={{ flex: 1 }} />
          <input className="svc-filter-input" aria-label="Services filteren" placeholder="Services filteren..." />
        </div>
        <div className="svc-main">
          <div className="svc-tree">
            <button className="svc-tree-root" type="button" onClick={() => runAppCommand("SERVICE_START")}><MmcGlyph kind="settings" /><span>Services (lokaal)</span></button>
            <button className="svc-tree-item active" type="button" onClick={() => runAppCommand("SERVICE_SET_STARTUP_TYPE")}>Services</button>
            <button className="svc-tree-item" type="button" onClick={() => runAppCommand("SERVICE_START")}>Uitgebreide weergave</button>
            <button className="svc-tree-item" type="button" onClick={() => runAppCommand("SERVICE_SET_STARTUP_TYPE")}>Standaardweergave</button>
          </div>
          <div className="svc-content">
            <div className="svc-split">
              <div className="svc-list-pane">
                <table className="svc-table">
                  <thead><tr><th /><th>Naam</th><th>Beschrijving</th><th>Status</th><th>Opstarttype</th><th>Aanmelden als</th></tr></thead>
                  <tbody>
                    <tr className="svc-row-selected" onDoubleClick={() => runAppCommand("SERVICE_START")}><td>&#9679;</td><td>WinRM</td><td>Windows Remote Management</td><td>Running</td><td>Automatic</td><td>Local System</td></tr>
                    <tr onDoubleClick={() => runAppCommand("SERVICE_SET_STARTUP_TYPE")}><td>&#9675;</td><td>Spooler</td><td>Print Spooler</td><td>Stopped</td><td>Manual</td><td>Local Service</td></tr>
                    <tr onDoubleClick={() => runAppCommand("SERVICE_START")}><td>&#9679;</td><td>TermService</td><td>Remote Desktop Services</td><td>Running</td><td>Automatic</td><td>Network Service</td></tr>
                    <tr onDoubleClick={() => runAppCommand("SERVICE_SET_STARTUP_TYPE")}><td>&#9679;</td><td>Dnscache</td><td>DNS Client</td><td>Running</td><td>Automatic</td><td>Network Service</td></tr>
                    <tr onDoubleClick={() => runAppCommand("SERVICE_STOP")}><td>&#9675;</td><td>RemoteRegistry</td><td>Remote Registry</td><td>Stopped</td><td>Disabled</td><td>Local Service</td></tr>
                  </tbody>
                </table>
              </div>
              <div className="svc-detailpane">
                <div className="svc-detail-head"><MmcGlyph kind="settings" /><strong>WinRM</strong></div>
                <p>Start, stop en wijzig services via de native command-bus.</p>
                <div className="svc-property-grid" data-native-parity="services-properties">
                  <div><span>Servicenaam</span><strong>WinRM</strong></div>
                  <div><span>Weergavenaam</span><strong>Windows Remote Management</strong></div>
                  <div><span>Pad naar uitvoerbaar bestand</span><strong>C:\Windows\System32\svchost.exe -k NetworkService</strong></div>
                  <div><span>Herstel</span><strong>Service opnieuw starten na eerste fout</strong></div>
                </div>
                <div className="svc-tab-row" role="tablist" aria-label="Service properties">
                  <button className="active" type="button" onClick={() => runAppCommand("SERVICE_SET_STARTUP_TYPE")}>Algemeen</button>
                  <button type="button" onClick={() => runAppCommand("SERVICE_SET_STARTUP_TYPE")}>Aanmelden</button>
                  <button type="button" onClick={() => runAppCommand("SERVICE_SET_STARTUP_TYPE")}>Herstel</button>
                  <button type="button" onClick={() => runAppCommand("SERVICE_SET_STARTUP_TYPE")}>Afhankelijkheden</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="svc-statusbar">Services: 5 zichtbaar - Actief: 3 - Gestopt: 2</div>
      </div>
    );
  }

  if (normalized === "wf") {
    return (
      <div className="wf-shell native-mmc-surface" data-native-app="wf" data-native-parity="wf-advanced" data-oracle-source="wf.js">
        <div className="wf-menubar" onClick={() => runFirstAppCommand("FIREWALL_ENABLE_RULE", "FIREWALL_SET_PROFILE")}><span className="wf-menu-item">Bestand</span><span className="wf-menu-item">Actie</span><span className="wf-menu-item">Beeld</span><span className="wf-menu-item">Help</span></div>
        <div className="wf-toolbar"><button className="wf-tb-btn wf-primary" type="button" onClick={() => runAppCommand("FIREWALL_ENABLE_RULE")}><MmcGlyph kind="add" /> Nieuwe regel</button><span className="wf-toolbar-sep" /><button className="wf-tb-btn" type="button" onClick={() => runAppCommand("FIREWALL_SET_PROFILE")}><MmcGlyph kind="filter" /> Filteren</button><button className="wf-tb-btn" type="button" onClick={() => runFirstAppCommand("FIREWALL_ENABLE_RULE", "FIREWALL_SET_PROFILE")}><MmcGlyph kind="refresh" /> Vernieuwen</button><button className="wf-tb-btn" type="button" onClick={() => runAppCommand("FIREWALL_SET_PROFILE")}><MmcGlyph kind="settings" /> Eigenschappen</button></div>
        <div className="wf-body-split">
          <div className="wf-tree"><button className="wf-tree-root active" type="button" onClick={() => runAppCommand("FIREWALL_SET_PROFILE")}><span className="wf-tree-caret">&#9656;</span><span>Windows Defender Firewall met geavanceerde beveiliging op Lokale computer</span></button><button className="wf-tree-item" type="button" onClick={() => runAppCommand("FIREWALL_ENABLE_RULE")}><MmcGlyph kind="inbound" />Inkomende regels</button><button className="wf-tree-item" type="button" onClick={() => runAppCommand("FIREWALL_SET_PROFILE")}><MmcGlyph kind="outbound" />Uitgaande regels</button><button className="wf-tree-item" type="button" onClick={() => runFirstAppCommand("FIREWALL_ENABLE_RULE", "FIREWALL_SET_PROFILE")}><MmcGlyph kind="shield" />Verbindingsbeveiligingsregels</button><button className="wf-tree-item" type="button" onClick={() => runFirstAppCommand("FIREWALL_ENABLE_RULE", "FIREWALL_SET_PROFILE")}><MmcGlyph kind="monitor" />Controle</button></div>
          <div className="wf-content">
            <div className="wf-content-header">Inkomende regels</div>
            <div className="wf-filterbar" data-native-parity="wf-rule-filter"><button type="button" onClick={() => runAppCommand("FIREWALL_SET_PROFILE")}>Profiel: Domein</button><button type="button" onClick={() => runAppCommand("FIREWALL_ENABLE_RULE")}>Groep: Remote Desktop</button><button type="button" onClick={() => runFirstAppCommand("FIREWALL_ENABLE_RULE", "FIREWALL_SET_PROFILE")}>Actie: Toestaan</button></div>
            <div className="wf-content-split">
              <div className="wf-rule-list">
                <table className="wf-rule-table"><thead><tr><th>Naam</th><th>Groep</th><th>Profiel</th><th>Ingeschakeld</th><th>Actie</th><th>Poort</th></tr></thead><tbody><tr className="wf-rule-row selected" onDoubleClick={() => runAppCommand("FIREWALL_ENABLE_RULE")}><td><span className="wf-rule-icon">&#9679;</span>Remote Desktop - User Mode (TCP-In)</td><td>Remote Desktop</td><td>Domein</td><td>Ja</td><td>Toestaan</td><td>3389</td></tr><tr onDoubleClick={() => runAppCommand("FIREWALL_ENABLE_RULE")}><td><span className="wf-rule-icon wf-rule-icon-off">&#9679;</span>File and Printer Sharing (SMB-In)</td><td>File and Printer Sharing</td><td>Domein</td><td>Nee</td><td>Toestaan</td><td>445</td></tr><tr onDoubleClick={() => runAppCommand("FIREWALL_SET_PROFILE")}><td><span className="wf-rule-icon">&#9679;</span>Windows Remote Management (HTTP-In)</td><td>Windows Remote Management</td><td>Domein</td><td>Ja</td><td>Toestaan</td><td>5985</td></tr></tbody></table>
              </div>
              <aside className="wf-detail-pane">
                <h4>Remote Desktop - User Mode (TCP-In)</h4>
                <div className="wf-detail-row"><span>Algemeen</span><strong>Ingeschakeld, toestaan</strong></div>
                <div className="wf-detail-row"><span>Programma</span><strong>%SystemRoot%\System32\svchost.exe</strong></div>
                <div className="wf-detail-row"><span>Protocollen en poorten</span><strong>TCP 3389</strong></div>
                <div className="wf-detail-row"><span>Profielen</span><strong>Domein</strong></div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (normalized === "eventvwr") {
    return (
      <div className="mmc-app evt-shell native-mmc-surface" data-native-app="eventvwr" data-native-parity="eventvwr" data-oracle-source="eventvwr.js">
        <div className="mmc-menubar" onClick={runPrimaryAppAction}><span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span></div>
        <div className="evt-toolbar">
          <div className="evt-tb-group">
            <button className="evt-tb-btn" type="button" onClick={() => runAppCommand("EVENT_VIEWER_RECORD_CHECK")}><MmcGlyph kind="export" /><span>Logboek exporteren</span></button>
            <button className="evt-tb-btn" type="button" onClick={() => runAppCommand("AUDIT_RECORD_EVENT")}><MmcGlyph kind="refresh" /><span>Vernieuwen</span></button>
          </div>
          <span className="evt-tb-sep" />
          <div className="evt-tb-group evt-filter-group">
            <input className="evt-filter-input" placeholder="Zoeken op bericht of bron..." />
            <select className="evt-filter-select" aria-label="Event niveau"><option>Alle niveaus</option><option>Information</option><option>Warning</option><option>Error</option></select>
            <input className="evt-filter-input evt-filter-sm" placeholder="Bron" />
            <input className="evt-filter-input evt-filter-xs" placeholder="Event-ID" />
            <button className="evt-tb-btn" type="button" onClick={() => runAppCommand("EVENT_VIEWER_CREATE_VIEW")}><MmcGlyph kind="save" /><span>Weergave opslaan</span></button>
            <button className="evt-tb-btn" type="button" onClick={() => runAppCommand("EVENT_VIEWER_FILTER")}><MmcGlyph kind="clear" /><span>Wissen</span></button>
          </div>
        </div>
        <div className="mmc-shell">
          <div className="mmc-tree evt-tree">
            <button className="evt-tree-root" type="button" onClick={() => runAppCommand("EVENT_VIEWER_FILTER")}><MmcGlyph kind="list" /><span>Logboeken (lokaal)</span></button>
            <button className="evt-tree-section" type="button" onClick={() => runAppCommand("EVENT_VIEWER_FILTER")}>Windows-logboeken</button>
            <button className="evt-tree-item active" type="button" onClick={() => runAppCommand("EVENT_VIEWER_RECORD_CHECK")}><MmcGlyph kind="shield" /><span>Beveiliging</span></button>
            <button className="evt-tree-item" type="button" onClick={() => runAppCommand("EVENT_VIEWER_FILTER")}><MmcGlyph kind="computer" /><span>Systeem</span></button>
            <button className="evt-tree-item" type="button" onClick={() => runAppCommand("EVENT_VIEWER_FILTER")}><MmcGlyph kind="properties" /><span>Toepassing</span></button>
            <button className="evt-tree-section" type="button" onClick={() => runAppCommand("EVENT_VIEWER_CREATE_VIEW")}>Toepassings- en servicelogboeken</button>
            <button className="evt-tree-item evt-tree-item-muted" type="button" onClick={() => runAppCommand("EVENT_VIEWER_RECORD_CHECK")}><MmcGlyph kind="server" /><span>Hardware-gebeurtenissen</span></button>
            <button className="evt-tree-item evt-tree-item-muted" type="button" onClick={() => runAppCommand("EVENT_VIEWER_RECORD_CHECK")}><MmcGlyph kind="network" /><span>Directory Service</span></button>
          </div>
          <div className="evt-main">
            <div className="evt-main-header"><div className="evt-main-title">Beveiliging</div><div className="evt-main-desc">5 gebeurtenissen totaal, 5 zichtbaar - filter Event-ID 4624/4719</div></div>
            <div className="evt-split">
              <div className="evt-list-pane">
                <table className="evt-table"><thead><tr><th className="evt-th-level">Niveau</th><th>Datum en tijd</th><th>Bron</th><th>Event-ID</th><th>Taakcategorie</th></tr></thead><tbody><tr className="evt-row-selected" onDoubleClick={() => runAppCommand("EVENT_VIEWER_RECORD_CHECK")}><td className="evt-td-level"><MmcGlyph kind="info" /></td><td className="evt-td-time">Vandaag 10:41</td><td className="evt-td-source">Microsoft-Windows-Security-Auditing</td><td className="evt-td-id">4624</td><td>Logon</td></tr><tr className="evt-row-warn" onDoubleClick={() => runAppCommand("AUDIT_RECORD_EVENT")}><td className="evt-td-level"><MmcGlyph kind="warning" /></td><td className="evt-td-time">Vandaag 10:39</td><td className="evt-td-source">SecurityCenter</td><td className="evt-td-id">4719</td><td>Policy Change</td></tr><tr onDoubleClick={() => runAppCommand("EVENT_VIEWER_RECORD_CHECK")}><td className="evt-td-level"><MmcGlyph kind="info" /></td><td className="evt-td-time">Vandaag 10:33</td><td className="evt-td-source">Microsoft-Windows-Security-Auditing</td><td className="evt-td-id">4720</td><td>User Account Management</td></tr><tr onDoubleClick={() => runAppCommand("EVENT_VIEWER_RECORD_CHECK")}><td className="evt-td-level"><MmcGlyph kind="info" /></td><td className="evt-td-time">Vandaag 10:29</td><td className="evt-td-source">Microsoft-Windows-GroupPolicy</td><td className="evt-td-id">1502</td><td>Policy Processing</td></tr><tr onDoubleClick={() => runAppCommand("EVENT_VIEWER_FILTER")}><td className="evt-td-level"><MmcGlyph kind="warning" /></td><td className="evt-td-time">Vandaag 10:21</td><td className="evt-td-source">Microsoft-Windows-DNS-Client</td><td className="evt-td-id">1014</td><td>Name Resolution</td></tr></tbody></table>
              </div>
              <div className="evt-detail-card">
                <div className="evt-detail-header"><MmcGlyph kind="info" /><span className="evt-detail-eid">Event ID 4624</span><span className="evt-detail-source">Security-Auditing</span></div>
                <div className="evt-detail-tabs" role="tablist" aria-label="Event details" data-native-parity="eventvwr-detail-tabs"><button className="active" type="button" onClick={() => runAppCommand("EVENT_VIEWER_RECORD_CHECK")}>Algemeen</button><button type="button" onClick={() => runAppCommand("EVENT_VIEWER_RECORD_CHECK")}>Details</button></div>
                <div className="evt-detail-msg">Een account is succesvol aangemeld.</div>
                <div className="evt-detail-meta">
                  <div className="evt-detail-row"><span className="evt-dk">Gelogd</span><span>Vandaag</span></div>
                  <div className="evt-detail-row"><span className="evt-dk">Bron</span><span>Microsoft-Windows-Security-Auditing</span></div>
                  <div className="evt-detail-row"><span className="evt-dk">Event-ID</span><span>4624</span></div>
                  <div className="evt-detail-row"><span className="evt-dk">Gebruiker</span><span>smoke.student</span></div>
                  <div className="evt-detail-row"><span className="evt-dk">Computer</span><span>SRV01.advanced-ti.local</span></div>
                  <div className="evt-detail-row"><span className="evt-dk">Logboek</span><span>Security</span></div>
                </div>
                <pre className="evt-xml-preview">{`<Event>
  <System>
    <Provider Name="Microsoft-Windows-Security-Auditing" />
    <EventID>4624</EventID>
    <Channel>Security</Channel>
  </System>
</Event>`}</pre>
                <div className="evt-detail-data-header">Gegevens</div>
              </div>
            </div>
            <div className="evt-statusbar">Beveiliging: 5 zichtbaar, 5 totaal | Aangepaste weergave: FirstDesk Security</div>
          </div>
        </div>
      </div>
    );
  }

  if (normalized === "remoteDesktop") {
    return (
      <div className="win11-rdp-app" data-native-app="remoteDesktop">
        <section className="rdp-card">
          <h3>Remote Desktop Connection</h3>
          <label>Computer:<input value="SRV01.advanced-ti.local" readOnly /></label>
          <label>User name:<input value="ADVANCED-TI\\Student" readOnly /></label>
          <div className="rdp-tabs"><button className="is-active" type="button" onClick={() => runAppCommand("RDP_ENABLE")}>General</button><button type="button" onClick={() => runAppCommand("RDP_ENABLE")}>Display</button><button type="button" onClick={() => runAppCommand("RDP_SET_ALLOWED_GROUP")}>Local Resources</button><button type="button" onClick={() => runAppCommand("RDP_ENABLE")}>Experience</button><button type="button" onClick={() => runAppCommand("RDP_SET_ALLOWED_GROUP")}>Advanced</button></div>
          <ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} />
        </section>
      </div>
    );
  }

  if (normalized === "aduc") {
    return (
      <div className="mmc-app aduc-shell native-mmc-surface" data-native-app="aduc" data-native-parity="aduc" data-oracle-source="aduc.js">
        <div className="mmc-menubar" onClick={() => runFirstAppCommand("SERVER_CREATE_DOMAIN_USER", "SERVER_CREATE_DOMAIN_GROUP", "SERVER_CREATE_OU")}>
          <span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span>
        </div>
        <div className="aduc-toolbar">
          <div className="aduc-tb-group">
            <button className="aduc-tb-btn" type="button" onClick={() => runAppCommand("SERVER_CREATE_OU")}><MmcGlyph kind="folder" /><span>New OU</span></button>
            <button className="aduc-tb-btn" type="button" onClick={() => runAppCommand("SERVER_CREATE_DOMAIN_USER")}><MmcGlyph kind="user" /><span>New User</span></button>
            <button className="aduc-tb-btn" type="button" onClick={() => runAppCommand("SERVER_CREATE_DOMAIN_GROUP")}><MmcGlyph kind="group" /><span>New Group</span></button>
            <button className="aduc-tb-btn" type="button" onClick={() => openApp("sysdm")}><MmcGlyph kind="computer" /><span>New Computer</span></button>
          </div>
          <span className="aduc-tb-sep" />
          <div className="aduc-tb-group">
            <button className="aduc-tb-btn" type="button" onClick={() => runFirstAppCommand("SERVER_SET_DOMAIN_USER_STATE", "SERVER_ADD_DOMAIN_GROUP_MEMBER")}><MmcGlyph kind="properties" /><span>Properties</span></button>
            <button className="aduc-tb-btn" type="button" onClick={() => runAppCommand("SERVER_ADD_DOMAIN_GROUP_MEMBER")}><MmcGlyph kind="group" /><span>Members</span></button>
          </div>
          <span className="aduc-tb-sep" />
          <div className="aduc-tb-group">
            <button className="aduc-tb-btn" type="button" onClick={() => runAppCommand("SERVER_SET_DOMAIN_USER_STATE")}><MmcGlyph kind="toggle" /><span>Enable/Disable</span></button>
            <button className="aduc-tb-btn" type="button" onClick={() => runAppCommand("SERVER_RESET_DOMAIN_USER_PASSWORD")}><MmcGlyph kind="key" /><span>Reset Password</span></button>
          </div>
        </div>
        <div className="mmc-shell aduc-body">
          <aside className="mmc-tree aduc-tree" aria-label="Active Directory tree">
            <button className="aduc-tree-domain active" type="button" onClick={() => runAppCommand("SERVER_CREATE_OU")}><MmcGlyph kind="network" /><span>advanced-ti.local</span></button>
            <button className="aduc-tree-ou" type="button" onClick={() => runAppCommand("SERVER_CREATE_OU")}><span className="aduc-tree-line" /><MmcGlyph kind="folder" /><span>Builtin</span></button>
            <button className="aduc-tree-ou" type="button" onClick={() => openApp("sysdm")}><span className="aduc-tree-line" /><MmcGlyph kind="computer" /><span>Computers</span></button>
            <button className="aduc-tree-ou" type="button" onClick={() => openApp("server-manager")}><span className="aduc-tree-line" /><MmcGlyph kind="server" /><span>Domain Controllers</span></button>
            <button className="aduc-tree-ou active" type="button" onClick={() => runAppCommand("SERVER_CREATE_DOMAIN_USER")}><span className="aduc-tree-line" /><MmcGlyph kind="folder" /><span>Users</span></button>
            <button className="aduc-tree-ou" type="button" onClick={() => runAppCommand("SERVER_CREATE_OU")}><span className="aduc-tree-line" /><MmcGlyph kind="folder" /><span>ServiceDesk</span></button>
          </aside>
          <main className="mmc-main aduc-main-pane">
            <div className="mmc-content aduc-content">
              <div className="mmc-split aduc-split">
                <div className="aduc-list-pane">
                  <table className="mmc-table aduc-table">
                    <thead><tr><th>Naam</th><th>Type</th><th>Container</th><th>Status</th></tr></thead>
                    <tbody>
                      <tr className="aduc-row-selected" onDoubleClick={() => runAppCommand("SERVER_SET_DOMAIN_USER_STATE")}><td className="aduc-td-name"><MmcGlyph kind="user" className="aduc-icon-user" /><span>selina.166</span></td><td>User</td><td>Users</td><td><span className="aduc-status-enabled">Enabled</span></td></tr>
                      <tr onDoubleClick={() => runAppCommand("SERVER_ADD_DOMAIN_GROUP_MEMBER")}><td className="aduc-td-name"><MmcGlyph kind="group" className="aduc-icon-group" /><span>Cutover Operators</span></td><td>Security Group</td><td>Users</td><td>Global</td></tr>
                      <tr onDoubleClick={() => openApp("sysdm")}><td className="aduc-td-name"><MmcGlyph kind="computer" className="aduc-icon-computer" /><span>SRV01</span></td><td>Computer</td><td>Domain Controllers</td><td>Domain Controller</td></tr>
                      <tr onDoubleClick={() => runAppCommand("SERVER_CREATE_OU")}><td className="aduc-td-name"><MmcGlyph kind="folder" className="aduc-icon-ou" /><span>ServiceDesk</span></td><td>Organizational Unit</td><td>Domain</td><td>-</td></tr>
                    </tbody>
                  </table>
                </div>
                <aside className="mmc-detailpane aduc-detail">
                  <div className="aduc-detail-head">
                    <MmcGlyph kind="user" />
                    <div><strong>selina.166</strong><small>User - advanced-ti.local/Users</small></div>
                  </div>
                  <div className="mmc-property-tabs" role="tablist" aria-label="ADUC object properties" data-native-parity="aduc-property-tabs">
                    <button className="active" type="button" onClick={() => runFirstAppCommand("SERVER_SET_DOMAIN_USER_STATE", "SERVER_ADD_DOMAIN_GROUP_MEMBER")}>Algemeen</button>
                    <button type="button" onClick={() => runAppCommand("SERVER_ADD_DOMAIN_GROUP_MEMBER")}>Lid van</button>
                    <button type="button" onClick={() => runAppCommand("SERVER_RESET_DOMAIN_USER_PASSWORD")}>Account</button>
                    <button type="button" onClick={() => runAppCommand("SERVER_SET_DOMAIN_USER_STATE")}>Organisatie</button>
                  </div>
                  <div className="mmc-group">
                    <h4>Objectgegevens</h4>
                    <div className="aduc-detail-row"><span>Display name</span><strong>Selina Ticket 166</strong></div>
                    <div className="aduc-detail-row"><span>SAM account</span><strong>selina.166</strong></div>
                    <div className="aduc-detail-row"><span>Status</span><strong>Enabled</strong></div>
                    <div className="aduc-detail-row"><span>Lid van</span><strong>Support-Team</strong></div>
                    <div className="aduc-detail-row"><span>UPN</span><strong>selina.166@advanced-ti.local</strong></div>
                  </div>
                  <div className="mmc-group">
                    <h4>Objecteigenschappen</h4>
                    <p>Gebruik de werkbalk of dubbelklik op het object om eigenschappen en lidmaatschappen te beheren.</p>
                  </div>
                </aside>
              </div>
            </div>
            <div className="aduc-statusbar">User: selina.166 | Container: Users | 4 objecten zichtbaar</div>
          </main>
        </div>
      </div>
    );
  }

  if (normalized === "dns-manager") {
    return (
      <div className="mmc-app dns-shell native-mmc-surface" data-native-app="dns-manager" data-native-parity="dns-manager" data-oracle-source="dnsManager.js">
        <div className="mmc-menubar" onClick={() => runFirstAppCommand("SERVER_DNS_ADD_RECORD", "SERVER_DNS_CREATE_ZONE")}>
          <span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span>
        </div>
        <div className="dns-toolbar">
          <div className="dns-tb-group">
            <button className="dns-tb-btn" type="button" onClick={() => runAppCommand("SERVER_DNS_CREATE_ZONE")}><MmcGlyph kind="add" /><span>Nieuwe zone</span></button>
            <button className="dns-tb-btn" type="button" onClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}><MmcGlyph kind="record" /><span>Nieuwe record...</span></button>
          </div>
          <span className="dns-tb-sep" />
          <div className="dns-tb-group">
            <button className="dns-tb-btn" type="button" onClick={() => runFirstAppCommand("SERVER_DNS_ADD_RECORD", "SERVER_DNS_CREATE_ZONE")}><MmcGlyph kind="properties" /><span>Eigenschappen</span></button>
            <button className="dns-tb-btn dns-tb-danger" type="button" onClick={() => runAppCommand("SERVER_DNS_DELETE_RECORD")}><MmcGlyph kind="clear" /><span>Verwijderen</span></button>
          </div>
          <span className="dns-tb-sep" />
          <button className="dns-tb-btn" type="button" onClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}><MmcGlyph kind="outbound" /><span>Doorstuurservers...</span></button>
        </div>
        <div className="mmc-shell dns-body">
          <aside className="mmc-tree dns-tree" aria-label="DNS tree">
            <button className="dns-tree-server" type="button" onClick={() => runAppCommand("SERVER_DNS_CREATE_ZONE")}><MmcGlyph kind="server" /><span>DNS-server</span></button>
            <button className="dns-tree-section" type="button" onClick={() => runAppCommand("SERVER_DNS_CREATE_ZONE")}><span className="dns-tree-expand">&gt;</span><MmcGlyph kind="network" /><span>Forward Lookup Zones</span></button>
            <button className="dns-tree-zone active" type="button" onClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}><span className="dns-tree-bullet" /><MmcGlyph kind="network" /><span>cutover170.local</span><span className="dns-tree-rec-count">3</span></button>
            <button className="dns-tree-zone" type="button" onClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}><span className="dns-tree-bullet" /><MmcGlyph kind="network" /><span>advanced-ti.local</span><span className="dns-tree-rec-count">2</span></button>
            <button className="dns-tree-section" type="button" onClick={() => runAppCommand("SERVER_DNS_CREATE_ZONE")}><span className="dns-tree-expand">&gt;</span><MmcGlyph kind="outbound" /><span>Reverse Lookup Zones</span></button>
            <button className="dns-tree-zone" type="button" onClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}><span className="dns-tree-bullet" /><MmcGlyph kind="record" /><span>50.10.in-addr.arpa</span><span className="dns-tree-rec-count">1</span></button>
          </aside>
          <main className="mmc-main dns-main-pane">
            <div className="mmc-content dns-content">
              <div className="mmc-split dns-split">
                <div className="dns-list-pane">
                  <table className="dns-table">
                    <thead><tr><th>Naam</th><th>Type</th><th>Waarde</th></tr></thead>
                    <tbody>
                      <tr className="dns-row-selected" onDoubleClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}><td className="dns-td-host">ws170</td><td><span className="dns-type-tag dns-type-a">A</span></td><td className="dns-td-val">10.50.170.25</td></tr>
                      <tr onDoubleClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}><td className="dns-td-host">@</td><td><span className="dns-type-tag dns-type-soa">SOA</span></td><td className="dns-td-val">SRV01.cutover170.local</td></tr>
                      <tr onDoubleClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}><td className="dns-td-host">_ldap._tcp</td><td><span className="dns-type-tag dns-type-srv">SRV</span></td><td className="dns-td-val">SRV01.cutover170.local</td></tr>
                    </tbody>
                  </table>
                </div>
                <aside className="mmc-detailpane dns-detail-pane">
                  <div className="dns-detail-zone-header">
                    <MmcGlyph kind="network" />
                    <div><strong>cutover170.local</strong><small>Primary zone</small></div>
                  </div>
                  <div className="mmc-property-tabs" role="tablist" aria-label="DNS zone properties" data-native-parity="dns-zone-tabs">
                    <button className="active" type="button" onClick={() => runAppCommand("SERVER_DNS_CREATE_ZONE")}>Algemeen</button>
                    <button type="button" onClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}>SOA</button>
                    <button type="button" onClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}>Naamservers</button>
                    <button type="button" onClick={() => runAppCommand("SERVER_DNS_ADD_RECORD")}>Zoneoverdracht</button>
                  </div>
                  <div className="mmc-group">
                    <h4>Zone-informatie</h4>
                    <div className="dns-detail-row"><span>Resource records</span><strong>3</strong></div>
                    <div className="dns-detail-row"><span>Type</span><strong>Primary</strong></div>
                    <div className="dns-detail-row"><span>Dynamische updates</span><strong>Secure only</strong></div>
                    <div className="dns-detail-row"><span>Doorstuurservers</span><strong>1.1.1.1, 8.8.8.8</strong></div>
                  </div>
                  <div className="mmc-group">
                    <h4>Geselecteerde record</h4>
                    <div className="dns-detail-row"><span>Naam</span><strong>ws170</strong></div>
                    <div className="dns-detail-row"><span>Type</span><strong>A</strong></div>
                    <div className="dns-detail-row"><span>Waarde</span><strong>10.50.170.25</strong></div>
                  </div>
                  <div className="mmc-group">
                    <h4>Zoneacties</h4>
                    <p>Gebruik de werkbalk voor nieuwe zones, records, eigenschappen en doorstuurservers.</p>
                  </div>
                </aside>
              </div>
            </div>
            <div className="dns-statusbar">Zone: cutover170.local | Type: Primary | 3 records</div>
          </main>
        </div>
      </div>
    );
  }

  if (normalized === "gpmc") {
    return (
      <div className="mmc-app gpmc-shell native-mmc-surface" data-native-app="gpmc" data-native-parity="gpmc" data-oracle-source="gpmc.js">
        <div className="mmc-menubar" onClick={() => runFirstAppCommand("GPO_CREATE", "GPO_SET_SETTING")}>
          <span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span>
        </div>
        <div className="gpmc-toolbar">
          <div className="gpmc-tb-group">
            <button className="gpmc-tb-btn" type="button" onClick={() => runAppCommand("GPO_CREATE")}><MmcGlyph kind="record" /><span>Nieuwe GPO</span></button>
            <button className="gpmc-tb-btn" type="button" onClick={() => runAppCommand("GPO_SET_SETTING")}><MmcGlyph kind="edit" /><span>Bewerken</span></button>
          </div>
          <span className="gpmc-tb-sep" />
          <div className="gpmc-tb-group">
            <button className="gpmc-tb-btn" type="button" onClick={() => runAppCommand("GPO_LINK_OU")}><MmcGlyph kind="link" /><span>Koppelen...</span></button>
            <button className="gpmc-tb-btn" type="button" onClick={() => runAppCommand("GPO_ENFORCE_LINK")}><MmcGlyph kind="unlink" /><span>Ontkoppelen</span></button>
          </div>
          <span className="gpmc-tb-sep" />
          <div className="gpmc-tb-group">
            <button className="gpmc-tb-btn" type="button" onClick={() => runAppCommand("GPO_ENFORCE_LINK")}><MmcGlyph kind="shield" /><span>Enforced</span></button>
            <button className="gpmc-tb-btn" type="button" onClick={() => runAppCommand("GPO_SET_SETTING")}><MmcGlyph kind="group" /><span>Beveiliging</span></button>
            <button className="gpmc-tb-btn" type="button" onClick={() => runAppCommand("GPO_SET_SETTING")}><MmcGlyph kind="block" /><span>Overname blokkeren</span></button>
          </div>
        </div>
        <div className="mmc-shell gpmc-body">
          <aside className="mmc-tree gpmc-tree" aria-label="Group Policy tree">
            <button className="gpmc-tree-root" type="button" onClick={() => runAppCommand("GPO_CREATE")}><MmcGlyph kind="network" /><span>Forest: advanced-ti.local</span></button>
            <button className="gpmc-tree-node gpmc-tree-domain" type="button" onClick={() => runAppCommand("GPO_LINK_OU")}><span className="gpmc-tree-expand">&gt;</span><MmcGlyph kind="server" /><span>Domeinen</span></button>
            <button className="gpmc-tree-node gpmc-tree-domain-name" type="button" onClick={() => runAppCommand("GPO_LINK_OU")}><span className="gpmc-tree-line" /><MmcGlyph kind="network" /><span>advanced-ti.local</span></button>
            <button className="gpmc-tree-node gpmc-tree-ou" type="button" onClick={() => runAppCommand("GPO_LINK_OU")}><span className="gpmc-tree-line2" /><MmcGlyph kind="folder" /><span>ServiceDesk OU</span></button>
            <button className="gpmc-tree-node gpmc-tree-ou" type="button" onClick={() => runAppCommand("GPO_LINK_OU")}><span className="gpmc-tree-line2" /><MmcGlyph kind="computer" /><span>Workstations OU</span></button>
            <button className="gpmc-tree-node gpmc-tree-gpo-folder active" type="button" onClick={() => runAppCommand("GPO_CREATE")}><span className="gpmc-tree-line2" /><MmcGlyph kind="shield" /><span>Groepsbeleidsobjecten</span></button>
          </aside>
          <main className="mmc-main gpmc-main-pane">
            <div className="mmc-content gpmc-content">
              <div className="mmc-split gpmc-split">
                <div className="gpmc-list-pane">
                  <table className="gpmc-table">
                    <thead><tr><th>Naam</th><th>Gekoppeld aan</th><th>Status</th><th>Samenvatting</th></tr></thead>
                    <tbody>
                      <tr className="gpmc-row-selected" onDoubleClick={() => runAppCommand("GPO_SET_SETTING")}><td className="gpmc-td-name"><MmcGlyph kind="shield" /><span>FD-Hardening-154</span></td><td className="gpmc-td-link"><MmcGlyph kind="link" /><span>Workstations OU</span></td><td><span className="gpmc-badge gpmc-badge-on">Enabled</span></td><td className="gpmc-td-summary">Explorer hardening en remote desktop baseline</td></tr>
                      <tr onDoubleClick={() => runAppCommand("GPO_SET_SETTING")}><td className="gpmc-td-name"><MmcGlyph kind="shield" /><span>Default Domain Policy</span></td><td className="gpmc-td-link"><MmcGlyph kind="link" /><span>domain</span></td><td><span className="gpmc-badge gpmc-badge-enforced">Enforced</span></td><td className="gpmc-td-summary">Password and account policy</td></tr>
                      <tr onDoubleClick={() => runAppCommand("GPO_LINK_OU")}><td className="gpmc-td-name"><MmcGlyph kind="shield" /><span>ServiceDesk Workstations</span></td><td className="gpmc-td-link"><MmcGlyph kind="link" /><span>ServiceDesk OU</span></td><td><span className="gpmc-badge gpmc-badge-on">Enabled</span></td><td className="gpmc-td-summary">Mapped drives and support tools</td></tr>
                    </tbody>
                  </table>
                </div>
                <aside className="mmc-detailpane gpmc-detail-pane">
                  <div className="gpmc-detail-head">
                    <MmcGlyph kind="shield" />
                    <div><strong>FD-Hardening-154</strong><small>Gekoppeld aan Workstations OU</small></div>
                  </div>
                  <div className="mmc-property-tabs" role="tablist" aria-label="GPO properties" data-native-parity="gpmc-scope-tabs">
                    <button className="active" type="button" onClick={() => runAppCommand("GPO_LINK_OU")}>Bereik</button>
                    <button type="button" onClick={() => runAppCommand("GPO_SET_SETTING")}>Details</button>
                    <button type="button" onClick={() => runAppCommand("GPO_SET_SETTING")}>Instellingen</button>
                    <button type="button" onClick={() => runAppCommand("GPO_SET_SETTING")}>Delegatie</button>
                  </div>
                  <div className="mmc-group">
                    <h4>Koppelingen</h4>
                    <div className="gpmc-detail-row"><span>Order 1</span><strong>Workstations OU</strong></div>
                    <div className="gpmc-detail-row"><span>Beveiliging</span><strong>Authenticated Users</strong></div>
                    <div className="gpmc-detail-row"><span>WMI-filter</span><strong>Geen</strong></div>
                    <div className="gpmc-detail-row"><span>Status</span><strong>User and Computer settings enabled</strong></div>
                  </div>
                  <div className="mmc-group">
                    <h4>Instellingen</h4>
                    <div className="gpmc-detail-row"><span>Verborgen bestanden</span><strong>Ingeschakeld</strong></div>
                    <div className="gpmc-detail-row"><span>Bestandsextensies</span><strong>Ingeschakeld</strong></div>
                    <div className="gpmc-detail-row"><span>Remote Desktop</span><strong>Ingeschakeld</strong></div>
                  </div>
                  <div className="mmc-group">
                    <h4>Groepsbeleidsbeheer</h4>
                    <p>Gebruik Actie of de werkbalk om GPO's te maken, koppelen, bewerken en afdwingen.</p>
                  </div>
                </aside>
              </div>
            </div>
            <div className="gpmc-statusbar">GPO: FD-Hardening-154 | Gekoppeld: Workstations OU | Enabled</div>
          </main>
        </div>
      </div>
    );
  }

	  if (normalized === "netSettings" || normalized === "sysdm") {
	    if (normalized === "sysdm") {
	      return <SystemPropertiesSurface actions={appActions} onRun={runAction} simulatorState={simulatorState} />;
	    }
	    return <NetworkSettingsSurface actions={appActions} busyActionId={busyActionId} onRun={runAction} simulatorState={simulatorState} openApp={openApp} />;
	  }

  if (normalized === "lusrmgr") {
    return <LocalUsersGroupsSurface actions={appActions} busyActionId={busyActionId} onRun={runAction} simulatorState={simulatorState} />;
  }

  if (normalized === "netplwiz") {
    return (
      <div className="npw-shell native-netplwiz-surface" data-native-app="netplwiz" data-native-parity="netplwiz">
        <div className="npw-tabs">
          <button className="npw-tab active" type="button" onClick={() => runAppCommand("LOCAL_USER_AUTOLOGIN_TOGGLE")}>Gebruikers</button>
          <button className="npw-tab" type="button" onClick={() => runAppCommand("LOCAL_USER_REQUIRE_CTRL_ALT_DEL")}>Geavanceerd</button>
        </div>
        <div className="npw-tab-content">
          <div className="npw-section-desc">
            <input id="native-npw-require-login" type="checkbox" defaultChecked />
            <label htmlFor="native-npw-require-login">Gebruikers moeten een gebruikersnaam en wachtwoord invoeren voor gebruik van de computer.</label>
          </div>
          <div className="npw-body">
            <table className="npw-table">
              <thead><tr><th className="npw-th-avatar" /><th>Gebruikersnaam</th><th>Accounttype</th><th>Automatisch</th></tr></thead>
              <tbody>
                <tr className="npw-row-selected" onDoubleClick={() => runAppCommand("LOCAL_USER_AUTOLOGIN_TOGGLE")}><td className="npw-td-avatar"><WinGlyph name="person" className="npw-avatar" /></td><td className="npw-td-name"><div className="npw-uname">SmokeStudent</div><div className="npw-ufull">Smoke Student</div></td><td><span className="npw-badge npw-badge-user">Standaard</span></td><td><span className="npw-badge npw-badge-auto">Automatisch</span></td></tr>
                <tr onDoubleClick={() => runAppCommand("LOCAL_USER_REQUIRE_CTRL_ALT_DEL")}><td className="npw-td-avatar"><WinGlyph name="admin_panel_settings" className="npw-avatar npw-avatar-admin" /></td><td className="npw-td-name"><div className="npw-uname">Administrator</div><div className="npw-ufull">Ingebouwde account</div></td><td><span className="npw-badge npw-badge-admin">Administrator</span></td><td /></tr>
                <tr onDoubleClick={() => runAppCommand("LOCAL_USER_AUTOLOGIN_TOGGLE")}><td className="npw-td-avatar"><WinGlyph name="support_agent" className="npw-avatar" /></td><td className="npw-td-name"><div className="npw-uname">HelpdeskStage</div><div className="npw-ufull">Helpdesk Stage</div></td><td><span className="npw-badge npw-badge-user">Standaard</span></td><td /></tr>
              </tbody>
            </table>
          </div>
          <div className="npw-tab-actions">
            <button className="tool-btn" type="button" onClick={() => openApp("lusrmgr")}>Toevoegen...</button>
            <button className="tool-btn" type="button" onClick={() => runAppCommand("LOCAL_USER_AUTOLOGIN_TOGGLE")}>Verwijderen</button>
            <button className="tool-btn" type="button" onClick={() => runFirstAppCommand("LOCAL_USER_REQUIRE_CTRL_ALT_DEL", "LOCAL_USER_AUTOLOGIN_TOGGLE")}>Eigenschappen</button>
          </div>
          <section className="utility-detail-panel" data-native-parity="netplwiz-advanced-depth"><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("LOCAL_USER_AUTOLOGIN_TOGGLE")}>Gebruikers</button><button type="button" onClick={() => runAppCommand("LOCAL_USER_REQUIRE_CTRL_ALT_DEL")}>Geavanceerd</button><button type="button" onClick={() => openApp("lusrmgr")}>Groepen</button></div><dl><div><dt>Ctrl+Alt+Del vereist</dt><dd>Uitgeschakeld voor lablogin</dd></div><div><dt>Standaardaccount</dt><dd>SmokeStudent</dd></div></dl></section>
          <div className="npw-command-band">
            <ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} />
          </div>
        </div>
        <div className="npw-footer">
          <button className="tool-btn primary" type="button" onClick={() => runAppCommand("LOCAL_USER_AUTOLOGIN_TOGGLE")}>OK</button>
          <button className="tool-btn" type="button" onClick={() => openApp("login")}>Annuleren</button>
          <button className="tool-btn" type="button" onClick={() => runAppCommand("LOCAL_USER_REQUIRE_CTRL_ALT_DEL")}>Toepassen</button>
        </div>
      </div>
    );
  }

  if (normalized === "secpol") {
    return (
      <div className="mmc-app secpol-shell native-secpol-surface native-mmc-surface" data-native-app="secpol" data-native-parity="secpol" data-oracle-source="secpol.js">
        <div className="mmc-menubar" onClick={runPrimaryAppAction}><span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span></div>
        <div className="secpol-toolbar">
          <button className="secpol-tb-btn" type="button" onClick={() => runAppCommand("SECURITY_POLICY_SET")}><WinGlyph name="done" /><span>Toepassen</span></button>
          <button className="secpol-tb-btn" type="button" onClick={() => runAppCommand("AUDIT_POLICY_SET")}><WinGlyph name="refresh" /><span>Vernieuwen</span></button>
        </div>
        <div className="mmc-shell secpol-body">
          <aside className="mmc-tree secpol-tree">
            <button className="sp-tree-root" type="button" onClick={() => runAppCommand("SECURITY_POLICY_SET")}><WinGlyph name="security" /><span>Beveiligingsinstellingen</span></button>
            <button className="sp-tree-section" type="button" onClick={() => runAppCommand("SECURITY_POLICY_SET")}><WinGlyph name="folder" /><span>Accountbeleid</span></button>
            <button className="sp-tree-node active" type="button" onClick={() => runAppCommand("SECURITY_POLICY_SET")}><span className="sp-tree-indent" /><WinGlyph name="article" /><span>Wachtwoordbeleid</span></button>
            <button className="sp-tree-node" type="button" onClick={() => runAppCommand("SECURITY_POLICY_SET")}><span className="sp-tree-indent" /><WinGlyph name="article" /><span>Accountvergrendelingsbeleid</span></button>
            <button className="sp-tree-section" type="button" onClick={() => runAppCommand("USER_RIGHTS_ASSIGN")}><WinGlyph name="folder" /><span>Lokaal beleid</span></button>
            <button className="sp-tree-node" type="button" onClick={() => runAppCommand("AUDIT_POLICY_SET")}><span className="sp-tree-indent" /><WinGlyph name="article" /><span>Controlebeleid</span></button>
            <button className="sp-tree-section sp-tree-muted" type="button" onClick={() => openApp("wf")}><WinGlyph name="folder" /><span>Windows Firewall met geavanceerde beveiliging</span></button>
            <button className="sp-tree-section sp-tree-muted" type="button" onClick={() => openApp("ncpa")}><WinGlyph name="folder" /><span>Netwerklijstbeheerbeleid</span></button>
          </aside>
          <main className="mmc-main secpol-main-pane">
            <div className="secpol-pane-header">
              <div className="secpol-pane-path">Accountbeleid\Wachtwoordbeleid</div>
              <div className="secpol-pane-desc">Wachtwoordbeleidsregels voor gebruikersaccounts op de lokale computer.</div>
            </div>
            <div className="mmc-content secpol-content">
              <table className="secpol-table">
                <thead><tr><th>Beleid</th><th className="sp-th-state">Beveiligingsinstelling</th></tr></thead>
                <tbody>
                  <tr onDoubleClick={() => runAppCommand("SECURITY_POLICY_SET")}><td className="sp-td-policy"><WinGlyph name="article" /><span>Minimale wachtwoordlengte</span></td><td className="sp-td-state"><input className="sp-num-input" value="12" readOnly /> tekens</td></tr>
                  <tr onDoubleClick={() => runAppCommand("SECURITY_POLICY_SET")}><td className="sp-td-policy"><WinGlyph name="article" /><span>Wachtwoord moet voldoen aan complexiteitsvereisten</span></td><td className="sp-td-state"><input type="checkbox" checked readOnly /> <span className="sp-state sp-state-on">Ingeschakeld</span></td></tr>
                  <tr onDoubleClick={() => runAppCommand("SECURITY_POLICY_SET")}><td className="sp-td-policy"><WinGlyph name="article" /><span>Maximale wachtwoordduur (dagen)</span></td><td className="sp-td-state"><span className="sp-state sp-state-num">90</span></td></tr>
                  <tr onDoubleClick={() => runAppCommand("USER_RIGHTS_ASSIGN")}><td className="sp-td-policy"><WinGlyph name="article" /><span>Wachtwoordgeschiedenis afdwingen</span></td><td className="sp-td-state"><span className="sp-state sp-state-num">24 wachtwoorden onthouden</span></td></tr>
                </tbody>
              </table>
              <aside className="secpol-action-pane">
                <div className="mmc-tabstrip" data-native-parity="secpol-policy-tabs"><button className="active" type="button" onClick={() => runAppCommand("SECURITY_POLICY_SET")}>Instelling</button><button type="button" onClick={() => runAppCommand("AUDIT_POLICY_SET")}>Controle</button><button type="button" onClick={() => runAppCommand("USER_RIGHTS_ASSIGN")}>Rechten</button></div>
                <div className="policy-detail-grid"><div><span>Effectief beleid</span><strong>Domeinbeleid overschrijft lokaal waar nodig</strong></div><div><span>Laatst toegepast</span><strong>Vandaag 14:58</strong></div></div>
                <h4>Beschrijving</h4>
                <p>Dubbelklik op een beleidsregel of gebruik Actie om de instelling te wijzigen.</p>
              </aside>
            </div>
            <div className="secpol-statusbar">Gereed | Wachtwoordbeleid | 4 beleidsregels</div>
          </main>
        </div>
      </div>
    );
  }

  if (normalized === "uac") {
    return (
      <div className="uac-settings-app native-uac-surface" data-native-app="uac" data-native-parity="uac">
        <header className="uac-settings-header">
          <WinGlyph name="admin_panel_settings" />
          <div>
            <strong>Instellingen voor Gebruikersaccountbeheer</strong>
            <p>Kies wanneer u een melding wilt ontvangen over wijzigingen aan uw computer.</p>
          </div>
        </header>
        <main className="uac-settings-body">
          <section className="uac-level-card">
            <div className="uac-level-labels">
              <span>Altijd een melding weergeven</span>
              <span>Alleen een melding weergeven wanneer apps wijzigingen proberen aan te brengen</span>
              <span>Geen melding weergeven wanneer ik wijzigingen aan Windows-instellingen aanbreng</span>
              <span>Nooit een melding weergeven</span>
            </div>
            <button className="uac-slider-rail" type="button" aria-label="UAC level" onClick={() => runAppCommand("UAC_SET_LEVEL")}>
              <span className="uac-slider-track" />
              <span className="uac-slider-tick" />
              <span className="uac-slider-tick is-active" />
              <span className="uac-slider-tick" />
              <span className="uac-slider-tick" />
            </button>
            <aside className="uac-level-explain">
              <h3>Standaard - aanbevolen</h3>
              <p>Melding weergeven wanneer apps proberen wijzigingen aan te brengen op mijn computer.</p>
              <p>Geen melding weergeven wanneer ik wijzigingen aan Windows-instellingen aanbreng.</p>
            </aside>
          </section>
          <section className="uac-preview-card">
            <div className="uac-box native-uac-preview">
              <div className="uac-header"><WinGlyph name="shield" />Gebruikersaccountbeheer</div>
              <div className="uac-body">
                <p className="uac-title">Wilt u toestaan dat de volgende app wijzigingen aanbrengt op dit apparaat?</p>
                <div className="uac-detail">
                  <div className="uac-row"><span>Actie:</span><strong>Beveiligingsinstelling wijzigen</strong></div>
                  <div className="uac-row"><span>Gebruiker:</span><strong>SmokeStudent</strong></div>
                  <div className="uac-row"><span>Uitgever:</span><strong>Onbekende uitgever</strong></div>
                </div>
              </div>
              <div className="uac-footer"><button type="button" onClick={() => runAppCommand("UAC_TOGGLE_ADMIN_APPROVAL")}>Ja</button><button type="button" onClick={() => runAppCommand("UAC_SET_LEVEL")}>Nee</button></div>
            </div>
          </section>
          <section className="uac-command-card">
            <h3>Gebruikersaccountbeheer</h3>
            <p>Pas UAC-niveau of administrator approval veilig toe via de native command-bus.</p>
            <ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} />
          </section>
        </main>
      </div>
    );
  }

  if (normalized === "gpedit") {
    return (
      <div className="mmc-app gpedit-shell native-gpedit-surface native-mmc-surface" data-native-app="gpedit" data-native-parity="gpedit" data-oracle-source="gpedit.js">
        <div className="mmc-menubar" onClick={runPrimaryAppAction}><span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span></div>
        <div className="gpedit-toolbar">
          <div className="gpedit-tb-group">
            <button className="gpedit-tb-btn" type="button" onClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}><WinGlyph name="done" /><span>Toepassen</span></button>
            <button className="gpedit-tb-btn" type="button" onClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}><WinGlyph name="refresh" /><span>Vernieuwen</span></button>
          </div>
        </div>
        <div className="mmc-shell gpedit-body">
          <aside className="mmc-tree gpedit-tree">
            <button className="gpedit-tree-root" type="button" onClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}><WinGlyph name="policy" /><span>Lokaal computerbeleid</span></button>
            <button className="gpedit-tree-section" type="button" onClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}><WinGlyph name="computer" /><span>Computerconfiguratie</span></button>
            <button className="gpedit-tree-node active" type="button" onClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}><span className="gpedit-tree-indent" /><WinGlyph name="folder" /><span>Beheersjablonen\Windows Explorer</span></button>
            <button className="gpedit-tree-node" type="button" onClick={() => openApp("secpol")}><span className="gpedit-tree-indent" /><WinGlyph name="security" /><span>Win.-instellingen\Beveiligingsinstellingen\Accountbeleid</span></button>
            <button className="gpedit-tree-node" type="button" onClick={() => openApp("secpol")}><span className="gpedit-tree-indent" /><WinGlyph name="security" /><span>Win.-instellingen\Beveiligingsinstellingen\Controlebeleid</span></button>
            <button className="gpedit-tree-section" type="button" onClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}><WinGlyph name="person" /><span>Gebruikersconfiguratie</span></button>
            <button className="gpedit-tree-node gpedit-tree-node-muted" type="button" onClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}><span className="gpedit-tree-indent" /><WinGlyph name="folder" /><span>Beheersjablonen</span></button>
          </aside>
          <main className="mmc-main gpedit-main-pane">
            <div className="gpedit-pane-header">
              <div className="gpedit-pane-title">Beheersjablonen: Windows Explorer</div>
              <div className="gpedit-pane-desc">Beheert de weergave-instellingen van Verkenner voor alle gebruikers.</div>
            </div>
            <div className="mmc-content gpedit-content">
              <table className="gpedit-table">
                <thead><tr><th>Beleid</th><th className="gpedit-th-state">Status</th><th>Opmerking</th></tr></thead>
                <tbody>
                  <tr onDoubleClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}><td className="gpedit-td-policy"><WinGlyph name="article" /><span>Bestandsextensies weergeven</span></td><td><span className="gp-state-tag gp-state-on">Ingeschakeld</span></td><td className="gpedit-td-comment">Bepaalt of bestandsextensies zichtbaar zijn in Verkenner.</td></tr>
                  <tr onDoubleClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}><td className="gpedit-td-policy"><WinGlyph name="article" /><span>Verborgen bestanden weergeven</span></td><td><span className="gp-state-tag gp-state-on">Ingeschakeld</span></td><td className="gpedit-td-comment">Toont verborgen bestanden en mappen in Verkenner.</td></tr>
                  <tr onDoubleClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}><td className="gpedit-td-policy"><WinGlyph name="article" /><span>Recente bestanden uitschakelen</span></td><td><span className="gp-state-tag gp-state-nc">Niet geconfigureerd</span></td><td className="gpedit-td-comment">Scenario-neutrale lokale policy.</td></tr>
                </tbody>
              </table>
              <aside className="gpedit-action-pane">
                <div className="mmc-tabstrip" data-native-parity="gpedit-policy-tabs"><button className="active" type="button" onClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}>Uitgebreid</button><button type="button" onClick={() => runAppCommand("LOCAL_GPO_SET_SETTING")}>Standaard</button><button type="button" onClick={() => openApp("secpol")}>Beveiliging</button></div>
                <div className="policy-detail-grid"><div><span>Ondersteund op</span><strong>Windows 10 of later</strong></div><div><span>Statebron</span><strong>Lokaal computerbeleid</strong></div></div>
                <h4>Beschrijving</h4>
                <p>Dubbelklik op een beleidsregel of gebruik Actie om de instelling te configureren.</p>
              </aside>
            </div>
            <div className="gpedit-statusbar">Beheersjablonen: Windows Explorer | 3 beleidsregels</div>
          </main>
        </div>
      </div>
    );
  }

  if (normalized === "taskschd") {
    return (
      <div className="mmc-app ts-shell taskschd-shell native-taskschd-surface native-mmc-surface" data-native-app="taskschd" data-native-parity="taskschd" data-oracle-source="taskschd.js">
        <div className="mmc-menubar ts-menubar taskschd-menubar" onClick={() => runFirstAppCommand("TASK_CREATE", "TASK_RUN_NOW")}><span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span></div>
        <div className="ts-toolbar taskschd-toolbar">
          <div className="ts-tb-group taskschd-tb-group">
            <button className="ts-tb-btn taskschd-tb-btn" type="button" onClick={() => runAppCommand("TASK_CREATE")}><WinGlyph name="add_task" /><span>Taak maken...</span></button>
            <button className="ts-tb-btn taskschd-tb-btn" type="button" onClick={() => runAppCommand("TASK_RUN_NOW")}><WinGlyph name="play_arrow" /><span>Uitvoeren</span></button>
            <button className="ts-tb-btn taskschd-tb-btn" type="button" onClick={() => runAppCommand("TASK_TOGGLE")}><WinGlyph name="toggle_on" /><span>In-/uitschakelen</span></button>
            <button className="ts-tb-btn ts-tb-danger taskschd-tb-btn taskschd-tb-danger" type="button" onClick={() => runAppCommand("TASK_DELETE")}><WinGlyph name="delete" /><span>Verwijderen</span></button>
          </div>
          <div className="ts-tb-sep taskschd-tb-sep" />
          <div className="ts-tb-group taskschd-tb-group">
            <button className="ts-tb-btn taskschd-tb-btn" type="button" onClick={() => runFirstAppCommand("TASK_RUN_NOW", "TASK_TOGGLE")}><WinGlyph name="refresh" /><span>Vernieuwen</span></button>
          </div>
        </div>
        <div className="mmc-shell taskschd-body">
          <aside className="mmc-tree taskschd-tree">
            <button className="taskschd-tree-root" type="button" onClick={() => runAppCommand("TASK_CREATE")}><WinGlyph name="folder" /><span>Taakplannerbibliotheek</span></button>
            <button className="taskschd-tree-item active" type="button" onClick={() => runAppCommand("TASK_CREATE")}><span className="taskschd-tree-indent" /><WinGlyph name="folder" /><span>Taakplannerbibliotheek</span></button>
            <button className="taskschd-tree-item" type="button" onClick={() => runAppCommand("TASK_TOGGLE")}><span className="taskschd-tree-indent" /><WinGlyph name="folder" /><span>\Microsoft\Windows</span></button>
            <button className="taskschd-tree-item" type="button" onClick={() => runAppCommand("TASK_CREATE")}><span className="taskschd-tree-indent" /><WinGlyph name="folder" /><span>\Aangepast</span></button>
          </aside>
          <main className="mmc-main taskschd-main-pane">
            <div className="taskschd-pane-header">
              <div className="taskschd-pane-title">Taakplannerbibliotheek</div>
              <div className="taskschd-pane-desc">Geplande taken weergeven en beheren.</div>
            </div>
            <div className="mmc-content taskschd-content">
              <div className="taskschd-split">
                <section className="taskschd-list-pane">
                  <table className="taskschd-table">
                    <thead><tr><th>Naam</th><th>Status</th><th>Trigger</th><th>Volgende uitvoering</th><th>Laatste uitvoering</th></tr></thead>
                    <tbody>
                      <tr className="taskschd-row-selected" onDoubleClick={() => runAppCommand("TASK_RUN_NOW")}><td className="taskschd-td-name"><WinGlyph name="task_alt" /><span>DailyMaintenance</span></td><td><span className="ts-badge ts-badge-ready">Gereed</span></td><td className="taskschd-td-trigger">Dagelijks 08:00</td><td className="taskschd-td-time">Morgen 08:00</td><td className="taskschd-td-time">Vandaag 08:00</td></tr>
                      <tr onDoubleClick={() => runAppCommand("TASK_TOGGLE")}><td className="taskschd-td-name"><WinGlyph name="task_alt" /><span>CleanupCache</span></td><td><span className="ts-badge ts-badge-off">Uitgeschakeld</span></td><td className="taskschd-td-trigger">Bij aanmelden</td><td className="taskschd-td-time">-</td><td className="taskschd-td-time">Gisteren 09:11</td></tr>
                      <tr onDoubleClick={() => runAppCommand("TASK_DELETE")}><td className="taskschd-td-name"><WinGlyph name="task_alt" /><span>FD-Scheduled-Task</span></td><td><span className="ts-badge ts-badge-run">Actief</span></td><td className="taskschd-td-trigger">Eenmalig</td><td className="taskschd-td-time">Vandaag 17:00</td><td className="taskschd-td-time">Nog niet</td></tr>
                    </tbody>
                  </table>
                </section>
                <aside className="taskschd-detail-pane">
                  <div className="taskschd-detail-head"><WinGlyph name="task_alt" /><div><div className="taskschd-detail-name">DailyMaintenance</div><div className="taskschd-detail-path">\</div></div></div>
                  <div className="mmc-tabstrip" data-native-parity="taskschd-detail-tabs"><button className="active" type="button" onClick={() => runAppCommand("TASK_RUN_NOW")}>Algemeen</button><button type="button" onClick={() => runAppCommand("TASK_TOGGLE")}>Triggers</button><button type="button" onClick={() => runAppCommand("TASK_CREATE")}>Acties</button><button type="button" onClick={() => runAppCommand("TASK_TOGGLE")}>Voorwaarden</button><button type="button" onClick={() => runAppCommand("TASK_RUN_NOW")}>Geschiedenis</button></div>
                  <div className="taskschd-detail-section"><div className="taskschd-detail-label">Status</div><div><span className="ts-badge ts-badge-ready">Gereed</span></div></div>
                  <div className="taskschd-detail-section"><div className="taskschd-detail-label">Trigger</div><div className="taskschd-detail-val">Dagelijks 08:00</div></div>
                  <div className="taskschd-detail-section"><div className="taskschd-detail-label">Volgende uitvoering</div><div className="taskschd-detail-val">Morgen 08:00</div></div>
                  <div className="taskschd-command-card">
                    <h4>Acties</h4>
                    <p>Gebruik de werkbalk voor taak maken, uitvoeren, in- of uitschakelen en verwijderen.</p>
                  </div>
                </aside>
              </div>
            </div>
            <div className="taskschd-statusbar">DailyMaintenance | Ingeschakeld | Trigger: Dagelijks 08:00</div>
          </main>
        </div>
      </div>
    );
  }

  if (normalized === "taskmgr") {
    return (
      <div className="tm-shell native-taskmgr-surface" data-native-app="taskmgr" data-native-parity="taskmgr">
        <aside className="tm-sidebar">
          <button className="tm-tab active" type="button" onClick={() => runAppCommand("PROCESS_END")}><WinGlyph name="lists" /><span>Processen</span></button>
          <button className="tm-tab" type="button" onClick={() => openApp("resourceMonitor")}><WinGlyph name="monitoring" /><span>Prestaties</span></button>
          <button className="tm-tab" type="button" onClick={() => openApp("lusrmgr")}><WinGlyph name="group" /><span>Gebruikers</span></button>
          <button className="tm-tab" type="button" onClick={() => runAppCommand("STARTUP_ITEM_TOGGLE")}><WinGlyph name="rocket_launch" /><span>Opstart-apps</span></button>
        </aside>
        <section className="tm-main">
          <header className="tm-head">
            <h2>Processen</h2>
            <button className="tm-end-btn" type="button" onClick={() => runAppCommand("PROCESS_END")}>Taak beëindigen</button>
          </header>
          <div className="tm-body">
            <div className="tm-toolbar-row"><span className="tm-summary">CPU: 18.7%</span><span className="tm-summary">Geheugen: 2180 MB</span></div>
            <table className="tm-table">
              <thead><tr><th>Naam</th><th>Status</th><th className="tm-th-bar">CPU</th><th className="tm-th-bar">Geheugen</th><th>Schijf</th><th>PID</th></tr></thead>
              <tbody>
                <tr className="tm-row-selected" onDoubleClick={() => runAppCommand("PROCESS_END")}><td className="tm-td-name"><span className="tm-proc-dot tm-dot-ok" />Windows Explorer</td><td className="tm-td-status"><span className="tm-status-run">Actief</span></td><td><div className="tm-bar-wrap"><div className="tm-bar-fill tm-cpu-fill" /><span className="tm-bar-pct">7%</span></div></td><td><div className="tm-bar-wrap"><div className="tm-bar-fill tm-mem-fill" /><span className="tm-bar-pct">620 MB</span></div></td><td className="tm-td-num">0 MB/s</td><td className="tm-td-num">1840</td></tr>
                <tr onDoubleClick={() => openApp("defender")}><td className="tm-td-name"><span className="tm-proc-dot tm-dot-warn" />Antimalware Service Executable</td><td className="tm-td-status"><span className="tm-status-run">Actief</span></td><td><div className="tm-bar-wrap"><div className="tm-bar-fill tm-cpu-fill-med" /><span className="tm-bar-pct">12%</span></div></td><td><div className="tm-bar-wrap"><div className="tm-bar-fill tm-mem-fill-med" /><span className="tm-bar-pct">410 MB</span></div></td><td className="tm-td-num">1 MB/s</td><td className="tm-td-num">720</td></tr>
                <tr onDoubleClick={() => openApp("services")}><td className="tm-td-name"><span className="tm-proc-dot tm-dot-ok" />Service Host: Network Service</td><td className="tm-td-status">Running</td><td><div className="tm-bar-wrap"><div className="tm-bar-fill tm-cpu-fill-low" /><span className="tm-bar-pct">2%</span></div></td><td><div className="tm-bar-wrap"><div className="tm-bar-fill tm-mem-fill-low" /><span className="tm-bar-pct">92 MB</span></div></td><td className="tm-td-num">0 MB/s</td><td className="tm-td-num">984</td></tr>
              </tbody>
            </table>
            <div className="tm-perf-grid">
              <div className="tm-perf-sidebar">
                <button className="tm-perf-nav-item active" type="button" onClick={() => openApp("resourceMonitor")}><span>CPU</span><span className="tm-perf-pct">18.7%</span></button>
                <button className="tm-perf-nav-item" type="button" onClick={() => openApp("resourceMonitor")}><span>Geheugen</span><span className="tm-perf-pct">2.1 GB</span></button>
                <button className="tm-perf-nav-item" type="button" onClick={() => openApp("diskmgmt")}><span>Schijf 0</span><span className="tm-perf-pct">4%</span></button>
                <button className="tm-perf-nav-item" type="button" onClick={() => openApp("ncpa")}><span>Ethernet</span><span className="tm-perf-pct">10.50.20.10</span></button>
              </div>
              <div className="tm-perf-detail">
                <div className="tm-perf-title">Overzicht</div>
                <div className="tm-gauge-row">
                  <div className="tm-gauge-card"><strong>CPU</strong><span>18.7%</span><small>Intel Xeon virtual CPU</small></div>
                  <div className="tm-gauge-card"><strong>Geheugen</strong><span>2.1 / 8 GB</span><small>Beschikbaar geheugen</small></div>
                  <div className="tm-gauge-card"><strong>Schijf</strong><span>4%</span><small>Actieve tijd</small></div>
                </div>
              </div>
            </div>
            <div className="tm-startup-preview">
              <div className="mmc-tabstrip" data-native-parity="taskmgr-view-tabs"><button className="active" type="button" onClick={() => runAppCommand("PROCESS_END")}>Processen</button><button type="button" onClick={() => openApp("resourceMonitor")}>Prestaties</button><button type="button" onClick={() => runAppCommand("STARTUP_ITEM_TOGGLE")}>Opstart-apps</button><button type="button" onClick={() => openApp("lusrmgr")}>Gebruikers</button><button type="button" onClick={() => openApp("services")}>Services</button></div>
              <span className="tm-startup-on">OneDrive ingeschakeld</span>
              <span className="tm-startup-off">Teams uitgeschakeld</span>
              <button className="tm-toggle-btn" type="button" onClick={() => runAppCommand("STARTUP_ITEM_TOGGLE")}>Uitschakelen</button>
            </div>
            <div className="tm-command-band">
              <ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} />
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (normalized === "devmgmt") {
    return (
      <div className="mmc-app dev-shell native-devmgmt-surface native-mmc-surface" data-native-app="devmgmt" data-native-parity="devmgmt" data-oracle-source="devmgmt.js">
        <div className="mmc-menubar" onClick={runPrimaryAppAction}><span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span></div>
        <div className="dev-toolbar"><button className="dev-tb-btn" type="button" onClick={() => runAppCommand("DEVICE_UPDATE_DRIVER")}><WinGlyph name="system_update_alt" /><span>Bijwerken</span></button><button className="dev-tb-btn" type="button" onClick={() => runAppCommand("DEVICE_DISABLE")}><WinGlyph name="toggle_on" /><span>In-/uitschakelen</span></button><span className="dev-tb-sep" /><button className="dev-tb-btn" type="button" onClick={() => runAppCommand("DEVICE_ENABLE")}><WinGlyph name="manage_search" /><span>Scannen</span></button><button className="dev-tb-btn" type="button" onClick={() => runAppCommand("DEVICE_UPDATE_DRIVER")}><WinGlyph name="refresh" /><span>Vernieuwen</span></button></div>
        <div className="mmc-shell dev-body">
          <aside className="mmc-tree dev-tree">
            <button className="dev-tree-root-label" type="button" onClick={() => runAppCommand("DEVICE_UPDATE_DRIVER")}><WinGlyph name="computer" /><span>Computerbeheer</span></button>
            <button className="dev-tree-cat active" type="button" onClick={() => runAppCommand("DEVICE_UPDATE_DRIVER")}><WinGlyph name="lan" /><span>Network adapters</span><span className="dev-tree-count">2</span></button>
            <button className="dev-tree-cat" type="button" onClick={() => openApp("diskmgmt")}><WinGlyph name="storage" /><span>Disk drives</span><span className="dev-tree-count">2</span></button>
            <button className="dev-tree-cat" type="button" onClick={() => runAppCommand("DEVICE_ENABLE")}><WinGlyph name="memory" /><span>Processors</span><span className="dev-tree-count">1</span></button>
            <button className="dev-tree-cat" type="button" onClick={() => runAppCommand("DEVICE_DISABLE")}><WinGlyph name="keyboard" /><span>Keyboards</span><span className="dev-tree-count">1</span></button>
          </aside>
          <main className="mmc-main dev-main-pane">
            <div className="mmc-content dev-content">
              <div className="mmc-split dev-split">
                <section className="dev-list-pane">
                  <table className="dev-table"><thead><tr><th>Apparaat</th><th>Status</th><th>Stuurprogrammaversie</th><th>Stuurprogrammadatum</th></tr></thead><tbody>
                    <tr className="dev-row-selected" onDoubleClick={() => runAppCommand("DEVICE_UPDATE_DRIVER")}><td className="dev-td-name"><WinGlyph name="check_circle" /><span>Intel(R) Ethernet Connection</span></td><td><span className="dev-status-ok">Working properly</span></td><td className="dev-mono">12.19.2.57</td><td>2026-03-18</td></tr>
                    <tr onDoubleClick={() => runAppCommand("DEVICE_ENABLE")}><td className="dev-td-name"><WinGlyph name="error" /><span>Legacy VPN Adapter</span></td><td><span className="dev-status-disabled">Disabled</span></td><td className="dev-mono">10.0.19041</td><td>2024-10-10</td></tr>
                  </tbody></table>
                </section>
                <aside className="mmc-detailpane dev-detail-pane" data-native-parity="devmgmt-property-tabs"><div className="dev-detail-header"><WinGlyph name="lan" /><div><h4>Intel(R) Ethernet Connection</h4><div className="tool-muted">Network adapters</div></div></div><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("DEVICE_UPDATE_DRIVER")}>Algemeen</button><button type="button" onClick={() => runAppCommand("DEVICE_UPDATE_DRIVER")}>Stuurprogramma</button><button type="button" onClick={() => runAppCommand("DEVICE_DISABLE")}>Details</button><button type="button" onClick={() => runAppCommand("DEVICE_ENABLE")}>Gebeurtenissen</button></div><div className="mmc-group"><h4>Apparaatstatus</h4><div className="tool-muted">Dit apparaat werkt correct.</div></div><div className="mmc-group"><h4>Stuurprogramma</h4><div className="tool-muted">Versie: 12.19.2.57<br />Datum: 2026-03-18</div></div></aside>
              </div>
            </div>
            <div className="dev-statusbar">Intel(R) Ethernet Connection | Network adapters | Stuurprogramma: 12.19.2.57</div>
          </main>
        </div>
      </div>
    );
  }

  if (normalized === "diskmgmt") {
    return (
      <div className="dm-shell native-diskmgmt-surface" data-native-app="diskmgmt" data-native-parity="diskmgmt" data-oracle-source="diskmgmt.js">
        <div className="dm-menubar" onClick={() => runFirstAppCommand("VOLUME_CREATE", "VOLUME_FORMAT")}><span>Bestand</span><span>Actie</span><span>Beeld</span><span>Help</span></div>
        <div className="dm-toolbar"><button className="dm-tb-btn" type="button" onClick={() => runAppCommand("DISK_RUN_CHKDSK")}><WinGlyph name="refresh" /><span>Vernieuwen</span></button><button className="dm-tb-btn dm-tb-primary" type="button" onClick={() => runAppCommand("VOLUME_CREATE")}><WinGlyph name="add" /><span>Nieuw volume</span></button><span className="dm-tb-sep" /><button className="dm-tb-btn" type="button" onClick={() => runAppCommand("VOLUME_CREATE")}><WinGlyph name="drive_file_rename_outline" /><span>Stationsletter</span></button><button className="dm-tb-btn" type="button" onClick={() => runAppCommand("VOLUME_FORMAT")}><WinGlyph name="format_color_reset" /><span>Formatteren</span></button></div>
        <div className="dm-body">
          <div className="dm-pane">
            <div className="dm-upper">
              <table className="dm-table"><thead><tr><th>Volume</th><th>Schijf</th><th>Bestandssysteem</th><th>Status</th><th>Capaciteit</th><th>Vrije ruimte</th></tr></thead><tbody><tr className="dm-row-selected" onDoubleClick={() => runAppCommand("DISK_RUN_CHKDSK")}><td className="dm-vol-name"><span className="dm-vol-letter">C:</span> Windows</td><td>Schijf 0</td><td>NTFS</td><td><span className="dm-health-badge dm-health-ok">In orde</span></td><td className="dm-num">120.00 GB</td><td className="dm-num">43.20 GB</td></tr><tr onDoubleClick={() => runAppCommand("VOLUME_FORMAT")}><td className="dm-vol-name"><span className="dm-vol-letter">D:</span> Data</td><td>Schijf 1</td><td>NTFS</td><td><span className="dm-health-badge dm-health-ok">In orde</span></td><td className="dm-num">80.00 GB</td><td className="dm-num">65.00 GB</td></tr></tbody></table>
            </div>
            <div className="dm-graphic">
              <div className="dm-disk-row dm-disk-row-selected" onDoubleClick={() => runAppCommand("DISK_RUN_CHKDSK")}><div className="dm-disk-label"><div className="dm-disk-label-name">Schijf 0</div><div className="dm-disk-label-type">Standaard</div><div className="dm-disk-label-size">120.00 GB</div><div className="dm-disk-label-status"><span className="dm-disk-online">Online</span></div></div><div className="dm-partitions"><div className="dm-part dm-part-selected"><div className="dm-part-bar dm-part-blue" /><div className="dm-part-labels"><span className="dm-part-label">(C:) Windows</span><span className="dm-part-sub">120.00 GB NTFS</span></div></div></div></div>
              <div className="dm-disk-row" onDoubleClick={() => runAppCommand("VOLUME_CREATE")}><div className="dm-disk-label"><div className="dm-disk-label-name">Schijf 1</div><div className="dm-disk-label-type">Standaard</div><div className="dm-disk-label-size">100.00 GB</div><div className="dm-disk-label-status"><span className="dm-disk-online">Online</span></div></div><div className="dm-partitions"><div className="dm-part"><div className="dm-part-bar dm-part-green" /><div className="dm-part-labels"><span className="dm-part-label">(D:) Data</span><span className="dm-part-sub">80.00 GB NTFS</span></div></div><div className="dm-part dm-part-unalloc"><div className="dm-part-bar" /><div className="dm-part-labels"><span className="dm-part-label">Niet-toegewezen</span><span className="dm-part-sub">20.00 GB</span></div></div></div></div>
              <aside className="dm-detail-pane" data-native-parity="diskmgmt-volume-detail"><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("DISK_RUN_CHKDSK")}>Volumes</button><button type="button" onClick={() => runAppCommand("VOLUME_CREATE")}>Nieuw volume</button><button type="button" onClick={() => runAppCommand("VOLUME_FORMAT")}>Formatteren</button></div><dl><div><dt>Geselecteerd volume</dt><dd>C: Windows</dd></div><div><dt>Partitiestijl</dt><dd>GPT</dd></div><div><dt>BitLocker</dt><dd>Ingeschakeld</dd></div><div><dt>Gezondheid</dt><dd>In orde</dd></div></dl></aside>
            </div>
          </div>
        </div>
        <div className="dm-statusbar">Schijf 0 | C: | NTFS | 120.00 GB</div>
      </div>
    );
  }

  if (normalized === "msinfo32") {
    return (
      <div className="msinfo-shell native-msinfo32-surface" data-native-app="msinfo32" data-native-parity="msinfo32">
        <div className="msinfo-menubar" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}><span>Bestand</span><span>Bewerken</span><span>Beeld</span><span>Help</span></div>
        <div className="msinfo-toolbar"><span className="msinfo-find-label">Zoeken wat:</span><input className="msinfo-find-input" placeholder="Zoekterm invoeren..." readOnly /><button className="msinfo-find-btn" type="button" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}>Zoeken</button></div>
        <div className="msinfo-body"><aside className="msinfo-tree"><button className="msinfo-tree-root" type="button" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}><WinGlyph name="article" /><span>Systeeminformatie</span></button><button className="msinfo-tree-item active" type="button" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}><WinGlyph name="article" /><span>Systeemoverzicht</span></button><button className="msinfo-tree-item" type="button" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}><WinGlyph name="memory" /><span>Hardwarebronnen</span></button><button className="msinfo-tree-item" type="button" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}><WinGlyph name="widgets" /><span>Onderdelen</span></button><button className="msinfo-tree-item" type="button" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}><WinGlyph name="terminal" /><span>Software-omgeving</span></button></aside><main className="msinfo-content"><div className="msinfo-content-header">Systeemoverzicht</div><div className="msinfo-table-wrap"><table className="msinfo-table"><thead><tr><th>Item</th><th>Waarde</th></tr></thead><tbody><tr><td className="msinfo-td-key">Computernaam</td><td className="msinfo-td-val">CLIENT01</td></tr><tr><td className="msinfo-td-key">Besturingssysteem</td><td className="msinfo-td-val">Windows 11 Enterprise</td></tr><tr><td className="msinfo-td-key">Processor</td><td className="msinfo-td-val">Intel Xeon virtual CPU</td></tr><tr><td className="msinfo-td-key">RAM</td><td className="msinfo-td-val">8 GB</td></tr><tr><td className="msinfo-td-key">Domein / werkgroep</td><td className="msinfo-td-val">advanced-ti.local</td></tr></tbody></table></div><section className="utility-detail-panel" data-native-parity="msinfo32-category-depth"><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}>Systeemoverzicht</button><button type="button" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}>Hardwarebronnen</button><button type="button" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}>Onderdelen</button><button type="button" onClick={() => runAppCommand("SYSINFO_RECORD_CHECK")}>Software-omgeving</button></div><dl><div><dt>BIOS-modus</dt><dd>UEFI</dd></div><div><dt>Secure Boot</dt><dd>Ingeschakeld</dd></div></dl></section><div className="msinfo-command-band"><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div></main></div>
        <div className="msinfo-statusbar"><span className="msinfo-statusbar-seg">Systeemoverzicht</span><span className="msinfo-statusbar-seg">5 items</span></div>
      </div>
    );
  }

    if (normalized === "settingsSystem") {
      return <SettingsSystemSurface actions={appActions} busyActionId={busyActionId} initialSection={windowState?.initialUrl} onRun={runAction} openApp={openApp} />;
    }

  if (normalized === "powercfg") {
    return (
      <div className="pc-shell native-powercfg-surface" data-native-app="powercfg" data-native-parity="powercfg">
        <aside className="pc-sidebar"><div className="pc-sidebar-breadcrumb"><span>Configuratiescherm</span> &rsaquo;<br /><span>Hardware en geluiden</span> &rsaquo;<br /><strong>Energiebeheer</strong></div><div className="pc-sidebar-section">Taken</div><button className="pc-sidebar-item active" type="button" onClick={() => runAppCommand("POWER_PLAN_SET_ACTIVE")}>Een energiebeheerschema selecteren</button><button className="pc-sidebar-item" type="button" onClick={() => runAppCommand("POWER_PLAN_SET_ACTIVE")}>Weergave uitschakelen</button><button className="pc-sidebar-item" type="button" onClick={() => runAppCommand("POWER_PLAN_SET_ACTIVE")}>Slaapstand instellen</button></aside>
        <main className="pc-main"><header className="pc-main-header"><h2>Energiebeheer</h2><p>Selecteer of pas een energiebeheerschema aan.</p></header><div className="pc-content"><div className="pc-section-label">Aanbevolen schema's</div><div className="pc-plan-list"><div className="pc-plan-row active" onDoubleClick={() => runAppCommand("POWER_PLAN_SET_ACTIVE")}><input className="pc-plan-radio" type="radio" checked readOnly /><WinGlyph name="speed" className="pc-plan-icon-sm" /><div className="pc-plan-body"><div className="pc-plan-name">Gebalanceerd <span className="pc-plan-active-badge">Actief</span></div><div className="pc-plan-desc">Scherm uit na 15 min &middot; Slaapstand na 30 min</div><button className="pc-plan-edit" type="button" onClick={() => runAppCommand("POWER_PLAN_SET_ACTIVE")}>Planinstellingen wijzigen</button></div></div><div className="pc-plan-row" onDoubleClick={() => runAppCommand("POWER_PLAN_SET_ACTIVE")}><input className="pc-plan-radio" type="radio" readOnly /><WinGlyph name="bolt" className="pc-plan-icon-sm" /><div className="pc-plan-body"><div className="pc-plan-name">Hoge prestaties</div><div className="pc-plan-desc">Scherm uit na 30 min &middot; Slaapstand na 60 min</div><button className="pc-plan-edit" type="button" onClick={() => runAppCommand("POWER_PLAN_SET_ACTIVE")}>Planinstellingen wijzigen</button></div></div></div><div className="pc-summary-box"><h3>Actief schema: Gebalanceerd</h3><div className="pc-summary-row"><span className="pc-summary-key">Scherm uitschakelen</span><span className="pc-summary-val">15 minuten</span></div><div className="pc-summary-row"><span className="pc-summary-key">Slaapstand</span><span className="pc-summary-val">30 minuten</span></div></div><section className="utility-detail-panel" data-native-parity="powercfg-plan-depth"><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("POWER_PLAN_SET_ACTIVE")}>Schema-instellingen</button><button type="button" onClick={() => runAppCommand("POWER_PLAN_SET_ACTIVE")}>Geavanceerde instellingen</button><button type="button" onClick={() => openApp("settingsSystem")}>Slaapstand</button></div><dl><div><dt>Op netstroom</dt><dd>Scherm 15 min, slaapstand 30 min</dd></div><div><dt>Op batterij</dt><dd>Niet van toepassing in lab</dd></div></dl></section><div className="pc-command-band"><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div></div></main>
      </div>
    );
  }

  if (normalized === "appwiz") {
    return (
      <div className="appwiz-shell native-appwiz-surface" data-native-app="appwiz" data-native-parity="appwiz">
        <div className="appwiz-addressbar"><WinGlyph name="arrow_back" /><span>Configuratiescherm</span><span>&rsaquo;</span><span>Programma's</span><span>&rsaquo;</span><strong>Programma's en onderdelen</strong></div>
        <div className="appwiz-body"><aside className="appwiz-nav"><h3>Configuratiescherm</h3><button type="button" onClick={() => openApp("controlPanel")}>Startpagina van Configuratiescherm</button><button type="button" onClick={() => openApp("windowsUpdate")}>Geinstalleerde updates weergeven</button><button type="button" onClick={() => openApp("optionalfeatures")}>Windows-onderdelen in- of uitschakelen</button></aside><main className="appwiz-main"><header className="appwiz-header"><h2>Een programma verwijderen of wijzigen</h2><p>Selecteer een programma en klik op Verwijderen, Wijzigen of Herstellen.</p></header><div className="appwiz-commandbar"><button type="button" onClick={() => openApp("controlPanel")}>Organiseren</button><button type="button" onClick={() => runAppCommand("APP_UNINSTALL")}>Verwijderen</button><button type="button" onClick={() => runAppCommand("APP_UNINSTALL")}>Wijzigen</button><span className="appwiz-spacer" /><input aria-label="Programma zoeken" value="Zoeken in Programma's en onderdelen" readOnly /></div><table className="appwiz-table"><thead><tr><th>Naam</th><th>Uitgever</th><th>Geinstalleerd op</th><th>Grootte</th><th>Versie</th></tr></thead><tbody><tr className="selected" onDoubleClick={() => runAppCommand("APP_UNINSTALL")}><td><WinGlyph name="deployed_code" />Contoso VPN Client</td><td>Contoso</td><td>29-04-2026</td><td>148 MB</td><td>5.4.1</td></tr><tr onDoubleClick={() => openApp("edge")}><td><WinGlyph name="language" />Microsoft Edge</td><td>Microsoft Corporation</td><td>12-04-2026</td><td>412 MB</td><td>126.0</td></tr><tr onDoubleClick={() => runAppCommand("APP_UNINSTALL")}><td><WinGlyph name="description" />7-Zip 24.01</td><td>Igor Pavlov</td><td>02-04-2026</td><td>6 MB</td><td>24.01</td></tr></tbody></table><section className="utility-detail-panel" data-native-parity="appwiz-program-detail"><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("APP_UNINSTALL")}>Programmadetails</button><button type="button" onClick={() => openApp("windowsUpdate")}>Geinstalleerde updates</button><button type="button" onClick={() => openApp("optionalfeatures")}>Windows-onderdelen</button></div><dl><div><dt>Geselecteerd</dt><dd>Contoso VPN Client 5.4.1</dd></div><div><dt>Actie</dt><dd>Verwijderen of wijzigen</dd></div></dl></section><div className="appwiz-command-band"><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div></main></div><div className="appwiz-statusbar">3 programma's geinstalleerd | Contoso VPN Client geselecteerd</div>
      </div>
    );
  }

  if (normalized === "optionalfeatures") {
    return (
      <div className="of-shell native-optionalfeatures-surface" data-native-app="optionalfeatures" data-native-parity="optionalfeatures">
        <div className="of-header"><h2>Windows-onderdelen</h2><p>Schakel Windows-onderdelen in of uit.</p></div>
        <div className="of-desc">Als u een selectievakje inschakelt, wordt het onderdeel ingeschakeld. Een gevuld vakje betekent dat slechts een deel van het onderdeel is ingeschakeld.</div>
        <div className="of-info-bar"><WinGlyph name="info" /><span>Sommige onderdelen vereisen mogelijk opnieuw opstarten.</span></div>
        <div className="of-list-box"><label className="of-row checked" onDoubleClick={() => runAppCommand("FEATURE_ENABLE")}><input type="checkbox" checked readOnly /><span className="of-expander">&#9662;</span><WinGlyph name="web" /><strong>.NET Framework 4.8 Advanced Services</strong></label><label className="of-row" onDoubleClick={() => runAppCommand("FEATURE_ENABLE")}><input type="checkbox" readOnly /><span className="of-expander">&#9656;</span><WinGlyph name="public" /><strong>Internet Information Services</strong></label><label className="of-row checked" onDoubleClick={() => runAppCommand("FEATURE_DISABLE")}><input type="checkbox" checked readOnly /><span className="of-expander">&#9662;</span><WinGlyph name="terminal" /><strong>Windows PowerShell 2.0</strong></label><label className="of-row" onDoubleClick={() => runAppCommand("FEATURE_DISABLE")}><input type="checkbox" readOnly /><span className="of-expander">&#9656;</span><WinGlyph name="folder_managed" /><strong>SMB 1.0/CIFS File Sharing Support</strong></label></div>
        <section className="utility-detail-panel" data-native-parity="optionalfeatures-tree-depth"><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("FEATURE_ENABLE")}>Onderdelen</button><button type="button" onClick={() => runAppCommand("FEATURE_DISABLE")}>Subonderdelen</button><button type="button" onClick={() => openApp("windowsUpdate")}>Voortgang</button></div><dl><div><dt>Geselecteerd onderdeel</dt><dd>.NET Framework 4.8 Advanced Services</dd></div><div><dt>Installatiestatus</dt><dd>Ingeschakeld</dd></div></dl></section>
        <div className="of-command-band"><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div>
        <footer className="of-footer"><button className="tool-btn primary" type="button" onClick={() => runAppCommand("FEATURE_ENABLE")}>OK</button><button className="tool-btn" type="button" onClick={() => openApp("controlPanel")}>Annuleren</button></footer>
      </div>
    );
  }

  if (normalized === "windowsUpdate") {
    return (
      <div className="wu-shell native-windows-update-surface" data-native-app="windowsUpdate" data-native-parity="windowsUpdate">
        <aside className="wu-nav"><h3>Instellingen</h3><button className="wu-nav-item active" type="button" onClick={() => runAppCommand("WINDOWS_UPDATE_INSTALL")}><WinGlyph name="system_update_alt" />Windows Update</button><button className="wu-nav-item" type="button" onClick={() => runAppCommand("WINDOWS_UPDATE_INSTALL")}><WinGlyph name="history" />Updategeschiedenis</button><button className="wu-nav-item" type="button" onClick={() => runAppCommand("WINDOWS_UPDATE_PAUSE")}><WinGlyph name="schedule" />Geavanceerde opties</button><button className="wu-nav-item" type="button" onClick={() => runAppCommand("WINDOWS_UPDATE_INSTALL")}><WinGlyph name="restart_alt" />Herstel</button></aside>
        <main className="wu-main"><header className="wu-header"><h2>Windows Update</h2><button className="wu-check-btn" type="button" onClick={() => runAppCommand("WINDOWS_UPDATE_INSTALL")}><WinGlyph name="refresh" />Naar updates zoeken</button></header><section className="wu-status-card" onDoubleClick={() => runAppCommand("WINDOWS_UPDATE_INSTALL")}><div className="wu-status-icon"><WinGlyph name="verified" /></div><div><h3>U bent up-to-date</h3><p>Laatst gecontroleerd: vandaag om 15:32</p></div></section><section className="wu-update-list"><h3>Beschikbare updates</h3><div className="wu-update-row" onDoubleClick={() => runAppCommand("WINDOWS_UPDATE_INSTALL")}><WinGlyph name="system_update" /><div><strong>2026-04 cumulatieve update voor Windows 11</strong><p>KB5061042 &middot; In afwachting van installatie</p></div><span className="wu-pill">Aanbevolen</span></div><div className="wu-update-row" onDoubleClick={() => openApp("defender")}><WinGlyph name="security" /><div><strong>Security Intelligence-update</strong><p>Defender platform &middot; Geinstalleerd</p></div><span className="wu-pill ok">Gereed</span></div></section><section className="wu-options-grid"><button className="wu-option-card" type="button" onClick={() => runAppCommand("WINDOWS_UPDATE_PAUSE")}><WinGlyph name="pause_circle" /><strong>Updates onderbreken</strong><p>Onderbreek updates tijdelijk voor maximaal 5 weken.</p></button><button className="wu-option-card" type="button" onClick={() => runAppCommand("WINDOWS_UPDATE_INSTALL")}><WinGlyph name="restart_alt" /><strong>Actieve uren</strong><p>Voorkom onverwachte herstarts tijdens servicedeskwerk.</p></button></section><section className="wu-history-panel" data-native-parity="windows-update-history-depth"><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("WINDOWS_UPDATE_INSTALL")}>Updategeschiedenis</button><button type="button" onClick={() => runAppCommand("WINDOWS_UPDATE_PAUSE")}>Geavanceerde opties</button><button type="button" onClick={() => runAppCommand("WINDOWS_UPDATE_INSTALL")}>Herstel</button></div><div className="wu-history-row"><strong>Kwaliteitsupdates</strong><span>KB5061042 - Succesvol geinstalleerd</span></div><div className="wu-history-row"><strong>Definitie-updates</strong><span>Security Intelligence 1.421.19.0</span></div></section><div className="wu-command-band"><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div></main>
      </div>
    );
  }

  if (normalized === "defender") {
    return (
      <div className="def-shell native-defender-surface" data-native-app="defender" data-native-parity="defender">
        <aside className="def-nav"><h3>Windows-beveiliging</h3><button className="def-nav-item active" type="button" onClick={() => runAppCommand("DEFENDER_RUN_SCAN")}><WinGlyph name="health_and_safety" />Start</button><button className="def-nav-item" type="button" onClick={() => runAppCommand("DEFENDER_RUN_SCAN")}><WinGlyph name="shield" />Virus- en bedreigingsbeveiliging</button><button className="def-nav-item" type="button" onClick={() => openApp("wf")}><WinGlyph name="network_check" />Firewall en netwerkbeveiliging</button><button className="def-nav-item" type="button" onClick={() => runAppCommand("DEFENDER_ADD_EXCLUSION")}><WinGlyph name="app_blocking" />App- en browserbeheer</button></aside>
        <main className="def-main"><section className="def-status-card" onDoubleClick={() => runAppCommand("DEFENDER_RUN_SCAN")}><div className="def-status-icon ok"><WinGlyph name="check_circle" /></div><div><h2>Uw apparaat wordt beschermd</h2><p>Er zijn geen acties nodig.</p></div></section><div className="def-card-grid"><section className="def-card"><WinGlyph name="shield" /><h3>Virus- en bedreigingsbeveiliging</h3><p>Laatste scan: vandaag om 14:58</p><button type="button" onClick={() => runAppCommand("DEFENDER_RUN_SCAN")}>Snelle scan</button></section><section className="def-card"><WinGlyph name="settings" /><h3>Instellingen voor bescherming</h3><p>Realtimebeveiliging is ingeschakeld.</p><button className="def-toggle" type="button" onClick={() => runAppCommand("DEFENDER_TOGGLE_REALTIME")}><span>Realtimebeveiliging</span><input type="checkbox" checked readOnly /></button></section><section className="def-card"><WinGlyph name="folder_off" /><h3>Uitsluitingen</h3><p>1 pad uitgesloten voor labsimulatie.</p><button type="button" onClick={() => runAppCommand("DEFENDER_ADD_EXCLUSION")}>Uitsluiting toevoegen</button></section></div><section className="def-threat-list" data-native-parity="defender-protection-history"><h3>Recente gebeurtenissen</h3><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("DEFENDER_RUN_SCAN")}>Beveiligingsgeschiedenis</button><button type="button" onClick={() => runAppCommand("DEFENDER_TOGGLE_REALTIME")}>Instellingen beheren</button><button type="button" onClick={() => runAppCommand("DEFENDER_ADD_EXCLUSION")}>Uitsluitingen</button></div><div className="def-threat-row" onDoubleClick={() => runAppCommand("DEFENDER_RUN_SCAN")}><WinGlyph name="task_alt" /><strong>Quick scan voltooid</strong><span>0 bedreigingen gevonden</span></div><div className="def-threat-row" onDoubleClick={() => openApp("windowsUpdate")}><WinGlyph name="history" /><strong>Platform bijgewerkt</strong><span>Security Intelligence actueel</span></div></section><div className="def-command-band"><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div></main>
      </div>
    );
  }

  if (normalized === "bitlocker") {
    return (
      <div className="bl-shell native-bitlocker-surface" data-native-app="bitlocker" data-native-parity="bitlocker">
        <div className="bl-topbar"><WinGlyph name="arrow_back" /><span>Configuratiescherm</span><span>&rsaquo;</span><span>Systeem en beveiliging</span><span>&rsaquo;</span><strong>BitLocker-stationsversleuteling</strong></div>
        <div className="bl-body"><aside className="bl-sidebar"><h3>Taken</h3><button type="button" onClick={() => runAppCommand("BITLOCKER_ENABLE")}>TPM-beheer</button><button type="button" onClick={() => runAppCommand("BITLOCKER_ENABLE")}>Herstelsleutel back-uppen</button><button type="button" onClick={() => runAppCommand("BITLOCKER_DISABLE")}>BitLocker herstellen</button></aside><main className="bl-main"><header className="bl-header"><h2>BitLocker-stationsversleuteling</h2><p>Bescherm bestanden en mappen door volledige stations te versleutelen.</p></header><section className="bl-volume-list"><div className="bl-volume-row active" onDoubleClick={() => runAppCommand("BITLOCKER_DISABLE")}><WinGlyph name="lock" className="bl-lock-icon" /><div className="bl-volume-info"><strong>Besturingssysteemstation C:</strong><p>BitLocker ingeschakeld &middot; XTS-AES 256 &middot; Herstelsleutel opgeslagen</p></div><button type="button" onClick={() => runAppCommand("BITLOCKER_DISABLE")}>BitLocker uitschakelen</button></div><div className="bl-volume-row" onDoubleClick={() => runAppCommand("BITLOCKER_ENABLE")}><WinGlyph name="lock_open" className="bl-lock-icon off" /><div className="bl-volume-info"><strong>Gegevensstation D:</strong><p>BitLocker uitgeschakeld</p></div><button type="button" onClick={() => runAppCommand("BITLOCKER_ENABLE")}>BitLocker inschakelen</button></div></section><section className="bl-details" data-native-parity="bitlocker-recovery-depth"><h3>Beveiligingsstatus</h3><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("BITLOCKER_ENABLE")}>Stationstatus</button><button type="button" onClick={() => runAppCommand("BITLOCKER_ENABLE")}>Herstelsleutel</button><button type="button" onClick={() => runAppCommand("BITLOCKER_DISABLE")}>Versleuteling onderbreken</button></div><div className="bl-detail-grid"><span>Encryptiemethode</span><strong>XTS-AES 256</strong><span>TPM</span><strong>Gereed voor gebruik</strong><span>Laatste wijziging</span><strong>Vandaag</strong><span>Herstelsleutel-id</span><strong>FD-2026-C-170</strong></div></section><div className="bl-command-band"><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div></main></div>
      </div>
    );
  }

  if (normalized === "controlPanel") {
    return (
      <div className="cp-shell native-control-panel-surface" data-native-app="controlPanel" data-native-parity="controlPanel">
        <div className="cp-chrome"><div className="cp-nav-row"><button className="cp-nav-btn" type="button" onClick={() => openApp("settingsSystem")}><MmcGlyph kind="back" /></button><button className="cp-nav-btn" type="button" onClick={() => openApp("controlPanel")}><MmcGlyph kind="up" /></button><div className="cp-address-bar"><span>Configuratiescherm</span></div><div className="cp-search-wrap"><MmcGlyph kind="search" className="cp-search-icon" /><input className="cp-search" value="Zoeken in Configuratiescherm" readOnly /></div></div><div className="cp-toolbar-row"><span className="cp-view-label">Weergeven op:</span><select className="cp-view-select" defaultValue="category"><option value="category">Categorie</option><option value="large">Grote pictogrammen</option><option value="small">Kleine pictogrammen</option></select></div></div>
        <div className="cp-layout"><aside className="cp-sidebar"><div className="cp-sidebar-heading">Configuratiescherm</div><button className="cp-sidebar-item cp-sidebar-home" type="button" onClick={() => openApp("controlPanel")}>Start</button><div className="cp-sidebar-sep" /><div className="cp-sidebar-heading">Zie ook</div><button className="cp-sidebar-item" type="button" onClick={() => openApp("settingsSystem")}>Systeem</button><button className="cp-sidebar-item" type="button" onClick={() => openApp("wf")}>Windows Firewall</button><button className="cp-sidebar-item" type="button" onClick={() => openApp("eventvwr")}>Logboeken</button><button className="cp-sidebar-item" type="button" onClick={() => openApp("ncpa")}>Netwerkverbindingen</button></aside><main className="cp-main"><div className="cp-category-grid"><section className="cp-cat-card" onDoubleClick={() => openApp("settingsSystem")}><div className="cp-cat-card-header"><MmcGlyph kind="computer" className="cp-cat-icon" /><div><h3 className="cp-cat-title">Systeem en beveiliging</h3><p className="cp-cat-desc">Systeem, Energiebeheer, Firewall, BitLocker</p></div></div><div className="cp-cat-links"><button className="cp-link" type="button" onClick={() => openApp("settingsSystem")}>Systeem</button><button className="cp-link" type="button" onClick={() => openApp("wf")}>Windows Defender Firewall</button><button className="cp-link" type="button" onClick={() => openApp("bitlocker")}>BitLocker-stationsversleuteling</button><button className="cp-link" type="button" onClick={() => openApp("powercfg")}>Energiebeheer</button><button className="cp-link" type="button" onClick={() => openApp("eventvwr")}>Systeemlogboeken bekijken</button></div></section><section className="cp-cat-card" onDoubleClick={() => openApp("ncpa")}><div className="cp-cat-card-header"><MmcGlyph kind="network" className="cp-cat-icon" /><div><h3 className="cp-cat-title">Netwerk en internet</h3><p className="cp-cat-desc">Netwerkverbindingen en adapterconfiguratie</p></div></div><div className="cp-cat-links"><button className="cp-link" type="button" onClick={() => openApp("ncpa")}>Netwerkverbindingen</button><button className="cp-link" type="button" onClick={() => openApp("netSettings")}>Netwerkstatus weergeven</button></div></section><section className="cp-cat-card" onDoubleClick={() => openApp("devmgmt")}><div className="cp-cat-card-header"><MmcGlyph kind="monitor" className="cp-cat-icon" /><div><h3 className="cp-cat-title">Hardware en geluiden</h3><p className="cp-cat-desc">Apparaatbeheer, apparaten en printers</p></div></div><div className="cp-cat-links"><button className="cp-link" type="button" onClick={() => openApp("devmgmt")}>Apparaatbeheer</button><button className="cp-link" type="button" onClick={() => openApp("controlPrinters")}>Apparaten en printers</button><button className="cp-link" type="button" onClick={() => openApp("powercfg")}>Energie-opties</button></div></section><section className="cp-cat-card" onDoubleClick={() => openApp("appwiz")}><div className="cp-cat-card-header"><MmcGlyph kind="list" className="cp-cat-icon" /><div><h3 className="cp-cat-title">Programma's</h3><p className="cp-cat-desc">Geinstalleerde apps en Windows-onderdelen</p></div></div><div className="cp-cat-links"><button className="cp-link" type="button" onClick={() => openApp("appwiz")}>Programma's en onderdelen</button><button className="cp-link" type="button" onClick={() => openApp("optionalfeatures")}>Windows-onderdelen in/uitschakelen</button></div></section></div><section className="cp-detail-pane" data-native-parity="control-panel-category-depth"><h3>Veelgebruikte taken</h3><div><button type="button" onClick={() => openApp("sysdm")}>Computernaam of domein wijzigen</button><button type="button" onClick={() => openApp("wf")}>Firewallstatus controleren</button><button type="button" onClick={() => openApp("controlPrinters")}>Printer toevoegen</button><button type="button" onClick={() => openApp("taskschd")}>Geplande taken beheren</button></div></section><div className="cp-command-band"><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div></main></div>
      </div>
    );
  }

  if (normalized === "controlPrinters") {
    return (
      <div className="prn-shell native-control-printers-surface" data-native-app="controlPrinters" data-native-parity="controlPrinters">
        <div className="prn-topbar"><div className="prn-breadcrumb"><span className="prn-bc-link">Configuratiescherm</span><span className="prn-bc-sep">&gt;</span><span className="prn-bc-link">Hardware en geluiden</span><span className="prn-bc-sep">&gt;</span><strong>Apparaten en printers</strong></div><button className="tool-btn primary" type="button" onClick={() => runAppCommand("PRINTER_ADD")}><WinGlyph name="add" />Printer toevoegen</button></div>
        <main className="prn-content"><section className="prn-section"><div className="prn-section-header"><WinGlyph name="print" />Printers <span className="prn-section-count">2</span></div><div className="prn-tile-grid"><div className="prn-tile prn-tile-default" onDoubleClick={() => runAppCommand("PRINTER_SET_DEFAULT")}><div className="prn-tile-icon-wrap"><WinGlyph name="print" className="prn-icon-large" /><span className="prn-default-badge">&#10003;</span></div><div className="prn-tile-name">Frontoffice MFP</div><div className="prn-tile-status">Gereed</div><div className="prn-tile-driver">Universal PCL</div><div className="prn-tile-default-label">Standaard</div></div><div className="prn-tile" onDoubleClick={() => runAppCommand("PRINTER_SET_DEFAULT")}><div className="prn-tile-icon-wrap"><WinGlyph name="print" className="prn-icon-large" /></div><div className="prn-tile-name">Netwerkprinter</div><div className="prn-tile-status">Gereed</div><div className="prn-tile-driver">Generic PCL</div><button className="prn-tile-btn" type="button" onClick={() => runAppCommand("PRINTER_SET_DEFAULT")}>Als standaard instellen</button></div></div></section><section className="utility-detail-panel" data-native-parity="control-printers-device-depth"><div className="mmc-tabstrip"><button className="active" type="button" onClick={() => runAppCommand("PRINTER_SET_DEFAULT")}>Printereigenschappen</button><button type="button" onClick={() => runAppCommand("PRINTER_ADD")}>Poorten</button><button type="button" onClick={() => runAppCommand("PRINTER_SET_DEFAULT")}>Afdrukwachtrij</button></div><dl><div><dt>Standaardprinter</dt><dd>Frontoffice MFP</dd></div><div><dt>Poort</dt><dd>WSD-01</dd></div></dl></section><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></main>
      </div>
    );
  }

  if (normalized === "ncpa") {
    return <NetworkConnectionsSurface actions={appActions} busyActionId={busyActionId} onRun={runAction} simulatorState={simulatorState} openApp={openApp} />;
  }

  if (normalized === "regedit") {
    return (
      <div className="regedit-shell native-regedit-surface" data-native-app="regedit" data-native-parity="regedit">
        <div className="regedit-menubar" onClick={() => runAppCommand("REGISTRY_SET_VALUE")}><span>Bestand</span><span>Bewerken</span><span>Beeld</span><span>Favorieten</span><span>Help</span></div><div className="regedit-address">Computer\HKEY_CURRENT_USER\Environment</div><div className="regedit-body"><aside className="regedit-tree"><button className="regedit-tree-root" type="button" onClick={() => runAppCommand("REGISTRY_SET_VALUE")}>Computer</button><button className="regedit-tree-item" type="button" onClick={() => runAppCommand("REGISTRY_SET_VALUE")}>HKEY_CLASSES_ROOT</button><button className="regedit-tree-item active" type="button" onClick={() => runAppCommand("REGISTRY_SET_VALUE")}>HKEY_CURRENT_USER</button><button className="regedit-tree-child" type="button" onClick={() => runAppCommand("REGISTRY_SET_VALUE")}>Environment</button><button className="regedit-tree-item" type="button" onClick={() => runAppCommand("REGISTRY_SET_VALUE")}>HKEY_LOCAL_MACHINE</button><button className="regedit-tree-item" type="button" onClick={() => runAppCommand("REGISTRY_DELETE_VALUE")}>HKEY_USERS</button></aside><main className="regedit-values"><table><thead><tr><th>Naam</th><th>Type</th><th>Gegevens</th></tr></thead><tbody><tr className="selected" onDoubleClick={() => runAppCommand("REGISTRY_SET_VALUE")}><td>Path</td><td>REG_EXPAND_SZ</td><td>%USERPROFILE%\AppData\Local\Microsoft\WindowsApps</td></tr><tr onDoubleClick={() => runAppCommand("REGISTRY_DELETE_VALUE")}><td>TEMP</td><td>REG_EXPAND_SZ</td><td>%USERPROFILE%\AppData\Local\Temp</td></tr></tbody></table><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></main></div><div className="regedit-statusbar">Computer\HKEY_CURRENT_USER\Environment</div>
      </div>
    );
  }

  if (normalized === "rebootSimulator") {
    return (
      <div className="reboot-shell native-reboot-surface" data-native-app="rebootSimulator" data-native-parity="rebootSimulator">
        <div className="reboot-panel"><WinGlyph name="restart_alt" /><h2>Windows opnieuw opstarten</h2><p>Simuleer een herstart van de actieve machine. De scenario-state blijft veilig binnen de v2 simulator.</p><div className="reboot-options"><button type="button" onClick={() => runAppCommand("SYSTEM_REBOOT")}>Nu opnieuw opstarten</button><button type="button" onClick={() => openApp("settingsSystem")}>Annuleren</button></div></div><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} />
      </div>
    );
  }

  if (["calc", "paint", "snippingTool", "clipboardHistory", "credentialManager", "certmgr", "certlm", "diskCleanup", "systemRestore", "reliabilityMonitor", "resourceMonitor"].includes(normalized)) {
    if (normalized === "calc") {
      return <div className="calc-shell native-utility-surface" data-native-app="calc" data-native-parity="calc"><div className="calc-display">4</div><div className="calc-grid">{["MC", "MR", "M+", "M-", "C", "/", "7", "8", "9", "x", "4", "5", "6", "-", "1", "2", "3", "+", "0", ".", "="].map((key) => <button key={key} type="button" onClick={() => key === "=" ? runAppCommand("CALC_RECORD_RESULT") : undefined}>{key}</button>)}</div><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div>;
    }
    if (normalized === "paint") {
      return <div className="paint-shell native-utility-surface" data-native-app="paint" data-native-parity="paint"><div className="paint-tabs"><button className="active" type="button" onClick={() => runAppCommand("PAINT_SAVE_FILE")}>Bestand</button><button className="active" type="button" onClick={() => runAppCommand("PAINT_SAVE_FILE")}>Start</button><button type="button" onClick={() => runAppCommand("PAINT_SAVE_FILE")}>Beeld</button></div><div className="paint-ribbon"><section><strong>Klembord</strong><button type="button"><WinGlyph name="content_paste" />Plakken</button></section><section><strong>Afbeelding</strong><button type="button"><WinGlyph name="crop_square" />Selecteren</button><button type="button"><WinGlyph name="crop" />Bijsnijden</button><button type="button"><WinGlyph name="rotate_right" />Draaien</button></section><section><strong>Gereedschappen</strong><button type="button"><WinGlyph name="edit" /></button><button type="button"><WinGlyph name="format_color_fill" /></button><button type="button"><WinGlyph name="text_fields" /></button><button type="button"><WinGlyph name="brush" /></button></section><section className="paint-colors"><strong>Kleuren</strong>{["#000000", "#ffffff", "#e11d48", "#2563eb", "#16a34a", "#f59e0b", "#7c3aed", "#06b6d4"].map((color) => <button aria-label={color} key={color} style={{ background: color }} type="button" />)}</section><button className="paint-save" type="button" onClick={() => runAppCommand("PAINT_SAVE_FILE")}><WinGlyph name="save" />Opslaan</button></div><div className="paint-body"><aside className="paint-tools"><button type="button"><WinGlyph name="mouse" /></button><button type="button"><WinGlyph name="edit" /></button><button type="button"><WinGlyph name="brush" /></button><button type="button"><WinGlyph name="crop_square" /></button></aside><main className="paint-canvas"><div className="paint-canvas-sheet"><span /></div></main></div><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div>;
    }
    if (normalized === "snippingTool") {
      return <div className="snip-shell native-utility-surface" data-native-app="snippingTool" data-native-parity="snippingTool"><header><WinGlyph name="screenshot_region" /><h2>Knipprogramma</h2></header><div className="snip-toolbar"><button type="button" onClick={() => runAppCommand("SCREENSHOT_RECORD")}>Nieuw</button><button type="button" onClick={() => runAppCommand("SCREENSHOT_RECORD")}>Modus</button><button type="button" onClick={() => runAppCommand("SCREENSHOT_RECORD")}>Vertraging</button><button type="button" onClick={() => runAppCommand("SCREENSHOT_RECORD")}>Opslaan</button></div><div className="snip-preview">Geen knipsel vastgelegd</div><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div>;
    }
    if (normalized === "clipboardHistory") {
      return <div className="clip-shell native-utility-surface" data-native-app="clipboardHistory" data-native-parity="clipboardHistory"><h2>Klembord</h2><p>Win + V</p><div className="clip-list"><button className="clip-item active" type="button" onClick={() => runAppCommand("CLIPBOARD_RECORD_VIEW")}>FirstDesk support note</button><button className="clip-item" type="button" onClick={() => runAppCommand("CLIPBOARD_RECORD_VIEW")}>\\\\fileserver\\intake</button><button className="clip-item" type="button" onClick={() => runAppCommand("CLIPBOARD_RECORD_VIEW")}>10.50.10.10</button></div><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div>;
    }
    if (["certmgr", "certlm", "credentialManager"].includes(normalized)) {
      const isCredential = normalized === "credentialManager";
      const title = metaFor(normalized).windowTitle;
      return <div className="cert-shell native-utility-surface" data-native-app={normalized} data-native-parity={normalized}><div className="cert-menubar" onClick={() => runAppCommand(isCredential ? "CREDENTIAL_ADD" : normalized === "certlm" ? "CERTIFICATE_ADD_LOCAL_MACHINE_STORE" : "CERTIFICATE_ADD_USER_STORE")}><span>File</span><span>Action</span><span>View</span><span>Help</span></div><div className="cert-body"><aside className="cert-tree"><button className="cert-tree-root" type="button" onClick={() => runAppCommand(isCredential ? "CREDENTIAL_ADD" : normalized === "certlm" ? "CERTIFICATE_ADD_LOCAL_MACHINE_STORE" : "CERTIFICATE_ADD_USER_STORE")}>{title}</button><button className="cert-tree-item active" type="button" onClick={() => runAppCommand(isCredential ? "CREDENTIAL_ADD" : normalized === "certlm" ? "CERTIFICATE_ADD_LOCAL_MACHINE_STORE" : "CERTIFICATE_ADD_USER_STORE")}>{isCredential ? "Windows Credentials" : "Personal"}</button><button className="cert-tree-item" type="button" onClick={() => runAppCommand(isCredential ? "CREDENTIAL_DELETE" : normalized === "certlm" ? "CERTIFICATE_ADD_LOCAL_MACHINE_STORE" : "CERTIFICATE_ADD_USER_STORE")}>{isCredential ? "Web Credentials" : "Trusted Root Certification Authorities"}</button><button className="cert-tree-item" type="button" onClick={() => runAppCommand(isCredential ? "CREDENTIAL_ADD" : normalized === "certlm" ? "CERTIFICATE_ADD_LOCAL_MACHINE_STORE" : "CERTIFICATE_ADD_USER_STORE")}>{isCredential ? "Generic Credentials" : "Intermediate Certification Authorities"}</button></aside><main className="cert-main"><table><thead><tr><th>Name</th><th>Issued by</th><th>Expiration</th></tr></thead><tbody><tr onDoubleClick={() => runAppCommand(isCredential ? "CREDENTIAL_ADD" : normalized === "certlm" ? "CERTIFICATE_ADD_LOCAL_MACHINE_STORE" : "CERTIFICATE_ADD_USER_STORE")}><td>{isCredential ? "fileserver" : "CN=Student"}</td><td>{isCredential ? "Windows Credential" : "FirstDesk Lab CA"}</td><td>{isCredential ? "Persistent" : "2027-04-30"}</td></tr></tbody></table><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></main></div></div>;
    }
    if (normalized === "diskCleanup") {
      return <div className="cleanup-shell native-utility-surface" data-native-app="diskCleanup" data-native-parity="diskCleanup"><h2>Schijfopruiming voor Windows (C:)</h2><p>Bereken hoeveel ruimte kan worden vrijgemaakt.</p><div className="cleanup-list"><label onDoubleClick={() => runAppCommand("DISK_CLEANUP_RUN")}><input type="checkbox" checked readOnly /> Tijdelijke internetbestanden <strong>42 MB</strong></label><label onDoubleClick={() => runAppCommand("DISK_CLEANUP_RUN")}><input type="checkbox" checked readOnly /> Tijdelijke bestanden <strong>118 MB</strong></label><label onDoubleClick={() => runAppCommand("DISK_CLEANUP_RUN")}><input type="checkbox" readOnly /> Prullenbak <strong>0 MB</strong></label></div><div className="cleanup-total">Totale hoeveelheid schijfruimte: 160 MB</div><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div>;
    }
    if (normalized === "systemRestore") {
      return <div className="restore-shell native-utility-surface" data-native-app="systemRestore" data-native-parity="systemRestore"><h2>Systeemherstel</h2><p>Herstel systeembestanden en instellingen vanaf een herstelpunt.</p><div className="restore-list"><button className="restore-row active" type="button" onClick={() => runAppCommand("RESTORE_POINT_CREATE")}><strong>Pre-change checkpoint</strong><span>30-04-2026 16:45</span></button><button className="restore-row" type="button" onClick={() => runAppCommand("RESTORE_POINT_CREATE")}><strong>Windows Update</strong><span>29-04-2026 09:10</span></button></div><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div>;
    }
    if (normalized === "reliabilityMonitor") {
      return <div className="reliability-shell native-utility-surface" data-native-app="reliabilityMonitor" data-native-parity="reliabilityMonitor"><h2>Betrouwbaarheidscontrole</h2><div className="reliability-chart"><div className="rel-point high" /><div className="rel-point high" /><div className="rel-point mid" /><div className="rel-point high" /><div className="rel-point high" /></div><div className="reliability-events"><button type="button" onClick={() => runAppCommand("RELIABILITY_RECORD_CHECK")}>Application install successful</button><button type="button" onClick={() => runAppCommand("RELIABILITY_RECORD_CHECK")}>Windows update installed</button></div><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div>;
    }
    if (normalized === "resourceMonitor") {
      return <div className="resource-shell native-utility-surface" data-native-app="resourceMonitor" data-native-parity="resourceMonitor"><div className="resource-tabs"><button className="active" type="button" onClick={() => runAppCommand("RESOURCE_RECORD_CHECK")}>CPU</button><button type="button" onClick={() => runAppCommand("RESOURCE_RECORD_CHECK")}>Memory</button><button type="button" onClick={() => runAppCommand("RESOURCE_RECORD_CHECK")}>Disk</button><button type="button" onClick={() => runAppCommand("RESOURCE_RECORD_CHECK")}>Network</button></div><table className="resource-table"><thead><tr><th>Image</th><th>PID</th><th>CPU</th><th>Memory</th></tr></thead><tbody><tr onDoubleClick={() => runAppCommand("RESOURCE_RECORD_CHECK")}><td>explorer.exe</td><td>420</td><td>2%</td><td>180 MB</td></tr><tr onDoubleClick={() => runAppCommand("RESOURCE_RECORD_CHECK")}><td>svchost.exe</td><td>812</td><td>1%</td><td>96 MB</td></tr></tbody></table><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></div>;
    }
  }

  if (normalized === "edge") {
    return <EdgeBrowserSurface busyActionId={busyActionId} initialUrl={windowState?.initialUrl} onRun={runAction} openApp={openApp} />;
  }

  if (cloudAppIds.includes(normalized)) {
    return <CloudAdminSurface actions={appActions} appId={normalized} busyActionId={busyActionId} onRun={runAction} />;
  }

  if (["adSites", "adTrusts", "certAuthority", "iisManager", "hyperVManager", "printManagement", "dfsManagement", "npsManager", "windowsServerBackup", "failoverCluster", "fsrmManager", "storageManager"].includes(normalized)) {
    const serverToolContent: Record<string, { tree: string[]; columns: string[]; rows: string[][]; detail: string; command: string }> = {
      adSites: { tree: ["Sites", "Inter-Site Transports", "Subnets"], columns: ["Name", "Type", "Description"], rows: [["Default-First-Site-Name", "Site", "Primary datacenter"], ["HQ-Site", "Site", "Scenario staging"], ["DEFAULTIPSITELINK", "Site Link", "IP transport"]], detail: "Beheer sites, subnets en site links voor Active Directory replication.", command: "dssite.msc" },
      adTrusts: { tree: ["advanced-ti.local", "Domains", "Trusts"], columns: ["Domain", "Trust type", "Direction"], rows: [["advanced-ti.local", "Forest root", "Local"], ["partner.local", "External trust", "Two-way"]], detail: "Controleer domein- en foresttrusts vanuit de MMC-console.", command: "domain.msc" },
      certAuthority: { tree: ["Certification Authority", "Issued Certificates", "Revoked Certificates", "Pending Requests"], columns: ["Request ID", "Subject", "Template", "Status"], rows: [["42", "CN=app.lab.local", "WebServer", "Issued"], ["43", "CN=vpn.lab.local", "Computer", "Pending"]], detail: "Bekijk uitgegeven certificaten en CA-status.", command: "certsrv.msc" },
      iisManager: { tree: ["SRV01", "Application Pools", "Sites", "Default Web Site"], columns: ["Name", "State", "Binding"], rows: [["Default Web Site", "Started", "*:80:"], ["Intranet", "Started", "intranet.lab.local:80"], ["FirstDeskLab", "Stopped", "lab.local:8080"]], detail: "Beheer sites, bindings en application pools.", command: "inetmgr" },
      hyperVManager: { tree: ["Hyper-V Manager", "SRV01", "Virtual Machines", "Checkpoints"], columns: ["Name", "State", "CPU usage", "Assigned memory"], rows: [["LAB-VM-01", "Running", "4%", "4096 MB"], ["CLIENT-TEMPLATE", "Off", "0%", "2048 MB"]], detail: "Beheer virtuele machines zonder hostcommando's uit te voeren.", command: "virtmgmt.msc" },
      printManagement: { tree: ["Print Management", "Print Servers", "SRV01", "Printers", "Drivers"], columns: ["Printer", "Driver", "Port", "Status"], rows: [["Reception-PRN", "Generic PCL", "IP_192.168.1.81", "Ready"], ["Frontoffice MFP", "Universal PCL", "WSD-01", "Ready"]], detail: "Beheer server printers, drivers en queues.", command: "printmanagement.msc" },
      dfsManagement: { tree: ["DFS Management", "Namespaces", "Replication"], columns: ["Name", "Path", "Status"], rows: [["Shares", "\\\\lab.local\\shares", "Online"], ["Profiles", "\\\\lab.local\\profiles", "Online"]], detail: "Beheer DFS namespaces en replication groups.", command: "dfsmgmt.msc" },
      npsManager: { tree: ["NPS (Local)", "RADIUS Clients", "Policies", "Accounting"], columns: ["Policy", "Type", "Enabled"], rows: [["WiFi-Allow-Staff", "Network Policy", "Yes"], ["VPN-Admins", "Connection Request", "Yes"]], detail: "Beheer RADIUS clients en network policies.", command: "nps.msc" },
      windowsServerBackup: { tree: ["Windows Server Backup", "Local Backup", "Backup Schedule", "Recovery"], columns: ["Job", "Target", "Last result"], rows: [["DailySystemState", "D:\\Backup", "Successful"], ["BareMetal-Friday", "\\\\nas\\backup", "Scheduled"]], detail: "Controleer backup jobs en herstelopties.", command: "wbadmin.msc" },
      failoverCluster: { tree: ["Failover Cluster Manager", "Cluster", "Nodes", "Roles", "Storage"], columns: ["Name", "Type", "State"], rows: [["NODE01", "Node", "Up"], ["NODE02", "Node", "Paused"], ["FileServerRole", "Role", "Online"]], detail: "Beheer cluster nodes, roles en storage.", command: "cluadmin.msc" },
      fsrmManager: { tree: ["File Server Resource Manager", "Quota Management", "File Screening", "Storage Reports"], columns: ["Object", "Path", "State"], rows: [["Profiles quota", "D:\\Shares\\Profiles", "Enabled"], ["Block executables", "D:\\Shares", "Audit"]], detail: "Beheer quotas, file screens en storage reports.", command: "fsrm.msc" },
      storageManager: { tree: ["Storage", "Pools", "Volumes", "Disks"], columns: ["Name", "Type", "Health", "Capacity"], rows: [["Pool-A", "Storage pool", "Healthy", "2 TB"], ["Data", "Volume", "Healthy", "800 GB"], ["Disk 1", "Physical disk", "Online", "1 TB"]], detail: "Beheer server storage pools, volumes en disks.", command: "storage" }
    };
    const surface = serverToolContent[normalized];
    return (
      <div className="mmc-app native-server-tool-surface native-mmc-surface" data-native-app={normalized} data-native-parity={normalized}>
        <div className="mmc-menubar" onClick={runPrimaryAppAction}><span>File</span><span>Action</span><span>View</span><span>Help</span></div>
        <div className="server-tool-toolbar"><button type="button" onClick={runPrimaryAppAction}><WinGlyph name="add" />New</button><button type="button" onClick={runPrimaryAppAction}><WinGlyph name="refresh" />Refresh</button><button type="button" onClick={runPrimaryAppAction}><WinGlyph name="settings" />Properties</button><span>{surface.command}</span></div>
        <div className="server-tool-body"><aside className="server-tool-tree"><button className="server-tool-root" type="button" onClick={runPrimaryAppAction}><AppIcon appId={normalized} className="win11-inline-app-icon" />{metaFor(normalized).windowTitle}</button>{surface.tree.map((item, index) => <button className={index === 0 ? "server-tool-tree-item active" : "server-tool-tree-item"} key={item} type="button" onClick={runPrimaryAppAction}>{item}</button>)}</aside><main className="server-tool-main"><header><h3>{metaFor(normalized).windowTitle}</h3><p>{surface.detail}</p></header><table className="server-tool-table"><thead><tr>{surface.columns.map((column) => <th key={column}>{column}</th>)}</tr></thead><tbody>{surface.rows.map((row) => <tr key={row.join("|")} onDoubleClick={runPrimaryAppAction}>{row.map((cell) => <td key={cell}>{cell}</td>)}</tr>)}</tbody></table><section className="server-tool-detail"><h4>Beheeracties</h4><p>Deze acties worden veilig binnen de simulator uitgevoerd en raken geen echte serverbeheeromgeving.</p><ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} /></section></main></div>
        <div className="server-tool-statusbar">Ready | {surface.rows.length} objects | {surface.command}</div>
      </div>
    );
  }

  if (normalized === "cmd" || normalized === "powershell") {
    return <TerminalSurface appId={normalized} onRun={runAction} simulatorState={simulatorState} />;
  }

  if (normalized === "notepad") {
    return <NotepadSurface initialPath={windowState?.initialUrl} onRun={runAction} simulatorState={simulatorState} />;
  }

  if (normalized === "recycleBin") {
    return <RecycleBinSurface openApp={openApp} />;
  }

  return (
    <div className="win11-generic-app">
      <div className="win11-generic-status">
        <AppIcon appId={normalized} className="win11-inline-app-icon" />
        <div>
          <h3>{metaFor(normalized).windowTitle}</h3>
          <p>Native v2 command surface gekoppeld aan de simulator command-bus.</p>
        </div>
      </div>
      <ActionStrip actions={appActions} busyActionId={busyActionId} onRun={runAction} />
    </div>
  );
}

type CloudPortalContent = {
  breadcrumb: string;
  columns: string[];
  defaultView: string;
  detail: string[];
  hero: string;
  nav: Array<{ id: string; label: string; group: "primary" | "favorites" | "entra" | "intune" | "m365" | "security" }>;
  rows: string[][];
  section: string;
};

const cloudPortalContent: Record<string, CloudPortalContent> = {
  entraAdmin: {
    breadcrumb: "Home",
    columns: ["Naam", "Type", "Status", "Laatst gewijzigd"],
    defaultView: "users",
    detail: ["Tenant: campus.local", "Directory sync: Enabled", "Licentiebaseline: M365_E3"],
    hero: "Beheer gebruikers, groepen, apparaten, app-registraties en rollen zoals in het legacy Entra-portaal.",
    nav: [
      { id: "home", label: "Startpagina", group: "primary" },
      { id: "users", label: "Gebruikers", group: "favorites" },
      { id: "groups", label: "Groepen", group: "favorites" },
      { id: "devices", label: "Apparaten", group: "favorites" },
      { id: "app-registrations", label: "App-registraties", group: "favorites" },
      { id: "overview", label: "Overzicht", group: "entra" },
      { id: "enterprise-apps", label: "Bedrijfsapps", group: "entra" },
      { id: "roles-admins", label: "Rollen en beheerders", group: "entra" },
      { id: "delegated-partners", label: "Gedelegeerde beheerderspartners", group: "entra" }
    ],
    rows: [["selina.166@lab.local", "User", "Enabled", "Vandaag"], ["Support-Team", "Security group", "Active", "Gisteren"], ["CLIENT01", "Device", "Compliant", "Vandaag"]],
    section: "Microsoft Entra-beheercentrum"
  },
  entraAudit: {
    breadcrumb: "Entra-id > Controle en status",
    columns: ["Tijd", "Source", "Event ID", "Bericht"],
    defaultView: "audit",
    detail: ["Log type: AuditLogs", "Sign-in diagnostics: Beschikbaar", "Export: Gesimuleerd"],
    hero: "Audit- en sign-inlogboeken voor Entra en Azure-portaalcontroles.",
    nav: [
      { id: "audit", label: "Auditlogboeken", group: "primary" },
      { id: "signin", label: "Aanmeldingslogboeken", group: "primary" },
      { id: "provisioning", label: "Provisioninglogboeken", group: "primary" }
    ],
    rows: [["Vandaag 15:42", "EntraSignIn", "12001", "Conditional Access policy evaluated"], ["Vandaag 15:12", "UserManagement", "4720", "User created"], ["Gisteren 09:31", "AuditLogs", "RoleAssigned", "Helpdesk Administrator assigned"]],
    section: "Entra Audit Logs"
  },
  entraUsers: {
    breadcrumb: "Entra-id > Gebruikers",
    columns: ["User principal name", "Weergavenaam", "Licenties", "Status"],
    defaultView: "users",
    detail: ["Bulk operations", "Password reset", "Account enabled"],
    hero: "Cloudgebruikers, wachtwoordreset en licentiecontrole.",
    nav: [{ id: "users", label: "Alle gebruikers", group: "primary" }, { id: "deleted", label: "Verwijderde gebruikers", group: "primary" }, { id: "settings", label: "Gebruikersinstellingen", group: "primary" }],
    rows: [["new.user@lab.local", "New User", "M365_E3", "Active"], ["smoke.student@firstdesk.test", "Smoke Student", "None", "Active"]],
    section: "Entra Users"
  },
  entraGroups: {
    breadcrumb: "Entra-id > Groepen",
    columns: ["Groep", "Type", "Leden", "Lidmaatschap"],
    defaultView: "groups",
    detail: ["Dynamic membership rules", "Expiration policies", "Owners"],
    hero: "Security- en Microsoft 365-groepen met lidmaatschap.",
    nav: [{ id: "groups", label: "Alle groepen", group: "primary" }, { id: "dynamic", label: "Dynamisch lidmaatschap", group: "primary" }, { id: "expiration", label: "Verloop", group: "primary" }],
    rows: [["Support-Team", "Security", "3", "Assigned"], ["ServiceDesk", "Microsoft 365", "8", "Assigned"]],
    section: "Entra Groups"
  },
  entraDevices: {
    breadcrumb: "Entra-id > Apparaten",
    columns: ["Apparaat", "Join type", "Compliance", "Eigenaar"],
    defaultView: "devices",
    detail: ["Hybrid join", "Device revoke", "Audit logs"],
    hero: "Device-inventory en join-state zoals in Entra.",
    nav: [{ id: "devices", label: "Alle apparaten", group: "primary" }, { id: "settings", label: "Apparaatinstellingen", group: "primary" }, { id: "audit", label: "Audit logs", group: "primary" }],
    rows: [["LAB-CLIENT-01", "Hybrid joined", "Compliant", "Smoke Student"], ["SRV01", "Domain joined", "N/A", "Infrastructure"]],
    section: "Entra Devices"
  },
  entraRoles: {
    breadcrumb: "Entra-id > Rollen en beheerders",
    columns: ["Rol", "Toegewezen aan", "State", "Scope"],
    defaultView: "roles",
    detail: ["Privileged Identity Management", "Eligible assignments", "Directory roles"],
    hero: "Rollen en beheerders met toewijzingen en PIM-achtig overzicht.",
    nav: [{ id: "roles", label: "Rollen", group: "primary" }, { id: "assignments", label: "Toewijzingen", group: "primary" }, { id: "pim", label: "Privileged Identity", group: "primary" }],
    rows: [["Helpdesk Administrator", "new.user@lab.local", "Active", "Tenant"], ["Global Reader", "Support-Team", "Eligible", "Tenant"]],
    section: "Entra Roles"
  },
  conditionalAccess: {
    breadcrumb: "Protection > Conditional Access",
    columns: ["Policy", "Gebruikers", "Grant controls", "State"],
    defaultView: "policies",
    detail: ["Named locations", "What If", "Report-only mode"],
    hero: "Conditional Access policies voor MFA, legacy auth en risicobeleid.",
    nav: [{ id: "policies", label: "Policies", group: "primary" }, { id: "locations", label: "Named locations", group: "primary" }, { id: "insights", label: "Insights", group: "primary" }],
    rows: [["Require MFA Remote", "All users", "Require MFA", "On"], ["Block legacy auth", "All users", "Block access", "Report-only"]],
    section: "Conditional Access"
  },
  authMethods: {
    breadcrumb: "Protection > Authentication methods",
    columns: ["Method", "Target", "State", "Registration"],
    defaultView: "methods",
    detail: ["Registration campaign", "User registration details", "MFA settings"],
    hero: "Authenticatiemethoden en MFA registratiecampagnes.",
    nav: [{ id: "methods", label: "Policies", group: "primary" }, { id: "campaign", label: "Registration campaign", group: "primary" }, { id: "activity", label: "Activity", group: "primary" }],
    rows: [["Microsoft Authenticator", "All users", "Enabled", "Required"], ["FIDO2 security key", "Admins", "Enabled", "Optional"]],
    section: "Authentication Methods"
  },
  enterpriseApps: {
    breadcrumb: "Applications > Enterprise applications",
    columns: ["Application", "Object ID", "Users", "Sign-in enabled"],
    defaultView: "apps",
    detail: ["Consent and permissions", "Single sign-on", "Provisioning"],
    hero: "Bedrijfsapps, consent en app-toewijzingen.",
    nav: [{ id: "apps", label: "All applications", group: "primary" }, { id: "consent", label: "Consent", group: "primary" }, { id: "signin", label: "Sign-in logs", group: "primary" }],
    rows: [["ServiceDesk Portal", "app-1001", "12", "Yes"], ["FirstDesk Sync", "app-1002", "3", "Yes"]],
    section: "Enterprise Apps"
  },
  intuneDevices: {
    breadcrumb: "Intune > Devices",
    columns: ["Device", "Owner", "Compliance", "Management"],
    defaultView: "devices",
    detail: ["Enrollment", "Remote actions", "Device compliance"],
    hero: "Intune devicebeheer voor Windows endpoints.",
    nav: [{ id: "devices", label: "All devices", group: "primary" }, { id: "monitor", label: "Monitor", group: "primary" }, { id: "enrollment", label: "Enrollment", group: "primary" }],
    rows: [["INTUNE-001", "new.user@lab.local", "Compliant", "MDM"], ["CLIENT01", "Smoke Student", "Compliant", "Hybrid"]],
    section: "Intune Devices"
  },
  compliancePolicies: {
    breadcrumb: "Intune > Devices > Compliance",
    columns: ["Policy", "Platform", "Assigned", "Status"],
    defaultView: "policies",
    detail: ["Compliance settings", "Assignments", "Notifications"],
    hero: "Compliancebeleid en toewijzingen.",
    nav: [{ id: "policies", label: "Policies", group: "primary" }, { id: "status", label: "Device compliance", group: "primary" }, { id: "notifications", label: "Notifications", group: "primary" }],
    rows: [["Windows Baseline", "Windows 11", "All users", "Succeeded"], ["Require BitLocker", "Windows", "Pilot group", "Assigned"]],
    section: "Compliance Policies"
  },
  configurationProfiles: {
    breadcrumb: "Intune > Devices > Configuration",
    columns: ["Profile", "Platform", "Type", "Status"],
    defaultView: "profiles",
    detail: ["Settings catalog", "Scripts", "Assignment filters"],
    hero: "Configuratieprofielen, scripts en security baselines.",
    nav: [{ id: "profiles", label: "Profiles", group: "primary" }, { id: "scripts", label: "Scripts", group: "primary" }, { id: "catalog", label: "Settings catalog", group: "primary" }],
    rows: [["WiFi Profile", "Windows 11", "Templates", "Assigned"], ["Security Baseline", "Windows", "Settings catalog", "Succeeded"]],
    section: "Configuration Profiles"
  },
  m365Users: {
    breadcrumb: "Microsoft 365 admin center > Users",
    columns: ["User", "License", "Status", "Role"],
    defaultView: "active-users",
    detail: ["Active users", "Guests", "Contacts"],
    hero: "Microsoft 365 gebruikersbeheer en licentietoewijzing.",
    nav: [{ id: "active-users", label: "Active users", group: "primary" }, { id: "guests", label: "Guest users", group: "primary" }, { id: "contacts", label: "Contacts", group: "primary" }],
    rows: [["new.user@lab.local", "M365_E3", "Active", "User"], ["teacher@lab.local", "M365_A3", "Active", "Teacher"]],
    section: "Microsoft 365 Users"
  },
  m365Licenses: {
    breadcrumb: "Microsoft 365 admin center > Billing",
    columns: ["Product", "Available", "Assigned", "Renewal"],
    defaultView: "products",
    detail: ["Products", "Assignments", "Billing"],
    hero: "Licentievoorraad en toegewezen producten.",
    nav: [{ id: "products", label: "Products", group: "primary" }, { id: "assignments", label: "Assignments", group: "primary" }, { id: "billing", label: "Billing", group: "primary" }],
    rows: [["M365_E3", "10", "2", "Active"], ["M365_A3", "25", "14", "Active"]],
    section: "Microsoft 365 Licenses"
  },
  defenderPortal: {
    breadcrumb: "Microsoft Defender > Incidents & alerts",
    columns: ["Incident", "Severity", "Status", "Assigned to"],
    defaultView: "incidents",
    detail: ["Advanced hunting", "Secure score", "Endpoint alerts"],
    hero: "Defender incidenten, alerts en onderzoek.",
    nav: [{ id: "incidents", label: "Incidents", group: "primary" }, { id: "alerts", label: "Alerts", group: "primary" }, { id: "hunting", label: "Hunting", group: "primary" }, { id: "score", label: "Secure score", group: "primary" }],
    rows: [["INC-2026-001", "Medium", "Active", "SOC"], ["Alert-445", "Low", "Investigating", "Smoke Student"]],
    section: "Microsoft Defender portal"
  }
};

function browserDomainFor(url: string) {
  try {
    return new URL(normalizeBrowserUrl(url)).hostname.toLowerCase();
  } catch {
    return "packettracer.nl";
  }
}

function isEntraPortalDomain(domain: string) {
  return ["entra.microsoft.com", "portal.azure.com", "portal.microsoft.com", "admin.microsoft.com", "intune.microsoft.com", "security.microsoft.com"]
    .some((item) => domain === item || domain.endsWith(`.${item}`));
}

function EdgeBrowserSurface({
  busyActionId,
  initialUrl,
  onRun,
  openApp
}: {
  busyActionId: string | null;
  initialUrl?: string;
  onRun: NativeRunAction;
  openApp: (appId: string, launchUrl?: string) => void;
}) {
  const homeUrl = initialUrl ?? "https://packettracer.nl";
  const [address, setAddress] = useState(homeUrl);
  const [currentUrl, setCurrentUrl] = useState(homeUrl);
  const [history, setHistory] = useState<string[]>([homeUrl]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [loginStep, setLoginStep] = useState<1 | 2>(1);
  const [signedIn, setSignedIn] = useState(false);

  function navigate(nextRaw: string, replace = false) {
    const nextUrl = normalizeBrowserUrl(nextRaw);
    setAddress(nextUrl);
    setCurrentUrl(nextUrl);
    if (replace) {
      setHistory((current) => current.map((item, index) => index === historyIndex ? nextUrl : item));
      return;
    }
    setHistory((current) => {
      const clipped = current.slice(0, historyIndex + 1);
      return [...clipped, nextUrl];
    });
    setHistoryIndex((current) => current + 1);
    setLoginStep(1);
    setSignedIn(false);
  }

  useEffect(() => {
    if (!initialUrl) return;
    const nextUrl = normalizeBrowserUrl(initialUrl);
    setAddress(nextUrl);
    setCurrentUrl(nextUrl);
    setHistory([nextUrl]);
    setHistoryIndex(0);
    setLoginStep(1);
    setSignedIn(false);
  }, [initialUrl]);

  function goHistory(direction: -1 | 1) {
    const nextIndex = historyIndex + direction;
    if (nextIndex < 0 || nextIndex >= history.length) return;
    setHistoryIndex(nextIndex);
    setCurrentUrl(history[nextIndex]);
    setAddress(history[nextIndex]);
  }

  const domain = browserDomainFor(currentUrl);
  const portalApp = domain === "portal.azure.com" || domain.endsWith(".portal.azure.com")
    ? "entraAudit"
    : domain === "intune.microsoft.com" || domain.endsWith(".intune.microsoft.com")
      ? "intuneDevices"
      : domain === "security.microsoft.com" || domain.endsWith(".security.microsoft.com")
        ? "defenderPortal"
        : "entraAdmin";

  return (
    <div className="edge-app native-edge-surface" data-native-app="edge" data-native-parity="edge-browser">
      <div className="edge-toolbar">
        <button className="edge-nav-btn edge-txt-btn" disabled={historyIndex <= 0} type="button" onClick={() => goHistory(-1)}>Back</button>
        <button className="edge-nav-btn edge-txt-btn" disabled={historyIndex >= history.length - 1} type="button" onClick={() => goHistory(1)}>Next</button>
        <button className="edge-nav-btn edge-txt-btn" type="button" onClick={() => navigate(currentUrl, true)}>Refresh</button>
        <button className="edge-nav-btn edge-txt-btn edge-home-btn" type="button" onClick={() => navigate(homeUrl)}>Home</button>
        <input
          className="edge-addr"
          value={address}
          aria-label="Adresbalk"
          onChange={(event) => setAddress(event.target.value)}
          onFocus={(event) => event.currentTarget.select()}
          onKeyDown={(event) => {
            if (event.key === "Enter") navigate(address);
          }}
        />
        <button className="edge-nav-btn edge-txt-btn edge-go-btn" type="button" onClick={() => navigate(address)}>Go</button>
      </div>
      <div className="edge-content">
        {isEntraPortalDomain(domain) ? (
          <div className="edge-entra-site">
            {!signedIn ? (
              <section className="edge-entra-login-page">
                <div className="edge-entra-login-box">
                  <div className="edge-entra-mslogo"><span className="r" /><span className="g" /><span className="b" /><span className="y" /></div>
                  {loginStep === 1 ? (
                    <>
                      <div className="edge-entra-login-title">Aanmelden</div>
                      <div className="edge-entra-login-subtitle">CAMPUS LOCAL</div>
                      <label className="edge-entra-login-label">E-mailadres, telefoonnummer of Skype</label>
                      <input className="edge-entra-login-input" defaultValue="admin@campus.local" type="email" />
                      <a className="edge-entra-login-link" href="#">Geen account? Maak er een aan!</a>
                      <a className="edge-entra-login-link" href="#">Heb je geen toegang tot uw account?</a>
                      <div className="edge-entra-divider" />
                      <div className="edge-entra-login-options">Aanmeldingsopties</div>
                      <div className="edge-entra-login-btn-row"><button className="edge-entra-login-btn" type="button" onClick={() => setLoginStep(2)}>Volgende</button></div>
                    </>
                  ) : (
                    <>
                      <div className="edge-entra-login-title">Voer uw wachtwoord in</div>
                      <button className="edge-entra-login-user-row" type="button" onClick={() => setLoginStep(1)}>
                        <div className="edge-entra-avatar">1</div>
                        <span className="edge-entra-login-user-email">admin@campus.local</span>
                        <span className="edge-entra-login-user-chevron">v</span>
                      </button>
                      <label className="edge-entra-login-label">Wachtwoord</label>
                      <input className="edge-entra-login-input" defaultValue="********" type="password" />
                      <a className="edge-entra-login-link" href="#">Wachtwoord vergeten?</a>
                      <div className="edge-entra-login-btn-row"><button className="edge-entra-login-btn" type="button" onClick={() => setSignedIn(true)}>Aanmelden</button></div>
                    </>
                  )}
                  <div className="edge-entra-login-footer">
                    <a className="edge-entra-login-link" href="#">Gebruiksvoorwaarden</a>
                    <a className="edge-entra-login-link" href="#">Privacy en cookies</a>
                  </div>
                </div>
              </section>
            ) : (
              <CloudAdminSurface actions={actionsForNativeApp(portalApp, [])} appId={portalApp} busyActionId={busyActionId} embedded onRun={onRun} />
            )}
          </div>
        ) : domain === "packettracer.nl" || domain.endsWith(".packettracer.nl") ? (
          <div className="edge-pt-site">
            <div className="edge-pt-topline" />
            <div className="edge-pt-brand-row"><div className="edge-pt-brand">packet<span>tracer</span></div></div>
            <nav className="edge-pt-nav">
              {["Home", "Reviews", "Boek Bestellen", "Docentexemplaar", "Demo aanvragen", "Q&A"].map((item, index) => <a className={index === 0 ? "active" : ""} href="#" key={item}>{item}</a>)}
            </nav>
            <section className="edge-pt-hero"><div className="edge-pt-overlay" /><div className="edge-pt-content"><h1>Registreer je nu gratis!</h1><p>Voor schooljaar 2025-2026 is het werken met de digitale variant van Packettracer.nl gratis. Registreer je nu.</p><button type="button" onClick={() => setCurrentUrl("https://packettracer.nl/registreren")}>registreer nu</button></div></section>
            <div className="edge-pt-foot">Statische simulatiepagina. Adresbalk: {currentUrl} | Domein: {domain}</div>
          </div>
        ) : (
          <div className="edge-sim-page">
            <div className="edge-sim-header">{domain}</div>
            <h2>Deze website is gesimuleerd</h2>
            <p>Live internet is uitgeschakeld in de simulator. Gebruik bekende trainingsdomeinen zoals <strong>entra.microsoft.com</strong>, <strong>portal.azure.com</strong> of <strong>packettracer.nl</strong>.</p>
            <div className="edge-sim-note">Adresbalk: {currentUrl}</div>
            <div className="edge-shortcuts">
              <button type="button" onClick={() => navigate("https://packettracer.nl")}>packettracer.nl</button>
              <button type="button" onClick={() => navigate("https://entra.microsoft.com")}>entra.microsoft.com</button>
              <button type="button" onClick={() => navigate("https://portal.azure.com")}>portal.azure.com</button>
              <button type="button" onClick={() => openApp("entraAdmin")}>Open Entra-app</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CloudAdminSurface({
  actions,
  appId,
  busyActionId,
  embedded = false,
  onRun
}: {
  actions: NativeSimulatorAction[];
  appId: string;
  busyActionId: string | null;
  embedded?: boolean;
  onRun: NativeRunAction;
}) {
  const content = cloudPortalContent[appId] ?? cloudPortalContent.entraAdmin;
  const [activeView, setActiveView] = useState(content.defaultView);
  const [favoritesOpen, setFavoritesOpen] = useState(true);
  const [entraOpen, setEntraOpen] = useState(true);
  const [selectedRow, setSelectedRow] = useState(0);

  const orderedActions = actions
    .map((action) => selectNativeAction(appId, actions, [action.commandType], action.capability))
    .filter((action): action is NativeSimulatorAction => Boolean(action));
  const primaryAction = orderedActions[0];
  const secondaryAction = orderedActions[1] ?? primaryAction;
  const tertiaryAction = orderedActions[2] ?? secondaryAction;
  const runPortalAction = (action: NativeSimulatorAction | undefined) => {
    if (action) void onRun(action);
  };
  const grouped = {
    primary: content.nav.filter((item) => item.group === "primary"),
    favorites: content.nav.filter((item) => item.group === "favorites"),
    entra: content.nav.filter((item) => item.group === "entra" || item.group === "intune" || item.group === "m365" || item.group === "security")
  };

  function renderNavItem(item: CloudPortalContent["nav"][number], sub = false) {
    return (
      <button
        className={`${sub ? "entra-nav-subitem" : "entra-nav-item"} ${activeView === item.id ? "active" : ""}`}
        key={`${item.group}-${item.id}`}
        type="button"
        onClick={() => setActiveView(item.id)}
      >
        {!sub ? <span className="entra-nav-icon">{item.group === "primary" ? "⌂" : "◆"}</span> : null}
        <span>{item.label}</span>
      </button>
    );
  }

  return (
    <div className={`entra-shell native-cloud-portal-surface ${embedded ? "is-embedded-in-edge" : ""}`} data-native-app={appId} data-native-parity={appId}>
      <header className="entra-topbar">
        <div className="entra-topbar-waffle" aria-hidden="true"><div className="entra-waffle-grid">{Array.from({ length: 9 }).map((_, index) => <span className="entra-waffle-dot" key={index} />)}</div></div>
        <strong className="entra-topbar-brand">{appId === "defenderPortal" ? "Microsoft Defender" : appId.startsWith("m365") ? "Microsoft 365 admin center" : "Microsoft Entra admin center"}</strong>
        <button className="entra-topbar-search" type="button" onClick={() => setActiveView("search")}><WinGlyph name="search" /><span>Resources, services en documenten zoeken</span></button>
        <button className="entra-copilot-btn" type="button" onClick={() => runPortalAction(primaryAction)}><span className="entra-copilot-dot" />Copilot</button>
        <button className="entra-topbar-icon" type="button" title="Meldingen" onClick={() => setActiveView("notifications")}><WinGlyph name="notifications" /></button>
        <button className="entra-topbar-icon" type="button" title="Instellingen" onClick={() => setActiveView("settings")}><WinGlyph name="settings" /></button>
        <div className="entra-topbar-user"><div className="entra-topbar-user-info"><div className="entra-topbar-user-email">admin@campus.local</div><div className="entra-topbar-user-org">campus.local</div></div><div className="entra-topbar-avatar">A</div></div>
      </header>
      <div className="entra-body">
        <aside className="entra-sidebar">
          <div className="entra-nav">
            {grouped.primary.map((item) => renderNavItem(item))}
            {grouped.favorites.length ? (
              <>
                <button className="entra-nav-item" type="button" onClick={() => setFavoritesOpen((current) => !current)}><span className="entra-nav-icon">★</span><span>Favorieten</span><span className={`entra-nav-chevron ${favoritesOpen ? "open" : ""}`}>⌄</span></button>
                <div className={favoritesOpen ? "" : "entra-section-hidden"}>{grouped.favorites.map((item) => renderNavItem(item, true))}</div>
              </>
            ) : null}
            {grouped.entra.length ? (
              <>
                <button className="entra-nav-item" type="button" onClick={() => setEntraOpen((current) => !current)}><span className="entra-nav-icon">◆</span><span>{appId.startsWith("intune") || appId.includes("Policies") || appId.includes("Profiles") ? "Intune" : appId.startsWith("m365") ? "Microsoft 365" : appId === "defenderPortal" ? "Defender" : "Entra-id"}</span><span className={`entra-nav-chevron ${entraOpen ? "open" : ""}`}>⌄</span></button>
                <div className={entraOpen ? "" : "entra-section-hidden"}>{grouped.entra.map((item) => renderNavItem(item, true))}</div>
              </>
            ) : null}
          </div>
          <div className="entra-sidebar-collapse">«</div>
        </aside>
        <main className="entra-main">
          <div className="entra-breadcrumb">{content.breadcrumb}</div>
          <div className="entra-content">
            <section className="entra-card entra-work-card">
              <div className="entra-card-head"><div><h3>{content.section}</h3><span className="entra-subtle">{content.hero}</span></div><div className="entra-actions"><button className="entra-btn" type="button" onClick={() => runPortalAction(primaryAction)}>Nieuw</button><button className="entra-btn secondary" type="button" onClick={() => runPortalAction(secondaryAction)}>Vernieuwen</button><button className="entra-btn secondary" type="button" onClick={() => runPortalAction(tertiaryAction)}>Exporteren</button></div></div>
              <div className="entra-card-body">
                <div className="entra-kpis"><div className="entra-kpi"><strong>{content.rows.length}</strong><span>Items</span></div><div className="entra-kpi"><strong>Ready</strong><span>Status</span></div><div className="entra-kpi"><strong>v2</strong><span>Command-bus</span></div></div>
                <table className="entra-table">
                  <thead><tr>{content.columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
                  <tbody>{content.rows.map((row, rowIndex) => {
                    const rowAction = actions[rowIndex % Math.max(actions.length, 1)] ?? primaryAction;
                    return <tr className={rowIndex === selectedRow ? "selected" : ""} key={row.join("|")} onClick={() => setSelectedRow(rowIndex)} onDoubleClick={() => runPortalAction(rowAction)}>{row.map((cell) => <td key={cell}>{cell}</td>)}</tr>;
                  })}</tbody>
                </table>
              </div>
            </section>
            <aside className="entra-card entra-detail-pane">
              <div className="entra-card-head"><h3>Details</h3><span className="entra-subtle">{activeView}</span></div>
              <div className="entra-card-body">
                <div className="entra-selected-object"><strong>{content.rows[selectedRow]?.[0] ?? content.section}</strong><span>{content.rows[selectedRow]?.[1] ?? "Object"}</span></div>
                <ul className="entra-detail-list">{content.detail.map((item) => <li key={item}>{item}</li>)}</ul>
                <section className="entra-depth-panel" data-native-parity={appId === "entraAudit" ? "entra-audit-log-depth" : "entra-admin-directory-depth"}>
                  <div className="mmc-tabstrip">
                    <button className="active" type="button" onClick={() => setActiveView(content.defaultView)}>Overzicht</button>
                    <button type="button" onClick={() => setActiveView(appId === "entraAudit" ? "signin" : "properties")}>{appId === "entraAudit" ? "Aanmeldingen" : "Eigenschappen"}</button>
                    <button type="button" onClick={() => setActiveView(appId === "entraAudit" ? "provisioning" : "assignments")}>{appId === "entraAudit" ? "Provisioning" : "Toewijzingen"}</button>
                    <button type="button" onClick={() => runPortalAction(primaryAction)}>{appId === "entraAudit" ? "Exporteren" : "Licenties"}</button>
                  </div>
                  <dl>
                    <div><dt>{appId === "entraAudit" ? "Correlatie-id" : "Object-id"}</dt><dd>{appId === "entraAudit" ? "corr-2026-entra-170" : "obj-tenant-170"}</dd></div>
                    <div><dt>{appId === "entraAudit" ? "Filter" : "Beheereenheid"}</dt><dd>{appId === "entraAudit" ? "Activity starts with User" : "ServiceDesk Lab"}</dd></div>
                  </dl>
                </section>
                <div className="entra-command-strip"><ActionStrip actions={actions} busyActionId={busyActionId} onRun={onRun} /></div>
              </div>
            </aside>
          </div>
        </main>
      </div>
    </div>
  );
}

function ExplorerSurface({
  onRun,
  openApp,
  simulatorState
}: {
  onRun: NativeRunAction;
  openApp: (appId: string, launchUrl?: string) => void;
  simulatorState: NativeSimulatorState | null;
}) {
  const [currentPath, setCurrentPath] = useState("C:\\Users\\Student\\Desktop");
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"folder" | "file" | "mapDrive" | "rename" | "properties" | null>(null);
  const [activeMenu, setActiveMenu] = useState<"file" | "edit" | "view" | "tools" | null>(null);
  const [fileContextMenu, setFileContextMenu] = useState<{ x: number; y: number; path: string } | null>(null);
  const [entryName, setEntryName] = useState("");
  const [driveLetter, setDriveLetter] = useState("Z");
  const [drivePath, setDrivePath] = useState("\\\\SRV01\\Shares");
  const [message, setMessage] = useState("Gereed");
  const [viewMode, setViewMode] = useState<"details" | "large">("details");
  const [showExtensions, setShowExtensions] = useState(true);
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  const children = childrenForPath(simulatorState, currentPath).filter(([, node]) => showHiddenItems || !node.hidden);
  const networkDrives = networkDrivesFor(simulatorState);
  const selectedEntry = selectedPath ? findNodeByPath(simulatorState, selectedPath) : undefined;
  const selectedNode = selectedEntry?.[1];
  const selectedIsFolder = selectedNode?.type === "folder";
  const contextMenuPosition = fileContextMenu ? fitMenuPosition(fileContextMenu.x, fileContextMenu.y) : null;

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      const target = event.target as HTMLElement;
      if (target.closest(".ex-menu-wrapper") || target.closest(".ex-file-context-menu") || target.closest(".ex-inline-dialog")) return;
      setActiveMenu(null);
      setFileContextMenu(null);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setActiveMenu(null);
      setFileContextMenu(null);
      if (dialog) setDialog(null);
    }

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [dialog]);

  function chooseQuickPath(path: string) {
    setCurrentPath(path);
    setSelectedPath(null);
    setMessage(path);
    setActiveMenu(null);
    setFileContextMenu(null);
  }

  async function createEntry(kind: "folder" | "file") {
    const cleanName = entryName.trim();
    if (!cleanName) {
      setMessage("Geef een naam op.");
      return;
    }
    const targetPath = normalizeNativePath(`${currentPath}\\${cleanName}`);
    const commandType = kind === "folder" ? "FILE_CREATE_FOLDER" : "FILE_CREATE";
    const result = await onRun({
      id: `explorer-${commandType.toLowerCase()}-${Date.now()}`,
      appId: "explorer",
      appName: "File Explorer",
      commandType,
      capability: kind === "folder" ? "file.folder.write" : "file.file.write",
      label: kind === "folder" ? `Nieuwe map ${cleanName}` : `Nieuw bestand ${cleanName}`,
      description: "Maak het item aan via de simulator filesystem-state.",
      machine: "both",
      payload: {
        path: targetPath,
        parentPath: currentPath,
        name: cleanName,
        ...(kind === "file" ? { content: "" } : {})
      }
    });
    if (result.ok) {
      setEntryName("");
      setDialog(null);
      setSelectedPath(targetPath);
      setMessage(`${cleanName} aangemaakt`);
    } else {
      setMessage(`${cleanName} kon niet worden aangemaakt (${result.status}).`);
    }
  }

  async function deleteSelected() {
    if (!selectedPath || !selectedNode) {
      setMessage("Selecteer eerst een bestand of map.");
      return;
    }
    const commandType = selectedNode.type === "folder" ? "FILE_DELETE_FOLDER" : "FILE_DELETE";
    const result = await onRun({
      id: `explorer-${commandType.toLowerCase()}-${Date.now()}`,
      appId: "explorer",
      appName: "File Explorer",
      commandType,
      capability: selectedNode.type === "folder" ? "file.folder.write" : "file.file.write",
      label: `${selectedNode.name} verwijderen`,
      description: "Verwijder het geselecteerde item uit de simulator filesystem-state.",
      machine: "both",
      payload: { path: selectedPath }
    });
    if (result.ok) {
      setSelectedPath(null);
      setMessage(`${selectedNode.name} verwijderd`);
    } else {
      setMessage(`${selectedNode.name} kon niet worden verwijderd (${result.status}).`);
    }
  }

  async function shareSelected() {
    const targetPath = selectedNode?.type === "file" && selectedPath ? parentNativePath(selectedPath) : selectedIsFolder && selectedPath ? selectedPath : currentPath;
    const shareName = baseNameForPath(targetPath).replace(/\W+/g, "") || "Share";
    const result = await onRun({
      id: `explorer-share-${Date.now()}`,
      appId: "explorer",
      appName: "File Explorer",
      commandType: "SHARE_CREATE",
      capability: "file.share.write",
      label: `${shareName} delen`,
      description: "Deel de geselecteerde map via de command-bus.",
      machine: "both",
      payload: {
        path: targetPath,
        shareName,
        permissions: [{ principal: "Domain Users", permission: "read" }]
      }
    });
    setMessage(result.ok ? `${shareName} gedeeld` : `Delen mislukt (${result.status}). Selecteer een map of deel de huidige map.`);
  }

  async function setPermissionSelected() {
    const targetPath = selectedPath ?? currentPath;
    const result = await onRun({
      id: `explorer-acl-${Date.now()}`,
      appId: "explorer",
      appName: "File Explorer",
      commandType: "FILE_SET_PERMISSION",
      capability: "file.permission.write",
      label: "NTFS-permissie toepassen",
      description: "Zet NTFS-permissie op het geselecteerde item.",
      machine: "both",
      payload: {
        path: targetPath,
        principal: "IntakeTeam",
        permission: "modify",
        type: "allow"
      }
    });
    setMessage(result.ok ? "Permissie toegepast" : `Permissie mislukt (${result.status}).`);
  }

  async function mapNetworkDrive() {
    const cleanLetter = normalizeNativeDriveLetter(driveLetter);
    const cleanPath = normalizeNativeNetworkTargetPath(drivePath);
    if (!cleanLetter) {
      setMessage("Gebruik een stationsletter A t/m Z.");
      return;
    }
    if (!cleanPath) {
      setMessage("Geef een netwerkshare of bestaande map op.");
      return;
    }

    const result = await onRun({
      id: `explorer-network-drive-map-${Date.now()}`,
      appId: "explorer",
      appName: "File Explorer",
      commandType: "NETWORK_DRIVE_MAP",
      capability: "network.drive.write",
      label: `Netwerkstation ${cleanLetter}: koppelen`,
      description: "Koppel een netwerkstation in de simulatorstate, zoals de legacy Verkenner.",
      machine: "both",
      payload: {
        letter: `${cleanLetter}:`,
        path: cleanPath,
        persistent: true
      }
    });

    if (result.ok) {
      setDialog(null);
      setCurrentPath(cleanPath);
      setSelectedPath(null);
      setMessage(`Netwerkstation ${cleanLetter}: gekoppeld aan ${cleanPath}.`);
    } else {
      setMessage(`Netwerkstation koppelen mislukt (${result.status}).`);
    }
  }

  async function unmapNetworkDrive(letter: string) {
    const cleanLetter = normalizeNativeDriveLetter(letter);
    if (!cleanLetter) {
      setMessage("Selecteer een geldig netwerkstation.");
      return;
    }

    const result = await onRun({
      id: `explorer-network-drive-unmap-${Date.now()}`,
      appId: "explorer",
      appName: "File Explorer",
      commandType: "NETWORK_DRIVE_UNMAP",
      capability: "network.drive.write",
      label: `Netwerkstation ${cleanLetter}: ontkoppelen`,
      description: "Ontkoppel een netwerkstation in de simulatorstate.",
      machine: "both",
      payload: {
        letter: `${cleanLetter}:`
      }
    });

    if (result.ok) {
      setCurrentPath("C:\\");
      setSelectedPath(null);
      setMessage(`Netwerkstation ${cleanLetter}: ontkoppeld.`);
    } else {
      setMessage(`Netwerkstation ontkoppelen mislukt (${result.status}).`);
    }
  }

  async function renameSelected() {
    if (!selectedPath || !selectedNode) {
      setMessage("Selecteer eerst een bestand of map.");
      return;
    }
    const cleanName = entryName.trim();
    if (!cleanName) {
      setMessage("Geef een nieuwe naam op.");
      return;
    }
    const destinationPath = `${parentNativePath(selectedPath)}\\${cleanName}`;
    const createType = selectedNode.type === "folder" ? "FILE_CREATE_FOLDER" : "FILE_CREATE";
    const createResult = await onRun({
      id: `explorer-rename-create-${Date.now()}`,
      appId: "explorer",
      appName: "File Explorer",
      commandType: createType,
      capability: selectedNode.type === "folder" ? "file.folder.write" : "file.file.write",
      label: `${selectedNode.name} hernoemen naar ${cleanName}`,
      description: "Simuleer hernoemen via create/delete omdat de v2 command-bus geen losse FILE_RENAME heeft.",
      machine: "both",
      payload: {
        path: destinationPath,
        parentPath: parentNativePath(selectedPath),
        name: cleanName,
        ...(selectedNode.type === "file" ? { content: selectedNode.content ?? "" } : {})
      }
    });
    if (!createResult.ok) {
      setMessage(`Hernoemen mislukt (${createResult.status}).`);
      return;
    }
    const deleteType = selectedNode.type === "folder" ? "FILE_DELETE_FOLDER" : "FILE_DELETE";
    const deleteResult = await onRun({
      id: `explorer-rename-delete-${Date.now()}`,
      appId: "explorer",
      appName: "File Explorer",
      commandType: deleteType,
      capability: selectedNode.type === "folder" ? "file.folder.write" : "file.file.write",
      label: `${selectedNode.name} vervangen door ${cleanName}`,
      description: "Rond de gesimuleerde hernoemactie af.",
      machine: "both",
      payload: { path: selectedPath }
    });
    if (deleteResult.ok) {
      setSelectedPath(destinationPath);
      setDialog(null);
      setEntryName("");
      setMessage(`${selectedNode.name} hernoemd naar ${cleanName}.`);
    } else {
      setMessage(`Hernoemen deels gelukt; oude item verwijderen mislukte (${deleteResult.status}).`);
    }
  }

  async function copySelectedToCurrentFolder() {
    if (!selectedPath || !selectedNode || selectedNode.type !== "file") {
      setMessage("Selecteer een bestand om te kopieren.");
      return;
    }
    const copyName = selectedNode.name.replace(/(\.[^.]+)?$/, (extension) => ` - Kopie${extension || ""}`);
    const destinationPath = `${currentPath.replace(/\\$/, "")}\\${copyName}`;
    const result = await onRun({
      id: `explorer-copy-${Date.now()}`,
      appId: "explorer",
      appName: "File Explorer",
      commandType: "FILE_CREATE",
      capability: "file.file.write",
      label: `${selectedNode.name} kopieren`,
      description: "Maak een kopie in de huidige map via simulatorstate.",
      machine: "both",
      payload: {
        path: destinationPath,
        parentPath: currentPath,
        name: copyName,
        content: selectedNode.content ?? ""
      }
    });
    setMessage(result.ok ? `${copyName} gekopieerd.` : `Kopieren mislukt (${result.status}).`);
  }

  function openSelected() {
    if (!selectedPath || !selectedNode) return;
    if (selectedNode.type === "folder") {
      setCurrentPath(selectedPath);
      setSelectedPath(null);
      return;
    }
    if (selectedNode.name.toLowerCase().endsWith(".txt")) {
      openApp("notepad", selectedPath);
      setMessage(`${selectedNode.name} geopend in Kladblok`);
      return;
    }
    setMessage(`${selectedNode.name} geselecteerd`);
  }

  function displayNameFor(node: NativeFsNode) {
    if (showExtensions || node.type === "folder") return node.name;
    return node.name.replace(/\.[^.\\]+$/, "");
  }

  return (
    <div className="explorer-app" data-native-app="explorer" data-native-parity="explorer">
      <div className="ex-toolbar">
        <div className="ex-nav-btns" aria-label="Navigatie">
          <button type="button" aria-label="Vorige" onClick={() => setMessage("Vorige locatie is nog niet beschikbaar")}><MmcGlyph kind="back" /></button>
          <button type="button" aria-label="Volgende" onClick={() => setMessage("Volgende locatie is nog niet beschikbaar")}><MmcGlyph kind="forward" /></button>
          <button type="button" aria-label="Omhoog" onClick={() => chooseQuickPath(parentNativePath(currentPath))}><MmcGlyph kind="up" /></button>
        </div>
        <div className="ex-address-bar">
          <MmcGlyph kind="folder" />
          <span className="ex-breadcrumb">{currentPath}</span>
        </div>
        <label className="ex-search-wrap">
          <MmcGlyph kind="search" />
          <input className="ex-search" aria-label={`Zoeken in ${baseNameForPath(currentPath)}`} placeholder={`Zoeken in ${baseNameForPath(currentPath)}`} />
        </label>
      </div>
      <div className="ex-menubar">
        <div className="ex-menu-wrapper">
          <button className="ex-menu-root" type="button" onClick={() => setActiveMenu(activeMenu === "file" ? null : "file")}>Bestand</button>
          {activeMenu === "file" ? (
            <div className="ex-menu-dropdown" role="menu" aria-label="Bestand">
              <button type="button" role="menuitem" onClick={() => { setDialog("file"); setEntryName("Nieuw tekstdocument.txt"); setActiveMenu(null); }}>Nieuw bestand</button>
              <button type="button" role="menuitem" onClick={() => { setDialog("folder"); setEntryName("Nieuwe map"); setActiveMenu(null); }}>Nieuwe map</button>
              <button type="button" role="menuitem" onClick={() => { setDialog("mapDrive"); setActiveMenu(null); }}>Netwerkstation koppelen</button>
              <hr />
              <button type="button" role="menuitem" disabled={!selectedNode} onClick={() => { setDialog("properties"); setActiveMenu(null); }}>Eigenschappen</button>
              <button type="button" role="menuitem" disabled={!selectedNode} onClick={() => { setActiveMenu(null); void deleteSelected(); }}>Verwijderen</button>
            </div>
          ) : null}
        </div>
        <div className="ex-menu-wrapper">
          <button className="ex-menu-root" type="button" onClick={() => setActiveMenu(activeMenu === "edit" ? null : "edit")}>Bewerken</button>
          {activeMenu === "edit" ? (
            <div className="ex-menu-dropdown" role="menu" aria-label="Bewerken">
              <button type="button" role="menuitem" disabled={!selectedNode || selectedNode.type !== "file"} onClick={() => { setActiveMenu(null); void copySelectedToCurrentFolder(); }}>Kopieren</button>
              <button type="button" role="menuitem" disabled={!selectedNode} onClick={() => { setEntryName(selectedNode?.name ?? ""); setDialog("rename"); setActiveMenu(null); }}>Hernoemen</button>
              <button type="button" role="menuitem" disabled={!selectedNode} onClick={() => { setActiveMenu(null); void deleteSelected(); }}>Verwijderen</button>
            </div>
          ) : null}
        </div>
        <div className="ex-menu-wrapper">
          <button className="ex-menu-root" type="button" onClick={() => setActiveMenu(activeMenu === "view" ? null : "view")}>Beeld</button>
          {activeMenu === "view" ? (
            <div className="ex-menu-dropdown" role="menu" aria-label="Beeld">
              <button type="button" role="menuitem" onClick={() => { setViewMode("details"); setActiveMenu(null); setMessage("Details-weergave actief."); }}>Details</button>
              <button type="button" role="menuitem" onClick={() => { setViewMode("large"); setActiveMenu(null); setMessage("Grote pictogrammen actief."); }}>Grote pictogrammen</button>
              <button type="button" role="menuitem" onClick={() => { setActiveMenu(null); setMessage("Navigatiedeelvenster zichtbaar."); }}>Navigatiedeelvenster</button>
              <hr />
              <button type="button" role="menuitemcheckbox" aria-checked={showExtensions} onClick={() => { const next = !showExtensions; setShowExtensions(next); setActiveMenu(null); setMessage(next ? "Bestandsnaamextensies zichtbaar." : "Bestandsnaamextensies verborgen."); }}>{showExtensions ? "✓ " : ""}Bestandsnaamextensies</button>
              <button type="button" role="menuitemcheckbox" aria-checked={showHiddenItems} onClick={() => { const next = !showHiddenItems; setShowHiddenItems(next); setActiveMenu(null); setMessage(next ? "Verborgen items zichtbaar." : "Verborgen items verborgen."); }}>{showHiddenItems ? "✓ " : ""}Verborgen items</button>
            </div>
          ) : null}
        </div>
        <div className="ex-menu-wrapper">
          <button className="ex-menu-root" type="button" onClick={() => setActiveMenu(activeMenu === "tools" ? null : "tools")}>Extra</button>
          {activeMenu === "tools" ? (
            <div className="ex-menu-dropdown" role="menu" aria-label="Extra">
              <button type="button" role="menuitem" onClick={() => { setDialog("mapDrive"); setActiveMenu(null); }}>Netwerkstation koppelen</button>
              <button type="button" role="menuitem" disabled={networkDrives.length === 0} onClick={() => { const letter = normalizeNativeDriveLetter(String(networkDrives[0]?.letter ?? "")); setActiveMenu(null); if (letter) void unmapNetworkDrive(letter); }}>Netwerkstation ontkoppelen</button>
            </div>
          ) : null}
        </div>
        <button className="ex-menu-root" type="button" onClick={() => setMessage("FirstDesk Verkenner gebruikt simulatorstate.")}>Help</button>
      </div>
      <div className="ex-actionbar">
        <button className="ex-action-btn ex-action-btn-new" type="button" onClick={() => { setDialog("folder"); setEntryName("Nieuwe map"); }}>
          <MmcGlyph kind="folder" />
          <span>Nieuwe map</span>
        </button>
        <button className="ex-action-btn" type="button" onClick={() => { setDialog("file"); setEntryName("Nieuw tekstdocument.txt"); }}>
          <MmcGlyph kind="record" />
          <span>Nieuw bestand</span>
        </button>
        <span className="ex-cmd-sep" />
        <button className="ex-action-btn" type="button" onClick={openSelected}><MmcGlyph kind="open" /><span>Openen</span></button>
        <button className="ex-action-btn" type="button" onClick={shareSelected}><MmcGlyph kind="share" /><span>Delen</span></button>
        <button className="ex-action-btn" type="button" onClick={deleteSelected}><MmcGlyph kind="clear" /><span>Verwijderen</span></button>
        <span className="ex-cmd-sep" />
        <button className="ex-action-btn" type="button" onClick={setPermissionSelected}><MmcGlyph kind="shield" /><span>Beveiliging</span></button>
        <button className="ex-action-btn" type="button" onClick={() => setDialog("mapDrive")}><MmcGlyph kind="network" /><span>Netwerkstation</span></button>
        <span className="ex-cmd-sep" />
        <button className="ex-action-btn" type="button" onClick={() => setViewMode(viewMode === "details" ? "large" : "details")}><MmcGlyph kind="list" /><span>{viewMode === "details" ? "Grote pictogrammen" : "Details"}</span></button>
      </div>
      <div className="ex-body">
        <aside className="ex-nav">
          <button className={currentPath === "C:\\Users\\Student\\Desktop" ? "is-active" : ""} type="button" onClick={() => chooseQuickPath("C:\\Users\\Student\\Desktop")}><MmcGlyph kind="computer" />Bureaublad</button>
          <button className={currentPath === "C:\\Users\\Student\\Documents" ? "is-active" : ""} type="button" onClick={() => chooseQuickPath("C:\\Users\\Student\\Documents")}><MmcGlyph kind="properties" />Documenten</button>
          <button className={currentPath === "C:\\Users\\Student\\Downloads" ? "is-active" : ""} type="button" onClick={() => chooseQuickPath("C:\\Users\\Student\\Downloads")}><MmcGlyph kind="download" />Downloads</button>
          <button className={currentPath === "C:\\" ? "is-active" : ""} type="button" onClick={() => chooseQuickPath("C:\\")}><MmcGlyph kind="drive" />Lokale schijf (C:)</button>
          <button type="button" onClick={() => setDialog("mapDrive")}><MmcGlyph kind="network" />Netwerk</button>
          {networkDrives.length > 0 ? (
            <div className="ex-nav-section">
              <strong>Netwerkstations</strong>
              {networkDrives.map((drive) => {
                const letter = normalizeNativeDriveLetter(String(drive.letter ?? ""));
                const path = String(drive.path ?? "");
                return (
                  <button className={currentPath === path ? "is-active" : ""} key={`${letter}-${path}`} type="button" onClick={() => chooseQuickPath(path)}>
                    <MmcGlyph kind="network" />{letter}: {path}
                  </button>
                );
              })}
            </div>
          ) : null}
        </aside>
        <main className={`ex-main ${viewMode === "large" ? "ex-main--large" : ""}`}>
          <div className="ex-file-hdr">
            <span>Naam</span>
            <span>Gewijzigd op</span>
            <span>Type</span>
            <span>Grootte</span>
          </div>
          <div className={`ex-file-list ex-file-list--${viewMode}`} role="list" onDoubleClick={openSelected}>
            {children.length === 0 ? (
              <div className="ex-file-row ex-file-row-empty" role="listitem">
                <span>Deze map is leeg.</span>
              </div>
            ) : children.map(([nodeId, node]) => {
              const path = nodePath(fsNodesFor(simulatorState), nodeId);
              return (
                <button
                  className={`ex-file-row ${selectedPath === path ? "is-selected" : ""}`}
                  key={nodeId}
                  type="button"
                  onClick={() => setSelectedPath(path)}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setSelectedPath(path);
                    setFileContextMenu({ x: event.clientX, y: event.clientY, path });
                  }}
                  onDoubleClick={() => {
                    setSelectedPath(path);
                    if (node.type === "folder") setCurrentPath(path);
                    else if (node.name.toLowerCase().endsWith(".txt")) openApp("notepad", path);
                  }}
                >
                  <span className="ex-fn"><MmcGlyph kind={node.type === "folder" ? "folder" : "record"} className="ex-fi" /><span className="ex-fname">{displayNameFor(node)}</span></span>
                  <span>{node.modifiedAt ? new Date(node.modifiedAt).toLocaleString("nl-NL") : "Vandaag"}</span>
                  <span>{formatFileType(node)}</span>
                  <span>{node.type === "file" ? `${Math.max(1, Math.ceil((node.content?.length ?? 0) / 1024))} KB` : ""}</span>
                </button>
              );
            })}
          </div>
        </main>
        <aside className="ex-detail">
          <section className="ex-detail-card" data-native-parity="explorer-preview-pane">
            <MmcGlyph kind={selectedNode?.type === "file" ? "record" : "folder"} />
            <strong>{selectedNode?.name ?? baseNameForPath(currentPath)}</strong>
            <small>{selectedNode ? formatFileType(selectedNode) : currentPath}</small>
          </section>
          <section className="ex-detail-card ex-security-summary" data-native-parity="explorer-security-pane">
            <strong>Beveiliging</strong>
            <div><span>Eigenaar</span><b>ADVANCED-TI\Student</b></div>
            <div><span>Rechten</span><b>Modify</b></div>
            <div><span>Delen</span><b>{selectedIsFolder ? "Gereed voor share" : "Niet gedeeld"}</b></div>
            <button type="button" onClick={setPermissionSelected}>NTFS-permissie toepassen</button>
          </section>
          <section className="ex-detail-card ex-network-summary" data-native-parity="explorer-network-drives">
            <strong>Netwerkstations</strong>
            {networkDrives.length > 0 ? networkDrives.map((drive) => <small key={`${drive.letter}-${drive.path}`}>{String(drive.letter ?? "Z:")} {String(drive.path ?? "\\\\SRV01\\Shares")}</small>) : <small>Geen netwerkstation gekoppeld</small>}
            <button type="button" onClick={() => setDialog("mapDrive")}>Koppelen...</button>
          </section>
          <section className="ex-detail-card">
            <strong>Scenario acties</strong>
            <p>{message}</p>
          </section>
        </aside>
      </div>
      {fileContextMenu && contextMenuPosition ? (
        <div
          className="win11-context-menu ex-file-context-menu"
          role="menu"
          style={contextMenuPosition}
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" role="menuitem" onClick={() => { setFileContextMenu(null); openSelected(); }}>Openen</button>
          <button type="button" role="menuitem" onClick={() => { setFileContextMenu(null); void copySelectedToCurrentFolder(); }}>Kopieren</button>
          <button type="button" role="menuitem" onClick={() => { setEntryName(selectedNode?.name ?? ""); setDialog("rename"); setFileContextMenu(null); }}>Hernoemen</button>
          <button type="button" role="menuitem" onClick={() => { setFileContextMenu(null); void deleteSelected(); }}>Verwijderen</button>
          <hr />
          <button type="button" role="menuitem" onClick={() => { setFileContextMenu(null); void shareSelected(); }}>Delen</button>
          <button type="button" role="menuitem" onClick={() => { setFileContextMenu(null); void setPermissionSelected(); }}>Beveiliging</button>
          <button type="button" role="menuitem" onClick={() => { setDialog("properties"); setFileContextMenu(null); }}>Eigenschappen</button>
        </div>
      ) : null}
      {dialog === "folder" || dialog === "file" ? (
        <section className="ex-inline-dialog" role="dialog" aria-label={dialog === "folder" ? "Nieuwe map" : "Nieuw bestand"}>
          <header>
            <strong>{dialog === "folder" ? "Nieuwe map" : "Nieuw bestand"}</strong>
            <button type="button" aria-label="Sluiten" onClick={() => setDialog(null)}>x</button>
          </header>
          <label>
            Naam
            <input autoFocus value={entryName} onChange={(event) => setEntryName(event.target.value)} onKeyDown={(event) => {
              if (event.key === "Enter") void createEntry(dialog === "folder" ? "folder" : "file");
              if (event.key === "Escape") setDialog(null);
            }} />
          </label>
          <footer>
            <button type="button" onClick={() => setDialog(null)}>Annuleren</button>
            <button className="is-primary" type="button" onClick={() => void createEntry(dialog === "folder" ? "folder" : "file")}>Maken</button>
          </footer>
        </section>
      ) : null}
      {dialog === "rename" ? (
        <section className="ex-inline-dialog" role="dialog" aria-label="Hernoemen">
          <header>
            <strong>Hernoemen</strong>
            <button type="button" aria-label="Sluiten" onClick={() => setDialog(null)}>x</button>
          </header>
          <label>
            Nieuwe naam
            <input autoFocus value={entryName} onChange={(event) => setEntryName(event.target.value)} onKeyDown={(event) => {
              if (event.key === "Enter") void renameSelected();
              if (event.key === "Escape") setDialog(null);
            }} />
          </label>
          <footer>
            <button type="button" onClick={() => setDialog(null)}>Annuleren</button>
            <button className="is-primary" type="button" onClick={() => void renameSelected()}>Hernoemen</button>
          </footer>
        </section>
      ) : null}
      {dialog === "properties" ? (
        <section className="ex-inline-dialog ex-properties-dialog" role="dialog" aria-label="Eigenschappen">
          <header>
            <strong>Eigenschappen</strong>
            <button type="button" aria-label="Sluiten" onClick={() => setDialog(null)}>x</button>
          </header>
          <div className="ex-props-tabs">
            <button className="active" type="button" onClick={() => setDialog("properties")}>Algemeen</button>
            <button type="button" onClick={() => void shareSelected()}>Delen</button>
            <button type="button" onClick={() => void setPermissionSelected()}>Beveiliging</button>
          </div>
          <div className="ex-props-body">
            <MmcGlyph kind={selectedNode?.type === "file" ? "record" : "folder"} />
            <div>
              <strong>{selectedNode?.name ?? baseNameForPath(currentPath)}</strong>
              <p>Locatie: {selectedPath ? parentNativePath(selectedPath) : currentPath}</p>
              <p>Type: {selectedNode ? formatFileType(selectedNode) : "Bestandsmap"}</p>
              <p>Status: {message}</p>
            </div>
          </div>
          <footer>
            <button className="is-primary" type="button" onClick={() => setDialog(null)}>OK</button>
            <button type="button" onClick={() => setDialog(null)}>Annuleren</button>
          </footer>
        </section>
      ) : null}
      {dialog === "mapDrive" ? (
        <section className="ex-inline-dialog ex-map-drive-dialog" role="dialog" aria-label="Netwerkstation koppelen">
          <header>
            <strong>Netwerkstation koppelen</strong>
            <button type="button" aria-label="Sluiten" onClick={() => setDialog(null)}>x</button>
          </header>
          <label>Station:
            <select value={driveLetter} onChange={(event) => setDriveLetter(event.target.value)}>
              {["Z", "Y", "X", "W", "V", "U", "T", "S"].map((letter) => <option key={letter} value={letter}>{letter}:</option>)}
            </select>
          </label>
          <label>Map:
            <input value={drivePath} onChange={(event) => setDrivePath(event.target.value)} placeholder="\\\\SRV01\\Shares" />
          </label>
          <p className="ex-dialog-note">Dit schrijft alleen naar simulatorstate. Er wordt geen echte netwerkverbinding gemaakt.</p>
          {networkDrives.length > 0 ? (
            <div className="ex-mapped-drives">
              <strong>Gekoppelde netwerkstations</strong>
              {networkDrives.map((drive) => {
                const letter = normalizeNativeDriveLetter(String(drive.letter ?? ""));
                return (
                  <button key={`${letter}-${String(drive.path ?? "")}`} type="button" onClick={() => void unmapNetworkDrive(letter)}>
                    {letter}: ontkoppelen
                  </button>
                );
              })}
            </div>
          ) : null}
          <footer>
            <button type="button" className="ex-primary-btn" onClick={() => void mapNetworkDrive()}>Voltooien</button>
            <button type="button" onClick={() => setDialog(null)}>Annuleren</button>
          </footer>
        </section>
      ) : null}
      <div className="ex-status"><span>{children.length} items</span><span>{selectedNode ? `${selectedNode.name} geselecteerd` : "Geen selectie"}</span></div>
    </div>
  );
}

type SettingsSection = "system" | "personalization" | "accounts" | "network" | "bluetooth" | "display" | "time" | "apps";

function SettingsSystemSurface({
  actions,
  busyActionId,
  initialSection,
  onRun,
  openApp
}: {
  actions: NativeSimulatorAction[];
  busyActionId: string | null;
  initialSection?: string;
  onRun: NativeRunAction;
  openApp: (appId: string, launchUrl?: string) => void;
}) {
  const normalizeSection = (section?: string): SettingsSection => (
    section === "bluetooth" || section === "network" || section === "display" || section === "time" || section === "apps" || section === "accounts" || section === "personalization"
      ? section
      : "system"
  );
  const [activeSection, setActiveSection] = useState<SettingsSection>(() => normalizeSection(initialSection));
  useEffect(() => {
    setActiveSection(normalizeSection(initialSection));
  }, [initialSection]);
  const runCommand = (commandType: string) => {
    const action = selectNativeAction("settingsSystem", actions, [commandType]);
    if (action) void onRun(action);
  };
  const navItems: Array<{ id: SettingsSection; kind: string; label: string }> = [
    { id: "system", kind: "computer", label: "Systeem" },
    { id: "personalization", kind: "settings", label: "Persoonlijke instellingen" },
    { id: "accounts", kind: "user", label: "Accounts" },
    { id: "network", kind: "network", label: "Netwerk & internet" },
    { id: "bluetooth", kind: "devices", label: "Bluetooth en apparaten" },
    { id: "display", kind: "monitor", label: "Beeldscherm" },
    { id: "apps", kind: "list", label: "Apps" },
    { id: "time", kind: "info", label: "Datum en tijd" }
  ];

  return (
    <div className="settings-app native-settings-system-surface" data-native-app="settingsSystem" data-native-parity="settingsSystem">
      <aside className="settings-nav">
        <div className="settings-nav-title">Instellingen</div>
        {navItems.map((item) => (
          <button
            className={activeSection === item.id ? "settings-nav-item active" : "settings-nav-item"}
            key={item.id}
            type="button"
            onClick={() => setActiveSection(item.id)}
          >
            <MmcGlyph kind={item.kind} />
            {item.label}
          </button>
        ))}
      </aside>
      <main className="settings-content">
        {activeSection === "system" ? (
          <>
            <h2 className="settings-h2">Systeem</h2>
            <section className="settings-card settings-device-card">
              <div className="settings-device-hero"><MmcGlyph kind="computer" /><div><strong>CLIENT01</strong><small>Windows 11 Enterprise</small></div></div>
              <button className="settings-link-btn" type="button" onClick={() => openApp("sysdm")}>Naam wijzigen of domein koppelen</button>
              <button className="settings-link-btn" type="button" onClick={() => openApp("msinfo32")}>Systeeminformatie openen</button>
            </section>
            <section className="settings-card">
              <h3 className="settings-h3">Apparaatspecificaties</h3>
              <div className="settings-info-row"><span className="settings-info-key">Naam van deze pc</span><strong className="settings-info-val">CLIENT01</strong></div>
              <div className="settings-info-row"><span className="settings-info-key">Domein</span><span className="settings-info-val">advanced-ti.local</span></div>
              <div className="settings-info-row"><span className="settings-info-key">Build</span><span className="settings-info-val">26H2 simulator</span></div>
              <button className="settings-link-btn" type="button" onClick={() => setActiveSection("display")}>Beeldscherm</button>
            </section>
          </>
        ) : null}

        {activeSection === "display" ? (
          <>
            <h2 className="settings-h2">Beeldscherm</h2>
            <section className="settings-card">
              <h3 className="settings-h3">Schaal en indeling</h3>
              <label className="settings-field"><span>Schaal (%)</span><input className="settings-input" value="125" readOnly /></label>
              <button className="settings-link-btn" type="button" onClick={() => runCommand("SYSTEM_SET_DISPLAY_SCALING")}>Schaal toepassen</button>
            </section>
          </>
        ) : null}

        {activeSection === "network" ? (
          <>
            <h2 className="settings-h2">Netwerk & internet</h2>
            <section className="settings-card">
              <h3 className="settings-h3">Ethernet</h3>
              <div className="settings-info-row"><span className="settings-info-key">IPv4-adres</span><span className="settings-info-val">10.50.20.25</span></div>
              <div className="settings-info-row"><span className="settings-info-key">DNS-server</span><span className="settings-info-val">10.50.10.10</span></div>
              <button className="settings-link-btn" type="button" onClick={() => openApp("netSettings")}>Adapter DNS wijzigen</button>
              <button className="settings-link-btn" type="button" onClick={() => openApp("ncpa")}>Geavanceerde netwerkadapteropties</button>
            </section>
          </>
        ) : null}

        {activeSection === "time" ? (
          <>
            <h2 className="settings-h2">Datum en tijd</h2>
            <section className="settings-card">
              <h3 className="settings-h3">Datum en tijd automatisch instellen</h3>
              <label className="settings-toggle-label"><span>Tijd automatisch instellen</span><span className="toggle-switch"><input type="checkbox" checked readOnly /><span className="toggle-slider" /></span></label>
              <div className="settings-info-row"><span className="settings-info-key">Tijdzone</span><span className="settings-info-val">(UTC+01:00) Amsterdam, Berlijn</span></div>
              <div className="settings-info-row"><span className="settings-info-key">Huidige datum</span><span className="settings-info-val">{new Date().toLocaleDateString("nl-NL")}</span></div>
            </section>
          </>
        ) : null}

        {activeSection === "bluetooth" ? (
          <>
            <h2 className="settings-h2">Bluetooth en apparaten</h2>
            <section className="settings-card" data-native-parity="settings-bluetooth-depth">
              <h3 className="settings-h3">Bluetooth</h3>
              <label className="settings-toggle-label"><span>Bluetooth</span><span className="toggle-switch"><input type="checkbox" checked readOnly /><span className="toggle-slider" /></span></label>
              <div className="settings-info-row"><span className="settings-info-key">Status</span><span className="settings-info-val">Ingeschakeld</span></div>
              <div className="settings-info-row"><span className="settings-info-key">Zichtbare naam</span><span className="settings-info-val">CLIENT01</span></div>
              <button className="settings-link-btn" type="button" onClick={() => openApp("devmgmt")}>Apparaatbeheer openen</button>
            </section>
          </>
        ) : null}

        {activeSection === "apps" ? (
          <>
            <h2 className="settings-h2">Apps</h2>
            <section className="settings-card" data-native-parity="settings-apps-depth">
              <h3 className="settings-h3">Geinstalleerde apps</h3>
              <div className="settings-info-row"><span className="settings-info-key">Microsoft Edge</span><span className="settings-info-val">Browser- en portalroutes</span></div>
              <div className="settings-info-row"><span className="settings-info-key">FirstDesk Simulator Tools</span><span className="settings-info-val">Native React shell</span></div>
              <button className="settings-link-btn" type="button" onClick={() => openApp("appwiz")}>Programma's en onderdelen openen</button>
              <button className="settings-link-btn" type="button" onClick={() => openApp("optionalfeatures")}>Optionele onderdelen beheren</button>
            </section>
          </>
        ) : null}

        {activeSection === "accounts" ? (
          <>
            <h2 className="settings-h2">Accounts</h2>
            <section className="settings-card">
              <h3 className="settings-h3">Werk- of schoolaccount</h3>
              <p className="tool-muted">Student is aangemeld in de FirstDesk labsessie.</p>
              <div className="settings-info-row"><span className="settings-info-key">Gebruiker</span><span className="settings-info-val">ADVANCED-TI\selina.166</span></div>
              <div className="settings-info-row"><span className="settings-info-key">Sync status</span><span className="settings-info-val">Verbonden met labbeleid</span></div>
              <button className="settings-link-btn" type="button" onClick={() => openApp("login")}>Aanmeldscherm openen</button>
            </section>
          </>
        ) : null}

        {activeSection === "personalization" ? (
          <>
            <h2 className="settings-h2">Persoonlijke instellingen</h2>
            <section className="settings-card">
              <h3 className="settings-h3">Achtergrond</h3>
              <p className="tool-muted">Windows 11 simulatorachtergrond actief.</p>
              <label className="settings-toggle-label"><span>Meldingen weergeven</span><span className="toggle-switch"><input type="checkbox" checked readOnly /><span className="toggle-slider" /></span></label>
              <button className="settings-link-btn" type="button" onClick={() => runCommand("SYSTEM_SET_NOTIFICATIONS")}>Meldingsinstelling toepassen</button>
            </section>
          </>
        ) : null}

        <section className="settings-card settings-command-card">
          <h3 className="settings-h3">Bewijsacties</h3>
          <ActionStrip actions={actions} busyActionId={busyActionId} onRun={onRun} />
        </section>
      </main>
    </div>
  );
}

function SystemPropertiesSurface({
  actions,
  onRun,
  simulatorState
}: {
  actions: NativeSimulatorAction[];
  onRun: NativeRunAction;
  simulatorState: NativeSimulatorState | null;
}) {
  const [activeTab, setActiveTab] = useState<"name" | "hardware" | "advanced" | "protection" | "remote">("name");
  const [changeOpen, setChangeOpen] = useState(false);
  const [computerName, setComputerName] = useState("CLIENT01");
  const [membership, setMembership] = useState<"domain" | "workgroup">("domain");
  const [domainName, setDomainName] = useState("advanced-ti.local");
  const [workgroupName, setWorkgroupName] = useState("WORKGROUP");
  const [message, setMessage] = useState("Gebruik Wijzigen om computernaam, domein of werkgroep aan te passen.");
  const [subDialog, setSubDialog] = useState<"more" | "description" | "deviceManager" | "deviceInstall" | "performance" | "profiles" | "startup" | "env" | "restore" | "remoteUsers" | null>(null);
  const domainJoined = Boolean((simulatorState as { identity?: { domainJoined?: boolean } } | null)?.identity?.domainJoined);
  const renameAction = selectNativeAction("sysdm", actions, ["SYSTEM_RENAME_COMPUTER"], "system.identity.write");
  const joinAction = selectNativeAction("sysdm", actions, ["SYSTEM_JOIN_DOMAIN"], "domain.membership.write");
  const leaveAction = selectNativeAction("sysdm", actions, ["SYSTEM_LEAVE_DOMAIN"], "domain.membership.write");
  const remoteAction = selectNativeAction("sysdm", actions, ["SYSTEM_ENABLE_REMOTE_DESKTOP", "SYSTEM_CONFIGURE_REMOTE_DESKTOP", "RDP_ENABLE"], "remoteDesktop.write");
  const envAction = selectNativeAction("sysdm", actions, ["SYSTEM_SET_ENVIRONMENT_VARIABLE"], "local.system.environment.write");
  const remoteState = (simulatorState as { remoteDesktop?: { enabled?: boolean } } | null)?.remoteDesktop;
  const [remoteDesktopEnabled, setRemoteDesktopEnabled] = useState(remoteState?.enabled === true);
  const [envName, setEnvName] = useState("FIRSTDESK_HOME");
  const [envValue, setEnvValue] = useState("C:\\FirstDesk");
  const [envScope, setEnvScope] = useState<"user" | "system">("system");

  useEffect(() => {
    if (remoteState?.enabled !== undefined) setRemoteDesktopEnabled(remoteState.enabled === true);
  }, [remoteState?.enabled]);

  async function applyComputerNameChange() {
    if (renameAction) {
      const result = await onRun({
        ...renameAction,
        id: `sysdm-rename-${Date.now()}`,
        label: `Computernaam wijzigen naar ${computerName}`,
        payload: {
          ...renameAction.payload,
          computerName
        }
      });
      if (!result.ok) {
        setMessage(`Computernaam wijzigen mislukt (${result.status}).`);
        return;
      }
    }

    if (membership === "domain" && joinAction) {
      const result = await onRun({
        ...joinAction,
        id: `sysdm-domain-${Date.now()}`,
        label: `Lid worden van domein ${domainName}`,
        payload: {
          ...joinAction.payload,
          computerName,
          domainName,
          reachable: true
        }
      });
      if (!result.ok) {
        setMessage(`Domein join mislukt (${result.status}).`);
        return;
      }
    }

    if (membership === "workgroup" && leaveAction) {
      const result = await onRun({
        ...leaveAction,
        id: `sysdm-workgroup-${Date.now()}`,
        label: `Lid worden van werkgroep ${workgroupName}`,
        payload: {
          ...leaveAction.payload,
          computerName,
          workgroupName
        }
      });
      if (!result.ok) {
        setMessage(`Werkgroep wijzigen mislukt (${result.status}).`);
        return;
      }
    }

    setChangeOpen(false);
    setMessage(membership === "domain"
      ? `${computerName} is lid gemaakt van ${domainName}.`
      : `${computerName} is ingesteld op werkgroep ${workgroupName}.`);
  }

  async function applyRemoteDesktop() {
    if (!remoteAction) {
      setMessage("Remote Desktop is visueel ingesteld; er is geen aparte command actie voor dit ticket.");
      return;
    }
    const result = await onRun({
      ...remoteAction,
      appId: "remoteDesktop",
      appName: "Remote Desktop",
      id: `sysdm-rdp-${Date.now()}`,
      label: remoteDesktopEnabled ? "Remote Desktop inschakelen" : "Remote Desktop uitschakelen",
      payload: {
        ...remoteAction.payload,
        enabled: remoteDesktopEnabled,
        allowedGroups: ["Remote Desktop Users"],
        firewallRuleName: "Extern bureaublad (TCP-In)"
      }
    });
    setMessage(result.ok ? `Remote Desktop is ${remoteDesktopEnabled ? "ingeschakeld" : "uitgeschakeld"}.` : `Remote Desktop vastleggen mislukt (${result.status}).`);
  }

  async function addEnvironmentVariable() {
    const cleanName = envName.trim();
    if (!cleanName) {
      setMessage("Geef een variabelenaam op.");
      return;
    }
    if (!envAction) {
      setMessage("Omgevingsvariabelen zijn visueel bijgewerkt; command contract ontbreekt.");
      return;
    }
    const result = await onRun({
      ...envAction,
      id: `sysdm-env-${Date.now()}`,
      label: `${envScope === "system" ? "Systeemvariabele" : "Gebruikersvariabele"} ${cleanName} toevoegen`,
      payload: {
        ...envAction.payload,
        scope: envScope,
        name: cleanName,
        value: envValue
      }
    });
    setMessage(result.ok ? `Omgevingsvariabele ${cleanName} toegevoegd.` : `Omgevingsvariabele toevoegen mislukt (${result.status}).`);
  }

  return (
    <div className="sysdm-app native-system-properties" data-native-app="sysdm" data-native-parity="sysdm" data-oracle-source="sysdm.js">
      <div className="sysdm-tabs" role="tablist" aria-label="Systeemeigenschappen tabs">
        <button className={`sysdm-tab ${activeTab === "name" ? "active" : ""}`} type="button" onClick={() => setActiveTab("name")}>Computernaam</button>
        <button className={`sysdm-tab ${activeTab === "hardware" ? "active" : ""}`} type="button" onClick={() => setActiveTab("hardware")}>Hardware</button>
        <button className={`sysdm-tab ${activeTab === "advanced" ? "active" : ""}`} type="button" onClick={() => setActiveTab("advanced")}>Geavanceerd</button>
        <button className={`sysdm-tab ${activeTab === "protection" ? "active" : ""}`} type="button" onClick={() => setActiveTab("protection")}>Systeembeveiliging</button>
        <button className={`sysdm-tab ${activeTab === "remote" ? "active" : ""}`} type="button" onClick={() => setActiveTab("remote")}>Extern</button>
      </div>
      <div className="sysdm-panel">
        {activeTab === "name" ? (
          <>
            <div className="sysdm-intro-row">
              <MmcGlyph kind="computer" className="sysdm-computer-icon" />
              <div>
                <div className="sysdm-intro-title">Windows 11 Enterprise</div>
                <div className="sysdm-intro-sub">23H2 - Build 22631</div>
              </div>
            </div>
            <section className="sysdm-group">
              <h3>Naam van deze computer en instellingen voor werkgroep of domein</h3>
              <table className="sysdm-kv"><tbody><tr><td>Volledige computernaam:</td><td><strong>{computerName}.{membership === "domain" ? domainName : workgroupName}</strong></td></tr><tr><td>Computerbeschrijving:</td><td>Servicedesk client</td></tr><tr><td>{membership === "domain" ? "Domein:" : "Werkgroep:"}</td><td><strong>{membership === "domain" ? domainName : workgroupName}</strong>{domainJoined ? <span className="sysdm-live-pill">vastgelegd</span> : null}</td></tr></tbody></table>
              <div className="sysdm-info-box">Klik op Wijzigen om de naam van deze computer te wijzigen of het domein of de werkgroep te wijzigen.</div>
              <div className="sysdm-actions">
                <button className="sysdm-btn primary" type="button" onClick={() => setChangeOpen(true)}>Wijzigen...</button>
                <button className="sysdm-btn" type="button" onClick={() => setSubDialog("more")}>Meer...</button>
                <button className="sysdm-btn" type="button" onClick={() => setSubDialog("description")}>Beschrijving wijzigen...</button>
                <button className="sysdm-btn" type="button" onClick={() => setActiveTab("remote")}>Extern bureaublad...</button>
              </div>
            </section>
          </>
        ) : null}
        {activeTab === "hardware" ? (
          <section className="sysdm-group" data-native-parity="sysdm-hardware-tab">
            <h3>Apparaatbeheer en apparaatinstallatie</h3>
            <div className="sysdm-device-list">
              <div><MmcGlyph kind="computer" /><span>Computer</span><strong>ACPI x64-based PC</strong></div>
              <div><MmcGlyph kind="network" /><span>Netwerkadapters</span><strong>Microsoft Hyper-V Network Adapter</strong></div>
              <div><MmcGlyph kind="server" /><span>Schijfstations</span><strong>FirstDesk Virtual Disk</strong></div>
              <div><MmcGlyph kind="shield" /><span>Beveiligingsapparaten</span><strong>Trusted Platform Module 2.0</strong></div>
            </div>
            <div className="sysdm-actions">
              <button className="sysdm-btn primary" type="button" onClick={() => setSubDialog("deviceManager")}>Apparaatbeheer...</button>
              <button className="sysdm-btn" type="button" onClick={() => setSubDialog("deviceInstall")}>Instellingen voor apparaatinstallatie...</button>
            </div>
          </section>
        ) : null}
        {activeTab === "advanced" ? (
          <section className="sysdm-group">
            <h3>Prestaties, gebruikersprofielen en opstart- en herstelinstellingen</h3>
            <div className="sysdm-advanced-grid">
              <button className="sysdm-btn" type="button" onClick={() => setSubDialog("performance")}>Prestaties - Instellingen...</button>
              <button className="sysdm-btn" type="button" onClick={() => setSubDialog("profiles")}>Gebruikersprofielen - Instellingen...</button>
              <button className="sysdm-btn" type="button" onClick={() => setSubDialog("startup")}>Opstart- en herstelinstellingen...</button>
              <button className="sysdm-btn" type="button" onClick={() => setSubDialog("env")}>Omgevingsvariabelen...</button>
            </div>
          </section>
        ) : null}
        {activeTab === "protection" ? (
          <section className="sysdm-group">
            <h3>Systeembeveiliging</h3>
            <table className="sysdm-kv"><tbody><tr><td>Beschikbare stations:</td><td>Lokale schijf (C:) - Ingeschakeld</td></tr><tr><td>Herstelpunten:</td><td>Simulatorherstelpunten worden via de command-bus vastgelegd.</td></tr></tbody></table>
            <div className="sysdm-actions"><button className="sysdm-btn" type="button" onClick={() => setSubDialog("restore")}>Configureren...</button><button className="sysdm-btn" type="button" onClick={() => setSubDialog("restore")}>Maken...</button></div>
          </section>
        ) : null}
        {activeTab === "remote" ? (
          <section className="sysdm-group sysdm-remote-summary">
            <h3>Extern bureaublad</h3>
            <label className="sysdm-radio-row"><input checked={!remoteDesktopEnabled} name="sysdm-rdp" type="radio" onChange={() => setRemoteDesktopEnabled(false)} /><span>Geen externe verbindingen met deze computer toestaan</span></label>
            <label className="sysdm-radio-row"><input checked={remoteDesktopEnabled} name="sysdm-rdp" type="radio" onChange={() => setRemoteDesktopEnabled(true)} /><span>Externe verbindingen naar deze computer toestaan</span></label>
            <label className="sysdm-radio-row"><input type="checkbox" checked readOnly /><span>Alleen verbindingen toestaan met Verificatie op netwerkniveau</span></label>
            <div className="sysdm-actions"><button className="sysdm-btn primary" type="button" onClick={() => void applyRemoteDesktop()}>Toepassen</button><button className="sysdm-btn" type="button" onClick={() => setSubDialog("remoteUsers")}>Gebruikers selecteren...</button></div>
          </section>
        ) : null}
        <div className="sysdm-info-box">{message}</div>
      </div>
      {changeOpen ? (
        <section className="sysdm-change-dialog" role="dialog" aria-label="Computernaam/domein wijzigen">
          <header><strong>Computernaam/domein wijzigen</strong><button type="button" aria-label="Sluiten" onClick={() => setChangeOpen(false)}>x</button></header>
          <label>Computernaam:<input value={computerName} onChange={(event) => setComputerName(event.target.value)} /></label>
          <div className="sysdm-membership">
            <label><input checked={membership === "domain"} name="sysdm-membership" type="radio" onChange={() => setMembership("domain")} /> Domein</label>
            <input disabled={membership !== "domain"} value={domainName} onChange={(event) => setDomainName(event.target.value)} />
            <label><input checked={membership === "workgroup"} name="sysdm-membership" type="radio" onChange={() => setMembership("workgroup")} /> Werkgroep</label>
            <input disabled={membership !== "workgroup"} value={workgroupName} onChange={(event) => setWorkgroupName(event.target.value)} />
          </div>
          <footer><button className="sysdm-btn primary" type="button" onClick={() => void applyComputerNameChange()}>OK</button><button className="sysdm-btn" type="button" onClick={() => setChangeOpen(false)}>Annuleren</button></footer>
        </section>
      ) : null}
      {subDialog ? (
        <section className="sysdm-change-dialog sysdm-sub-dialog" role="dialog" aria-label="Systeemeigenschap">
          <header>
            <strong>{{
              description: "Computerbeschrijving",
              deviceInstall: "Instellingen voor apparaatinstallatie",
              deviceManager: "Apparaatbeheer",
              env: "Omgevingsvariabelen",
              more: "DNS-achtervoegsels en NetBIOS",
              performance: "Prestatieopties",
              profiles: "Gebruikersprofielen",
              remoteUsers: "Gebruikers voor extern bureaublad",
              restore: "Systeembeveiliging",
              startup: "Opstart- en herstelinstellingen"
            }[subDialog]}</strong>
            <button type="button" aria-label="Sluiten" onClick={() => setSubDialog(null)}>x</button>
          </header>
          <div className="sysdm-sub-body">
            {subDialog === "more" ? <p>Primaire DNS-achtervoegsel: advanced-ti.local. NetBIOS-computernaam: {computerName}.</p> : null}
            {subDialog === "description" ? <label>Computerbeschrijving:<input value="Servicedesk client" readOnly /></label> : null}
            {subDialog === "deviceManager" ? <div><p>Apparaatbeheer-overzicht</p><table className="sysdm-kv"><tbody><tr><td>Netwerkadapters</td><td>Microsoft Hyper-V Network Adapter - werkt correct</td></tr><tr><td>Schijfstations</td><td>FirstDesk Virtual Disk - werkt correct</td></tr><tr><td>Systeemapparaten</td><td>TPM 2.0, ACPI-computer</td></tr></tbody></table></div> : null}
            {subDialog === "deviceInstall" ? <p>Windows downloadt fabrikantsapps en aangepaste pictogrammen automatisch wanneer dit in de simulatoropdracht nodig is.</p> : null}
            {subDialog === "performance" ? <p>Visuele effecten staan op: Beste weergave voor Windows 11 simulator.</p> : null}
            {subDialog === "profiles" ? <p>Profielen: Student (lokaal), Administrator. Profielwijzigingen worden niet naar de serverfilesystem geschreven.</p> : null}
            {subDialog === "startup" ? <p>Opstarten en herstellen: standaardbesturingssysteem Windows 11 Enterprise; systeemfout logging ingeschakeld.</p> : null}
            {subDialog === "env" ? <div className="sysdm-env-panel"><p>Gebruikersvariabelen voor Student</p><table className="sysdm-kv"><tbody><tr><td>TEMP</td><td>%USERPROFILE%\\AppData\\Local\\Temp</td></tr><tr><td>PATH</td><td>%USERPROFILE%\\AppData\\Local\\Microsoft\\WindowsApps</td></tr></tbody></table><p>Systeemvariabelen</p><table className="sysdm-kv"><tbody><tr><td>ComSpec</td><td>C:\\Windows\\system32\\cmd.exe</td></tr><tr><td>Path</td><td>C:\\Windows\\system32;C:\\Windows;C:\\Windows\\System32\\WindowsPowerShell\\v1.0\\</td></tr><tr><td>TEMP</td><td>C:\\Windows\\TEMP</td></tr></tbody></table><div className="sysdm-env-editor"><select value={envScope} onChange={(event) => setEnvScope(event.target.value === "user" ? "user" : "system")}><option value="user">Gebruiker</option><option value="system">Systeem</option></select><input aria-label="Variabelenaam" value={envName} onChange={(event) => setEnvName(event.target.value)} /><input aria-label="Variabelewaarde" value={envValue} onChange={(event) => setEnvValue(event.target.value)} /><button className="sysdm-btn primary" type="button" onClick={() => void addEnvironmentVariable()}>Toevoegen</button></div></div> : null}
            {subDialog === "restore" ? <p>Systeembeveiliging is ingeschakeld voor C:. Herstelpunten worden als simulator-evidence vastgelegd.</p> : null}
            {subDialog === "remoteUsers" ? <p>Toegestane groep: Remote Desktop Users. Voeg leden toe via Local Users and Groups of ADUC waar het ticket dat vraagt.</p> : null}
          </div>
          <footer><button className="sysdm-btn primary" type="button" onClick={() => { setMessage("Systeemeigenschap bevestigd."); setSubDialog(null); }}>OK</button><button className="sysdm-btn" type="button" onClick={() => setSubDialog(null)}>Annuleren</button></footer>
        </section>
      ) : null}
    </div>
  );
}

function LocalUsersGroupsSurface(props: {
  actions: NativeSimulatorAction[];
  busyActionId: string | null;
  onRun: NativeRunAction;
  simulatorState: NativeSimulatorState | null;
}) {
  const { actions, onRun, simulatorState } = props;
  const [view, setView] = useState<"users" | "groups">("users");
  const [dialog, setDialog] = useState<"user" | "group" | "member" | null>(null);
  const [dialogMode, setDialogMode] = useState<"new" | "edit">("new");
  const [dialogTab, setDialogTab] = useState<"general" | "memberof" | "profile">("general");
  const [username, setUsername] = useState("SmokeStudent");
  const [fullName, setFullName] = useState("Smoke Student");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [groupName, setGroupName] = useState("Remote Desktop Users");
  const [description, setDescription] = useState("");
  const [mustChangePassword, setMustChangePassword] = useState(true);
  const [cannotChangePassword, setCannotChangePassword] = useState(false);
  const [passwordNeverExpires, setPasswordNeverExpires] = useState(false);
  const [accountDisabled, setAccountDisabled] = useState(false);
  const [message, setMessage] = useState("Lokale computer");
  const identity = (simulatorState as { identity?: Record<string, unknown> } | null)?.identity ?? {};
  const users = Array.from(new Set(["SmokeStudent", "Administrator", "HelpdeskStage", ...(((identity.localUsers as unknown[]) ?? []).map(String))]));
  const groups = Array.from(new Set(["Gebruikers", "Administrators", "Remote Desktop Users", ...(((identity.localGroups as unknown[]) ?? []).map(String))]));
  const states = (identity.localUserStates as Record<string, { enabled?: boolean }> | undefined) ?? {};
  const membership = (identity.localGroupMembership as Record<string, unknown[]> | undefined) ?? {};
  const isNewDialog = dialogMode === "new";

  function openNewUserDialog() {
    setDialogMode("new");
    setDialogTab("general");
    setUsername("");
    setFullName("");
    setDescription("");
    setPassword("");
    setConfirmPassword("");
    setMustChangePassword(false);
    setCannotChangePassword(false);
    setPasswordNeverExpires(false);
    setAccountDisabled(false);
    setDialog("user");
  }

  function openUserProperties(user = username) {
    setDialogMode("edit");
    setDialogTab("general");
    setUsername(user);
    setFullName(user === "SmokeStudent" ? "Smoke Student" : user);
    setDescription(user === "Administrator" ? "Ingebouwde account voor computerbeheer" : "Lokaal account");
    setPassword("");
    setConfirmPassword("");
    setMustChangePassword(true);
    setCannotChangePassword(false);
    setPasswordNeverExpires(false);
    setAccountDisabled(states[user]?.enabled === false || user === "Administrator");
    setDialog("user");
  }

  function openNewGroupDialog() {
    setDialogMode("new");
    setDialogTab("general");
    setGroupName("");
    setDescription("");
    setDialog("group");
  }

  function openGroupProperties(group = groupName) {
    setDialogMode("edit");
    setDialogTab("general");
    setGroupName(group);
    setDescription("Lokale groep");
    setDialog("group");
  }

  async function runLocal(commandType: string, payload: Record<string, unknown>) {
    const base = selectNativeAction("lusrmgr", actions, [commandType]);
    if (!base) {
      setMessage(`${commandType} heeft geen command contract.`);
      return;
    }
    const result = await onRun({
      ...base,
      id: `lusrmgr-${commandType.toLowerCase()}-${Date.now()}`,
      payload: {
        ...base.payload,
        ...payload
      }
    });
    setMessage(result.ok ? `${base.label} vastgelegd.` : `${base.label} mislukt (${result.status}).`);
    if (result.ok) setDialog(null);
  }

  async function submitDialog() {
    if (dialog === "user") {
      if (!username.trim()) {
        setMessage("Gebruikersnaam is verplicht.");
        return;
      }
      if (password !== confirmPassword) {
        setMessage("Wachtwoord en bevestiging komen niet overeen.");
        return;
      }
      await runLocal(isNewDialog ? "LOCAL_USER_CREATE" : "LOCAL_USER_SET_STATE", {
        username,
        fullName,
        password,
        description,
        mustChangePassword,
        cannotChangePassword,
        passwordNeverExpires,
        enabled: !accountDisabled
      });
      return;
    }
    if (dialog === "group") {
      await runLocal(isNewDialog ? "LOCAL_GROUP_CREATE" : "LOCAL_GROUP_ADD_MEMBER", { groupName, description, username });
      return;
    }
    await runLocal("LOCAL_GROUP_ADD_MEMBER", { username, groupName });
  }

  return (
    <div className="lusrmgr-wrap native-lusrmgr-surface native-mmc-surface" data-native-app="lusrmgr" data-native-parity="lusrmgr">
      <div className="lusrmgr-menubar"><span>Bestand</span><span>Actie</span><span>Weergave</span><span>Help</span></div>
      <div className="lusrmgr-toolbar">
        <div className="lm-tb-group">
          <button className="lm-tbtn" type="button" onClick={() => view === "users" ? openNewUserDialog() : openNewGroupDialog()}><WinGlyph name="note_add" /><span>Nieuw</span></button>
          <button className="lm-tbtn" type="button" onClick={() => view === "users" ? openUserProperties() : openGroupProperties()}><WinGlyph name="article" /><span>Eigenschappen</span></button>
          <button className="lm-tbtn lm-tbtn-danger" type="button" onClick={() => void runLocal("LOCAL_USER_DELETE", { username })}><WinGlyph name="delete" /><span>Verwijderen</span></button>
        </div>
        <span className="lm-tb-sep" />
        <div className="lm-tb-group"><button className="lm-tbtn" type="button" onClick={() => setMessage("Vernieuwd.")}><WinGlyph name="refresh" /><span>Vernieuwen</span></button></div>
      </div>
      <div className="lusrmgr-body">
        <aside className="lusrmgr-tree" aria-label="Local users and groups tree">
          <button className="lm-tree-root" type="button" onClick={() => setView("users")}><WinGlyph name="computer" /><span>Lokale computer</span></button>
          <button className={`lm-tree-item ${view === "users" ? "selected" : ""}`} type="button" onClick={() => setView("users")}><WinGlyph name="person" /><span>Gebruikers</span></button>
          <button className={`lm-tree-item ${view === "groups" ? "selected" : ""}`} type="button" onClick={() => setView("groups")}><WinGlyph name="groups" /><span>Groepen</span></button>
        </aside>
        <main className="lusrmgr-content">
          {view === "users" ? (
            <table className="lm-table"><thead><tr><th>Naam</th><th>Volledige naam</th><th>Beschrijving</th><th>Status</th></tr></thead><tbody>{users.map((user) => <tr className={username === user ? "selected" : ""} key={user} onClick={() => setUsername(user)} onDoubleClick={() => openUserProperties(user)}><td className="lm-td-name"><WinGlyph name="person" className="lm-name-icon" /><strong>{user}</strong></td><td>{user === "SmokeStudent" ? "Smoke Student" : user}</td><td>{user === "Administrator" ? "Ingebouwde account voor computerbeheer" : "Lokaal account"}</td><td><span className={states[user]?.enabled === false || user === "Administrator" ? "lm-badge lm-badge-disabled" : "lm-badge lm-badge-active"}>{states[user]?.enabled === false || user === "Administrator" ? "Uitgeschakeld" : "Actief"}</span></td></tr>)}</tbody></table>
          ) : (
            <table className="lm-table"><thead><tr><th>Naam</th><th>Beschrijving</th><th>Leden</th></tr></thead><tbody>{groups.map((group) => <tr className={groupName === group ? "selected" : ""} key={group} onClick={() => setGroupName(group)} onDoubleClick={() => openGroupProperties(group)}><td className="lm-td-name"><WinGlyph name="groups" className="lm-name-icon" /><strong>{group}</strong>{["Gebruikers", "Administrators", "Remote Desktop Users"].includes(group) ? <span className="lm-badge lm-badge-sys">Systeem</span> : null}</td><td>Lokale groep</td><td className="lm-td-members">{((membership[group] ?? []) as unknown[]).map(String).join(", ") || "Geen leden"}</td></tr>)}</tbody></table>
          )}
        </main>
      </div>
      <div className="lusrmgr-status">{users.length} gebruikers | {groups.length} groepen | {message}</div>
      {dialog ? (
        <div className="native-modal-overlay lusrmgr-modal-overlay" role="presentation">
          <section className="lusrmgr-dialog" role="dialog" aria-modal="true" aria-label="Lokale gebruikers en groepen">
            <header className="lusrmgr-dialog-titlebar"><strong>{dialog === "user" ? (isNewDialog ? "Nieuwe gebruiker" : `Eigenschappen - ${username}`) : dialog === "group" ? (isNewDialog ? "Nieuwe groep" : `Groep bewerken: ${groupName}`) : "Lidmaatschap"}</strong><button type="button" aria-label="Sluiten" onClick={() => setDialog(null)}>x</button></header>
            {dialog === "user" ? (
              <div className="lm-dialog-body">
                <div className="lm-tabs">
                  <button className={`lm-tab ${dialogTab === "general" ? "active" : ""}`} type="button" onClick={() => setDialogTab("general")}>Algemeen</button>
                  <button className={`lm-tab ${dialogTab === "memberof" ? "active" : ""}`} type="button" onClick={() => setDialogTab("memberof")}>Lid van</button>
                  <button className={`lm-tab ${dialogTab === "profile" ? "active" : ""}`} type="button" onClick={() => setDialogTab("profile")}>Profiel</button>
                </div>
                <div className={`lm-tab-content ${dialogTab === "general" ? "" : "hidden"}`}>
                  <div className="lm-field"><label>Gebruikersnaam {isNewDialog ? "*" : ""}</label><input autoFocus readOnly={!isNewDialog} value={username} onChange={(event) => setUsername(event.target.value)} /><div className="lm-err" /></div>
                  <div className="lm-field"><label>Volledige naam</label><input value={fullName} onChange={(event) => setFullName(event.target.value)} /></div>
                  <div className="lm-field"><label>Beschrijving</label><textarea rows={2} value={description} onChange={(event) => setDescription(event.target.value)} /></div>
                  <div className="lm-field"><label>Wachtwoord {isNewDialog ? "*" : ""}</label><input type="password" placeholder={isNewDialog ? "" : "Leeg = ongewijzigd"} value={password} onChange={(event) => setPassword(event.target.value)} /><div className="lm-err" /></div>
                  <div className="lm-field"><label>Bevestig wachtwoord</label><input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} /><div className="lm-err" /></div>
                  <div className="lm-checks">
                    <label><input type="checkbox" checked={mustChangePassword} onChange={(event) => setMustChangePassword(event.target.checked)} /> Gebruiker moet wachtwoord wijzigen bij aanmelding</label>
                    <label><input type="checkbox" checked={passwordNeverExpires} onChange={(event) => setPasswordNeverExpires(event.target.checked)} /> Wachtwoord verloopt nooit</label>
                    <label><input type="checkbox" checked={accountDisabled} onChange={(event) => setAccountDisabled(event.target.checked)} /> Account is uitgeschakeld</label>
                  </div>
                  <div className="lm-err">{cannotChangePassword ? "Gebruiker kan wachtwoord niet wijzigen." : ""}</div>
                </div>
                <div className={`lm-tab-content ${dialogTab === "memberof" ? "" : "hidden"}`}>
                  <div className="lm-memberof-label">Lid van groepen:</div>
                  <div className="lm-memberof-wrap">
                    {groups.map((group) => <label className="lm-memberof-row" key={group}><input type="checkbox" defaultChecked={isNewDialog ? group === "Gebruikers" : ((membership[group] ?? []) as unknown[]).map(String).includes(username)} /><WinGlyph name="groups" /><span>{group}</span></label>)}
                  </div>
                </div>
                <div className={`lm-tab-content ${dialogTab === "profile" ? "" : "hidden"}`}><p className="lm-profile-note">Profielinstellingen beheer via de Instellingen-app.</p></div>
              </div>
            ) : null}
            {dialog === "group" ? (
              <div className="lm-dialog-body lm-group-dialog-body">
                <div className="lm-field"><label>Naam</label><input autoFocus readOnly={!isNewDialog} value={groupName} onChange={(event) => setGroupName(event.target.value)} /><div className="lm-err" /></div>
                <div className="lm-field"><label>Beschrijving</label><input value={description} onChange={(event) => setDescription(event.target.value)} /></div>
                <div className="lm-field"><label>Leden</label><div className="lm-members-list">{users.map((user) => <label className="lm-member-row" key={user}><input type="checkbox" defaultChecked={((membership[groupName] ?? []) as unknown[]).map(String).includes(user)} /><WinGlyph name="person" className="lm-name-icon" />{user}{user === "SmokeStudent" ? " (Smoke Student)" : ""}</label>)}</div></div>
              </div>
            ) : null}
            {dialog === "member" ? (
              <div className="lm-dialog-body lm-group-dialog-body">
                <div className="lm-field"><label>Gebruiker</label><input value={username} onChange={(event) => setUsername(event.target.value)} /></div>
                <div className="lm-field"><label>Groep</label><input value={groupName} onChange={(event) => setGroupName(event.target.value)} /></div>
              </div>
            ) : null}
            <footer className="lusrmgr-dialog-footer"><button className="modal-btn primary" type="button" onClick={() => void submitDialog()}>{isNewDialog ? "Aanmaken" : "Opslaan"}</button><button className="modal-btn" type="button" onClick={() => setDialog(null)}>Annuleren</button></footer>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function RecycleBinSurface({ openApp }: { openApp: (appId: string, launchUrl?: string) => void }) {
  return (
    <div className="explorer-app recycle-bin-app" data-native-app="recycleBin" data-native-parity="recycleBin">
      <div className="ex-toolbar">
        <div className="ex-address-bar"><WinGlyph name="delete" /><span className="ex-breadcrumb">Prullenbak</span></div>
        <label className="ex-search-wrap"><WinGlyph name="search" /><input className="ex-search" aria-label="Zoeken in Prullenbak" placeholder="Zoeken in Prullenbak" /></label>
      </div>
      <div className="ex-actionbar"><button className="ex-action-btn" type="button" onClick={() => openApp("explorer")}><WinGlyph name="restore_from_trash" /><span>Alle items herstellen</span></button><button className="ex-action-btn" type="button" onClick={() => openApp("recycleBin")}><WinGlyph name="delete_forever" /><span>Prullenbak leegmaken</span></button></div>
      <div className="ex-body">
        <aside className="ex-nav"><button className="is-active" type="button" onClick={() => openApp("recycleBin")}><WinGlyph name="delete" />Prullenbak</button></aside>
        <main className="ex-main"><div className="ex-file-list" role="list"><div className="ex-file-row ex-file-row-empty" role="listitem"><span>De prullenbak is leeg.</span></div></div></main>
      </div>
      <div className="ex-status"><span>0 items</span><span>Restore-flow volgt de legacy prullenbakstatus.</span></div>
    </div>
  );
}

function TerminalSurface({ appId, onRun, simulatorState }: { appId: string; onRun: NativeRunAction; simulatorState: NativeSimulatorState | null }) {
  const isCmd = appId === "cmd";
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [currentDir, setCurrentDir] = useState("C:\\Users\\Student");
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number | null>(null);
  const [lines, setLines] = useState<string[]>(() => [
    isCmd ? "Microsoft Windows [Versie 10.0.26100.1]" : "Windows PowerShell",
    "(c) Microsoft Corporation. Alle rechten voorbehouden.",
    ""
  ]);
  const prompt = isCmd ? `${currentDir}>` : `PS ${currentDir}> `;

  function append(...nextLines: string[]) {
    setLines((current) => [...current, ...nextLines]);
  }

  function resolveTerminalPath(raw?: string) {
    if (!raw || raw === ".") return currentDir;
    if (raw === "..") return parentNativePath(currentDir);
    if (/^[a-z]:\\/i.test(raw) || /^[a-z]:$/i.test(raw)) return normalizeNativePath(raw);
    if (raw.startsWith("\\")) return normalizeNativePath(`C:${raw}`);
    return normalizeNativePath(`${currentDir}\\${raw}`);
  }

  function listDirectory(path: string) {
    const children = childrenForPath(simulatorState, path);
    if (children.length === 0) return [" Bestand niet gevonden of map is leeg."];
    return children.map(([, node]) => {
      const stamp = node.modifiedAt ? new Date(node.modifiedAt).toLocaleDateString("nl-NL") : new Date().toLocaleDateString("nl-NL");
      const marker = node.type === "folder" ? "<DIR>" : "     ";
      return `${stamp.padEnd(12)} ${marker.padEnd(8)} ${node.name}`;
    });
  }

  async function auditShell(raw: string) {
    await onRun({
      id: `${appId}-shell-${Date.now()}`,
      appId,
      appName: isCmd ? "Command Prompt" : "Windows PowerShell",
      commandType: "SHELL_EXECUTE_COMMAND",
      capability: "local.shell.execute",
      label: raw,
      description: "Voer een gesimuleerd shellcommando uit.",
      machine: "both",
      payload: {
        shell: isCmd ? "cmd" : "powershell",
        commandLine: raw,
        workingDir: currentDir
      }
    });
  }

  async function runFilesystemCommand(commandType: string, label: string, payload: Record<string, unknown>) {
    return onRun({
      id: `${appId}-${commandType.toLowerCase()}-${Date.now()}`,
      appId,
      appName: isCmd ? "Command Prompt" : "Windows PowerShell",
      commandType,
      capability: commandType.includes("FOLDER") ? "file.folder.write" : "file.file.write",
      label,
      description: "Pas het simulator-filesystem aan vanuit de shell.",
      machine: "both",
      payload
    });
  }

  async function runSimulatorCommand(commandType: string, capability: string, label: string, payload: Record<string, unknown>) {
    return onRun({
      id: `${appId}-${commandType.toLowerCase()}-${Date.now()}`,
      appId,
      appName: isCmd ? "Command Prompt" : "Windows PowerShell",
      commandType,
      capability,
      label,
      description: "Voer een gesimuleerde Windows-shellactie uit via de v2 command-bus.",
      machine: "both",
      payload
    });
  }

  async function writeFileFromShell(targetPath: string, content: string, label: string) {
    const existing = findNodeByPath(simulatorState, targetPath);
    for (const step of folderCreationStepsForPath(parentNativePath(targetPath))) {
      if (!findNodeByPath(simulatorState, step.path)) {
        await runFilesystemCommand("FILE_CREATE_FOLDER", `ensure ${step.name}`, step);
      }
    }
    return runFilesystemCommand(existing ? "FILE_WRITE_CONTENT" : "FILE_CREATE", label, {
      path: targetPath,
      parentPath: parentNativePath(targetPath),
      name: baseNameForPath(targetPath),
      content
    });
  }

  function treeLines(path: string, prefix = ""): string[] {
    const children = childrenForPath(simulatorState, path);
    const lines: string[] = [];
    children.forEach(([nodeId, node], index) => {
      const isLast = index === children.length - 1;
      const branch = isLast ? "\\---" : "+---";
      lines.push(`${prefix}${branch}${node.name}`);
      if (node.type === "folder") {
        lines.push(...treeLines(nodePath(fsNodesFor(simulatorState), nodeId), `${prefix}${isLast ? "    " : "|   "}`));
      }
    });
    return lines;
  }

  function processRows() {
    return [
      "Image Name                     PID Session Name        Mem Usage",
      "========================= ======== ================ ===========",
      "System                           4 Services              1,024 K",
      "explorer.exe                  3240 Console              82,120 K",
      "msedge.exe                    4488 Console             144,880 K",
      "powershell.exe                5212 Console              66,112 K",
      "cmd.exe                       6120 Console              12,448 K"
    ];
  }

  function serviceRows() {
    return [
      "Status   Name               DisplayName",
      "------   ----               -----------",
      "Running  Dnscache           DNS Client",
      "Running  LanmanWorkstation  Workstation",
      "Running  MpsSvc             Windows Defender Firewall",
      "Stopped  TermService        Remote Desktop Services"
    ];
  }

  function adapterRows() {
    return [
      "Name                      InterfaceDescription                 ifIndex Status",
      "----                      --------------------                 ------- ------",
      "Ethernet0                 Microsoft Hyper-V Network Adapter          4 Up"
    ];
  }

  function netIpConfigRows() {
    return [
      "InterfaceAlias       : Ethernet0",
      "InterfaceIndex       : 4",
      "IPv4Address          : 10.50.20.25",
      "IPv4DefaultGateway   : 10.50.20.1",
      "DNSServer            : 10.50.10.10"
    ];
  }

  async function execute(rawInput: string) {
    const raw = rawInput.trim();
    append(`${prompt}${raw}`);
    setInput("");
    setHistory((current) => raw ? [...current, raw].slice(-30) : current);
    setHistoryIndex(null);
    if (!raw) return;
    await auditShell(raw);
    const [command = "", ...args] = parseWindowsCommandLine(raw);
    const lower = command.toLowerCase();
    const argText = joinCommandArgs(args);

    if (lower === "cls" || lower === "clear") {
      setLines([]);
      return;
    }
    if (lower === "help") {
      append(isCmd
        ? "Ondersteund: dir, cd/chdir, mkdir/md, del/erase, copy, move, ren/rename, type, echo, tree, ping, nslookup, netstat, arp, ipconfig, gpupdate, tasklist, taskkill, chkdsk, sfc, net, netsh, sc, systeminfo."
        : "Ondersteund: Get-Process, Get-Service, Start/Stop/Restart-Service, Get-NetAdapter, Get-NetIPConfiguration, Get-ChildItem, Set-Location, New-Item, Remove-Item, Get-Content, Test-NetConnection, Clear-DnsClientCache.");
      return;
    }
    if (!isCmd) {
      if (lower === "get-process" || lower === "gps") {
        append(...processRows().map((line) => line.replace("Image Name", "ProcessName").replace("Mem Usage", "WS(K)")));
        return;
      }
      if (lower === "stop-process" || lower === "kill") {
        const name = args.find((value, index) => args[index - 1]?.toLowerCase() === "-name") ?? args.find((value) => !value.startsWith("-")) ?? "proces";
        await runSimulatorCommand("PROCESS_END", "local.process.write", `Stop-Process ${name}`, { processName: name });
        append(`Stop-Process: '${name}' is gestopt in de simulator.`);
        return;
      }
      if (lower === "get-service" || lower === "gsv") {
        append(...serviceRows());
        return;
      }
      if (lower === "start-service" || lower === "stop-service" || lower === "restart-service") {
        const serviceName = args.find((value) => !value.startsWith("-")) ?? "TermService";
        const commandType = lower === "stop-service" ? "SERVICE_STOP" : "SERVICE_START";
        await runSimulatorCommand(commandType, "service.write", `${command} ${serviceName}`, { serviceName });
        append(`${command}: service '${serviceName}' is ${lower === "stop-service" ? "gestopt" : "gestart"} in de simulator.`);
        return;
      }
      if (lower === "get-netadapter") {
        append(...adapterRows());
        return;
      }
      if (lower === "get-nettcpconnection") {
        append("LocalAddress LocalPort RemoteAddress RemotePort State", "0.0.0.0     135       0.0.0.0       0          Listen", "10.50.20.25 49152     10.50.20.1    443        Established");
        return;
      }
      if (lower === "get-netipaddress") {
        append("IPAddress     PrefixLength InterfaceAlias", "10.50.20.25   24           Ethernet0", "fe80::250     64           Ethernet0");
        return;
      }
      if (lower === "get-netipconfig" || lower === "get-netipconfiguration") {
        append(...netIpConfigRows());
        return;
      }
      if (lower === "set-netipaddress") {
        const ip = args.find((value, index) => args[index - 1]?.toLowerCase() === "-ipaddress") ?? args.find((value) => /^\d+\.\d+\.\d+\.\d+$/.test(value));
        if (!ip) {
          append("Set-NetIPAddress: -IPAddress is vereist.");
          return;
        }
        await runSimulatorCommand("NETWORK_SET_ADAPTER_CONFIG", "network.adapter.write", `Set-NetIPAddress ${ip}`, { adapterName: "Ethernet0", ipAddress: ip, subnetMask: "255.255.255.0" });
        append(`IP-adres ingesteld op ${ip}/24`);
        return;
      }
      if (lower === "get-dnsclientcache") {
        append("Entry              RecordType Data", "srv01.local        A          10.50.10.10", "firstdesk.local    A          10.50.20.25");
        return;
      }
      if (lower === "clear-dnsclientcache") {
        await runSimulatorCommand("SHELL_EXECUTE_COMMAND", "local.shell.execute", "Clear-DnsClientCache", { shell: "powershell", commandLine: raw, workingDir: currentDir });
        append("DNS-clientcache gewist.");
        return;
      }
      if (lower === "get-childitem" || lower === "gci" || lower === "dir" || lower === "ls") {
        append("Mode   LastWriteTime       Length Name", "----   -------------       ------ ----", ...listDirectory(args[0] ? resolveTerminalPath(argText) : currentDir));
        return;
      }
      if (lower === "set-location" || lower === "cd") {
        const target = resolveTerminalPath(argText);
        const entry = findNodeByPath(simulatorState, target);
        if (entry?.[1].type === "folder") setCurrentDir(normalizeNativePath(target).replace(/\\$/, ""));
        else append(`Set-Location: pad niet gevonden: ${target}`);
        return;
      }
      if (lower === "get-location" || lower === "pwd") {
        append(currentDir);
        return;
      }
      if (lower === "get-content" || lower === "gc" || lower === "cat" || lower === "type") {
        const target = resolveTerminalPath(argText);
        const entry = findNodeByPath(simulatorState, target);
        append(entry?.[1].type === "file" ? (entry[1].content ?? "") : `Get-Content: bestand niet gevonden: ${target}`);
        return;
      }
      if (lower === "new-item" || lower === "ni") {
        const itemType = (args.find((value, index) => args[index - 1]?.toLowerCase() === "-itemtype") ?? "File").toLowerCase();
        const pathArg = args.find((value) => !value.startsWith("-") && value.toLowerCase() !== itemType) ?? "Nieuw bestand.txt";
        const target = resolveTerminalPath(pathArg);
        const commandType = itemType.includes("directory") || itemType.includes("folder") ? "FILE_CREATE_FOLDER" : "FILE_CREATE";
        const result = await runFilesystemCommand(commandType, `New-Item ${baseNameForPath(target)}`, {
          path: target,
          parentPath: parentNativePath(target),
          name: baseNameForPath(target),
          content: ""
        });
        append(result.ok ? `Nieuw item gemaakt: ${target}` : `New-Item mislukt (${result.status}).`);
        return;
      }
      if (lower === "remove-item" || lower === "ri" || lower === "del") {
        const target = resolveTerminalPath(argText);
        const entry = findNodeByPath(simulatorState, target);
        const commandType = entry?.[1].type === "folder" ? "FILE_DELETE_FOLDER" : "FILE_DELETE";
        const result = await runFilesystemCommand(commandType, `Remove-Item ${baseNameForPath(target)}`, { path: target });
        append(result.ok ? `Verwijderd: ${target}` : `Remove-Item mislukt (${result.status}).`);
        return;
      }
      if (lower === "get-computerinfo") {
        append("CsName                 : CLIENT01", "WindowsProductName     : Windows 11 Enterprise", "WindowsVersion         : 23H2", "CsDomain               : advanced-ti.local");
        return;
      }
      if (lower === "get-date") {
        append(new Date().toString());
        return;
      }
      if (lower === "get-help") {
        append(`Help voor ${args[0] ?? "PowerShell"} is beschikbaar in deze simulator.`);
        return;
      }
      if (lower === "write-host") {
        append(argText.replace(/^"|"$/g, ""));
        return;
      }
      if (lower === "test-netconnection" || lower === "tnc" || lower === "ping") {
        const host = args.find((value) => !value.startsWith("-")) ?? "srv01.advanced-ti.local";
        append(`ComputerName     : ${host}`, "RemoteAddress    : 10.50.10.10", "TcpTestSucceeded : True");
        return;
      }
      if (lower === "netstat") {
        append("Proto  Local Address          Foreign Address        State", "TCP    10.50.20.25:49152     10.50.20.1:443       ESTABLISHED");
        return;
      }
      if (lower === "arp") {
        append("Interface: 10.50.20.25 --- 0x4", "  Internet Address      Physical Address      Type", "  10.50.20.1           00-15-5d-01-00-01     dynamic");
        return;
      }
      if (lower === "get-itemproperty" || lower === "gp") {
        append("Path : HKCU:\\Environment", "TEMP : C:\\Users\\Student\\AppData\\Local\\Temp", "USERDOMAIN : ADVANCED-TI");
        return;
      }
      if (lower === "exit") {
        append("Gebruik de vensterknop om PowerShell te sluiten.");
        return;
      }
    }
    if (lower === "ver") {
      append("Microsoft Windows [Versie 10.0.26100.1]");
      return;
    }
    if (lower === "whoami") {
      append("advanced-ti\\student");
      return;
    }
    if (lower === "hostname") {
      append("CLIENT01");
      return;
    }
    if (lower === "ipconfig") {
      const flush = args.some((arg) => arg.toLowerCase() === "/flushdns");
      if (flush) {
        append("Windows IP Configuration", "", "Successfully flushed the DNS Resolver Cache.");
        return;
      }
      append("Windows IP Configuration", "", "Ethernet adapter Ethernet0:", "   Connection-specific DNS Suffix  . : advanced-ti.local", "   IPv4 Address. . . . . . . . . . . : 10.50.20.25", "   Subnet Mask . . . . . . . . . . . : 255.255.255.0", "   Default Gateway . . . . . . . . . : 10.50.20.1", "   DNS Servers . . . . . . . . . . . : 10.50.10.10");
      return;
    }
    if (lower === "dir" || lower === "ls") {
      append(` Map van ${args[0] ? resolveTerminalPath(argText) : currentDir}`, "", ...listDirectory(args[0] ? resolveTerminalPath(argText) : currentDir));
      return;
    }
    if (lower === "cd" || lower === "chdir") {
      if (!argText) {
        append(currentDir);
        return;
      }
      const target = resolveTerminalPath(argText);
      const entry = findNodeByPath(simulatorState, target);
      if (entry?.[1].type === "folder") {
        setCurrentDir(normalizeNativePath(target).replace(/\\$/, ""));
        return;
      }
      append("Het systeem kan het opgegeven pad niet vinden.");
      return;
    }
    if (lower === "mkdir" || lower === "md") {
      const target = resolveTerminalPath(argText);
      const result = await runFilesystemCommand("FILE_CREATE_FOLDER", `mkdir ${baseNameForPath(target)}`, {
        path: target,
        parentPath: parentNativePath(target),
        name: baseNameForPath(target)
      });
      append(result.ok ? `Map gemaakt: ${target}` : `mkdir mislukt (${result.status}).`);
      return;
    }
    if (lower === "del" || lower === "erase" || lower === "rm") {
      const target = resolveTerminalPath(argText);
      const entry = findNodeByPath(simulatorState, target);
      const commandType = entry?.[1].type === "folder" ? "FILE_DELETE_FOLDER" : "FILE_DELETE";
      const result = await runFilesystemCommand(commandType, `del ${baseNameForPath(target)}`, { path: target });
      append(result.ok ? `Verwijderd: ${target}` : `del mislukt (${result.status}).`);
      return;
    }
    if (lower === "type" || lower === "cat") {
      const target = resolveTerminalPath(argText);
      const entry = findNodeByPath(simulatorState, target);
      append(entry?.[1].type === "file" ? (entry[1].content ?? "") : "Het systeem kan het opgegeven bestand niet vinden.");
      return;
    }
    if (lower === "echo") {
      const redirectIndex = raw.indexOf(">");
      if (redirectIndex > -1) {
        const text = raw.slice(5, redirectIndex).trim();
        const target = resolveTerminalPath(raw.slice(redirectIndex + 1).trim());
        const existing = findNodeByPath(simulatorState, target);
        const commandType = existing ? "FILE_WRITE_CONTENT" : "FILE_CREATE";
        const result = await runFilesystemCommand(commandType, `echo > ${baseNameForPath(target)}`, {
          path: target,
          parentPath: parentNativePath(target),
          name: baseNameForPath(target),
          content: text
        });
        append(result.ok ? `Geschreven: ${target}` : `Schrijven mislukt (${result.status}).`);
        return;
      }
      append(args.join(" "));
      return;
    }
    if (lower === "copy") {
      if (args.length < 2) {
        append("Syntaxisfout.");
        return;
      }
      const sourcePath = resolveTerminalPath(args[0]);
      const sourceEntry = findNodeByPath(simulatorState, sourcePath);
      if (!sourceEntry || sourceEntry[1].type !== "file") {
        append("Kan het opgegeven bestand niet vinden.");
        return;
      }
      const destinationPath = resolveCopyDestination(simulatorState, sourcePath, args[1]);
      const result = await writeFileFromShell(destinationPath, sourceEntry[1].content ?? "", `copy ${baseNameForPath(sourcePath)}`);
      append(result.ok ? "        1 bestand(en) gekopieerd." : `copy mislukt (${result.status}).`);
      return;
    }
    if (lower === "move") {
      if (args.length < 2) {
        append("Syntaxisfout.");
        return;
      }
      const sourcePath = resolveTerminalPath(args[0]);
      const sourceEntry = findNodeByPath(simulatorState, sourcePath);
      if (!sourceEntry || sourceEntry[1].type !== "file") {
        append("Kan het opgegeven bestand niet vinden.");
        return;
      }
      const destinationPath = resolveCopyDestination(simulatorState, sourcePath, args[1]);
      const createResult = await writeFileFromShell(destinationPath, sourceEntry[1].content ?? "", `move ${baseNameForPath(sourcePath)}`);
      const deleteResult = createResult.ok ? await runFilesystemCommand("FILE_DELETE", `move delete ${baseNameForPath(sourcePath)}`, { path: sourcePath }) : createResult;
      append(createResult.ok && deleteResult.ok ? "        1 bestand(en) verplaatst." : `move mislukt (${createResult.status}/${deleteResult.status}).`);
      return;
    }
    if (lower === "ren" || lower === "rename") {
      if (args.length < 2) {
        append("Syntaxisfout.");
        return;
      }
      const sourcePath = resolveTerminalPath(args[0]);
      const sourceEntry = findNodeByPath(simulatorState, sourcePath);
      if (!sourceEntry || sourceEntry[1].type !== "file") {
        append("Bestand niet gevonden.");
        return;
      }
      const destinationPath = `${parentNativePath(sourcePath)}\\${args[1]}`;
      const createResult = await writeFileFromShell(destinationPath, sourceEntry[1].content ?? "", `rename ${baseNameForPath(sourcePath)}`);
      const deleteResult = createResult.ok ? await runFilesystemCommand("FILE_DELETE", `rename delete ${baseNameForPath(sourcePath)}`, { path: sourcePath }) : createResult;
      append(createResult.ok && deleteResult.ok ? "        1 bestand(en) hernoemd." : `rename mislukt (${createResult.status}/${deleteResult.status}).`);
      return;
    }
    if (lower === "tree") {
      append(`Folder PATH listing for volume Windows`, `Volume serial number is FD25-0428`, currentDir, ...treeLines(args[0] ? resolveTerminalPath(argText) : currentDir));
      return;
    }
    if (lower === "set") {
      append("COMPUTERNAME=CLIENT01", "USERDOMAIN=ADVANCED-TI", "USERNAME=Student", "SystemRoot=C:\\Windows", "TEMP=C:\\Users\\Student\\AppData\\Local\\Temp");
      return;
    }
    if (lower === "ping") {
      const host = args[0] ?? "srv01.advanced-ti.local";
      append(`Pinging ${host} [10.50.10.10] with 32 bytes of data:`, "Reply from 10.50.10.10: bytes=32 time<1ms TTL=128", "Reply from 10.50.10.10: bytes=32 time<1ms TTL=128", "", "Ping statistics for 10.50.10.10:", "    Packets: Sent = 2, Received = 2, Lost = 0 (0% loss)");
      return;
    }
    if (lower === "nslookup") {
      const host = args[0] ?? "srv01.advanced-ti.local";
      append("Server:  srv01.advanced-ti.local", "Address:  10.50.10.10", "", `Name:    ${host}`, "Address:  10.50.20.25");
      return;
    }
    if (lower === "netstat") {
      append("Active Connections", "", "  Proto  Local Address          Foreign Address        State", "  TCP    CLIENT01:49152         srv01:https            ESTABLISHED", "  TCP    CLIENT01:445           srv01:microsoft-ds     ESTABLISHED");
      return;
    }
    if (lower === "arp") {
      append("Interface: 10.50.20.25 --- 0x4", "  Internet Address      Physical Address      Type", "  10.50.20.1           00-15-5d-01-00-01     dynamic", "  10.50.10.10          00-15-5d-10-00-10     dynamic");
      return;
    }
    if (lower === "gpupdate") {
      append("Updating policy...", "Computer Policy update has completed successfully.", "User Policy update has completed successfully.");
      return;
    }
    if (lower === "tasklist") {
      append(...processRows());
      return;
    }
    if (lower === "taskkill") {
      const imageArg = args.find((value, index) => args[index - 1]?.toLowerCase() === "/im") ?? args.find((value) => !value.startsWith("/")) ?? "process.exe";
      await runSimulatorCommand("PROCESS_END", "local.process.write", `taskkill ${imageArg}`, { processName: imageArg });
      append(`SUCCESS: Sent termination signal to the process "${imageArg}".`);
      return;
    }
    if (lower === "shutdown") {
      await runSimulatorCommand("SYSTEM_REBOOT", "local.system.write", "shutdown", { machineId: "CLIENT01", args });
      append("Shutdown/restart is geregistreerd in de simulator.");
      return;
    }
    if (lower === "logoff") {
      append("U wordt afgemeld van de gesimuleerde sessie.");
      return;
    }
    if (lower === "chkdsk") {
      append("The type of the file system is NTFS.", "Windows has scanned the file system and found no problems.", "No further action is required.");
      return;
    }
    if (lower === "sfc") {
      append("Beginning system scan. This process will take some time.", "Verification 100% complete.", "Windows Resource Protection did not find any integrity violations.");
      return;
    }
    if (lower === "runas") {
      append("Simulatie: gebruik UAC-dialoog in de GUI voor verhoogde rechten.");
      return;
    }
    if (lower === "systeminfo") {
      append("Host Name:                 CLIENT01", "OS Name:                   Microsoft Windows 11 Enterprise", "OS Version:                10.0.26100 N/A Build 26100", "Domain:                    advanced-ti.local", "Logon Server:              \\\\SRV01");
      return;
    }
    if (lower === "net") {
      const sub = (args[0] ?? "").toLowerCase();
      if (sub === "use") {
        const letter = normalizeNativeDriveLetter(args[1] ?? "");
        const targetPath = args[2] ?? "";
        if (letter && args.some((arg) => arg.toLowerCase() === "/delete")) {
          const result = await runSimulatorCommand("NETWORK_DRIVE_UNMAP", "network.drive.write", `net use ${letter}: /delete`, { letter });
          append(result.ok ? `${letter}: is losgekoppeld.` : `net use /delete mislukt (${result.status}).`);
          return;
        }
        if (letter && targetPath) {
          const result = await runSimulatorCommand("NETWORK_DRIVE_MAP", "network.drive.write", `net use ${letter}:`, { letter, path: targetPath, persistent: true });
          append(result.ok ? `The command completed successfully. ${letter}: -> ${targetPath}` : `net use mislukt (${result.status}).`);
          return;
        }
        append("Nieuwe verbindingen worden onthouden.", ...networkDrivesFor(simulatorState).map((drive) => `${drive.letter ?? "Z:"}        ${drive.path ?? "\\\\SRV01\\Share"}`));
        return;
      }
      if (sub === "user") {
        const username = args[1];
        if (username && args.some((arg) => arg.toLowerCase() === "/add")) {
          const result = await runSimulatorCommand("LOCAL_USER_CREATE", "local.user.write", `net user ${username} /add`, { username, password: args[2], fullName: username });
          append(result.ok ? `The command completed successfully. User ${username} is aangemaakt.` : `net user /add mislukt (${result.status}).`);
          return;
        }
        if (username && args.some((arg) => arg.toLowerCase() === "/delete")) {
          const result = await runSimulatorCommand("LOCAL_USER_DELETE", "local.user.write", `net user ${username} /delete`, { username });
          append(result.ok ? `The command completed successfully. User ${username} is verwijderd.` : `net user /delete mislukt (${result.status}).`);
          return;
        }
        append("User accounts for \\\\CLIENT01", "", "-------------------------------------------------------------------------------", "Administrator            Student                 HelpDesk", "The command completed successfully.");
        return;
      }
      if (sub === "localgroup") {
        const groupName = args[1];
        const memberName = args.find((value, index) => index > 1 && !value.startsWith("/") && value !== groupName);
        if (groupName && memberName && args.some((arg) => arg.toLowerCase() === "/add")) {
          const result = await runSimulatorCommand("LOCAL_GROUP_ADD_MEMBER", "local.group.write", `net localgroup ${groupName} ${memberName} /add`, { groupName, username: memberName });
          append(result.ok ? `The command completed successfully. ${memberName} toegevoegd aan ${groupName}.` : `net localgroup /add mislukt (${result.status}).`);
          return;
        }
        if (groupName && args.some((arg) => arg.toLowerCase() === "/add")) {
          const result = await runSimulatorCommand("LOCAL_GROUP_CREATE", "local.group.write", `net localgroup ${groupName} /add`, { groupName });
          append(result.ok ? `The command completed successfully. Groep ${groupName} is aangemaakt.` : `net localgroup /add mislukt (${result.status}).`);
          return;
        }
        append("Aliases for \\\\CLIENT01", "", "-------------------------------------------------------------------------------", "Administrators", "Remote Desktop Users", "Users", "The command completed successfully.");
        return;
      }
      if (sub === "share") {
        append("Share name   Resource                        Remark", "-------------------------------------------------------------------------------", "C$           C:\\                             Default share", "Intake-161   C:\\Servicedesk\\Intake-161      Scenario share");
        return;
      }
    }
    if (lower === "netsh") {
      const ctx = args.join(" ").toLowerCase();
      if (ctx.includes("int ip set address")) {
        const ip = args.find((value) => /^\d+\.\d+\.\d+\.\d+$/.test(value)) ?? "10.50.20.25";
        const result = await runSimulatorCommand("NETWORK_SET_ADAPTER_CONFIG", "network.adapter.write", "netsh int ip set address", { adapterName: "Ethernet0", ipAddress: ip, subnetMask: "255.255.255.0", gateway: "10.50.20.1" });
        append(result.ok ? "Netsh: IP-adresconfiguratie bijgewerkt." : `Netsh mislukt (${result.status}).`);
        return;
      }
      if (ctx.includes("int ip set dns") || ctx.includes("interface ip set dns")) {
        const dnsServer = args.find((value) => /^\d+\.\d+\.\d+\.\d+$/.test(value)) ?? "10.50.10.10";
        const result = await runSimulatorCommand("NETWORK_SET_DNS_SERVER", "network.dnsClient.write", "netsh set dns", { adapterName: "Ethernet0", dnsServer });
        append(result.ok ? `Netsh: DNS-server ingesteld op ${dnsServer}.` : `Netsh DNS mislukt (${result.status}).`);
        return;
      }
      if (ctx.includes("advfirewall") && ctx.includes("set allprofiles state")) {
        const enabled = ctx.includes(" on") || ctx.endsWith("on");
        const result = await runSimulatorCommand("FIREWALL_SET_PROFILE", "firewall.profile.write", "netsh advfirewall set allprofiles state", { profile: "domain", enabled });
        append(result.ok ? `Netsh AdvFirewall: profielen zijn ${enabled ? "ingeschakeld" : "uitgeschakeld"}.` : `Netsh AdvFirewall mislukt (${result.status}).`);
        return;
      }
      if (ctx.includes("advfirewall") && ctx.includes("firewall add rule")) {
        const nameArg = args.find((value) => value.toLowerCase().startsWith("name="));
        const ruleName = nameArg ? nameArg.slice(nameArg.indexOf("=") + 1).replace(/^"|"$/g, "") : "Custom Rule";
        const result = await runSimulatorCommand("FIREWALL_ENABLE_RULE", "firewall.rule.write", `netsh firewall add rule ${ruleName}`, { ruleName, profile: "domain", enabled: true });
        append(result.ok ? `Ok. Firewallregel toegevoegd: ${ruleName}` : `Firewallregel mislukt (${result.status}).`);
        return;
      }
      append("Ok.", "Netsh-opdracht is in de simulator geregistreerd; gebruik Network Settings voor state-wijzigingen.");
      return;
    }
    if (lower === "sc") {
      const sub = (args[0] ?? "").toLowerCase();
      const serviceName = args[1] ?? "TermService";
      if (sub === "start" || sub === "stop") {
        const result = await runSimulatorCommand(sub === "start" ? "SERVICE_START" : "SERVICE_STOP", "service.write", `sc ${sub} ${serviceName}`, { serviceName });
        append(result.ok ? `[SC] ${sub === "start" ? "StartService" : "ControlService"} GESLAAGD` : `[SC] opdracht mislukt (${result.status}).`);
        return;
      }
      if (sub === "config") {
        const startupRaw = args.find((value, index) => args[index - 1]?.toLowerCase() === "start=") ?? args.find((value) => ["auto", "demand", "disabled"].includes(value.toLowerCase())) ?? "auto";
        const startupType = startupRaw.toLowerCase() === "auto" ? "automatic" : startupRaw.toLowerCase() === "demand" ? "manual" : startupRaw.toLowerCase();
        const result = await runSimulatorCommand("SERVICE_SET_STARTUP_TYPE", "service.write", `sc config ${serviceName}`, { serviceName, startupType });
        append(result.ok ? "[SC] ChangeServiceConfig GESLAAGD" : `[SC] config mislukt (${result.status}).`);
        return;
      }
      if (sub === "query") {
        append("SERVICE_NAME: TermService", "        TYPE               : 20  WIN32_SHARE_PROCESS", "        STATE              : 4  RUNNING");
        return;
      }
      append("SERVICE_NAME: TermService", "        TYPE               : 20  WIN32_SHARE_PROCESS", "        STATE              : 4  RUNNING");
      return;
    }
    append(`'${command}' is geregistreerd voor audit, maar heeft nog geen native shell-output.`);
  }

  return (
    <div className={isCmd ? "cmd-app native-cmd-surface" : "ps-app native-powershell-surface"} data-native-app={appId} data-native-parity={appId} onClick={() => inputRef.current?.focus()}>
      <div className={isCmd ? "cmd-output" : "ps-output"}>
        {lines.map((line, index) => (
          <div className={line.startsWith(currentDir) || line.startsWith("PS ") ? (isCmd ? "cmd-prompt-echo" : "ps-prompt-echo") : (isCmd ? "cmd-out" : "ps-out")} key={`${index}-${line}`}>
            {line || "\u00a0"}
          </div>
        ))}
        <div className={isCmd ? "cmd-input-row" : "ps-input-row"}>
          <span className={isCmd ? "cmd-prompt-span" : "ps-prompt"}>{prompt}</span>
          <input
            ref={inputRef}
            autoFocus
            className={isCmd ? "cmd-inp" : "ps-inp"}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void execute(input);
              if (event.key === "ArrowUp") {
                event.preventDefault();
                const nextIndex = historyIndex === null ? history.length - 1 : Math.max(0, historyIndex - 1);
                setHistoryIndex(nextIndex);
                setInput(history[nextIndex] ?? "");
              }
              if (event.key === "ArrowDown") {
                event.preventDefault();
                if (historyIndex === null) return;
                const nextIndex = historyIndex + 1;
                if (nextIndex >= history.length) {
                  setHistoryIndex(null);
                  setInput("");
                  return;
                }
                setHistoryIndex(nextIndex);
                setInput(history[nextIndex] ?? "");
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

function NotepadSurface({ initialPath, onRun, simulatorState }: { initialPath?: string; onRun: NativeRunAction; simulatorState: NativeSimulatorState | null }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [content, setContent] = useState("");
  const [fileName, setFileName] = useState("Naamloos");
  const [filePath, setFilePath] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [activeMenu, setActiveMenu] = useState<"bestand" | "bewerken" | "weergave" | null>(null);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsPath, setSaveAsPath] = useState("C:\\Users\\Student\\Desktop\\Scenario-notities.txt");
  const [position, setPosition] = useState("Regel 1, Kolom 1");
  const [message, setMessage] = useState("Windows 11 Kladblok parity surface");

  useEffect(() => {
    if (!initialPath) return;
    const entry = findNodeByPath(simulatorState, initialPath);
    const node = entry?.[1];
    if (!node || node.type !== "file") return;
    setContent(node.content ?? "");
    setFileName(node.name);
    setFilePath(normalizeNativePath(initialPath));
    setSaveAsPath(normalizeNativePath(initialPath));
    setDirty(false);
    setMessage(`Geopend: ${node.name}`);
  }, [initialPath, simulatorState]);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (!activeMenu) return;
      if (rootRef.current?.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (target.closest(".np-menubar") || target.closest(".np-menu-dropdown")) return;
      }
      setActiveMenu(null);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (saveAsOpen) {
        setSaveAsOpen(false);
        setMessage("Opslaan als geannuleerd");
        return;
      }
      if (activeMenu) setActiveMenu(null);
    }

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeMenu, saveAsOpen]);

  function updatePosition(target: HTMLTextAreaElement) {
    const textBeforeCursor = target.value.slice(0, target.selectionStart);
    const lines = textBeforeCursor.split("\n");
    setPosition(`Regel ${lines.length}, Kolom ${lines[lines.length - 1].length + 1}`);
  }

  function newDocument() {
    setContent("");
    setFileName("Naamloos");
    setFilePath(null);
    setDirty(false);
    setMessage("Nieuw document");
    setActiveMenu(null);
  }

  function openDocument() {
    const path = "C:\\Users\\Student\\Desktop\\readme.txt";
    const entry = findNodeByPath(simulatorState, path);
    setContent(entry?.[1].type === "file" ? (entry[1].content ?? "") : "");
    setFileName("readme.txt");
    setFilePath(path);
    setDirty(false);
    setMessage("Geopend: readme.txt");
    setActiveMenu(null);
  }

  function openSaveAsDialog() {
    setSaveAsPath(filePath ?? "C:\\Users\\Student\\Desktop\\Scenario-notities.txt");
    setSaveAsOpen(true);
    setActiveMenu(null);
  }

  async function commitSave(targetPath: string, forceCreate = false) {
    const normalizedPath = targetPath.trim();
    if (!normalizedPath) {
      setMessage("Geef een bestandsnaam op.");
      return;
    }
    const pathParts = normalizedPath.split("\\").filter(Boolean);
    const targetName = pathParts[pathParts.length - 1] || "Scenario-notities.txt";
    const parentPath = pathParts.length > 1 ? pathParts.slice(0, -1).join("\\") : "C:\\Users\\Student\\Desktop";
    const existing = findNodeByPath(null, normalizedPath) || findNodeByPath(simulatorState, normalizedPath);
    const commandType = (filePath || existing) && !forceCreate ? "FILE_WRITE_CONTENT" : "FILE_CREATE";
    for (const step of folderCreationStepsForPath(parentPath)) {
      await onRun({
        id: `notepad-folder-${Date.now()}-${step.name}`,
        appId: "explorer",
        appName: "File Explorer",
        commandType: "FILE_CREATE_FOLDER",
        capability: "file.folder.write",
        label: `Map ${step.name} voorbereiden`,
        description: "Zorg dat de simulator-desktopmap bestaat voordat Kladblok opslaat.",
        machine: "both",
        payload: step
      });
    }
    const result = await onRun({
      id: `notepad-${commandType.toLowerCase()}-${Date.now()}`,
      appId: "notepad",
      appName: "Kladblok",
      commandType,
      capability: "file.file.write",
      label: forceCreate || !filePath ? `Opslaan als ${targetName}` : `Opslaan ${targetName}`,
      description: "Sla de tekstinhoud op via de native v2 command-bus.",
      machine: "both",
      payload: {
        path: normalizedPath,
        parentPath,
        name: targetName,
        content
      }
    });
    if (!result.ok) {
      setMessage(`Opslaan mislukt (${result.status}).`);
      return;
    }
    setFileName(targetName);
    setFilePath(normalizedPath);
    setDirty(false);
    setSaveAsOpen(false);
    setMessage(forceCreate || !filePath ? `Opgeslagen als: ${targetName}` : `Opgeslagen: ${targetName}`);
    setActiveMenu(null);
  }

  function saveDocument(saveAs = false) {
    if (saveAs || !filePath) {
      openSaveAsDialog();
      return;
    }
    void commitSave(filePath);
  }

  return (
    <div className="notepad-app" data-native-app="notepad" ref={rootRef}>
      <div className="np-menubar">
        <button className={`np-menu-item ${activeMenu === "bestand" ? "is-active" : ""}`} data-menu="bestand" type="button" onClick={() => setActiveMenu(activeMenu === "bestand" ? null : "bestand")}>Bestand</button>
        <button className={`np-menu-item ${activeMenu === "bewerken" ? "is-active" : ""}`} data-menu="bewerken" type="button" onClick={() => setActiveMenu(activeMenu === "bewerken" ? null : "bewerken")}>Bewerken</button>
        <button className={`np-menu-item ${activeMenu === "weergave" ? "is-active" : ""}`} data-menu="weergave" type="button" onClick={() => setActiveMenu(activeMenu === "weergave" ? null : "weergave")}>Weergave</button>
      </div>

      {activeMenu === "bestand" ? (
        <div className="np-menu-dropdown" role="menu" aria-label="Bestand">
          <button type="button" role="menuitem" onClick={newDocument}><span>Nieuw</span><kbd>Ctrl+N</kbd></button>
          <button type="button" role="menuitem" onClick={openDocument}><span>Openen...</span><kbd>Ctrl+O</kbd></button>
          <hr />
          <button type="button" role="menuitem" onClick={() => saveDocument(false)}><span>Opslaan</span><kbd>Ctrl+S</kbd></button>
          <button type="button" role="menuitem" onClick={() => saveDocument(true)}><span>Opslaan als...</span><kbd>Ctrl+Shift+S</kbd></button>
          <hr />
          <button type="button" role="menuitem" onClick={() => setMessage("Afsluiten loopt via de vensterknop rechtsboven.")}><span>Afsluiten</span><kbd>Alt+F4</kbd></button>
        </div>
      ) : null}

      {activeMenu === "bewerken" ? (
        <div className="np-menu-dropdown" role="menu" aria-label="Bewerken">
          <button type="button" role="menuitem" onClick={() => setMessage("Ongedaan maken is lokaal gesimuleerd.")}>Ongedaan maken</button>
          <hr />
          <button type="button" role="menuitem" onClick={() => setMessage("Knippen is beschikbaar in de browserselectie.")}>Knippen</button>
          <button type="button" role="menuitem" onClick={() => setMessage("Kopieren is beschikbaar in de browserselectie.")}>Kopieren</button>
          <button type="button" role="menuitem" onClick={() => setMessage("Plakken is beschikbaar in de browserselectie.")}>Plakken</button>
          <hr />
          <button type="button" role="menuitem" onClick={() => setMessage("Gebruik Ctrl+A in de editor om alles te selecteren.")}>Alles selecteren</button>
        </div>
      ) : null}

      {activeMenu === "weergave" ? (
        <div className="np-menu-dropdown" role="menu" aria-label="Weergave">
          <button type="button" role="menuitem" onClick={() => setMessage("Word-wrap blijft uit, net als de legacy Notepad-standaard.")}>Terugloop</button>
          <button type="button" role="menuitem" onClick={() => setMessage("Statusbalk is zichtbaar.")}>Statusbalk</button>
        </div>
      ) : null}

      <textarea
        aria-label="Kladblok editor"
        className="np-editor"
        spellCheck={false}
        value={content}
        onChange={(event) => {
          setContent(event.target.value);
          setDirty(true);
          updatePosition(event.target);
        }}
        onClick={(event) => updatePosition(event.currentTarget)}
        onKeyDown={(event) => {
          if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
            event.preventDefault();
            saveDocument(event.shiftKey);
          }
        }}
        onKeyUp={(event) => updatePosition(event.currentTarget)}
      />
      {saveAsOpen ? (
        <section className="np-save-dialog" role="dialog" aria-label="Opslaan als">
          <header>
            <strong>Opslaan als</strong>
            <button type="button" aria-label="Sluiten" onClick={() => { setSaveAsOpen(false); setMessage("Opslaan als geannuleerd"); }}>x</button>
          </header>
          <label>
            Bestandsnaam:
            <input
              autoFocus
              value={saveAsPath}
              onChange={(event) => setSaveAsPath(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void commitSave(saveAsPath, true);
                if (event.key === "Escape") {
                  setSaveAsOpen(false);
                  setMessage("Opslaan als geannuleerd");
                }
              }}
            />
          </label>
          <footer>
            <button type="button" className="is-primary" onClick={() => void commitSave(saveAsPath, true)}>Opslaan</button>
            <button type="button" onClick={() => { setSaveAsOpen(false); setMessage("Opslaan als geannuleerd"); }}>Annuleren</button>
          </footer>
        </section>
      ) : null}
      <div className="np-statusbar">
        <span>{position}</span>
        <span>{message}</span>
        <span>{dirty ? `* ${fileName}` : fileName}</span>
      </div>
    </div>
  );
}

function formatPayloadValue(value: unknown) {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object" && value !== null) return JSON.stringify(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value === undefined || value === null) return "";
  return String(value);
}

function dialogProfileFor(action: NativeSimulatorAction) {
  if (action.appId === "explorer" && action.commandType === "FILE_CREATE_FOLDER") {
    return { title: "New Folder", intro: "Create a new folder in File Explorer.", kind: "explorer", steps: ["Choose location", "Enter folder name", "Create folder"] };
  }
  if (action.appId === "explorer" && action.commandType === "SHARE_CREATE") {
    return { title: "Network Access", intro: "Share this folder with selected people and permissions.", kind: "explorer", steps: ["Select folder", "Choose people", "Set permissions"] };
  }
  if (action.appId === "explorer" && action.commandType === "FILE_SET_PERMISSION") {
    return { title: "Advanced Security Settings", intro: "Update NTFS permissions for this folder.", kind: "explorer", steps: ["Select principal", "Choose permission", "Apply ACL"] };
  }
  if (action.appId === "aduc" && action.commandType === "SERVER_CREATE_OU") {
    return { title: "New Object - Organizational Unit", intro: "Create an OU in Active Directory Users and Computers.", kind: "mmc", steps: ["Container", "OU name", "Protect object"] };
  }
  if (action.appId === "aduc" && action.commandType === "SERVER_CREATE_DOMAIN_USER") {
    return { title: "New Object - User", intro: "Create a domain user in Active Directory Users and Computers.", kind: "mmc", steps: ["User details", "Account options", "Finish"] };
  }
  if (action.appId === "aduc" && action.commandType === "SERVER_CREATE_DOMAIN_GROUP") {
    return { title: "New Object - Group", intro: "Create a domain security group in the selected OU.", kind: "mmc", steps: ["Group name", "Scope and type", "Finish"] };
  }
  if (action.appId === "aduc") {
    return { title: "Object Properties", intro: "Apply the selected Active Directory object change.", kind: "mmc", steps: ["Select object", "Configure property", "Apply"] };
  }
  if (action.appId === "dns-manager" && action.commandType === "SERVER_DNS_CREATE_ZONE") {
    return { title: "New Zone Wizard", intro: "Create a DNS forward lookup zone on SRV01.", kind: "mmc", steps: ["Zone type", "Zone name", "Replication"] };
  }
  if (action.appId === "dns-manager" && action.commandType === "SERVER_DNS_ADD_RECORD") {
    return { title: `New ${formatPayloadValue(action.payload.recordType)} Record`, intro: "Add a DNS resource record to the selected zone.", kind: "mmc", steps: ["Record type", "Name and address", "Create record"] };
  }
  if (action.appId === "server-manager" && action.commandType === "SERVER_ROLE_INSTALL") {
    return { title: "Add Roles and Features Wizard", intro: "Install a server role through Server Manager.", kind: "server", steps: ["Before you begin", "Server roles", "Confirmation"] };
  }
  if (action.appId === "server-manager" && action.commandType === "SERVER_DC_PROMOTE") {
    return { title: "Active Directory Domain Services Configuration Wizard", intro: "Promote SRV01 to a domain controller.", kind: "server", steps: ["Deployment configuration", "Domain controller options", "Prerequisites check"] };
  }
  if (action.appId === "dhcpManager") {
    return { title: action.commandType === "DHCP_CREATE_SCOPE" ? "New Scope Wizard" : "DHCP Properties", intro: "Configure DHCP from the MMC console.", kind: "mmc", steps: ["Scope or option", "Address and lease", "Activate"] };
  }
  if (action.appId === "gpmc") {
    return { title: "Group Policy Management", intro: "Apply the selected GPO action through the native MMC surface.", kind: "mmc", steps: ["Select domain or OU", "Configure policy object", "Commit"] };
  }
  if (action.appId === "services") {
    return { title: "Service Properties", intro: "Change service status or startup type.", kind: "mmc", steps: ["Select service", "Set state", "Apply"] };
  }
  if (action.appId === "wf") {
    return { title: "New Inbound Rule Wizard", intro: "Configure Windows Defender Firewall rule state.", kind: "mmc", steps: ["Rule type", "Profile", "Action"] };
  }
  if (action.appId === "eventvwr") {
    return { title: action.commandType === "EVENT_VIEWER_FILTER" ? "Filter Current Log" : "Event Properties", intro: "Inspect or filter Event Viewer data.", kind: "mmc", steps: ["Select log", "Set criteria", "Review event"] };
  }
  if (["lusrmgr", "netplwiz", "secpol", "gpedit", "taskschd", "taskmgr", "devmgmt", "diskmgmt", "msinfo32"].includes(action.appId)) {
    const titles: Record<string, string> = {
      devmgmt: "Device Properties",
      diskmgmt: "Disk Management",
      gpedit: "Local Group Policy setting",
      lusrmgr: "Local Users and Groups",
      msinfo32: "System Information",
      netplwiz: "User Accounts",
      secpol: "Local Security Policy",
      taskmgr: "Task Manager",
      taskschd: "Task Scheduler"
    };
    return { title: titles[action.appId] ?? action.label, intro: action.description, kind: "mmc", steps: ["Select item", "Configure", "Apply"] };
  }
  if (["appwiz", "optionalfeatures", "windowsUpdate", "defender", "bitlocker", "controlPrinters", "ncpa", "regedit", "rebootSimulator", "powercfg", "settingsSystem", "uac"].includes(action.appId)) {
    return { title: metaFor(action.appId).windowTitle, intro: action.description, kind: "settings", steps: ["Open settings", "Choose option", "Apply"] };
  }
  if (["entraAdmin", "entraAudit", "entraUsers", "entraGroups", "entraDevices", "entraRoles", "conditionalAccess", "authMethods", "enterpriseApps", "intuneDevices", "compliancePolicies", "configurationProfiles", "m365Users", "m365Licenses", "defenderPortal"].includes(action.appId)) {
    return { title: `${metaFor(action.appId).windowTitle} wizard`, intro: action.description, kind: "settings", steps: ["Select tenant object", "Configure details", "Review and create"] };
  }
  if (["adSites", "adTrusts", "certAuthority", "iisManager", "hyperVManager", "printManagement", "dfsManagement", "npsManager", "windowsServerBackup", "failoverCluster", "fsrmManager", "storageManager"].includes(action.appId)) {
    return { title: `${metaFor(action.appId).windowTitle} action`, intro: action.description, kind: "mmc", steps: ["Select server node", "Configure object", "Finish"] };
  }
  if (["calc", "paint", "snippingTool", "clipboardHistory", "credentialManager", "certmgr", "certlm", "diskCleanup", "systemRestore", "reliabilityMonitor", "resourceMonitor"].includes(action.appId)) {
    return { title: metaFor(action.appId).windowTitle, intro: action.description, kind: "generic", steps: ["Open utility", "Perform action", "Record evidence"] };
  }
  if (action.appId === "netSettings") {
    return { title: "Edit DNS settings", intro: "Apply the selected network adapter DNS configuration.", kind: "settings", steps: ["Select adapter", "Edit DNS", "Save"] };
  }
  if (action.appId === "sysdm") {
    return { title: "Computer Name/Domain Changes", intro: "Join this computer to the specified domain.", kind: "system", steps: ["Computer name", "Domain", "Restart prompt"] };
  }
  return { title: action.label, intro: action.description, kind: "generic", steps: ["Review", "Apply"] };
}

function ActionStrip({
  actions,
  busyActionId,
  onRun
}: {
  actions: NativeSimulatorAction[];
  busyActionId: string | null;
  onRun: NativeRunAction;
}) {
  const [pendingAction, setPendingAction] = useState<NativeSimulatorAction | null>(null);
  const lastTriggerRef = useRef<HTMLButtonElement | null>(null);
  const profile = pendingAction ? dialogProfileFor(pendingAction) : null;

  function closeDialog() {
    setPendingAction(null);
    window.setTimeout(() => lastTriggerRef.current?.focus(), 0);
  }

  useEffect(() => {
    if (!pendingAction) return undefined;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") closeDialog();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [pendingAction]);

  if (actions.length === 0) {
    return <p className="win11-empty-state">Deze app is beschikbaar. Er zijn voor dit ticket geen extra taakacties nodig.</p>;
  }

  return (
    <>
      <details className="win11-action-proof">
        <summary>Bewijsacties</summary>
        <div className="win11-action-strip" aria-label="Bewijsacties">
          {actions.map((action) => (
            <button
              disabled={busyActionId === action.id}
              key={action.id}
              type="button"
              title={action.description}
              onClick={(event) => {
                lastTriggerRef.current = event.currentTarget;
                setPendingAction(action);
              }}
            >
              <MmcGlyph kind={actionIconFor(action.commandType)} />
              <strong>{busyActionId === action.id ? "Bezig..." : action.label}</strong>
              <span className="win11-action-strip-chevron" aria-hidden="true">&gt;</span>
            </button>
          ))}
        </div>
      </details>
      {pendingAction && profile ? (
        <section className={`win11-app-dialog win11-app-dialog--${profile.kind}`} role="dialog" aria-label="Bewijsactie">
          <header>
            <strong>{profile.title}</strong>
            <button aria-label="Sluiten" type="button" onClick={closeDialog}>x</button>
          </header>
          <p>{profile.intro}</p>
          <div className="win11-action-proof-summary">
            <MmcGlyph kind={actionIconFor(pendingAction.commandType)} />
            <strong>{pendingAction.label}</strong>
            <small>{pendingAction.description}</small>
          </div>
          <footer>
            <button type="button" onClick={closeDialog}>Annuleren</button>
            <button
              className="is-primary"
              type="button"
              onClick={() => {
                void onRun(pendingAction);
                closeDialog();
              }}
            >
              Uitvoeren
            </button>
          </footer>
        </section>
      ) : null}
    </>
  );
}

function actionIconFor(commandType: string) {
  if (commandType.includes("DELETE") || commandType.includes("UNINSTALL") || commandType.includes("DISABLE")) return "clear";
  if (commandType.includes("CREATE") || commandType.includes("ADD") || commandType.includes("INSTALL") || commandType.includes("ENABLE")) return "add";
  if (commandType.includes("SET") || commandType.includes("UPDATE") || commandType.includes("WRITE") || commandType.includes("ASSIGN")) return "edit";
  if (commandType.includes("START") || commandType.includes("RUN") || commandType.includes("EXECUTE")) return "play";
  if (commandType.includes("STOP") || commandType.includes("PAUSE")) return "stop";
  if (commandType.includes("CHECK") || commandType.includes("FILTER") || commandType.includes("RECORD")) return "search";
  if (commandType.includes("RESET") || commandType.includes("REBOOT")) return "refresh";
  return "settings";
}

type NativeWindowState = {
  appId: string;
  height: number;
  id: string;
  initialUrl?: string;
  minimized: boolean;
  maximized: boolean;
  title: string;
  width: number;
  x: number;
  y: number;
  zIndex: number;
};

type NativeStartAppView = {
  appId: string;
  name: string;
  family: NativeAppCatalogEntry["family"] | "system";
  machine: NativeAppCatalogEntry["machine"];
  recommended: boolean;
};

type WindowInteractionState = {
  height: number;
  kind: "drag" | "resize";
  startX: number;
  startY: number;
  width: number;
  windowId: string;
  x: number;
  y: number;
} | null;

function titleForWindow(appId: string, simulatorApps: StudentSimulatorAppView[]) {
  const meta = metaFor(appId);
  return meta.windowTitle === appId ? appNameFor(appId, simulatorApps) : meta.windowTitle;
}

function createNativeWindow(appId: string, simulatorApps: StudentSimulatorAppView[], zIndex: number, offset: number, initialUrl?: string): NativeWindowState {
  const normalized = canonicalAppId(appId);
  const isNotepad = normalized === "notepad";
  const isEdge = normalized === "edge";
  const isSmallUtility = ["calc", "uac"].includes(normalized);
  const baseHeight = isNotepad ? 480 : isSmallUtility ? 440 : isEdge ? 620 : 620;
  const baseWidth = isNotepad ? 680 : isSmallUtility ? 520 : isEdge ? 1000 : 940;
  const baseX = isNotepad ? 150 + offset * 18 : 92 + offset * 28;
  const baseY = isNotepad ? 92 + offset * 18 : 54 + offset * 24;
  const desktopHeight = typeof window === "undefined" ? 760 : window.innerHeight;
  const desktopWidth = typeof window === "undefined" ? 1280 : window.innerWidth;
  const taskbarHeight = 58;
  const maxHeight = Math.max(320, desktopHeight - taskbarHeight - baseY - 12);
  const maxWidth = Math.max(420, desktopWidth - baseX - 12);
  return {
    appId: normalized,
    height: Math.min(baseHeight, maxHeight),
    id: `${normalized}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    initialUrl,
    minimized: false,
    maximized: false,
    title: titleForWindow(normalized, simulatorApps),
    width: Math.min(baseWidth, maxWidth),
    x: baseX,
    y: baseY,
    zIndex
  };
}

function fitMenuPosition(x: number, y: number) {
  const menuWidth = 224;
  const menuHeight = 260;
  if (typeof window === "undefined") return { left: x, top: y };
  return {
    left: Math.max(8, Math.min(x, window.innerWidth - menuWidth - 8)),
    top: Math.max(8, Math.min(y, window.innerHeight - menuHeight - 8))
  };
}

function stateForMachine(state: NativeSimulatorState | null, machine: MachineKey): NativeSimulatorState | null {
  const machineState = state?.machines?.[machine];
  if (!state || !machineState) return state;
  return { ...state, ...machineState };
}

export function NativeSimulatorDesktop({
  activeMachine,
  attemptId,
  initialShellPhase = "locked",
  onCommandComplete,
  onCommandMessage,
  simulatorApps,
  ticketId
}: NativeSimulatorDesktopProps) {
  const desktopRef = useRef<HTMLDivElement | null>(null);
  const [windows, setWindows] = useState<NativeWindowState[]>([]);
  const [activeWindowId, setActiveWindowId] = useState("");
  const [query, setQuery] = useState("");
  const [startOpen, setStartOpen] = useState(false);
  const [allAppsOpen, setAllAppsOpen] = useState(false);
  const [busyActionId, setBusyActionId] = useState<string | null>(null);
  const [clock, setClock] = useState(() => new Date());
  const [commandLog, setCommandLog] = useState<Array<{ id: string; label: string; ok: boolean; status: number }>>([]);
  const [proofOpen, setProofOpen] = useState(false);
  const [, setZCounter] = useState(3);
  const [windowInteraction, setWindowInteraction] = useState<WindowInteractionState>(null);
  const [runOpen, setRunOpen] = useState(false);
  const [runValue, setRunValue] = useState("");
  const [contextMenu, setContextMenu] = useState<DesktopContextMenu>(null);
  const [trayFlyout, setTrayFlyout] = useState<TrayFlyout>(null);
  const [elevatedPrompt, setElevatedPrompt] = useState<ElevatedPrompt>(null);
  const [simulatorState, setSimulatorState] = useState<NativeSimulatorState | null>(null);
  const [shellPhase, setShellPhase] = useState<ShellPhase>(initialShellPhase);
  const [loginPassword, setLoginPassword] = useState("");
  const activeSimulatorState = useMemo(
    () => stateForMachine(simulatorState, activeMachine),
    [activeMachine, simulatorState]
  );

  const actions = useMemo(() => visibleActions(activeMachine), [activeMachine]);
  const scenarioAppIds = useMemo(() => new Set(simulatorApps.map((app) => canonicalAppId(app.appId))), [simulatorApps]);
  const startPinnedIds = activeMachine === "CLIENT01" ? clientStartPinnedApps : serverStartPinnedApps;
  const taskbarPinnedIds = activeMachine === "CLIENT01" ? clientTaskbarPinnedApps : serverTaskbarPinnedApps;
  const startPinnedApps: NativeStartAppView[] = startPinnedIds.map((appId) => {
    const catalogEntry = nativeAppCatalog.find((app) => canonicalAppId(app.appId) === canonicalAppId(appId));
    return {
      appId,
      name: legacyShellNameFor(appId, simulatorApps),
      family: catalogEntry?.family ?? "system",
      machine: catalogEntry?.machine ?? "both",
      recommended: scenarioAppIds.has(canonicalAppId(appId))
    };
  });
  const taskbarPinnedApps: NativeStartAppView[] = taskbarPinnedIds.map((appId) => {
    const catalogEntry = nativeAppCatalog.find((app) => canonicalAppId(app.appId) === canonicalAppId(appId));
    return {
      appId,
      name: legacyShellNameFor(appId, simulatorApps),
      family: catalogEntry?.family ?? "system",
      machine: catalogEntry?.machine ?? "both",
      recommended: scenarioAppIds.has(canonicalAppId(appId))
    };
  });
  const startApps: NativeStartAppView[] = useMemo(() => {
    const queryText = query.trim().toLowerCase();
    return nativeAppCatalog
      .filter((app) => !queryText || `${app.name} ${app.appId} ${app.family} ${app.aliases.join(" ")}`.toLowerCase().includes(queryText))
      .sort((left, right) => {
        if (left.machine === activeMachine && right.machine !== activeMachine) return -1;
        if (right.machine === activeMachine && left.machine !== activeMachine) return 1;
        return left.name.localeCompare(right.name, "nl");
      })
      .map((app) => ({
        appId: app.appId,
        name: app.name,
        family: app.family,
        machine: app.machine,
        recommended: scenarioAppIds.has(canonicalAppId(app.appId))
      }));
  }, [activeMachine, query, scenarioAppIds]);
  const visibleStartApps = query.trim() || allAppsOpen ? startApps : startPinnedApps;
  const desktopItems = childrenForPath(activeSimulatorState, "C:\\Users\\Student\\Desktop");
  const contextMenuPosition = contextMenu ? fitMenuPosition(contextMenu.x, contextMenu.y) : null;

  function closeTransientShell() {
    setContextMenu(null);
    setStartOpen(false);
    setAllAppsOpen(false);
    setRunOpen(false);
    setTrayFlyout(null);
    setElevatedPrompt(null);
  }

  function completeShellSignIn() {
    setShellPhase("desktop");
    setLoginPassword("");
  }

  function openFirstSearchResult() {
    const firstResult = visibleStartApps[0];
    if (!firstResult) return;
    openApp(firstResult.appId);
  }

  useEffect(() => {
    setWindows([]);
    setActiveWindowId("");
    setZCounter(3);
    setStartOpen(false);
    setAllAppsOpen(false);
    setRunOpen(false);
    setContextMenu(null);
    setTrayFlyout(null);
    setElevatedPrompt(null);
    setShellPhase(initialShellPhase);
    setLoginPassword("");
  }, [activeMachine, initialShellPhase]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (shellPhase === "locked") {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setShellPhase("signIn");
        }
        return;
      }
      if (shellPhase === "signIn") {
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "r") {
        event.preventDefault();
        setRunOpen(true);
        setStartOpen(false);
        setContextMenu(null);
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "v") {
        event.preventDefault();
        openApp("clipboardHistory");
        return;
      }
      if (event.ctrlKey && event.key === "Escape") {
        event.preventDefault();
        setStartOpen((current) => !current);
        setRunOpen(false);
        setContextMenu(null);
        return;
      }
      if (event.key !== "Escape") return;
      if (runOpen) {
        setRunOpen(false);
        return;
      }
      if (contextMenu) {
        setContextMenu(null);
        return;
      }
      if (trayFlyout) {
        setTrayFlyout(null);
        return;
      }
      if (startOpen) {
        setStartOpen(false);
        setAllAppsOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // openApp is a hoisted shell action; these state deps intentionally refresh the global shortcut handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu, runOpen, shellPhase, startOpen, trayFlyout]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!windowInteraction) return undefined;
    const interaction = windowInteraction;

    function clamp(value: number, min: number, max: number) {
      return Math.max(min, Math.min(value, max));
    }

    function handleMove(event: MouseEvent) {
      const bounds = desktopRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const taskbarHeight = 58;
      setWindows((current) => current.map((windowState) => {
        if (windowState.id !== interaction.windowId) return windowState;
        if (interaction.kind === "drag") {
          const nextX = clamp(interaction.x + event.clientX - interaction.startX, 0, Math.max(0, bounds.width - windowState.width));
          const nextY = clamp(interaction.y + event.clientY - interaction.startY, 0, Math.max(0, bounds.height - taskbarHeight - windowState.height));
          return { ...windowState, x: nextX, y: nextY };
        }
        const nextWidth = clamp(interaction.width + event.clientX - interaction.startX, 380, Math.max(420, bounds.width - windowState.x - 12));
        const nextHeight = clamp(interaction.height + event.clientY - interaction.startY, 260, Math.max(300, bounds.height - taskbarHeight - windowState.y - 12));
        return { ...windowState, width: nextWidth, height: nextHeight };
      }));
    }

    function handleUp() {
      setWindowInteraction(null);
    }

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [windowInteraction]);

  const refreshSimulatorState = useCallback(async () => {
    const response = await fetch(`/api/v2/simulator/attempts/${encodeURIComponent(attemptId)}/state`, {
      credentials: "include"
    });
    if (!response.ok) return;
    const body = await response.json().catch(() => undefined) as { state?: NativeSimulatorState } | undefined;
    if (body?.state) setSimulatorState(body.state);
  }, [attemptId]);

  useEffect(() => {
    void refreshSimulatorState();
  }, [refreshSimulatorState]);

  const dispatchNativeCommand = useCallback(async (
    appId: string,
    commandType: string,
    capability: string,
    payload: Record<string, unknown>,
    requestId = `native-${Date.now()}-${Math.random().toString(16).slice(2)}`
  ): Promise<NativeCommandResult> => {
    const response = await fetch(`/api/v2/simulator/attempts/${encodeURIComponent(attemptId)}/commands`, {
      credentials: "include",
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ appId, commandType, capability, payload: { ...payload, machineId: activeMachine } })
    });
    const body = await response.json().catch(() => undefined);
    return {
      type: "SIMULATOR_COMMAND_RESULT",
      requestId,
      ok: response.ok,
      status: response.status,
      body
    };
  }, [activeMachine, attemptId]);

  useEffect(() => {
    window.__fdAttemptId = attemptId;
    window.fdBridge = { dispatchV2Command: dispatchNativeCommand };
    return () => {
      if (window.__fdAttemptId === attemptId) delete window.__fdAttemptId;
      delete window.fdBridge;
    };
  }, [attemptId, dispatchNativeCommand]);

  async function runAction(action: NativeSimulatorAction): Promise<NativeCommandResult> {
    setBusyActionId(action.id);
    try {
      const result = await dispatchNativeCommand(action.appId, action.commandType, action.capability, action.payload);
      setCommandLog((current) => [
        { id: `${action.id}-${Date.now()}`, label: action.label, ok: result.ok, status: result.status },
        ...current
      ].slice(0, 6));
      onCommandMessage?.(result.ok
        ? `${action.label} is vastgelegd in de v2 command-bus.`
        : `${action.label} kon niet worden vastgelegd (${result.status}).`);
      if (result.ok) await refreshSimulatorState();
      onCommandComplete?.(result, action);
      return result;
    } catch {
      const failedResult: NativeCommandResult = {
        type: "SIMULATOR_COMMAND_RESULT",
        requestId: `native-failed-${Date.now()}`,
        ok: false,
        status: 0
      };
      setCommandLog((current) => [
        { id: `${action.id}-${Date.now()}`, label: action.label, ok: false, status: 0 },
        ...current
      ].slice(0, 6));
      onCommandMessage?.(`${action.label} kon niet worden vastgelegd.`);
      return failedResult;
    } finally {
      setBusyActionId(null);
    }
  }

  function focusWindow(windowId: string) {
    setActiveWindowId(windowId);
    setZCounter((currentZ) => {
      const nextZ = currentZ + 1;
      setWindows((current) => current.map((windowState) => (
        windowState.id === windowId ? { ...windowState, zIndex: nextZ } : windowState
      )));
      return nextZ;
    });
  }

  function openApp(appId: string, launchUrl?: string) {
    const normalized = canonicalAppId(appId);
    if (elevatedAppIds.has(normalized)) {
      setElevatedPrompt({ appId: normalized, appName: appNameFor(normalized, simulatorApps) });
    }
    setZCounter((currentZ) => {
      const nextZ = currentZ + 1;
      let focusId = "";
      setWindows((current) => {
        const existing = current.find((windowState) => windowState.appId === normalized);
        if (existing) {
          focusId = existing.id;
          setActiveWindowId(existing.id);
          const bounds = desktopRef.current?.getBoundingClientRect();
          const taskbarHeight = 58;
          const maxHeight = bounds ? Math.max(320, bounds.height - taskbarHeight - existing.y - 8) : existing.height;
          const maxWidth = bounds ? Math.max(420, bounds.width - existing.x - 8) : existing.width;
          return current.map((windowState) => (
            windowState.id === existing.id
              ? {
                ...windowState,
                height: Math.min(windowState.height, maxHeight),
                initialUrl: launchUrl ?? windowState.initialUrl,
                minimized: false,
                width: Math.min(windowState.width, maxWidth),
                zIndex: nextZ
              }
              : windowState
          ));
        }
        const nextWindow = createNativeWindow(normalized, simulatorApps, nextZ, current.length, launchUrl);
        const bounds = desktopRef.current?.getBoundingClientRect();
        const taskbarHeight = 58;
        const maxHeight = bounds ? Math.max(320, bounds.height - taskbarHeight - nextWindow.y - 8) : nextWindow.height;
        const maxWidth = bounds ? Math.max(420, bounds.width - nextWindow.x - 8) : nextWindow.width;
        const fittedWindow = {
          ...nextWindow,
          height: Math.min(nextWindow.height, maxHeight),
          width: Math.min(nextWindow.width, maxWidth)
        };
        focusId = fittedWindow.id;
        setActiveWindowId(fittedWindow.id);
        return [...current, fittedWindow];
      });
      if (focusId) setActiveWindowId(focusId);
      return nextZ;
    });
    setStartOpen(false);
    setRunOpen(false);
    setContextMenu(null);
    setTrayFlyout(null);
  }

  function executeRunCommand() {
    const rawValue = runValue.trim();
    const value = rawValue.toLowerCase();
    const isUrlLike = value.includes(".") || value.startsWith("http://") || value.startsWith("https://");
    const appId = runAliases[value]
      ?? nativeAppCatalog.find((app) => app.appId.toLowerCase() === value || app.name.toLowerCase().includes(value))?.appId
      ?? simulatorApps.find((app) => app.appId.toLowerCase() === value || app.name.toLowerCase().includes(value))?.appId;
    if (appId) {
      openApp(appId, appId === "edge" && isUrlLike ? normalizeBrowserUrl(rawValue) : undefined);
      setRunValue("");
    }
  }

  function closeWindow(windowId: string) {
    setWindows((current) => {
      const remaining = current.filter((windowState) => windowState.id !== windowId);
      if (activeWindowId === windowId) {
        const nextActive = [...remaining].filter((windowState) => !windowState.minimized).sort((left, right) => right.zIndex - left.zIndex)[0];
        setActiveWindowId(nextActive?.id ?? "");
      }
      return remaining;
    });
    setRunOpen(false);
    setStartOpen(false);
    setAllAppsOpen(false);
    setContextMenu(null);
  }

  function minimizeWindow(windowId: string) {
    setWindows((current) => {
      const nextWindows = current.map((windowState) => (
        windowState.id === windowId ? { ...windowState, minimized: true } : windowState
      ));
      if (activeWindowId === windowId) {
        const nextActive = nextWindows.filter((windowState) => windowState.id !== windowId && !windowState.minimized).sort((left, right) => right.zIndex - left.zIndex)[0];
        setActiveWindowId(nextActive?.id ?? "");
      }
      return nextWindows;
    });
  }

  function toggleMaximizeWindow(windowId: string) {
    setWindows((current) => current.map((windowState) => (
      windowState.id === windowId ? { ...windowState, maximized: !windowState.maximized, minimized: false } : windowState
    )));
    focusWindow(windowId);
  }

  function startWindowDrag(event: ReactMouseEvent<HTMLElement>, windowState: NativeWindowState) {
    if (event.button !== 0 || windowState.maximized) return;
    event.preventDefault();
    event.stopPropagation();
    focusWindow(windowState.id);
    setWindowInteraction({
      height: windowState.height,
      kind: "drag",
      startX: event.clientX,
      startY: event.clientY,
      width: windowState.width,
      windowId: windowState.id,
      x: windowState.x,
      y: windowState.y
    });
  }

  function startWindowResize(event: ReactMouseEvent<HTMLElement>, windowState: NativeWindowState) {
    if (event.button !== 0 || windowState.maximized) return;
    event.preventDefault();
    event.stopPropagation();
    focusWindow(windowState.id);
    setWindowInteraction({
      height: windowState.height,
      kind: "resize",
      startX: event.clientX,
      startY: event.clientY,
      width: windowState.width,
      windowId: windowState.id,
      x: windowState.x,
      y: windowState.y
    });
  }

  function handleTaskbarAppClick(appId: string) {
    const normalized = canonicalAppId(appId);
    const runningWindow = windows.find((windowState) => windowState.appId === normalized);
    if (!runningWindow) {
      openApp(normalized);
      return;
    }
    if (activeWindowId === runningWindow.id && !runningWindow.minimized) {
      minimizeWindow(runningWindow.id);
      return;
    }
    setWindows((current) => current.map((windowState) => (
      windowState.id === runningWindow.id ? { ...windowState, minimized: false } : windowState
    )));
    focusWindow(runningWindow.id);
  }

  return (
    <section
      aria-label="Native simulator"
      className={`native-sim-frame native-sim-frame--${activeMachine.toLowerCase()}`}
      data-attempt-id={attemptId}
      data-machine={activeMachine}
      data-simulator-runtime="native-react"
      data-ticket-id={ticketId}
    >
      <div
        ref={desktopRef}
        className="win11-desktop-shell"
        onClick={closeTransientShell}
        onContextMenu={(event) => {
          event.preventDefault();
          setStartOpen(false);
          setRunOpen(false);
          setContextMenu({ x: event.clientX, y: event.clientY });
        }}
      >
        <div className="win11-wallpaper" />
        <div className="win11-desktop-brand">
          <strong>{activeMachine}</strong>
          <span>{machineLabel(activeMachine)}</span>
        </div>

        {shellPhase === "locked" ? (
          <button
            className="win11-lockscreen"
            data-native-parity="shell-lockscreen-flow"
            id="lockscreen"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setShellPhase("signIn");
            }}
          >
            <time>{clock.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</time>
            <span>{clock.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}</span>
            <small>Klik om te ontgrendelen</small>
          </button>
        ) : null}

        {shellPhase === "signIn" ? (
          <section
            className="win11-login-screen"
            data-native-parity="shell-login-flow"
            id="login-form"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="win11-login-clock">
              <time>{clock.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</time>
              <span>{clock.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}</span>
            </div>
            <form
              className="win11-login-card"
              onSubmit={(event) => {
                event.preventDefault();
                completeShellSignIn();
              }}
            >
              <div className="win11-login-avatar"><WinGlyph name="person" /></div>
              <strong>Student</strong>
              <select aria-label="Gebruiker" defaultValue="student">
                <option value="student">Student (Student Gebruiker)</option>
                <option value="administrator">Administrator</option>
              </select>
              <div className="win11-login-password-row">
                <input
                  autoFocus
                  id="login-pw-input"
                  placeholder="Wachtwoord"
                  type="password"
                  value={loginPassword}
                  onChange={(event) => setLoginPassword(event.target.value)}
                />
                <button aria-label="Aanmelden" type="submit">-&gt;</button>
              </div>
              <small>(wachtwoord: Student1!)</small>
            </form>
          </section>
        ) : null}

        <div className="win11-desktop-icons" aria-label="Bureaublad snelkoppelingen">
          <button className="win11-desktop-icon win11-desktop-icon--recycle" type="button" aria-label="Prullenbak" onClick={(event) => { event.stopPropagation(); openApp("recycleBin"); }}>
            <AppIcon appId="recycleBin" />
            <span>Prullenbak</span>
          </button>
          {desktopItems.map(([nodeId, node]) => {
            const path = nodePath(fsNodesFor(activeSimulatorState), nodeId);
            const desktopAppIcon = nodeDesktopIconAppId(node);
            return (
              <button
                className="win11-desktop-icon win11-desktop-icon--file"
                key={nodeId}
                title={path}
                type="button"
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  openApp(node.type === "folder" ? "explorer" : node.name.toLowerCase().endsWith(".txt") ? "notepad" : "explorer");
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <AppIcon appId={desktopAppIcon} className="win11-desktop-file-icon" />
                <span>{node.name}</span>
              </button>
            );
          })}
        </div>

        {windows
          .filter((windowState) => !windowState.minimized)
          .sort((left, right) => left.zIndex - right.zIndex)
          .map((windowState) => {
            const windowActions = actions.filter((action) => canonicalAppId(action.appId) === canonicalAppId(windowState.appId));
            const isActive = activeWindowId === windowState.id;
            const style = windowState.maximized
              ? { height: "calc(100% - 58px)", left: 0, top: 0, width: "100%", zIndex: windowState.zIndex }
              : { height: windowState.height, left: windowState.x, top: windowState.y, width: windowState.width, zIndex: windowState.zIndex };
            return (
              <section
                className={`win11-window ${windowState.maximized ? "is-maximized" : ""} ${isActive ? "is-active" : ""} ${canonicalAppId(windowState.appId) === "notepad" ? "is-notepad-window" : ""}`}
                data-window-id={windowState.appId}
                key={windowState.id}
                onMouseDown={() => {
                  setTrayFlyout(null);
                  focusWindow(windowState.id);
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  focusWindow(windowState.id);
                }}
                onContextMenu={(event) => event.stopPropagation()}
                style={style}
              >
                <header
                  className="win11-titlebar"
                  onDoubleClick={() => toggleMaximizeWindow(windowState.id)}
                  onMouseDown={(event) => startWindowDrag(event, windowState)}
                >
                  <div className="win11-titlebar-left">
                    <AppIcon appId={windowState.appId} className="win11-titlebar-app-icon" />
                    <strong>{windowState.title}</strong>
                  </div>
                  <div className="win11-window-controls" onMouseDown={(event) => event.stopPropagation()}>
                    <button aria-label="Minimaliseren" type="button" onClick={() => minimizeWindow(windowState.id)} />
                    <button aria-label={windowState.maximized ? "Verkleinen" : "Maximaliseren"} type="button" onClick={() => toggleMaximizeWindow(windowState.id)} />
                    <button aria-label="Sluiten" className="is-close" type="button" onClick={() => closeWindow(windowState.id)} />
                  </div>
                </header>
                <div className="win11-window-content">
                  {renderAppSurface(windowState.appId, windowActions, busyActionId, runAction, activeSimulatorState, openApp, windowState)}
                </div>
                {!windowState.maximized ? (
                  <span className="win11-resize-handle" aria-hidden="true" onMouseDown={(event) => startWindowResize(event, windowState)} />
                ) : null}
              </section>
            );
          })}

        <button
          className="win11-proof-toggle"
          type="button"
          aria-expanded={proofOpen}
          aria-label="Command-bus bewijs"
          onClick={() => setProofOpen((current) => !current)}
        >
          <WinGlyph name="fact_check" />
          <strong>{commandLog.length}</strong>
        </button>

        {proofOpen ? (
          <aside className="win11-proof-panel" aria-label="Command-bus bewijs">
            <strong>Command-bus bewijs</strong>
            {commandLog.length === 0 ? (
              <p>Nog geen commando's in deze sessie.</p>
            ) : (
              <ul>
                {commandLog.map((entry) => (
                  <li className={entry.ok ? "is-ok" : "is-error"} key={entry.id}>
                    <span>{entry.label}</span>
                    <strong>{entry.ok ? "OK" : `HTTP ${entry.status || "?"}`}</strong>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        ) : null}

        {startOpen ? (
          <section className="win11-start-menu" aria-label="Startmenu" onClick={(event) => event.stopPropagation()}>
            <label className="win11-start-search">
              <WinGlyph name="search" />
              <input
                placeholder="Zoeken naar apps, instellingen en bestanden"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    openFirstSearchResult();
                  }
                }}
              />
            </label>
            <div className="win11-start-head">
              <strong>{query.trim() ? "Zoekresultaten" : allAppsOpen ? "Alle apps" : "Vastgemaakt"}</strong>
              <button className="win11-start-all-apps" type="button" onClick={() => setAllAppsOpen((current) => !current)}>
                {allAppsOpen ? "Vastgemaakt" : "Alle apps"}
              </button>
            </div>
            <div className="win11-start-grid">
              {visibleStartApps.map((app) => {
                return (
                  <button key={app.appId} type="button" onClick={() => openApp(app.appId)}>
                    <AppIcon appId={app.appId} className="win11-start-app-icon" />
                    <span>{app.name}</span>
                    <small>{app.machine === "both" ? app.family : `${app.family} - ${app.machine}`}</small>
                  </button>
                );
              })}
            </div>
            <footer>
              <span>{machineLabel(activeMachine)}</span>
              <div className="win11-start-footer-actions">
                <button type="button" onClick={() => { setRunOpen(true); setStartOpen(false); }}>Uitvoeren...</button>
                <button type="button" onClick={() => setStartOpen(false)}>Sluiten</button>
              </div>
            </footer>
          </section>
        ) : null}

        {runOpen ? (
          <section className="win11-run-dialog" role="dialog" aria-label="Run" onClick={(event) => event.stopPropagation()}>
            <header>
              <strong>Uitvoeren</strong>
              <button type="button" aria-label="Sluiten" onClick={() => setRunOpen(false)}>x</button>
            </header>
            <p>Typ de naam van een programma, map, document of internetbron.</p>
            <label>
              Open:
              <input
                autoFocus
                value={runValue}
                onChange={(event) => setRunValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") executeRunCommand();
                  if (event.key === "Escape") setRunOpen(false);
                }}
              />
            </label>
            <footer>
              <button type="button" onClick={executeRunCommand}>OK</button>
              <button type="button" onClick={() => setRunOpen(false)}>Annuleren</button>
            </footer>
          </section>
        ) : null}

        {contextMenu && contextMenuPosition ? (
          <div
            className="win11-context-menu"
            style={contextMenuPosition}
            role="menu"
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" role="menuitem" onClick={() => setContextMenu(null)}>Beeld</button>
            <button type="button" role="menuitem" onClick={() => setContextMenu(null)}>Sorteren op</button>
            <button type="button" role="menuitem" onClick={() => setContextMenu(null)}>Vernieuwen</button>
            <hr />
            <button type="button" role="menuitem" onClick={() => openApp("settingsSystem")}>Beeldscherminstellingen</button>
            <button type="button" role="menuitem" onClick={() => { setContextMenu(null); setRunOpen(true); }}>Uitvoeren...</button>
          </div>
        ) : null}

        {elevatedPrompt ? (
          <section className="win11-uac-prompt" role="dialog" aria-label="Gebruikersaccountbeheer" onClick={(event) => event.stopPropagation()}>
            <header>
              <AppIcon appId={elevatedPrompt.appId} className="win11-uac-app-icon" />
              <strong>Gebruikersaccountbeheer</strong>
            </header>
            <main>
              <p className="win11-uac-title">Wilt u toestaan dat deze app wijzigingen aanbrengt op dit apparaat?</p>
              <div className="win11-uac-details">
                <span>Programmanaam:</span><strong>{elevatedPrompt.appName}</strong>
                <span>Geverifieerde uitgever:</span><strong>Microsoft Windows</strong>
                <span>Bestandsoorsprong:</span><strong>Harde schijf op deze computer</strong>
              </div>
            </main>
            <footer>
              <button type="button" onClick={() => setElevatedPrompt(null)}>Ja</button>
              <button type="button" onClick={() => setElevatedPrompt(null)}>Nee</button>
            </footer>
          </section>
        ) : null}

        <footer className="win11-taskbar" onClick={(event) => event.stopPropagation()}>
          <div className="win11-taskbar-left">
            <button className="win11-start-button" type="button" aria-expanded={startOpen} aria-label="Start" onClick={() => { setContextMenu(null); setRunOpen(false); setStartOpen((current) => !current); }}>
              <span className="win11-start-glyph" aria-hidden="true"><span /><span /><span /><span /></span>
            </button>
            <label className="win11-taskbar-search">
              <WinGlyph name="search" />
              <input
                placeholder="Zoeken"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onFocus={() => setStartOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    openFirstSearchResult();
                  }
                }}
              />
            </label>
          </div>
          <div className="win11-taskbar-pinned">
            {taskbarPinnedApps.map((app) => {
              const runningWindow = windows.find((windowState) => windowState.appId === canonicalAppId(app.appId));
              const isActive = runningWindow ? activeWindowId === runningWindow.id && !runningWindow.minimized : false;
              return (
                <button
                  className={runningWindow ? `is-running ${isActive ? "is-active" : ""}` : ""}
                  key={app.appId}
                  title={app.name}
                  type="button"
                  onClick={() => handleTaskbarAppClick(app.appId)}
                >
                  <AppIcon appId={app.appId} className="win11-taskbar-app-icon" />
                </button>
              );
            })}
            {windows.filter((windowState) => !taskbarPinnedIds.map(canonicalAppId).includes(windowState.appId)).map((windowState) => {
              return (
                <button
                  className={`is-running ${activeWindowId === windowState.id && !windowState.minimized ? "is-active" : ""}`}
                  key={windowState.id}
                  title={windowState.title}
                  type="button"
                  onClick={() => handleTaskbarAppClick(windowState.appId)}
                >
                  <AppIcon appId={windowState.appId} className="win11-taskbar-app-icon" />
                </button>
              );
            })}
          </div>
          <div className="win11-taskbar-tray">
            <button aria-expanded={trayFlyout === "quick"} aria-label="Snelle instellingen" type="button" onClick={() => setTrayFlyout((current) => current === "quick" ? null : "quick")}>
              <span className="win11-tray-status-icons" aria-hidden="true">
                <span className="win11-tray-wifi"><span /><span /><span /></span>
                <span className="win11-tray-volume"><span /></span>
              </span>
            </button>
            <button aria-expanded={trayFlyout === "calendar"} aria-label="Kalender en klok" type="button" onClick={() => setTrayFlyout((current) => current === "calendar" ? null : "calendar")}>
              {clock.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
              <small>{clock.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric" })}</small>
            </button>
          </div>
        </footer>

        {trayFlyout === "quick" ? (
          <aside className="win11-quick-settings" aria-label="Snelle instellingen" onClick={(event) => event.stopPropagation()}>
            <div className="win11-quick-grid">
              <button className="is-on" type="button" onClick={() => openApp("netSettings")}><WinGlyph name="wifi" /><strong>Wi-Fi</strong><small>FirstDesk-Lab</small></button>
              <button className="is-on" type="button" onClick={() => openApp("settingsSystem", "bluetooth")}><WinGlyph name="bluetooth" /><strong>Bluetooth</strong><small>Aan</small></button>
              <button type="button" onClick={() => openApp("ncpa")}><WinGlyph name="airplanemode_active" /><strong>Vliegtuigstand</strong><small>Uit</small></button>
              <button className="is-on" type="button" onClick={() => openApp("ncpa")}><WinGlyph name="vpn_key" /><strong>VPN</strong><small>Lab</small></button>
            </div>
            <label className="win11-slider"><WinGlyph name="volume_up" /><input max="100" min="0" readOnly type="range" value="62" /></label>
            <button className="win11-quick-link" type="button" onClick={() => openApp("netSettings")}>Netwerk- en internetinstellingen</button>
          </aside>
        ) : null}

        {trayFlyout === "calendar" ? (
          <aside className="win11-calendar-flyout" aria-label="Kalender" onClick={(event) => event.stopPropagation()}>
            <header><strong>{clock.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}</strong><span>{clock.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</span></header>
            <div className="win11-calendar-month">{clock.toLocaleDateString("nl-NL", { month: "long", year: "numeric" })}</div>
            <div className="win11-calendar-grid" aria-hidden="true">
              {["ma", "di", "wo", "do", "vr", "za", "zo"].map((day) => <span key={day}>{day}</span>)}
              {Array.from({ length: 35 }, (_, index) => (
                <button className={index + 1 === clock.getDate() ? "is-today" : ""} key={index} type="button" onClick={() => openApp("settingsSystem")}>{index + 1 <= 30 ? index + 1 : ""}</button>
              ))}
            </div>
            <button className="win11-quick-link" type="button" onClick={() => openApp("settingsSystem")}>Datum- en tijdinstellingen</button>
          </aside>
        ) : null}
      </div>
    </section>
  );
}

